import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createChart, IChartApi, ISeriesApi, CandlestickData, Time, CandlestickSeries, LineSeries, HistogramSeries, MouseEventParams, MismatchDirection, type SeriesType } from 'lightweight-charts';
import { SkippedMarkerPrimitive, SkippedMarker } from './Chart/SkippedMarkerPrimitive';
import { VolumeMarkerPrimitive, VolumeMarker } from './Chart/VolumeMarkerPrimitive';
import { SignalMarkerPrimitive, SignalMarkerData } from './Chart/SignalMarkerPrimitive';
import { NewsMarkerPrimitive, NewsMarker } from './Chart/NewsMarkerPrimitive';
import { CalendarLinePrimitive, CalendarLine } from './Chart/CalendarLinePrimitive';
import { UpcomingEventsPrimitive, UpcomingEvent } from './Chart/UpcomingEventsPrimitive';
import { FVGZonePrimitive } from './Chart/FVGZonePrimitive';
import { TrailingCandlePrimitive, TrailingCandleData, TrailingCandleColors } from './Chart/TrailingCandlePrimitive';
import { CumulativeDeltaPrimitive } from './Chart/CumulativeDeltaPrimitive';
import { VolumeProfilePrimitive } from './Chart/VolumeProfilePrimitive';
import { PriceLevelLabelsPrimitive, PriceLevelLabel } from './Chart/PriceLevelLabelsPrimitive';
import { RoundLevelWarningPrimitive } from './Chart/RoundLevelWarningPrimitive';
import { createPriceScaleWatcherPrimitive } from './Chart/PriceScaleWatcherPrimitive';
import { BosLabelPrimitive, BosOverlayItem } from './Chart/BosLabelPrimitive';
import { FairPricePrimitive, FairPriceAnchorItem, FairPriceSignalItem } from './Chart/FairPricePrimitive';
import { DisplacementPrimitive, DisplacementLevel } from './Chart/DisplacementPrimitive';
import { AbsorptionMarkerPrimitive, toAbsorptionItems } from './Chart/AbsorptionMarkerPrimitive';
import { useAbsorptionStore, absorptionTooltip } from '../store/absorptionStore';
import { FlowSignalMarkerPrimitive, toFlowItems, flowBarTime, setFlowPlacedData, holdFraction } from './Chart/FlowSignalMarkerPrimitive';
import { useFlowSignalsStore, flowTooltip, onLiveFlowSignals, FLOW_LEVEL_KEYS, FLOW_MASTER_KEY, type FlowLevelKey, type FlowSignal } from '../store/flowSignalsStore';
import TradeFinderToggle from './TradeFinder/TradeFinderToggle';
import TradeFinderStrip from './TradeFinder/TradeFinderStrip';
import { recentSignals } from './TradeFinder/SignalsTab';
import { currentEdgeByKind, levelTolFor, markerStyle, PULSE_MS, type MarkerStyleOptions } from './TradeFinder/tradeFinderUx';
import { useTradeFinderUxStore } from '../store/tradeFinderUxStore';
import { useFlowAlertsStore } from '../store/flowAlertsStore';
import { chimeForLive } from '../services/tradeFinderSound';
import { useFlowLinesStore, FLOW_LINES, type FlowLineName } from '../store/flowLinesStore';
import {
  KEY_LEVEL_CATEGORY, categoryForPriceLevelLabel, leftLabelTarget, paneTitleTarget, axisTagTarget,
  fixAxisTagOverlap, axisLabelHeight,
  hitTestLabelTargets, isClickNotDrag, hideCategoryWithUndo, SCALE_DBLCLICK_WAIT_MS, hideSRTimeframeWithUndo, exposeLevelLabels, type LabelTarget, type HideCategoryId,
} from './Chart/levelLabelHide';
import { FlowLinePanes, snapLinePoints, snapToBar, appendedPoints, isLinePaneEvent, linePaneSpan, belowPricePane, type PriceOverlayItem } from './Chart/flowLinePanes';
import { PricePaneClip, PaneEdgeBadges, priceOverlayItems, sameOverlayItems, type PriceOverlaySources } from './Chart/PricePaneClip';
import QueueTiltBand from './Chart/QueueTiltBand';
import { useSymbolChartDisplayStore, CHART_DISPLAY_REFRESH_MS } from '../store/symbolChartDisplayStore';
import { effectiveCmeSpikeSettings, effectiveWickRule } from '../utils/chartDisplayOverrides';
import { candleRangeLabel, displayVolume, priceSeriesCandles, priceSeriesLastBar, formatDisplayVolume, isVestFeedSymbol, tickDecimals, watermarkAtr, compensatedVisibleRange, vestPrevBarRedraw, vestTickSize, volumeDivisorFor, volumePaneData } from '../utils/vestFeed';
import { aggregateVestBars, chartContractName, chartPointValue, isVestVolumeSymbol, tailUpdates as vestTailUpdates, vestBarData, VestBarPoint } from '../utils/vestVolume';
import { VestVolumeSeriesView } from './Chart/VestVolumeSeries';
import { sharedPriceScaleWidth } from '../utils/paneSync';
import { VolumeSpikeMarkersPrimitive, VolumeSpikeItem } from './Chart/VolumeSpikeMarkersPrimitive';
import VolumePaneLegend from './Chart/VolumePaneLegend';
import { useVolumePaneSettingsStore } from '../store/volumePaneSettingsStore';
import { useVestCandleOverlayStore } from '../store/vestCandleOverlayStore';
import { VestCandleToggle } from './Chart/VestCandleToggle';
import { candleTailUpdates, cmeClosedCloses, latestGap, rollingGaps, vestCandleData, VEST_CANDLE_COLORS, type VestCandle } from '../utils/vestCandles';
import { useMesCandleOverlayStore } from '../store/mesCandleOverlayStore';
import { useMesCandleFeedStore } from '../store/mesCandleFeedStore';
import { MesCandleToggle } from './Chart/MesCandleToggle';
import { closedPairs, latestRatio, mesCandleData, mesCloses, MES_CANDLE_COLORS, resolveMesSymbol, rollingRatios, type MesCandle } from '../utils/mesCandles';
import { closedCmeRelVols, cmeVolumeColor, relVolLabel, withBaselineHistory } from '../utils/cmeVolumeSpikes';
import { useVestVolumeStore } from '../store/vestVolumeStore';
import { useCmeBaselineHistoryStore } from '../store/cmeBaselineHistoryStore';
import { summarizeRelVols, useRelVolStatusStore } from '../store/relVolStatusStore';
import { baselineFromMs } from '../utils/relVolBaseline';
import { useVestSpikeSettingsStore } from '../store/vestSpikeSettingsStore';
import { CandleSpikePrimitive } from './Chart/CandleSpikePrimitive';
import { candleSpikeMarkers, candleSpikeTooltip } from '../utils/volumeAgreement';
import { useCmeDeltaStore, cmeDeltaByChartBar } from '../store/cmeDeltaStore';
import { buildOccupancy, clampSpikeSettings, computeVestSpikes, vestRelVolsFor, occupancyKey, signalStackPx, MARKER_HEIGHTS, Occupancy } from '../utils/vestSpikes';
import { closedCmeVolumes, extrudingCatSlots, vestCats } from '../utils/vestCmeCat';
import { PaneGeometryWatcherPrimitive } from './Chart/PaneGeometryWatcherPrimitive';
import { closedWickBars, computeWickRejections, gradeWicks, visibleWicks, wickInputBars, type GradedWick } from '../utils/wickRejection';
import { useWickRejectionSettingsStore } from '../store/wickRejectionSettingsStore';
import { ABSORPTION_OFFSET, ABSORPTION_STACK } from './Chart/AbsorptionMarkerPrimitive';
import { CandidatePrimitive, CandidateMarker, markerStateFor } from './Chart/CandidatePrimitive';
import { TradeMarkerPrimitive } from './Chart/TradeMarkerPrimitive';
import { useTradeMarkerFeed, TRADE_MARKER_POLL_MS } from '../hooks/useTradeMarkerFeed';
import { resolveNetPositionAnchor } from '../utils/tradeMarkers';
import {
  useTradeMarkerStore,
  useTradeMarkersEnabled,
  useTradeMarkerLinksEnabled,
} from '../store/tradeMarkerStore';
import { DonchianMarkerPrimitive, DonchianMarker as DonchianPrimitiveMarker } from './Chart/DonchianMarkerPrimitive';
import { TrailingPullbackMarkerPrimitive, TrailingPullbackMarker as TPPrimitiveMarker } from './Chart/TrailingPullbackMarkerPrimitive';
import { MagnetCandlePrimitive, MagnetLevel } from './Chart/MagnetCandlePrimitive';
import { MarkerLayoutRegistry, chartLayoutEnv, exposeMarkerLayout, markerChipTooltip } from './Chart/markerLayout';
import { MarkerChipPrimitive, MarkerLeaderPrimitive } from './Chart/MarkerChipPrimitive';
import { LayoutSeriesMarkersPrimitive } from './Chart/LayoutSeriesMarkersPrimitive';
import { useVolumeProfileStore } from '../store/volumeProfileStore';
import { useMarketStore, ExtendedCandle } from '../store/marketStore';
import { useBrokerStore, brokerSelectors, useConnectedBrokers, useActiveBrokerOrders, useActiveBrokerPositions, BrokerPosition } from '../store/brokerStore';
import { addToast, useToastStore } from '../store/toastStore';
import { useSettingsStore, DEFAULT_BOS_SETTINGS, BreakOfStructureSettings } from '../store/settingsStore';
import { useSignalPredictorStore } from '../store/signalPredictorStore';
import { useFairPriceStore } from '../store/fairPriceStore';
import type { FairPriceAnchorKind } from '../store/fairPriceStore';
import { useDisplacementStore } from '../store/displacementStore';
import { useCandidateStore, hydrateThresholdsFromEngine, hydrateBotThresholdFromEngine, engineDiamondKey } from '../store/candidateStore';
import CandidateReviewPanel from './CandidateReviewPanel';
import CandidateQuickLabel from './CandidateQuickLabel';
import FloatingOrderPanel from './FloatingOrderPanel';
import { planManualOrder } from '../utils/exitOnly';
import { buildReviewSet, normaliseVerdict, autoTagCandidates } from '../utils/setupCandidates';
import FairPriceStatusChip from './FairPriceStatusChip';
import FairPriceSettingsModal from './FairPriceSettingsModal';
import DisplacementSettingsModal from './DisplacementSettingsModal';
import { useThemeStore } from '../store/themeStore';
import { useSymbolStore } from '../store/symbolStore';
import { usePreferences, updatePreferenceDirect } from '../hooks/usePreferences';
import { wsService } from '../services/WebSocketService';
import { getApiBaseUrl } from '../config/ports';
import { apiFetch } from '../services/api';
import DateRangeSelector from './DateRangeSelector';
import BacktestControls from './BacktestControls';
import GlobalSettingsModal from './GlobalSettingsModal';
import { getBacktestPlayer } from '../services/BacktestPlayer';
import { Candle, DateRangePreset, PinningVolumeAnalysis, WholeCandleAnalysis, SRLevel } from '@shared/types';
import TradingPanel from './TradingPanel';
import ActionBarTrading from './ActionBarTrading';
import LiveLog from './LiveLog';
import ThemeSelector from './ThemeSelector';
import OverlayLibrary from './OverlayLibrary';
import { useOverlayLibraryStore } from '../store/overlayLibraryStore';
import SymbolSelector from './SymbolSelector';
import { SignalInfoOverlay, AccountStatsOverlay, PinningVolumeInfoOverlay, WholeCandleContinuationInfoOverlay, ConfidenceInfoOverlay, FairPriceCoachOverlay, DiamondInfoOverlay, DiamondStatsOverlay, DiamondSignalExcursionOverlay, TradeMarkerInfoOverlay, GexInfoOverlay, InfoOverlayStack, InfoOverlayItem } from './info-overlays';
import FairPriceAnchorTooltip from './info-overlays/FairPriceAnchorTooltip';
import DrawingToggle from './DrawingToggle';
import { Calculator, Radar, Receipt } from 'lucide-react';
import { DrawingPrimitive } from './Chart/DrawingPrimitive';
import { useDrawingStore } from '../store/drawingStore';
import { useDrawingInteraction } from '../hooks/useDrawingInteraction';
import { ChartOverlays } from './Chart';
import { FibLevelsOverlayHandle } from './FibLevelsOverlay';
import { LivePositionLineHandle } from './LivePositionLine';
import { useVestPositionLines } from './Chart/useVestPositionLines';
import { VestTpslHandles } from './Chart/VestTpslHandles';
import { useVestSizingLines } from './Chart/useVestSizingLines';
import { VestSizingLegend } from './Chart/VestSizingLegend';
import { LiveOrderLineHandle } from './LiveOrderLine';
import { OrderLineHandle } from './OrderLine';
import PendingOrderLine from './PendingOrderLine';
import type { PendingOrder } from '@shared/types';
// Volume spike markers use native TradingView markers (arrowUp/arrowDown)
import { ActionBarPositions } from './ActionBarPositions';
import { strategyService } from '../services/StrategyService';
import { recordingService } from '../services/RecordingService';
import { CONFIG } from '../config';
import { roundToTick, getInstrumentSpec, isAlwaysOpen, calculateDCAQuantity } from '../config/instruments';
import ErrorBoundary from './ErrorBoundary';
import PanelErrorFallback from './PanelErrorFallback';
import { Activity, Settings, Users, Copy, WifiOff, Bell, Bot, BarChart3, ChevronsLeft, ChevronsRight, Eye } from 'lucide-react';
import { isAtOldestLoadedBar, oldestLogicalRange, targetLogicalRange } from '../utils/chartEdges';
import { findBarAt, findBarIndexAtOrAfter } from '../utils/findBarAt';
import {
  backendEmasAvailableForHistory,
  computeSeries,
  foldFormingCandle,
} from '../utils/candleSeries';
import GoToDatePopup, { GoToDateFetchState } from './GoToDatePopup';
import { DayKey, LoadedSpan, PickResolution } from '../utils/goToDate';
import SRToggle from './SRToggle';
import LevelsToggle from './LevelsToggle';
import CleanToggle from './CleanToggle';
import { GexLevelsPrimitive } from './Chart/GexLevelsPrimitive';
import { useGexStore, selectGexSlot, gexUnderlyingForSymbol, payloadUnderlying } from '../store/gexStore';
import type { GexUnderlying, GexLevels } from '../store/gexStore';
import { computeOnhOnl } from '../utils/onhOnl';
import { ConfluenceZonePrimitive } from './Chart/ConfluenceZonePrimitive';
import { useProfileLevelsStore, useKeyLevelPrefsStore } from '../store/profileLevelsStore';
import {
  gexKeyLevels, profileKeyLevels, sessionKeyLevels, keyLevelExplanation, gexSourceLine, gexMethodLine,
  onhOnlKeyLevels, ONH_ONL_DEVELOPING_NOTE,
  hitTestY, isGexKind, KeyLevel,
} from '../utils/keyLevels';
import { computeConfluenceZones, confluenceLabelFor, confluenceToleranceFor, confluenceTooltipFor } from '../utils/confluence';
import KeyLevelTooltip, { KeyLevelTooltipData } from './info-overlays/KeyLevelTooltip';
import { decideHover, isCoarsePointer, isPointerEvent, MARKER_HOVER_GRACE_MS } from './Chart/markerHover';
import SignalsMenu from './SignalsMenu';
import { useNewsStore, useBreakingNewsForChart, useCalendarEventsForChart } from '../store/newsStore';
import {
  useSRStore,
  SR_TIMEFRAME_CONFIG,
  SR_LABEL_GROUP_THRESHOLD_PX,
  buildSRLabelContent,
  groupSRLevelsByProximity,
  srGroupKey,
  toggleSRExpansion,
} from '../store/srStore';
import { SRLabel, SRLabelGroup } from './Chart/SRLabel';
import { useLevelsStore } from '../store/levelsStore';
import { useCleanChartStore, CleanTarget, cleanGroupForLevel, isCleanHidden } from '../store/cleanChartStore';
import { installPrimitiveRegistry, getPrimitiveRegistry } from '../utils/primitiveRegistry';
import { cleanGroupMap, makeCleanSuppressor } from '../utils/cleanSuppression';
import { useDonchianStore } from '../store/donchianStore';
import { useBosStore, BosMarker } from '../store/bosStore';
import { useDonchianSettingsStore } from '../store/donchianSettingsStore';
import { DonchianOverlay } from './Chart/DonchianOverlay';
import BrokerManagementModal from './BrokerManagementModal';
import CopyTradingSettingsModal from './CopyTradingSettingsModal';
import { AutoBotPanel } from './AutoBotPanel';
import { useAutoBotGlobalEnabled, useAutoBotActiveBots, initializeAutoBotStore, useAutoBotStore } from '../store/autoBotStore';
import AccountStatsModal from './AccountStatsModal';
import TradeAnalyzerModal from './TradeAnalyzerModal';
import TradeAnalyticsModal from './TradeAnalyticsModal';
import { ActionBarHealth } from './ActionBarHealth';
import { CMEInfoOverlay } from './info-overlays';
import { AnimatedLogo } from './AnimatedLogo';
import { AnimatedRainbowBorder } from './AnimatedRainbowBorder';
import { useIsMobile } from '../hooks/useIsMobile';
import NewsTicker from './NewsTicker';
import NewsEventsOverlay from './NewsEventsOverlay';
import TakeItBanner from './TakeItBanner';
import { AlertLine } from './AlertLine';
import { AlertButton } from './ActionBarTrading/AlertButton';
import { AlertsPanel } from './AlertsPanel';
import { AlertSettingsModal } from './AlertSettingsModal';
import { useAlertStore, Alert } from '../store/alertStore';
import './ChartView.css';
import { normalizeToBase } from '../utils/symbolUtils';
import { computeMocLevel, MOC_LABEL } from '../utils/mocLevel';

type Timeframe = '1m' | '2m' | '3m' | '5m' | '10m' | '15m' | '30m' | '1h' | '2h' | '4h' | '1D';

const TIMEFRAME_MINUTES: Record<Timeframe, number> = {
  '1m': 1,
  '2m': 2,
  '3m': 3,
  '5m': 5,
  '10m': 10,
  '15m': 15,
  '30m': 30,
  '1h': 60,
  '2h': 120,
  '4h': 240,
  '1D': 1440,
};

// Module-level tracking to survive React StrictMode double-mount
// Tracks which symbol+timeframe combos have been initialized this session
// This prevents visible range from being reset on component remount
const initializedCharts = new Set<string>();

// Helper to get a unique key for symbol+timeframe
const getChartKey = (symbol: string, timeframe: string): string => `${symbol}:${timeframe}`;

// Fixed Y-axis range per symbol (in points)
// This determines how many points are visible on the Y-axis
const SYMBOL_Y_AXIS_RANGE: Record<string, number> = {
  MNQ: 70,   // Micro Nasdaq futures
  NQ: 100,   // E-mini Nasdaq futures
  MES: 20,   // Micro S&P futures
  ES: 30,    // E-mini S&P futures
  SIL: 2,    // Micro Silver futures
  'ETHUSD-BITFINEX': 200,
  'SOLUSDT-BMEX': 20,
  'BTC-PERP.VEST': 500,
  'ETH-PERP.VEST': 30,
  'SOL-PERP.VEST': 1.25,
};

// Get Y-axis range for a symbol (defaults to 100 if unknown)
const getYAxisRange = (symbol: string): number => {
  const baseSymbol = normalizeToBase(symbol);
  return SYMBOL_Y_AXIS_RANGE[baseSymbol] || 100;
};

// Get price format precision based on symbol type
const getSymbolPriceFormat = (symbol: string): { minMove: number; precision: number } => {
  const base = normalizeToBase(symbol);
  const symbolStore = useSymbolStore.getState();
  const symDef = symbolStore.getSymbol(base);
  if (symDef) {
    const tickSize = symDef.tickSize;
    const precision = (String(tickSize).split('.')[1] || '').length;
    return { minMove: tickSize, precision };
  }
  return { minMove: 0.25, precision: 2 };
};

const markerMatchesChartSymbol = (markerSymbol: string | undefined, chartSymbol: string): boolean => {
  return normalizeToBase(markerSymbol || '') === normalizeToBase(chartSymbol);
};

interface ChartViewProps {
  onSymbolChange?: (symbol: string) => void;
  showBacktestModal?: boolean;
  onBacktestModalClose?: () => void;
  onBacktestClick?: () => void;
  connectionStatus?: string;
  marketStatus?: string;
  /** Whether to show trading overlays (position lines, order lines) - only active panel in grid mode */
  showTradingOverlays?: boolean;
}

/**
 * Which primitives each Clean group hides, by class. Read by the candlestick
 * series' primitive registry at attach time and whenever Clean changes, so a
 * primitive created while Clean is on (symbol switch, feature toggled on) is
 * hidden from the start. Anything not listed is never touched by Clean — level
 * toggles such as absorption or the spike markers are masked through
 * levelVisibility instead, so their owners remove them.
 */
const CLEAN_PRIMITIVE_GROUPS = cleanGroupMap({
  diamonds: [CandidatePrimitive],
  signals: [SignalMarkerPrimitive, SkippedMarkerPrimitive, VolumeMarkerPrimitive,
    DonchianMarkerPrimitive, BosLabelPrimitive],
  trades: [TradeMarkerPrimitive],
  news: [NewsMarkerPrimitive, CalendarLinePrimitive, UpcomingEventsPrimitive],
  volumeStudies: [VolumeProfilePrimitive, CumulativeDeltaPrimitive],
  structure: [FVGZonePrimitive, DisplacementPrimitive, MagnetCandlePrimitive,
    TrailingCandlePrimitive, TrailingPullbackMarkerPrimitive, FairPricePrimitive,
    RoundLevelWarningPrimitive],
  priceLabels: [PriceLevelLabelsPrimitive],
  // GEX walls and confluence zones are price levels.
  levels: [GexLevelsPrimitive, ConfluenceZonePrimitive],
});

const ChartView: React.FC<ChartViewProps> = ({
  onSymbolChange,
  showBacktestModal,
  onBacktestModalClose,
  onBacktestClick,
  connectionStatus = 'disconnected',
  showTradingOverlays = true,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const drawingPrimitiveRef = useRef<DrawingPrimitive | null>(null);
  const [drawingPrimitive, setDrawingPrimitive] = useState<DrawingPrimitive | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const seriesMarkersRef = useRef<LayoutSeriesMarkersPrimitive | null>(null);
  // One shared layout for every per-bar glyph on the price pane (Chart/markerLayout.ts).
  const markerLayoutRef = useRef<MarkerLayoutRegistry | null>(null);
  // Volume pane - separate chart for volume with synced time axis
  const volumeChartContainerRef = useRef<HTMLDivElement>(null);
  const volumeChartRef = useRef<IChartApi | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const [volumeChartReady, setVolumeChartReady] = useState<boolean>(false);
  // Volume pane resize state
  const DEFAULT_VOLUME_PANE_HEIGHT = 80; // Default height in pixels (smaller)
  const MIN_VOLUME_PANE_HEIGHT = 50;
  const MAX_VOLUME_PANE_HEIGHT = 300;
  const [volumePaneHeight, setVolumePaneHeight] = useState<number>(DEFAULT_VOLUME_PANE_HEIGHT);
  const [isResizingVolumePane, setIsResizingVolumePane] = useState<boolean>(false);
  const volumePaneResizeStartY = useRef<number>(0);
  const volumePaneResizeStartHeight = useRef<number>(0);
  const ema9SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const ema21SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const ema200SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const vwapSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const candleRangeMarkersRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const countdownYAxisRef = useRef<HTMLDivElement | null>(null);
  const updateRangeMarkerRef = useRef<(() => void) | null>(null);
  const hodLineRef = useRef<any>(null); // HOD (High of Day) price line
  const lodLineRef = useRef<any>(null); // LOD (Low of Day) price line
  // Price level lines (RTH, PDH/PDL, IB, Midnight Open, Weekly/Monthly)
  const rthHodLineRef = useRef<any>(null);
  const rthLodLineRef = useRef<any>(null);
  const pdhLineRef = useRef<any>(null);
  const pdlLineRef = useRef<any>(null);
  const ibHighLineRef = useRef<any>(null);
  const ibLowLineRef = useRef<any>(null);
  const midnightOpenLineRef = useRef<any>(null);
  const mocOpenLineRef = useRef<any>(null); // MOC imbalance (open of the 14:50 CT bar)
  const prevWeekHighLineRef = useRef<any>(null);
  const prevWeekLowLineRef = useRef<any>(null);
  const currWeekHighLineRef = useRef<any>(null);
  const currWeekLowLineRef = useRef<any>(null);
  const prevMonthHighLineRef = useRef<any>(null);
  const prevMonthLowLineRef = useRef<any>(null);
  const srLinesRef = useRef<Map<string, any>>(new Map()); // S/R price lines (keyed by price-timeframe)
  const skippedMarkerPrimitiveRef = useRef<SkippedMarkerPrimitive | null>(null); // Custom primitive for skipped markers
  const volumeMarkerPrimitiveRef = useRef<VolumeMarkerPrimitive | null>(null); // Custom primitive for volume emoji markers
  const donchianMarkerPrimitiveRef = useRef<DonchianMarkerPrimitive | null>(null); // Custom primitive for Donchian breakout markers
  const signalMarkerPrimitiveRef = useRef<SignalMarkerPrimitive | null>(null); // Custom primitive for signal markers (confidence, ML, profitability)
  const newsMarkerPrimitiveRef = useRef<NewsMarkerPrimitive | null>(null); // Custom primitive for breaking news markers
  const calendarLinePrimitiveRef = useRef<CalendarLinePrimitive | null>(null); // Custom primitive for calendar event lines
  const upcomingEventsPrimitiveRef = useRef<UpcomingEventsPrimitive | null>(null); // Custom primitive for upcoming event stars
  const gexLevelsPrimitiveRef = useRef<GexLevelsPrimitive | null>(null); // Gamma ladder (walls/zero gamma are tagged price lines)
  const confluencePrimitiveRef = useRef<ConfluenceZonePrimitive | null>(null); // Stacked-level zones
  // Tagged key-level price lines (GEX + volume profile), with the level each draws.
  const keyLevelLinesRef = useRef<{ level: KeyLevel; line: any }[]>([]);
  const fvgZonePrimitiveRef = useRef<FVGZonePrimitive | null>(null); // Custom primitive for FVG/IFVG zones
  const trailingCandlePrimitiveRef = useRef<TrailingCandlePrimitive | null>(null); // Custom primitive for trailing OHLC mini-candles
  const volumeProfilePrimitiveRef = useRef<VolumeProfilePrimitive | null>(null); // Custom primitive for volume profile histogram
  const cumulativeDeltaPrimitiveRef = useRef<CumulativeDeltaPrimitive | null>(null); // Custom primitive for cumulative delta line
  const priceLevelLabelsPrimitiveRef = useRef<PriceLevelLabelsPrimitive | null>(null); // Custom primitive for price level labels at left edge
  const roundLevelWarningPrimitiveRef = useRef<RoundLevelWarningPrimitive | null>(null); // ±10 band around round 100s / 50s
  const bosLabelPrimitiveRef = useRef<BosLabelPrimitive | null>(null); // Custom primitive for BOS/CHoCH text labels rendered on the level segment line
  const trailingPullbackPrimitiveRef = useRef<TrailingPullbackMarkerPrimitive | null>(null); // Custom primitive for Trailing Pullback markers
  const magnetCandlePrimitiveRef = useRef<MagnetCandlePrimitive | null>(null); // Custom primitive for magnet candle levels
  const fairPricePrimitiveRef = useRef<FairPricePrimitive | null>(null); // Custom primitive for Fair Price anchors/signals (attached only while enabled)
  const displacementPrimitiveRef = useRef<DisplacementPrimitive | null>(null); // Custom primitive for displacement candles (attached only while enabled)
  const absorptionPrimitiveRef = useRef<AbsorptionMarkerPrimitive | null>(null); // "ABS PW" absorption-at-level dots (attached only while shown)
  const flowPrimitiveRef = useRef<FlowSignalMarkerPrimitive | null>(null); // order-flow markers from flow-signals (attached only while any kind is shown)
  const vestSpikePrimitiveRef = useRef<CandleSpikePrimitive | null>(null); // 🐋 / 🦈 / 🐋🦈 volume spikes on candles (attached only while shown)
  // Volume pane: Vest thin bars (custom series), unusual-volume markers, CME spike bars drawn brighter
  const vestSeriesRef = useRef<ISeriesApi<'Custom'> | null>(null);
  const [vestSeriesVersion, setVestSeriesVersion] = useState(0);
  const volumeMarkersRef = useRef<VolumeSpikeMarkersPrimitive | null>(null);
  const cmeSpikeTimesRef = useRef<Set<number>>(new Set());
  // px other candle markers use beyond each candle's high/low ("time:side"), so the whale stacks past them
  const markerOccupancyRef = useRef<Occupancy>(new Map());
  const occupiedAt = useCallback((timeSec: number, side: 'above' | 'below'): number => {
    let px = markerOccupancyRef.current.get(occupancyKey(timeSec, side)) ?? 0;
    // ABS dots stack from the same extreme: bullish under the low, bearish over the high.
    let abs = 0;
    for (const it of absorptionPrimitiveRef.current?.items() ?? []) {
      if (it.time === timeSec && (it.direction === 'bullish' ? 'below' : 'above') === side) abs += 1;
    }
    if (abs > 0) px = Math.max(px, 0) + ABSORPTION_OFFSET + (abs - 1) * ABSORPTION_STACK + 5;
    return px;
  }, []);
  const candidatePrimitiveRef = useRef<CandidatePrimitive | null>(null);
  const tradeMarkerPrimitiveRef = useRef<TradeMarkerPrimitive | null>(null);
  const lastHodLodRef = useRef<{ high: number; low: number }>({ high: 0, low: Infinity }); // Track last known HOD/LOD values
  // Long-press refs for mobile order placement (price pane)
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const longPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const [longPressPreview, setLongPressPreview] = useState<{ y: number; price: number } | null>(null);
  const [isDraggingPreview, setIsDraggingPreview] = useState(false);
  // Long-press refs for mobile volume alert (volume pane)
  const volumeLongPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const volumeLongPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const [volumeLongPressPreview, setVolumeLongPressPreview] = useState<{ y: number; volume: number } | null>(null);
  const LONG_PRESS_DURATION = 500; // milliseconds
  const LONG_PRESS_MOVE_THRESHOLD = 10; // pixels - cancel if moved more than this
  // Go-to-date popup: long-press on the TIME AXIS (not the plot).
  // Reuses the two constants above rather than picking its own numbers — one
  // long-press feel across the chart, and the 10px movement threshold is what
  // keeps axis drag-to-pan working (see the handlers further down).
  const goToDateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const goToDatePressRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const [goToDateAnchor, setGoToDateAnchor] = useState<
    { x: number; y: number; focusMs: number | null } | null
  >(null);
  const goToDateAnchorRef = useRef(false);
  const goToDateScrollRafRef = useRef<number | null>(null);
  /** In-flight "page history back until we reach this day" job. */
  const [goToDatePending, setGoToDatePending] = useState<
    { day: DayKey; timestamp: number; pages: number; lastOldest: number | null } | null
  >(null);
  const [goToDateFetch, setGoToDateFetch] = useState<GoToDateFetchState | null>(null);
  /** Pages of 720 bars (~1 day of 2m bars) we are willing to walk back for one jump. */
  const GO_TO_DATE_MAX_PAGES = 40;
  /** A page that never comes back must not leave the popup spinning forever. */
  const GO_TO_DATE_PAGE_TIMEOUT_MS = 15_000;
  // Clean Chart mode. Persisted in cleanChartStore (see the SEMANTICS block
  // there): ON hides the configured groups, OFF is exactly the user's chart.
  const cleanChart = useCleanChartStore((state) => state.active);
  const toggleCleanChart = useCleanChartStore((state) => state.toggleActive);
  // Triple-tap refs for mobile news overlay toggle
  const tripleTapCountRef = useRef(0);
  const tripleTapTimerRef = useRef<NodeJS.Timeout | null>(null);
  // RAF coordinator: single requestAnimationFrame loop for all chart updaters
  const chartUpdatersRef = useRef<{
    rangeMarker: (() => void) | null;
    fibLevels: (() => void) | null;
    positionY: (() => void) | null;
    liveOrderPositions: (() => void) | null;
    orderLinePositions: (() => void) | null;
    srLabels: (() => void) | null;
    paneEdge: (() => void) | null;
  }>({
    rangeMarker: null,
    fibLevels: null,
    positionY: null,
    liveOrderPositions: null,
    orderLinePositions: null,
    srLabels: null,
    paneEdge: null,
  });
  const rafIdRef = useRef<number>(0);
  const scheduleChartUpdate = useCallback(() => {
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      const u = chartUpdatersRef.current;
      u.rangeMarker?.();
      u.fibLevels?.();
      u.positionY?.();
      u.liveOrderPositions?.();
      u.orderLinePositions?.();
      u.srLabels?.();
      u.paneEdge?.();
    });
  }, []);
  const [selectedTimeframe, setSelectedTimeframe] = useState<Timeframe>('2m');
  const [countdown, setCountdown] = useState<string>('');
  const [currentATR, setCurrentATR] = useState<number>(0);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [hoveredCandle, setHoveredCandle] = useState<{ time: number; open: number; high: number; low: number; close: number; volume: number } | null>(null);
  const [pinningVolumeAnalysis, setPinningVolumeAnalysis] = useState<PinningVolumeAnalysis | null>(null);
  const [isLoadingPinningAnalysis, setIsLoadingPinningAnalysis] = useState<boolean>(false);
  const [wholeCandleAnalysis, setWholeCandleAnalysis] = useState<WholeCandleAnalysis | null>(null);
  const [isLoadingWccAnalysis, setIsLoadingWccAnalysis] = useState<boolean>(false);
  const [showDateRangeSelector, setShowDateRangeSelector] = useState<boolean>(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; timestamp: number } | null>(null);
  const [orderLines, setOrderLines] = useState<Array<{ id: string; price: number; yPosition: number }>>([]);
  const [livePositionLineY, setLivePositionLineY] = useState<number | null>(null);
  const [liveOrderLinePositions, setLiveOrderLinePositions] = useState<Map<number, number>>(new Map());
  const [liveOrderLabelOffsets, setLiveOrderLabelOffsets] = useState<Map<number, number>>(new Map());
  // Draft TP/SL bracket line: a PENDING, user-draggable line that is only committed
  // to a real order once the user clicks the ✓ confirm control.
  const [draftBracket, setDraftBracket] = useState<{ kind: 'TP' | 'SL'; price: number } | null>(null);
  const [draftBracketSubmitting, setDraftBracketSubmitting] = useState(false);
  // Imperative refs for direct DOM position updates during scroll (bypass React re-renders)
  const fibLevelsOverlayRef = useRef<FibLevelsOverlayHandle>(null);
  const livePositionLineRef = useRef<LivePositionLineHandle>(null);
  const orderLineRefsMap = useRef<Map<string, OrderLineHandle>>(new Map());
  const liveOrderLineRefsMap = useRef<Map<number, LiveOrderLineHandle>>(new Map());
  const srLabelElRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Keep latest orderLines data in a ref for RAF updater (avoids stale closure)
  const orderLinesDataRef = useRef<Array<{ id: string; price: number; yPosition: number }>>([]);
  useEffect(() => { orderLinesDataRef.current = orderLines; }, [orderLines]);
  // Track when chart series is ready to trigger alert position recalculation
  const [chartSeriesVersion, setChartSeriesVersion] = useState<number>(0);
  const [volumeSeriesVersion, setVolumeSeriesVersion] = useState<number>(0);
  const [chartRangeVersion, setChartRangeVersion] = useState(0);
  // Volume spike markers now rendered as native TradingView markers
  const { preferences, updatePreference, isLoading: preferencesLoading } = usePreferences();
  const [showOrderMarkers] = useState<boolean>(
    preferences.chart?.showOrderMarkers ?? true
  );
  const [showStrategySettings, setShowStrategySettings] = useState<boolean>(false);
  const [strategySettingsTab, setStrategySettingsTab] = useState<string | undefined>(undefined);
  const isMobile = useIsMobile();
  const [showBrokerModal, setShowBrokerModal] = useState<boolean>(false);
  const [showCopyTradingModal, setShowCopyTradingModal] = useState<boolean>(false);
  const [showFairPriceSettings, setShowFairPriceSettings] = useState<boolean>(false);
  const [showDisplacementSettings, setShowDisplacementSettings] = useState<boolean>(false);
  // The floating order panel used to be behind a persisted toggle that defaulted
  // to OFF, so a fresh profile never saw it and there was no hint it existed. It
  // is always mounted on desktop now; there is no visibility state to keep.
  const [showAccountStatsModal, setShowAccountStatsModal] = useState<boolean>(false);
  const [showTradeAnalyzer, setShowTradeAnalyzer] = useState<boolean>(false);
  const [showTradeAnalytics, setShowTradeAnalytics] = useState<boolean>(false);
  const [showAutoBotPanel, setShowAutoBotPanel] = useState<boolean>(false);
  const [countdownYPosition, setCountdownYPosition] = useState<number | null>(null);
  const [countdownXPosition, setCountdownXPosition] = useState<number | null>(null);
  // Which S/R label is expanded, by stable cluster key. Exactly one at a time:
  // these clusters stack densely near the price axis, so two open cards would
  // overlap each other — which is the clutter this whole feature exists to fix.
  const [expandedSRKey, setExpandedSRKey] = useState<string | null>(null);
  // S/R consolidated labels (one per price cluster)
  const [srLabelGroups, setSrLabelGroups] = useState<SRLabelGroup[]>([]);
  // Mirror of the current clusters for event handlers, which close over stale
  // state otherwise.
  const srLabelGroupsRef = useRef<Array<{ key: string; levels: SRLevel[] }>>([]);
  // Same reason: the chart click handler is registered once and would otherwise
  // read whichever expandedSRKey was current when it was bound.
  const expandedSRKeyRef = useRef<string | null>(null);
  expandedSRKeyRef.current = expandedSRKey;

  // AUTO-BOT status (use primitive selectors to avoid infinite re-renders)
  const autoBotEnabled = useAutoBotGlobalEnabled();
  const activeBots = useAutoBotActiveBots();

  // Load volume pane height from preferences on mount
  useEffect(() => {
    const savedHeight = preferences.chart?.volumePaneHeight;
    if (savedHeight && savedHeight >= MIN_VOLUME_PANE_HEIGHT && savedHeight <= MAX_VOLUME_PANE_HEIGHT) {
      setVolumePaneHeight(savedHeight);
    }
  }, [preferences.chart?.volumePaneHeight]);

  // Sync overlay visibility from preferences when they load
  const syncOverlaysFromPreferences = useOverlayLibraryStore((state) => state.syncFromPreferences);
  const hasSyncedOverlays = useRef(false);
  useEffect(() => {
    if (!preferencesLoading && !hasSyncedOverlays.current) {
      hasSyncedOverlays.current = true;
      syncOverlaysFromPreferences();
    }
  }, [preferencesLoading, syncOverlaysFromPreferences]);

  // Volume pane resize handlers
  const handleVolumePaneResizeStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsResizingVolumePane(true);
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    volumePaneResizeStartY.current = clientY;
    volumePaneResizeStartHeight.current = volumePaneHeight;
  }, [volumePaneHeight]);

  useEffect(() => {
    if (!isResizingVolumePane) return;

    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      // Moving up (negative delta) increases height, moving down decreases it
      const delta = volumePaneResizeStartY.current - clientY;
      const newHeight = Math.max(
        MIN_VOLUME_PANE_HEIGHT,
        Math.min(MAX_VOLUME_PANE_HEIGHT, volumePaneResizeStartHeight.current + delta)
      );
      setVolumePaneHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizingVolumePane(false);
      // Save to preferences
      updatePreference('chart', { volumePaneHeight: volumePaneHeight });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isResizingVolumePane, volumePaneHeight, updatePreference]);

  // Drag handler for the long-press / TKT preview line — converts pointer Y to price,
  // snaps to instrument tick, and updates the preview state live.
  useEffect(() => {
    if (!isDraggingPreview) return;
    const updateFromClientY = (clientY: number) => {
      if (!chartContainerRef.current || !candlestickSeriesRef.current) return;
      const rect = chartContainerRef.current.getBoundingClientRect();
      // Not below the price pane: the order preview cannot be parked over flow line panes.
      const maxY = (chartRef.current && linePaneSpan(chartRef.current)?.top) ?? rect.height;
      const relativeY = Math.max(0, Math.min(maxY, clientY - rect.top));
      const newPrice = candlestickSeriesRef.current.coordinateToPrice(relativeY);
      if (newPrice === null || newPrice === undefined) return;
      const sym = useMarketStore.getState().symbol;
      const snapped = roundToTick(newPrice as number, sym);
      // Re-coordinate snapped price so the line renders exactly at the tick boundary.
      const snappedY = candlestickSeriesRef.current.priceToCoordinate(snapped);
      setLongPressPreview({
        y: (snappedY ?? relativeY) as number,
        price: snapped,
      });
    };
    const onMouseMove = (e: MouseEvent) => updateFromClientY(e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();
      updateFromClientY(e.touches[0].clientY);
    };
    const onEnd = () => setIsDraggingPreview(false);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [isDraggingPreview]);

  // News overlay state
  const newsOverlayVisible = useNewsStore((state) => state.isOverlayVisible);
  const toggleNewsOverlay = useNewsStore((state) => state.toggleOverlay);

  // News/calendar chart markers data
  const breakingNewsForChart = useBreakingNewsForChart();
  const calendarEventsForChart = useCalendarEventsForChart();

  // News tooltip state (for clicking on lightning bolt)
  const [newsTooltip, setNewsTooltip] = useState<{ x: number; y: number; headline: string } | null>(null);

  // TAKE IT banner state
  const [takeItBanner, setTakeItBanner] = useState<{
    visible: boolean;
    direction: 'LONG' | 'SHORT' | null;
    confidence: number;
    confluence: number;
    symbol: string;
  }>({ visible: false, direction: null, confidence: 0, confluence: 0, symbol: '' });
  const lastTakeItTimestampRef = useRef<number>(0); // Prevent duplicate banners for same signal
  const initialMarkerLoadRef = useRef<boolean>(true); // Skip TAKE IT banner on initial page load
  // Event tooltip state (for clicking on star)
  const [eventTooltip, setEventTooltip] = useState<{ x: number; y: number; event: string; country: string; time: string } | null>(null);

  // Initialize AUTO-BOT store on mount
  useEffect(() => {
    initializeAutoBotStore();
  }, []);

  // Close news tooltip on ESC key or clicking outside
  useEffect(() => {
    if (!newsTooltip) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNewsTooltip(null);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      // Check if click is on the tooltip itself
      const target = e.target as HTMLElement;
      if (target.closest('.news-marker-tooltip')) {
        return; // Don't close if clicking on tooltip
      }
      setNewsTooltip(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    // Use setTimeout to avoid immediate close from the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
      clearTimeout(timeoutId);
    };
  }, [newsTooltip]);

  // Close event tooltip on ESC key or clicking outside
  useEffect(() => {
    if (!eventTooltip) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEventTooltip(null);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.event-marker-tooltip')) {
        return;
      }
      setEventTooltip(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
      clearTimeout(timeoutId);
    };
  }, [eventTooltip]);

  // Strategy controls state (for Live mode) - Will be loaded from backend
  const [wickThreshold, setWickThreshold] = useState<number>(25);
  const [wickThresholdMin, setWickThresholdMin] = useState<number>(0);
  const [skipIfHOD, setSkipIfHOD] = useState<boolean>(false);
  const [skipIfLOD, setSkipIfLOD] = useState<boolean>(false);
  const [hodLodRangePoints, setHodLodRangePoints] = useState<number>(5);
  const [strategyEnabled] = useState<boolean>(true);

  // Profitability controls state - Will be loaded from backend (state values unused, only setters used)
  const [, setTpPoints] = useState<number>(5);
  const [, setSlPoints] = useState<number>(25);
  const [, setSessionStartHour] = useState<number>(21); // 21 UTC = 1 PM PST
  const [, setSessionEndHour] = useState<number>(22); // 22 UTC = 2 PM PST

  const marketCandles = useMarketStore(s => s.candles);
  const candlesVersion = useMarketStore(s => s.candlesVersion);
  /**
   * Changes whenever the candle array is altered anywhere other than its tail.
   *
   * Ticks arrive constantly and each one replaces the array, so array identity
   * is useless as a memo key — everything downstream re-ran on every tick and
   * the chart's lag scaled with how much history was loaded (#344). The epoch
   * is the store's promise that, for a given `(epoch, length)`, every candle
   * below the last one is the same object it was before, which is what makes
   * the "history" memos below sound.
   */
  const candlesEpoch = useMarketStore(s => s.candlesEpoch);
  const marketCurrentPrice = useMarketStore(s => s.currentPrice);
  const marketSymbol = useMarketStore(s => s.symbol);
  const dataMode = useMarketStore(s => s.dataMode);
  const backtestConfig = useMarketStore(s => s.backtestConfig);
  const isBacktestLoading = useMarketStore(s => s.isBacktestLoading);
  const isMarketOpen = useMarketStore(s => s.isMarketOpen);
  const marketSpec = getInstrumentSpec(marketSymbol);
  const isCrypto = isAlwaysOpen(marketSpec);
  const marketStatus = useMarketStore(s => s.marketStatus);
  const setBacktestConfig = useMarketStore(s => s.setBacktestConfig);
  const setDataMode = useMarketStore(s => s.setDataMode);
  const setBacktestLoading = useMarketStore(s => s.setBacktestLoading);
  const setCandles = useMarketStore(s => s.setCandles);
  const setSymbol = useMarketStore(s => s.setSymbol);

  const symbol = marketSymbol;
  const candles: ExtendedCandle[] = marketCandles;

  /**
   * Every candle except the one still forming.
   *
   * Deliberately keyed on `(candlesEpoch, candles.length)` rather than on the
   * array itself. Within an epoch the store only ever overwrites the last
   * element or appends, so those two numbers pin this slice exactly — and it
   * therefore stops changing identity on every tick, which is what lets the
   * expensive per-bar work below run once a bar instead of once a tick.
   *
   * This is the one memo in here that would show STALE data if the store ever
   * broke that promise, so `updateOrAddCandle` bumps the epoch on any write it
   * cannot prove is tail-only.
   */
  const historyCandles = useMemo(
    () => candles.slice(0, -1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candlesEpoch, candles.length]
  );
  /** The bar still forming — the only candle that can change without an epoch bump. */
  const formingCandle = candles.length > 0 ? candles[candles.length - 1] : null;

  /**
   * Timestamp index over the completed bars, for overlays that look up a
   * handful of specific bars. Rebuilt once a bar rather than once a tick; the
   * forming bar is checked separately at each call site because it is the one
   * candle this map is allowed to be missing.
   */
  const historyCandleByTime = useMemo(() => {
    const index = new Map<number, ExtendedCandle>();
    for (const candle of historyCandles) index.set(candle.timestamp, candle);
    return index;
  }, [historyCandles]);

  // Trailing OHLC for the candle the WCC analysis describes. Matched by
  // timestamp on purpose: pairing the analysis of one bar with the trailing
  // mini-candle of a different bar would read as a single coherent row and be
  // quietly wrong.
  // Binary search rather than `.find()`: this memo re-runs on every tick (the
  // candle array is replaced each time), and a linear scan here cost one pass
  // over the whole loaded history per tick (#344). Same answer either way —
  // both refuse a near miss and return null.
  const wccTrailing = useMemo(() => {
    if (!wholeCandleAnalysis) return null;
    const match = findBarAt(candles, wholeCandleAnalysis.timestamp);
    return match?.trailing ?? null;
  }, [candles, wholeCandleAnalysis]);
  const currentPrice = marketCurrentPrice;

  // Subscribe to session high/low for this symbol (triggers re-render when data arrives via WebSocket)
  // This fixes the race condition where HOD/LOD/Fib effects run before session data is available
  const normalizedSymbolForSession = useMemo(() => normalizeToBase(symbol), [symbol]);
  const sessionHighLow = useMarketStore((state) => state.sessionHighLowBySymbol.get(normalizedSymbolForSession));

  // Subscribe to price levels for this symbol (RTH HOD/LOD, PDH/PDL, IB, Midnight Open)
  const priceLevels = useMarketStore((state) => state.priceLevelsBySymbol.get(normalizedSymbolForSession));

  // MOC imbalance level: open of the first raw (base-interval) bar at/after
  // 14:50:00 CT on the latest trading day that has one (carries past the
  // 17:00 CT roll until the next 14:50 print). Raw candles, not the chart's
  // aggregated timeframe, so the level never depends on the selected TF.
  // Reduced to a number so the drawing effect only re-runs when it changes.
  const mocOpenPrice = useMemo(() => computeMocLevel(marketCandles)?.price ?? 0, [marketCandles]);

  // Subscribe to level visibility toggles
  const rawLevelVisibility = useLevelsStore((state) => state.visibility);
  const cleanHides = useCleanChartStore((state) => state.hides);
  // Re-runs the series-marker effect when Clean starts/stops hiding signals.
  const cleanHidesSignals = cleanChart && cleanHides.signals;

  // Clean mode masks this rather than being applied per-effect.
  //
  // Every effect that draws a level, EMA or VWAP already reads levelVisibility,
  // so masking here makes all of them respect Clean automatically — including
  // when they re-run. The previous implementation removed price lines once, in a
  // [cleanChart] effect, and anything redrawn afterwards simply came back; that
  // is why level lines survived Clean.
  //
  // Derived only. Never written back to levelsStore, or toggling Clean would
  // silently destroy the user's saved Levels configuration.
  const levelVisibility = useMemo(() => {
    if (!cleanChart) return rawLevelVisibility;

    const masked = { ...rawLevelVisibility };
    for (const key of Object.keys(masked) as (keyof typeof masked)[]) {
      const group: CleanTarget = cleanGroupForLevel(key);
      if (cleanHides[group]) masked[key] = false;
    }
    return masked;
  }, [rawLevelVisibility, cleanChart, cleanHides]);
  const levelColors = useLevelsStore((state) => state.colors);

  // Donchian settings (global, live-updated when modal saves)
  const donchianEntryLookback = useDonchianSettingsStore((s) => s.settings.entryLookback);

  // Fair Price indicator toggle (persisted; gates primitive attach + status chip)
  const fairPriceEnabled = useFairPriceStore((state) => state.enabled);
  const displacementEnabled = useDisplacementStore((state) => state.enabled);
  const candidatesEnabled = useCandidateStore((state) => state.enabled);
  /**
   * The engine's rule, or null until `/api/diamond-settings` answers.
   *
   * Displayed only. Nothing on this side scores with it — it is here so the
   * overlay can print the threshold a bar was measured against beside the
   * measurement the engine sent.
   */
  const candidateThresholds = useCandidateStore((state) => state.thresholds);
  /**
   * The ONLY source of a diamond on this chart.
   *
   * Both maps are stable references replaced wholesale on write, so selecting
   * them directly is safe. Deriving an array from either happens in a useMemo
   * BELOW the selector — a selector returning a fresh array makes the snapshot
   * look changed every render and loops React to death on error #185, which
   * blanks the chart.
   */
  const engineDiamonds = useCandidateStore((state) => state.engineDiamonds);
  const engineRejected = useCandidateStore((state) => state.engineRejected);
  const candidateHistoryStatus = useCandidateStore((state) => state.historyStatus);
  const candidateHistoryError = useCandidateStore((state) => state.historyError);
  const candidateHistoryCoverage = useCandidateStore((state) => state.historyCoverage);
  const fetchCandidateHistory = useCandidateStore((state) => state.fetchHistory);
  /**
   * Socket connection, watched only so the history fetch re-runs on reconnect.
   *
   * `diamond-signal` has no replay: every bar that completed while the socket
   * was down produced a diamond nobody received, and without a refetch those
   * bars stay blank until the next symbol switch. A scalar boolean, so it
   * cannot churn the deps it sits in.
   */
  const socketConnected = useMarketStore((state) => state.isConnected);

  /**
   * The candle the diamond overlay explains: whatever the crosshair is on,
   * falling back to the most recent bar.
   *
   * Matched by timestamp rather than index — hoveredCandle carries a seconds
   * time from the chart library while candles are in ms, and pairing one bar's
   * verdict with another bar's trailing data would read as coherent and be
   * quietly wrong (the same trap wccTrailing documents above).
   */
  const diamondInfoCandle = useMemo(() => {
    if (candles.length === 0) return null;
    if (!hoveredCandle) return candles[candles.length - 1] ?? null;
    const ms = hoveredCandle.time * 1000;
    // Binary search for the same reason as wccTrailing above — per-tick memo,
    // and the exact-match-or-nothing semantics are what findBarAt exists for.
    return findBarAt(candles, ms);
  }, [candles, hoveredCandle]);

  /**
   * The engine's verdict on the bar under the crosshair: its diamond if the
   * rule fired, its rejection if the rule threw the bar out, or neither.
   *
   * Two plain lookups into the maps already selected above — no extra
   * subscription, and no fresh object, so nothing re-renders on identity alone.
   *
   * This replaces a local `evaluateCandle` call that re-ran the whole rule —
   * ATR series, running session extremes, consecutive-signal count — purely to
   * populate a two-line overlay. Every one of those inputs was a chance to
   * disagree with the marker sitting under the same bar, and the extremes walk
   * did in fact disagree: it never reset at the 5:02pm CT session roll while
   * the engine does.
   */
  const diamondInfoKey = diamondInfoCandle
    ? engineDiamondKey(symbol, diamondInfoCandle.timestamp)
    : null;
  const diamondInfoDiamond = diamondInfoKey ? engineDiamonds[diamondInfoKey] ?? null : null;
  const diamondInfoRejection = diamondInfoKey ? engineRejected[diamondInfoKey] ?? null : null;
  const candidateLabels = useCandidateStore((state) => state.labels);
  const selectedCandidateId = useCandidateStore((state) => state.selectedId);
  const candidatePanelOpen = useCandidateStore((state) => state.panelOpen);
  const candidateScoreFrom = useCandidateStore((state) => state.scoreFrom);
  const candidateScoreFromPinned = useCandidateStore((state) => state.scoreFromPinned);
  const candidateLookbackDays = useCandidateStore((state) => state.lookbackDays);
  const candidateRecalcNonce = useCandidateStore((state) => state.recalcNonce);
  const botMinConfidence = useCandidateStore((state) => state.botMinConfidence);
  const autoTagEnabled = useCandidateStore((state) => state.autoTagEnabled);
  const autoTagOptions = useCandidateStore((state) => state.autoTagOptions);
  // Inline labeller opened by clicking a candidate diamond on the chart.
  const [quickLabel, setQuickLabel] = useState<{ id: string; x: number; y: number } | null>(null);
  // Read-only fill details, opened by clicking a trade marker. Holds the
  // marker id rather than the marker, so a refetch can't strand a stale copy.
  const [tradeInfo, setTradeInfo] = useState<{ id: string; x: number; y: number } | null>(null);
  const [anchorHover, setAnchorHover] = useState<{
    kind: FairPriceAnchorKind;
    x: number;
    y: number;
  } | null>(null);
  const tradeMarkersEnabled = useTradeMarkersEnabled();
  // Whether the faint dotted entry-to-exit connectors are drawn. A separate
  // preference from `enabled`: hiding the lines must not hide the exits.
  // Both selectors return a plain boolean — see tradeMarkerStore.
  const tradeLinksEnabled = useTradeMarkerLinksEnabled();

  // S/R levels for this symbol
  const srEnabled = useSRStore((state) => state.enabled);
  const srVisibleTimeframes = useSRStore((state) => state.visibleTimeframes);
  const srLevelsBySymbol = useSRStore((state) => state.levelsBySymbol);
  const srPriceRange = useSRStore((state) => state.priceRange);
  const srMinTouchCount = useSRStore((state) => state.minTouchCount);
  const srNearest = useSRStore((state) => state.nearestLevels);
  const srDisplayMode = useSRStore((state) => state.displayMode);
  const srLineOpacity = useSRStore((state) => state.lineOpacity);
  // S/R are price levels: Clean hides the lines AND their DOM labels. Gating the
  // drawing effect (rather than removing lines once when Clean turns on) is what
  // keeps them gone when srLevels recomputes, and brings them back on OFF.
  const srShownOnChart = srEnabled && srDisplayMode === 'chart' &&
    !(cleanChart && cleanHides.levels);

  // Bar-snapped reference price: only updates at bar boundaries to prevent SR line flicker
  const [srReferencePrice, setSrReferencePrice] = useState(0);
  useEffect(() => {
    if (currentPrice <= 0) return;
    if (srReferencePrice === 0) { setSrReferencePrice(currentPrice); return; }
    const intervalMs = TIMEFRAME_MINUTES[selectedTimeframe] * 60 * 1000;
    const now = Date.now();
    const currentBarStart = Math.floor(now / intervalMs) * intervalMs;
    const msUntilNextBar = (currentBarStart + intervalMs) - now;
    const timer = setTimeout(() => setSrReferencePrice(currentPrice), msUntilNextBar);
    return () => clearTimeout(timer);
  }, [selectedTimeframe, currentPrice, srReferencePrice]);

  // Track whether chart is scrolled to the live edge (rightmost bars visible)
  // When panning through history, disable nearest-N filter to show all levels
  // Subscription is set up inside the chart creation effect (where chartRef is available)
  const [srAtLiveEdge, setSrAtLiveEdge] = useState(true);

  // Mirror of the above for the left edge: is the oldest LOADED bar already on
  // screen? Drives the "go to oldest" button's visibility. Starts true so the
  // button never flashes up before the first range event tells us where we are.
  const [atOldestLoadedBar, setAtOldestLoadedBar] = useState(true);

  const srLevels = useMemo(() => {
    const levels = srLevelsBySymbol[symbol] || srLevelsBySymbol[normalizedSymbolForSession] || [];
    const refPrice = srReferencePrice || currentPrice;
    const lowerTimeframes = new Set(['5m', '15m', '1h']);

    const filtered = levels.filter((level) =>
      srVisibleTimeframes.has(level.timeframe) &&
      refPrice > 0 &&
      Math.abs(level.price - refPrice) <= srPriceRange &&
      (!lowerTimeframes.has(level.timeframe) || level.touchCount >= srMinTouchCount)
    );

    // Only apply nearest-N filter when at live edge; show all when panning history
    if (srNearest <= 0 || !srAtLiveEdge) return filtered;

    // Group by price proximity to match visual label consolidation (~18px on chart).
    // Levels within this threshold render as one label group, so count them as one.
    const groupThreshold = refPrice * 0.0003;

    const takeNGroups = (sorted: typeof filtered, n: number) => {
      const result: typeof filtered = [];
      let groupCount = 0;
      let groupAnchor = -Infinity;
      for (const level of sorted) {
        if (Math.abs(level.price - groupAnchor) > groupThreshold) {
          groupCount++;
          if (groupCount > n) break;
          groupAnchor = level.price;
        }
        result.push(level);
      }
      return result;
    };

    const above = takeNGroups(
      filtered.filter(l => l.price > refPrice).sort((a, b) => a.price - b.price),
      srNearest,
    );
    const below = takeNGroups(
      filtered.filter(l => l.price <= refPrice).sort((a, b) => b.price - a.price),
      srNearest,
    );

    return [...above, ...below];
  }, [srLevelsBySymbol, symbol, normalizedSymbolForSession, srVisibleTimeframes,
      srReferencePrice, currentPrice, srPriceRange, srMinTouchCount, srNearest, srAtLiveEdge]);

  // Live trading state - use memoized hooks for multi-broker support
  const activeBroker = brokerSelectors.useActiveBroker();
  const connectedBrokers = useConnectedBrokers();
  // Every broker, not just the active one — the trade-marker anchor has to see
  // whether each connected broker has reported its positions yet.
  const brokers = brokerSelectors.useBrokers();

  const modifyOrder = useBrokerStore(state => state.modifyOrder);
  const cancelOrder = useBrokerStore(state => state.cancelOrder);
  const placeOrder = useBrokerStore(state => state.placeOrder);
  const placeBracketOrder = useBrokerStore(state => state.placeBracketOrder);
  const fetchPositions = useBrokerStore(state => state.fetchPositions);
  const fetchOrders = useBrokerStore(state => state.fetchOrders);

  // Derived state from broker store
  // Use dedicated hooks for positions/orders to ensure proper reactivity
  const isLiveConnected = connectedBrokers.length > 0;
  const livePositions = useActiveBrokerPositions();
  const liveOrders = useActiveBrokerOrders();
  const selectedAccountId = activeBroker?.selectedAccountId ?? null;
  // Track broker updates to force re-render when orders/positions change
  const brokerLastUpdated = activeBroker?.lastUpdated ?? 0;

  // Get selected account name for display on Brokers button
  const selectedAccountName = useMemo(() => {
    if (!activeBroker || !selectedAccountId) return null;
    const account = activeBroker.accounts?.find(a => a.id === selectedAccountId);
    if (!account) return null;
    // Extract last 3 digits from account name (e.g., "MFFUEVCORE202783046" → "046")
    const digits = account.name.match(/\d{3,}/);
    const suffix = digits ? digits[0].slice(-3) : '';
    // Get broker prefix (e.g., "mffu" → "MFFU")
    const brokerPrefix = activeBroker.name.slice(0, 4).toUpperCase();
    return suffix ? `${brokerPrefix}-${suffix}` : brokerPrefix;
  }, [activeBroker, selectedAccountId]);

  // Signal Predictor state
  const spIsMinimized = useSignalPredictorStore((state) => state.isMinimized);
  const spSetMinimized = useSignalPredictorStore((state) => state.setMinimized);

  // Theme state
  const currentTheme = useThemeStore((state) => state.currentTheme);

  // Symbol registry (dynamic symbols from API)
  const symbolRegistry = useSymbolStore((state) => state.symbols);

  // Broker and Copy Trading state
  const copyTradingEnabled = useBrokerStore(s => s.copyTradingEnabled);
  const connectedBrokerCount = connectedBrokers.length;

  // Toast history
  const { history: toastHistory, toggleHistoryVisible } = useToastStore();
  const prevConnectedBrokerCountRef = useRef(connectedBrokerCount);

  // Alerts state
  const {
    alerts,
    placementMode,
    pendingAlertPrice,
    editingAlert,
    showSettingsModal,
    showAlertsPanel,
    fetchAlerts,
    createAlert,
    updateAlert,
    deleteAlert,
    pauseAlert,
    resumeAlert,
    exitPlacementMode,
    setPendingAlertPrice,
    openSettingsModal,
    closeSettingsModal,
    closeAlertsPanel,
  } = useAlertStore();

  // Ref to hold current placementMode for use in event handlers (avoids stale closure)
  const placementModeRef = useRef(placementMode);
  useEffect(() => {
    placementModeRef.current = placementMode;
  }, [placementMode]);

  // DCA calculator state
  const [dcaMode, setDcaMode] = useState(false);
  const [dcaTarget, setDcaTarget] = useState<{price: number, contracts: number, newAvg: number, currentPos: number, avgPrice: number} | null>(null);
  const [dcaTargetY, setDcaTargetY] = useState<number | null>(null);
  const [dcaTargetPrice, setDcaTargetPrice] = useState<number | null>(null);
  const dcaModeRef = useRef(dcaMode);
  useEffect(() => { dcaModeRef.current = dcaMode; }, [dcaMode]);
  const [dcaPinned, setDcaPinned] = useState(false);
  const dcaPinnedRef = useRef(dcaPinned);
  useEffect(() => { dcaPinnedRef.current = dcaPinned; }, [dcaPinned]);

  // Convert stored DCA price back to pixel Y on every tick so the overlay
  // tracks the pinned price through chart pan/zoom instead of drifting.
  const dcaDisplayY = useMemo(() => {
    if (dcaPinned && dcaTargetPrice != null && candlestickSeriesRef.current) {
      return candlestickSeriesRef.current.priceToCoordinate(dcaTargetPrice) as number | null;
    }
    return dcaTargetY;
  }, [dcaPinned, dcaTargetPrice, dcaTargetY, currentPrice]);

  // Ref to hold current candles for use in volume chart click handler (avoids stale closure)
  const candlesRef = useRef(candles);
  useEffect(() => {
    candlesRef.current = candles;
  }, [candles]);

  // Ref to hold current symbol for use in volume chart click handler (avoids stale closure)
  const symbolRef = useRef(symbol);
  useEffect(() => {
    symbolRef.current = symbol;
  }, [symbol]);

  // Exit DCA mode on symbol change
  useEffect(() => {
    setDcaMode(false);
    setDcaTarget(null);
    setDcaTargetY(null);
    setDcaTargetPrice(null);
    setDcaPinned(false);
  }, [symbol]);

  // Exit DCA mode on Escape key
  useEffect(() => {
    if (!dcaMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDcaMode(false);
        setDcaTarget(null);
        setDcaTargetY(null);
        setDcaTargetPrice(null);
        setDcaPinned(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dcaMode]);
  // Exit DCA mode when position is closed
  useEffect(() => {
    if (!dcaMode) return;
    const unsub = useBrokerStore.subscribe((state) => {
      const activeBroker = state.brokers.find(b => b.id === state.activeBrokerId);
      const positions = activeBroker?.positions ?? [];
      const chartBase = normalizeToBase(symbol);
      const hasPosition = positions.some(p => p.symbol && normalizeToBase(p.symbol) === chartBase && p.netPos !== 0);
      if (!hasPosition) {
        console.log(`[DEBUG:DCA] exiting: position closed for ${chartBase}`);
        setDcaMode(false);
        setDcaTarget(null);
        setDcaTargetY(null);
        setDcaTargetPrice(null);
        setDcaPinned(false);
      }
    });
    return unsub;
  }, [dcaMode, symbol]);


  // Refresh chart data when a new broker connects (workaround for chart rendering issue)
  useEffect(() => {
    if (connectedBrokerCount > prevConnectedBrokerCountRef.current && dataMode === 'live') {
      if (candles.length > 0) {
        console.log('🔄 [CHART] Broker connected, but chart is already hydrated - skipping history refresh');
        prevConnectedBrokerCountRef.current = connectedBrokerCount;
        return;
      }

      console.log('🔄 [CHART] New broker connected with empty chart, refreshing chart data...');
      const timer = setTimeout(() => {
        if (symbol && connectionStatus === 'connected' && useMarketStore.getState().candles.length === 0) {
          wsService.requestHistoricalData(symbol, 2160);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
    prevConnectedBrokerCountRef.current = connectedBrokerCount;
  }, [connectedBrokerCount, symbol, connectionStatus, dataMode, candles.length]);

  // Fetch alerts when symbol changes
  useEffect(() => {
    if (symbol && connectionStatus === 'connected') {
      fetchAlerts(symbol);
    }
  }, [symbol, connectionStatus, fetchAlerts]);

  // Filter alerts for current symbol (normalize both to base for comparison)
  const symbolAlerts = useMemo(() => {
    const normalizedSymbol = normalizeToBase(symbol);
    return alerts.filter((alert) => normalizeToBase(alert.symbol) === normalizedSymbol);
  }, [alerts, symbol]);

  // Calculate Y positions for alert lines
  const alertLinePositions = useMemo(() => {
    if (!candlestickSeriesRef.current) return new Map<string, number>();
    const positions = new Map<string, number>();
    for (const alert of symbolAlerts) {
      if (alert.price != null) {
        const y = candlestickSeriesRef.current.priceToCoordinate(alert.price);
        if (y != null) {
          positions.set(alert.id, y);
        }
      }
    }
    return positions;
  }, [symbolAlerts, chartSeriesVersion, chartRangeVersion]);

  // Calculate Y positions for volume alert lines in volume pane
  // Only volume_above alerts have a fixed position; volume_spike (relative) would need recalc on each bar
  const volumeAlertLinePositions = useMemo(() => {
    if (!volumeSeriesRef.current) return new Map<string, number>();
    const positions = new Map<string, number>();
    for (const alert of symbolAlerts) {
      if (alert.alertType === 'volume_above' && alert.volumeThreshold != null) {
        const y = volumeSeriesRef.current.priceToCoordinate(alert.volumeThreshold);
        if (y != null) {
          positions.set(alert.id, y);
        }
      }
    }
    return positions;
  }, [symbolAlerts, volumeSeriesVersion, chartRangeVersion]);

  // Feed the round-level warning band.
  //
  // Driven by `currentPrice` rather than the forming candle's close: the band
  // is a warning about where price IS, and on a 2-minute bar the close only
  // moves when the bar completes — the band would arm and disarm two minutes
  // late, which for a proximity warning is worse than not having it.
  //
  // The bar TIME still comes from the forming candle, because that is what
  // anchors the band's left edge to the live bar.
  useEffect(() => {
    roundLevelWarningPrimitiveRef.current?.setData({
      // ExtendedCandle carries `timestamp` in MILLISECONDS; the time scale is
      // keyed in seconds. Passing ms straight through resolves to a coordinate
      // ~55,000 years out and timeToCoordinate returns null, so the band would
      // silently never draw.
      time: formingCandle ? Math.floor(formingCandle.timestamp / 1000) : null,
      price: currentPrice ?? null,
    });
  }, [formingCandle, currentPrice, chartSeriesVersion]);

  // Recalculate alert positions when the chart range changes (debounced).
  //
  // Two scales can move an overlay that is positioned by priceToCoordinate, and
  // the library only lets you subscribe to one of them. The time scale has
  // subscribeVisibleLogicalRangeChange; the price scale has nothing, so a
  // vertical drag, an autoscale on new data, or a pane resize used to leave
  // alert lines sitting at their old y while the chart moved underneath them.
  // PriceScaleWatcherPrimitive supplies the missing half via updateAllViews.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    let debounceTimer: ReturnType<typeof setTimeout>;
    const handleRangeChange = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setChartRangeVersion(v => v + 1);
      }, 100);
    };

    chart.timeScale().subscribeVisibleLogicalRangeChange(handleRangeChange);

    const series = candlestickSeriesRef.current;
    const scaleWatcher = series
      ? createPriceScaleWatcherPrimitive(() => {
        setChartRangeVersion(v => v + 1);
        // The imperative overlay updaters too: a pane resize (flow line panes
        // toggled, a separator dragged) moves the mapping without a range change.
        scheduleChartUpdate();
      })
      : null;
    if (series && scaleWatcher) series.attachPrimitive(scaleWatcher);

    return () => {
      clearTimeout(debounceTimer);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleRangeChange);
      if (series && scaleWatcher) {
        try { series.detachPrimitive(scaleWatcher); } catch { /* series already gone */ }
      }
    };
  }, [chartSeriesVersion]); // Re-subscribe when chart is created/recreated

  // Get trading panel settings for chart order placement
  const tradingPanelPrefs = preferences.tradingPanel || {};
  const chartOrderQuantity = tradingPanelPrefs.quantity ?? 1;
  const chartUseOCO = tradingPanelPrefs.useOCO ?? true;
  const chartOcoTPOffset = tradingPanelPrefs.ocoTPOffset ?? 10;
  const chartOcoSLOffset = tradingPanelPrefs.ocoSLOffset ?? 5;
  const isTradingPanelVisible = tradingPanelPrefs.visible ?? true;

  // Helper to clean symbol for comparison (remove exchange suffix and contract month)
  // Handles formats: NQH6, NQH25, NQH2026, MNQH6.CME, etc.
  const cleanSymbol = useCallback((s: string) => normalizeToBase(s), []);

  // Memoize current position to avoid unnecessary re-renders when array reference changes
  // Filter by chart's symbol AND account - only show position if it matches THIS chart's symbol
  const currentLivePosition = useMemo(() => {
    const chartSymbolClean = cleanSymbol(symbol);
    return livePositions.find(pos => {
      const posSymbolClean = cleanSymbol(pos.symbol || '');
      // If position has no symbol, show it (better than hiding)
      const symbolMatches = !pos.symbol || posSymbolClean === chartSymbolClean;
      return symbolMatches &&
        (selectedAccountId === null || pos.accountId === selectedAccountId) &&
        pos.netPos !== 0;
    }) || null;
  }, [livePositions, selectedAccountId, symbol, cleanSymbol]);

  // Create a stable key for position data to prevent duplicate line creation
  const livePositionKey = useMemo(() => {
    if (!currentLivePosition) return '';
    return `${currentLivePosition.id}-${currentLivePosition.netPos}-${currentLivePosition.netPrice}`;
  }, [currentLivePosition]);

  // Filter liveOrders by chart's symbol - only show orders for THIS chart's instrument
  const filteredLiveOrders = useMemo(() => {
    const chartSymbolClean = cleanSymbol(symbol);
    return liveOrders.filter(order => {
      // If order has symbol, check it matches the chart's symbol (exact match only)
      if (order.symbol) {
        const orderSymbolClean = cleanSymbol(order.symbol);
        return orderSymbolClean === chartSymbolClean;
      }
      // If order has contractId, look up symbol from positions
      if (order.contractId) {
        // Find any position with this contractId to get the symbol
        const matchingPosition = livePositions.find(p => p.contractId === order.contractId);
        if (matchingPosition?.symbol) {
          const posSymbolClean = cleanSymbol(matchingPosition.symbol);
          return posSymbolClean === chartSymbolClean;
        }
        // If we have a position for this chart with same contractId, include it
        if (currentLivePosition?.contractId === order.contractId) {
          return true;
        }
      }
      // No symbol info available — show since there's only one chart
      return true;
    });
  }, [liveOrders, livePositions, symbol, cleanSymbol, currentLivePosition]);

  // Track previous symbol and dataMode to detect changes
  const previousSymbolRef = useRef<string>(symbol);
  const previousDataModeRef = useRef<any>(dataMode);

  // Initialize strategy on component mount
  useEffect(() => {
    // Strategy runs on server - initialize service and enable/disable
    console.log('🎯 [CHARTVIEW] Initializing strategy service...');
    strategyService.initializeStrategy();
    strategyService.setEnabled(strategyEnabled);
    console.log('✅ [CHARTVIEW] Strategy service initialized');
  }, []); // Run once on mount

  // Sync strategyEnabled state with StrategyService
  useEffect(() => {
    strategyService.setEnabled(strategyEnabled);
  }, [strategyEnabled]);

  // Handle showBacktestModal prop from App
  useEffect(() => {
    if (showBacktestModal) {
      setShowDateRangeSelector(true);
    }
  }, [showBacktestModal]);

  // Fetch Pinning Volume analysis when hovering over a candle
  // Track the currently requested timestamp
  const requestedPinVolTimestampRef = useRef<number | null>(null);

  // Helper to create cache keys with normalized symbol for multi-chart support
  const getCacheKey = useCallback((timestamp: number) => {
    const normalizedSymbol = normalizeToBase(symbol);
    return `${normalizedSymbol}:${timestamp}`;
  }, [symbol]);

  // Subscribe to cache updates from the store
  const pinningVolumeCache = useMarketStore((state) => state.pinningVolumeAnalysisCache);

  useEffect(() => {
    // Use hovered candle, or fall back to latest candle if nothing is hovered
    let timestampMs: number;

    if (hoveredCandle) {
      // TradingView uses timestamps in seconds, backend uses milliseconds
      timestampMs = hoveredCandle.time * 1000;
    } else if (candles.length > 0) {
      // Use latest candle when nothing is hovered
      // candles from store use milliseconds
      const latestCandle = candles[candles.length - 1];
      timestampMs = latestCandle.timestamp;
    } else {
      setPinningVolumeAnalysis(null);
      setIsLoadingPinningAnalysis(false);
      requestedPinVolTimestampRef.current = null;
      return;
    }

    requestedPinVolTimestampRef.current = timestampMs;

    // Check cache first using symbol-aware cache key
    const cacheKey = getCacheKey(timestampMs);
    const cachedAnalysis = pinningVolumeCache.get(cacheKey);
    if (cachedAnalysis) {
      setPinningVolumeAnalysis(cachedAnalysis);
      setIsLoadingPinningAnalysis(false);
      return;
    }

    // Request from backend (response will be cached by WebSocketService)
    setIsLoadingPinningAnalysis(true);
    setPinningVolumeAnalysis(null);
    // Use the chart's symbol (from props in panel mode) not global marketStore symbol
    wsService.emit('get-pinning-volume-analysis', { timestamp: timestampMs, symbol });

    // Subscribe to store updates instead of polling
    const unsubscribe = useMarketStore.subscribe((state) => {
      const cached = state.pinningVolumeAnalysisCache.get(cacheKey);
      if (cached && requestedPinVolTimestampRef.current === timestampMs) {
        setPinningVolumeAnalysis(cached);
        setIsLoadingPinningAnalysis(false);
        unsubscribe();
        clearTimeout(timeout);
      }
    });

    // Safety timeout: stop waiting after 5 seconds
    const timeout = setTimeout(() => {
      unsubscribe();
      if (requestedPinVolTimestampRef.current === timestampMs) {
        setIsLoadingPinningAnalysis(false);
      }
    }, 5000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [hoveredCandle?.time, pinningVolumeCache, candles.length, getCacheKey, symbol]);

  // Fetch Whole Candle analysis when hovering over a candle
  // For current forming candle, use real-time signal-prediction data
  const requestedWccTimestampRef = useRef<number | null>(null);
  // Note: Don't subscribe to wholeCandleAnalysisCache directly - it creates new Map on each update
  // causing infinite loops. Access via getState() instead.
  // Get symbol-specific prediction for multi-chart support
  const signalPrediction = useSignalPredictorStore((state) =>
    state.getPredictionForSymbol(symbol)
  );

  useEffect(() => {
    let timestampMs: number;
    let isCurrentCandle = false;

    if (hoveredCandle) {
      timestampMs = hoveredCandle.time * 1000;
    } else if (candles.length > 0) {
      const latestCandle = candles[candles.length - 1];
      timestampMs = latestCandle.timestamp;
      isCurrentCandle = true; // Latest candle is the current forming candle
    } else {
      setWholeCandleAnalysis(null);
      setIsLoadingWccAnalysis(false);
      requestedWccTimestampRef.current = null;
      return;
    }

    // Check if this is the current forming candle - use signal-prediction data
    if (isCurrentCandle && signalPrediction && signalPrediction.candleStartTime === timestampMs) {
      const liveCandle = signalPrediction.liveCandle;
      const open = liveCandle?.open ?? 0;
      const high = liveCandle?.high ?? 0;
      const low = liveCandle?.low ?? 0;
      const close = liveCandle?.close ?? 0;
      const candleLen = signalPrediction.candleLength;
      const upperWick = high - Math.max(open, close);
      const lowerWick = Math.min(open, close) - low;
      const upperWickPct = candleLen > 0 ? (upperWick / candleLen) * 100 : 0;
      const lowerWickPct = candleLen > 0 ? (lowerWick / candleLen) * 100 : 0;
      const distHOD = signalPrediction.distanceFromHOD ?? 999;
      const distLOD = signalPrediction.distanceFromLOD ?? 999;
      const hodLodRange = signalPrediction.hodLodRangePoints ?? 19;

      // Convert signal prediction to WholeCandleAnalysis format
      const liveAnalysis: WholeCandleAnalysis = {
        timestamp: timestampMs,
        open,
        high,
        low,
        close,
        isGreen: close > open,
        upperWickPct,
        lowerWickPct,
        combinedWickPct: signalPrediction.wickPct,
        candleLength: candleLen,
        wickThresholdMin: signalPrediction.wickThresholdMin ?? 0,
        wickThresholdMax: signalPrediction.wickThresholdMax ?? 25,
        isWickInRange: signalPrediction.isQualifying,
        atrValue: signalPrediction.atr,
        atrMinThreshold: signalPrediction.atrMinThreshold,
        skipIfLowATR: signalPrediction.atrMinThreshold > 0,
        passesAtrFilter: signalPrediction.passesAtrFilter ?? true,
        sessionHigh: signalPrediction.sessionHigh ?? 0,
        sessionLow: signalPrediction.sessionLow ?? 0,
        distanceFromHOD: distHOD,
        distanceFromLOD: distLOD,
        hodLodRangePoints: hodLodRange,
        skipIfHOD: hodLodRange > 0,
        skipIfLOD: hodLodRange > 0,
        isNearHOD: distHOD <= hodLodRange,
        isNearLOD: distLOD <= hodLodRange,
        passesHodFilter: signalPrediction.passesHodFilter ?? true,
        passesLodFilter: signalPrediction.passesLodFilter ?? true,
        minCandleLengthPoints: signalPrediction.minCandleLengthPoints ?? 0,
        skipIfSmallCandle: (signalPrediction.minCandleLengthPoints ?? 0) > 0,
        passesLengthFilter: signalPrediction.passesLengthFilter ?? true,
        isSignal: signalPrediction.isQualifying && !signalPrediction.willBeSkipped,
        signalType: signalPrediction.direction === 'LONG' ? 'bullish' : signalPrediction.direction === 'SHORT' ? 'bearish' : null,
        wasSkipped: signalPrediction.willBeSkipped,
        skipReason: signalPrediction.skipReason,
        rejectionReasons: signalPrediction.willBeSkipped && signalPrediction.skipReason
          ? [signalPrediction.skipReason === 'HOD' ? 'Near HOD' : signalPrediction.skipReason === 'LOD' ? 'Near LOD' : 'Low ATR']
          : (!signalPrediction.isQualifying ? ['Wick out of range'] : []),
        // Confidence scoring from live signal prediction
        confidence: signalPrediction.confidence,
        confidenceFactors: signalPrediction.confidenceFactors,
        emaStackAligned: signalPrediction.emaStackAligned,
        vwapAligned: signalPrediction.vwapAligned,
        levelDensity: signalPrediction.levelDensity,
        nextLevelDistance: signalPrediction.nextLevelDistance,
      };
      setWholeCandleAnalysis(liveAnalysis);
      setIsLoadingWccAnalysis(false);
      requestedWccTimestampRef.current = timestampMs;
      return;
    }

    requestedWccTimestampRef.current = timestampMs;

    // Check cache first using symbol-aware cache key
    const cacheKey = getCacheKey(timestampMs);
    const cachedAnalysis = useMarketStore.getState().wholeCandleAnalysisCache.get(cacheKey);
    if (cachedAnalysis) {
      setWholeCandleAnalysis(cachedAnalysis);
      setIsLoadingWccAnalysis(false);
      return;
    }

    // Request from backend with symbol for multi-chart support
    setIsLoadingWccAnalysis(true);
    setWholeCandleAnalysis(null);
    wsService.emit('get-whole-candle-analysis', { timestamp: timestampMs, symbol });

    // Subscribe to store updates instead of polling
    const unsubscribe = useMarketStore.subscribe((state) => {
      const cached = state.wholeCandleAnalysisCache.get(cacheKey);
      if (cached && requestedWccTimestampRef.current === timestampMs) {
        setWholeCandleAnalysis(cached);
        setIsLoadingWccAnalysis(false);
        unsubscribe();
        clearTimeout(timeout);
      }
    });

    // Safety timeout: stop waiting after 5 seconds
    const timeout = setTimeout(() => {
      unsubscribe();
      if (requestedWccTimestampRef.current === timestampMs) {
        setIsLoadingWccAnalysis(false);
      }
    }, 5000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [hoveredCandle?.time, candles.length, signalPrediction, getCacheKey, symbol]);

  // Notify parent when date range selector closes
  useEffect(() => {
    if (!showDateRangeSelector && onBacktestModalClose) {
      onBacktestModalClose();
    }
  }, [showDateRangeSelector, onBacktestModalClose]);

  // Load strategy settings from backend on mount
  useEffect(() => {
    const fetchStrategySettings = async () => {
      try {
        const backendUrl = CONFIG.getBackendUrl();
        const fetchUrl = `${backendUrl}/api/strategy/settings`;

        const response = await apiFetch(fetchUrl);

        if (!response.ok) {
          console.error('[STRATEGY SETTINGS] Failed! Status:', response.status);
          return;
        }

        const settings = await response.json();

        // Check if settings are valid
        if (!settings || typeof settings.wickThresholdMax === 'undefined') {
          console.error('[STRATEGY SETTINGS] Settings are invalid!', settings);
          return;
        }

        // Update all strategy control states
        setWickThresholdMin(settings.wickThresholdMin);
        setWickThreshold(settings.wickThresholdMax);
        setSkipIfHOD(settings.skipIfHOD);
        setSkipIfLOD(settings.skipIfLOD);
        setHodLodRangePoints(settings.hodLodRangePoints);
        setTpPoints(settings.tpPoints);
        setSlPoints(settings.slPoints);
        setSessionStartHour(settings.sessionStartHour);
        setSessionEndHour(settings.sessionEndHour);
      } catch (error) {
        console.error('[STRATEGY SETTINGS] Failed to load:', error);
      }
    };

    fetchStrategySettings();
  }, []);

  // Load Donchian settings once on mount (if not already loaded by modal)
  useEffect(() => {
    const store = useDonchianSettingsStore.getState();
    if (!store.loaded) {
      apiFetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/donchian-settings`)
        .then((r) => r.json())
        .then((data) => {
          store.setSettings({
            enabled: data.enabled ?? true,
            entryLookback: data.entryLookback ?? 20,
            trendLookback: data.trendLookback ?? 55,
            exitLookback: data.exitLookback ?? 10,
            volumeMultiplier: data.volumeMultiplier ?? 1.5,
            volumeLookback: data.volumeLookback ?? 10,
            requireTrendFilter: data.requireTrendFilter ?? true,
          });
        })
        .catch((err) => console.error('[DONCHIAN] Failed to load settings:', err));
    }
  }, []);

  // Load Break of Structure settings once on mount. Before this the chart only
  // learned the engine's BOS settings (enabled, showChoch, colors, label) when
  // the settings modal was opened, so after a reload it drew with the
  // frontend defaults instead of what was saved.
  useEffect(() => {
    apiFetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/break-of-structure-settings`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: Partial<BreakOfStructureSettings>) => {
        useSettingsStore.getState().setBosSettings({ ...DEFAULT_BOS_SETTINGS, ...data });
      })
      .catch((err) => console.error('[BOS] Failed to load settings:', err));
  }, []);

  // Fetch initial signal markers AFTER historical data loads (with profitability data)
  useEffect(() => {
    // Only fetch markers if we have candles loaded
    if (candles.length === 0) {
      console.log(`⏳ [MARKERS] Waiting for candles to load before fetching markers...`);
      return;
    }

    const existingMarkersForSymbol = useMarketStore.getState().signalMarkers.filter((marker) => {
      const isWholeCandle =
        marker.type !== 'volume_spike_bullish' &&
        marker.type !== 'volume_spike_bearish' &&
        marker.type !== 'trailing_pullback_bullish' &&
        marker.type !== 'trailing_pullback_bearish';
      return isWholeCandle && markerMatchesChartSymbol(marker.symbol, symbol);
    });

    if (existingMarkersForSymbol.length > 0) {
      console.log(`📍 [MARKERS] Already have ${existingMarkersForSymbol.length} whole-candle markers for ${symbol}, skipping fetch`);
      return;
    }

    const fetchSignalMarkers = async () => {
      try {
        console.log(`🔍 [MARKERS] Fallback fetch check for ${symbol}`, {
          candleCount: candles.length,
          existingMarkerCount: useMarketStore.getState().signalMarkers.length,
        });
        const markersBeforeFetch = useMarketStore.getState().signalMarkers.filter((marker) => {
          const isWholeCandle =
            marker.type !== 'volume_spike_bullish' &&
            marker.type !== 'volume_spike_bearish' &&
            marker.type !== 'trailing_pullback_bullish' &&
            marker.type !== 'trailing_pullback_bearish';
          return isWholeCandle && markerMatchesChartSymbol(marker.symbol, symbol);
        });

        if (markersBeforeFetch.length > 0) {
          console.log(`📍 [MARKERS] WebSocket delivered ${markersBeforeFetch.length} whole-candle markers for ${symbol}, skipping fallback fetch`);
          return;
        }

        console.log(`🔍 [MARKERS] Fetching signal markers from backend (${candles.length} candles loaded)...`);
        // Request markers with profitability enrichment
        const response = await apiFetch(
          `${CONFIG.getBackendUrl(window.location.hostname)}/api/signal-markers?profitability=true&days=3&symbol=${encodeURIComponent(symbol)}`
        );
        if (response.ok) {
          const data = await response.json();
          // IMPORTANT: Preserve existing volume markers (pinning volume strategy)
          // and trailing pullback markers (trailing pullback strategy)
          // These may have arrived before this fetch completes
          const marketStore = useMarketStore.getState();
          const existingVolumeMarkers = marketStore.signalMarkers.filter(
            m => m.type === 'volume_spike_bullish' || m.type === 'volume_spike_bearish'
          );
          const existingTrailingPullbackMarkers = marketStore.signalMarkers.filter(
            m => m.type === 'trailing_pullback_bullish' || m.type === 'trailing_pullback_bearish'
          );
          // Merge whole-candle markers with existing volume and trailing pullback markers
          marketStore.setSignalMarkers([...data.markers, ...existingVolumeMarkers, ...existingTrailingPullbackMarkers]);
          console.log(`📍 [MARKERS] Loaded ${data.count} whole-candle markers, preserved ${existingVolumeMarkers.length} volume + ${existingTrailingPullbackMarkers.length} TP markers`);
        }
      } catch (error) {
        console.error('❌ Failed to fetch signal markers:', error);
      }
    };

    // Give the socket batch a brief head start, then fall back quickly if markers are still missing.
    const timeoutId = setTimeout(fetchSignalMarkers, 250);
    return () => clearTimeout(timeoutId);
  }, [symbol, candles.length]);

  // Fetch volume markers as fallback (in case Socket.IO event was missed)
  useEffect(() => {
    // Only fetch if we have candles and no volume markers yet
    if (candles.length === 0) return;

    const existingVolumeMarkers = useMarketStore.getState().signalMarkers.filter(
      m => (m.type === 'volume_spike_bullish' || m.type === 'volume_spike_bearish') &&
        markerMatchesChartSymbol(m.symbol, symbol)
    );

    // If we already have volume markers, don't fetch again
    if (existingVolumeMarkers.length > 0) {
      console.log(`📊 [VOLUME MARKERS] Already have ${existingVolumeMarkers.length} volume markers, skipping fetch`);
      return;
    }

    const fetchVolumeMarkers = async () => {
      try {
        console.log(`📊 [VOLUME MARKERS] Fallback fetch check for ${symbol}`, {
          candleCount: candles.length,
          existingMarkerCount: useMarketStore.getState().signalMarkers.length,
        });
        const markersBeforeFetch = useMarketStore.getState().signalMarkers.filter(
          m => (m.type === 'volume_spike_bullish' || m.type === 'volume_spike_bearish') &&
            markerMatchesChartSymbol(m.symbol, symbol)
        );
        if (markersBeforeFetch.length > 0) {
          console.log(`📊 [VOLUME MARKERS] WebSocket delivered ${markersBeforeFetch.length} volume markers for ${symbol}, skipping fallback fetch`);
          return;
        }

        console.log(`📊 [VOLUME MARKERS] Fetching volume markers from backend...`);
        const response = await apiFetch(
          `${CONFIG.getBackendUrl(window.location.hostname)}/api/volume-markers?days=3&symbol=${encodeURIComponent(symbol)}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.markers && data.markers.length > 0) {
            const marketStore = useMarketStore.getState();
            // Get existing non-volume markers
            const nonVolumeMarkers = marketStore.signalMarkers.filter(
              m => m.type !== 'volume_spike_bullish' && m.type !== 'volume_spike_bearish'
            );
            // Merge with fetched volume markers
            marketStore.setSignalMarkers([...nonVolumeMarkers, ...data.markers]);
            console.log(`📊 [VOLUME MARKERS] Loaded ${data.count} volume markers from API`);
          }
        }
      } catch (error) {
        console.error('❌ Failed to fetch volume markers:', error);
      }
    };

    // Keep PV markers in sync with WCC load timing instead of waiting several seconds.
    const timeoutId = setTimeout(fetchVolumeMarkers, 250);
    return () => clearTimeout(timeoutId);
  }, [symbol, candles.length]);

  // Load settings on startup to sync enabled states to stores
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Fetch pinning volume settings and sync ALL settings to the store
        const pvResponse = await apiFetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/pinning-volume-settings`);
        if (pvResponse.ok) {
          const pvData = await pvResponse.json();
          useSettingsStore.getState().setPinningVolumeSettings(pvData);
        }

        // Fetch whole candle settings and sync ALL settings to the store
        const wcResponse = await apiFetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/strategy/settings`);
        if (wcResponse.ok) {
          const wcData = await wcResponse.json();
          // Sync full settings to the store (not just enabled)
          useSettingsStore.getState().setWholeCandleSettings(wcData);
        }

        // Fetch per-symbol WCC settings and populate perSymbolWCC
        const perSymbolResponse = await apiFetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/strategy/settings/per-symbol`);
        if (perSymbolResponse.ok) {
          const perSymbolData = await perSymbolResponse.json();
          // perSymbolData is keyed by base symbol (e.g., MNQ, NQ, BTC-PERP.VEST)
          const store = useSettingsStore.getState();
          for (const [symbol, settings] of Object.entries(perSymbolData)) {
            if (settings && typeof settings === 'object') {
              store.setWholeCandleForSymbol(symbol, settings as any);
            }
          }
          console.log('[SETTINGS] Loaded per-symbol WCC settings:', Object.keys(perSymbolData));
        }

        // Fetch per-symbol PV settings and populate perSymbolPV
        const perSymbolPVResponse = await apiFetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/strategy/settings/per-symbol-pv`);
        if (perSymbolPVResponse.ok) {
          const perSymbolPVData = await perSymbolPVResponse.json();
          const pvStore = useSettingsStore.getState();
          for (const [sym, settings] of Object.entries(perSymbolPVData)) {
            if (settings && typeof settings === 'object') {
              pvStore.setPinningVolumeForSymbol(sym, settings as any);
            }
          }
          console.log('[SETTINGS] Loaded per-symbol PV settings:', Object.keys(perSymbolPVData));
        }

        // Fetch trailing pullback settings and sync to store
        const tpResponse = await apiFetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-pullback-settings`);
        if (tpResponse.ok) {
          const tpData = await tpResponse.json();
          const tpSettings = {
            enabled: tpData.enabled ?? true,
            confidenceThreshold: tpData.confidenceThreshold ?? 60,
            includeContinuation: tpData.includeContinuation ?? false,
            requireWccAlignment: tpData.requireWccAlignment ?? true,
            minTrailingBodyPct: tpData.minTrailingBodyPct ?? 5,
            updatedAt: tpData.updatedAt ?? Date.now(),
          };
          useSettingsStore.getState().setTrailingPullbackSettings(tpSettings);
          console.log('[SETTINGS] Loaded trailing pullback settings: enabled=' + tpSettings.enabled);
        }

        // Fetch magnet candle settings (global) and sync to store
        const mcResponse = await apiFetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/magnet-candle-settings`);
        if (mcResponse.ok) {
          const mcData = await mcResponse.json();
          useSettingsStore.getState().setMagnetCandleSettings(mcData);
        }

        // Fetch per-symbol magnet candle settings for current symbol
        const baseSymbol = normalizeToBase(symbol);
        const mcSymResponse = await apiFetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/magnet-candle-settings/${baseSymbol}`);
        if (mcSymResponse.ok) {
          const mcSymData = await mcSymResponse.json();
          useSettingsStore.getState().setMagnetCandleForSymbol(baseSymbol, mcSymData);
        }
      } catch (error) {
        console.error('Failed to load strategy settings:', error);
      }
    };

    loadSettings();
  }, []); // Run once on mount


  // Hydrate candles from BacktestPlayer on component mount (for tab switching)
  useEffect(() => {
    if (dataMode === 'backtest' && candles.length === 0) {
      const backtestPlayer = getBacktestPlayer();
      const currentCandles = backtestPlayer.getCurrentCandles();

      if (currentCandles.length > 0) {
        console.log(`🔄 [HYDRATE] Restoring ${currentCandles.length} candles from BacktestPlayer`);
        // Sort candles by timestamp before setting to avoid data ordering errors
        const sortedCandles = [...currentCandles].sort((a, b) => a.timestamp - b.timestamp);
        useMarketStore.getState().setCandles(sortedCandles);
      }
    }
  }, [dataMode]); // Run when mode changes or on mount

  // Load state from URL on mount (wait for connection first)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlMode = params.get('mode');
    const urlStartDate = params.get('startDate');
    const urlEndDate = params.get('endDate');
    const urlPreset = params.get('preset');

    if (urlMode === 'backtest' && urlStartDate && urlEndDate && connectionStatus === 'connected') {
      try {
        const startDate = new Date(parseInt(urlStartDate));
        const endDate = new Date(parseInt(urlEndDate));
        const preset = (urlPreset as DateRangePreset) || 'custom';

        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const config = { startDate, endDate, preset };
          setBacktestConfig(config);
          wsService.switchToBacktestMode(symbol, startDate, endDate);
        }
      } catch (error) {
        console.error('[URL STATE] Failed to parse dates:', error);
      }
    }
  }, [connectionStatus, symbol]);

  // Sync URL when mode changes (skip on initial mount to preserve URL params)
  const hasLoadedFromUrlRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUrlParams = params.has('mode');

    if (hasUrlParams && !hasLoadedFromUrlRef.current) {
      return;
    }

    if (connectionStatus === 'connected' && !hasLoadedFromUrlRef.current) {
      hasLoadedFromUrlRef.current = true;
    }

    if (dataMode === 'backtest' && backtestConfig) {
      params.set('mode', 'backtest');
      params.set('startDate', backtestConfig.startDate.getTime().toString());
      params.set('endDate', backtestConfig.endDate.getTime().toString());
      params.set('preset', backtestConfig.preset);
    } else if (dataMode === 'live' && hasLoadedFromUrlRef.current) {
      params.delete('mode');
      params.delete('startDate');
      params.delete('endDate');
      params.delete('preset');
    }

    const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [dataMode, backtestConfig, connectionStatus]);

  // Helper function to clear all Ctrl+click order lines
  const clearAllOrderLines = () => {
    // Simply clear state - React OrderLine components handle the visual lines
    // No native TradingView price lines to remove
    setOrderLines([]);
  };

  // Reset data when dataMode changes
  useEffect(() => {
    if (previousDataModeRef.current !== dataMode) {
      // Clear initial load flag to force setData on next candle batch
      initialLoadRef.current = false;
      lastCandleCountRef.current = 0;
      emaDataSetRef.current = false; // Reset EMA data tracking on mode change
      // Clear initialized chart state for this symbol (all timeframes) to force range reset
      for (const key of initializedCharts) {
        if (key.startsWith(`${symbol}:`)) {
          initializedCharts.delete(key);
        }
      }
      // Clear dangling order lines when mode changes
      clearAllOrderLines();
      previousDataModeRef.current = dataMode;
    }
  }, [dataMode, symbol]);

  // Save showOrderMarkers preference to server
  useEffect(() => {
    updatePreference('chart', { showOrderMarkers });
  }, [showOrderMarkers, updatePreference]);

  // Reset data when symbol changes
  useEffect(() => {
    if (previousSymbolRef.current !== symbol) {
      const prevSymbol = previousSymbolRef.current;
      // Set loading state
      setIsLoadingData(true);
      // Clear initial load flag to force setData on next candle batch
      initialLoadRef.current = false;
      lastCandleCountRef.current = 0;
      emaDataSetRef.current = false; // Reset EMA data tracking on symbol change
      // Clear initialized chart state for previous symbol (all timeframes)
      for (const key of initializedCharts) {
        if (key.startsWith(`${prevSymbol}:`)) {
          initializedCharts.delete(key);
        }
      }
      // Reset ATR value
      setCurrentATR(0);
      // Clear dangling order lines when symbol changes
      clearAllOrderLines();

      // Note: Y-axis scaling now handled in candle effect with fixed range per symbol
      // (autoScale is set to false there, setVisibleRange is called)

      previousSymbolRef.current = symbol;
    }
  }, [symbol]);

  // Clear loading state when data arrives
  useEffect(() => {
    if (candles && candles.length > 0 && isLoadingData) {
      setIsLoadingData(false);
    }
  }, [candles, isLoadingData]);

  // Convert UTC timestamp to PST and format
  const formatPSTTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  /**
   * The completed part of the series, aggregated to the selected timeframe,
   * with ATR/EMA/VWAP derived from it.
   *
   * This is the expensive half — a pass over every loaded candle, two Maps, a
   * sort, and four series of points. It used to run on EVERY tick because its
   * dependency was the candle array, whose identity changes on every tick;
   * with 30 days loaded that was the single biggest cost behind #344. Keyed on
   * `historyCandles` it now runs when a new bar opens, roughly once every
   * couple of minutes.
   *
   * `useBackendEmas` is decided here and passed down rather than re-derived,
   * so the snapshot and the fold below can never end up on opposite branches.
   */
  const seriesSnapshot = React.useMemo(
    () =>
      computeSeries(
        historyCandles,
        TIMEFRAME_MINUTES[selectedTimeframe] * 60 * 1000,
        selectedTimeframe === '2m',
        backendEmasAvailableForHistory(historyCandles)
      ),
    [historyCandles, selectedTimeframe]
  );

  /**
   * The snapshot plus the bar still forming — what the chart actually draws.
   *
   * The forming candle can only touch the final aggregated bar and the final
   * point of each series, so this is O(1) arithmetic over a flat copy of each
   * output array. `candleSeries.diff.test.ts` asserts the result is identical
   * to recomputing the whole thing from scratch.
   */
  const { aggregatedCandles, atrData, ema9Data, ema21Data, ema200Data, vwapData } = React.useMemo(
    () => foldFormingCandle(seriesSnapshot, formingCandle),
    [seriesSnapshot, formingCandle]
  );

  // Vest NQ-PERP volume pane: MNQ/NQ only, Levels menu "Vest NQ vol" (Clean
  // hides it with the volume studies). One time per main-chart candle so the
  // pane's logical indices match the main chart's.
  const isVestSymbol = isVestVolumeSymbol(symbol);
  const showVestVolume = levelVisibility.vestVolume && isVestSymbol;
  const showVestSpikes = levelVisibility.vestSpikes && isVestSymbol;
  // One feed for the pane and the whales: live room + history from the first candle.
  const showVestVolSpikes = levelVisibility.vestVolSpikes && isVestSymbol;
  const showCmeVolSpikes = levelVisibility.cmeVolSpikes;
  const showCmeCandleSpikes = levelVisibility.cmeSpikes;
  const showBothSpikes = levelVisibility.bothSpikes && isVestSymbol;
  // 🐱 Vest bar sticks out above the CME bar in the volume pane (utils/vestCmeCat), display only.
  const showVestCat = levelVisibility.vestCat && isVestSymbol;
  // 🪝 wick rejection, possible reversal (utils/wickRejection), any symbol, display only.
  const showWick = !!levelVisibility.wickRejection;
  const showCandleSpikes = showVestSpikes || showCmeCandleSpikes || showBothSpikes || showVestCat || showWick;
  // 🦈 / 🐋 on the same bar make a 🪝 strong, so the wick needs the rel vols too.
  const needCmeRel = showCmeVolSpikes || showCmeCandleSpikes || showBothSpikes || showWick;
  // Vest NQ-PERP candles over the CME candles (the toggle on the price pane, off by default).
  const showVestCandles = useVestCandleOverlayStore((s) => s.enabled) && isVestSymbol;
  const vestFeedOn = showVestVolume || showVestSpikes || showVestVolSpikes || showBothSpikes || showVestCat || (showWick && isVestSymbol) || showVestCandles;
  const vestScale = useVolumePaneSettingsStore((s) => s.vestScale);
  // A Vest chart feed symbol's own chart display settings (its settings
  // document's `chartDisplay`) override the global spike/wick rule values.
  const symbolChartDisplay = useSymbolChartDisplayStore((s) => (symbol ? s.bySymbol[symbol.toUpperCase()] : undefined));
  useEffect(() => {
    if (!symbol || !isVestFeedSymbol(symbol)) return;
    void useSymbolChartDisplayStore.getState().ensure(symbol);
    // Settings edited elsewhere (the other engine, another tab) reach an open chart.
    const t = setInterval(() => void useSymbolChartDisplayStore.getState().refresh(symbol), CHART_DISPLAY_REFRESH_MS);
    return () => clearInterval(t);
  }, [symbol]);
  const globalCmeSpikeSettings = useVolumePaneSettingsStore((s) => s.cme);
  const cmeSpikeSettings = useMemo(() => effectiveCmeSpikeSettings(globalCmeSpikeSettings, symbolChartDisplay), [globalCmeSpikeSettings, symbolChartDisplay]);
  const showSpikeMultiples = useVolumePaneSettingsStore((s) => s.showSpikeMultiples);
  // CME rel vol per closed chart bar; recomputed when a bar closes (the
  // snapshot holds completed history), never on forming ticks. "Closed" comes
  // from the forming candle (every chart bar before the one holding it), not
  // Date.now(): the snapshot is rebuilt ~1s after the boundary, so a browser
  // clock even slightly behind dropped the just-closed bar until a reload.
  const formingMs = formingCandle ? formingCandle.timestamp : null;
  // Time of day needs ~10 earlier sessions; the chart holds ~1 day (#593).
  // CME 2m volumes behind the chart (live only, once per symbol per day),
  // never added to the chart's series: they only lead the rel-vol walk.
  const baselineLive = dataMode === 'live';
  useEffect(() => {
    if (!needCmeRel || !baselineLive || !symbol) return;
    const ensure = () => void useCmeBaselineHistoryStore.getState().ensure(symbol);
    ensure();
    // No-op while today's history is loaded; refetches after the Chicago day rolls.
    const timer = setInterval(ensure, 10 * 60_000);
    return () => clearInterval(timer);
  }, [needCmeRel, baselineLive, symbol]);
  const cmeBaselineBars = useCmeBaselineHistoryStore((s) => (needCmeRel && baselineLive ? s.bySymbol[symbol]?.bars : undefined));
  // Once per bar close (~5 ms with ~12 sessions of history, measured).
  const cmeRel = useMemo(() => {
    if (!needCmeRel) return null;
    const tf = TIMEFRAME_MINUTES[selectedTimeframe];
    const chartBars = seriesSnapshot.aggregatedCandles.map((c) => ({ time: c.time as number, volume: c.volume || 0 }));
    return closedCmeRelVols(withBaselineHistory(chartBars, cmeBaselineBars, tf), formingMs, tf, cmeSpikeSettings, Date.now(), {
      fromSec: chartBars[0]?.time,
    });
  }, [needCmeRel, seriesSnapshot, formingMs, selectedTimeframe, cmeSpikeSettings, cmeBaselineBars]);
  useEffect(() => useRelVolStatusStore.getState().setCme(summarizeRelVols(cmeRel)), [cmeRel]);
  // 🐱: raw CME volume per closed chart bar, same closure as cmeRel.
  const catCmeVols = useMemo(
    () =>
      showVestCat
        ? closedCmeVolumes(seriesSnapshot.aggregatedCandles.map((c) => ({ time: c.time as number, volume: c.volume || 0 })), formingMs, TIMEFRAME_MINUTES[selectedTimeframe])
        : null,
    [showVestCat, seriesSnapshot, formingMs, selectedTimeframe],
  );
  // 🪝 geometry, swept levels and lifecycle over the CLOSED chart bars (same
  // closure as cmeRel: the forming candle's chart bar never counts). Runs when
  // a bar closes or a rule setting changes; the spike / delta grading is cheap
  // and happens in the candle-marker feed below.
  const globalWickMinPct = useWickRejectionSettingsStore((s) => s.minWickPct);
  const globalWickMinAtr = useWickRejectionSettingsStore((s) => s.minWickAtr);
  const { minWickPct: wickMinPct, minWickAtr: wickMinAtr } = effectiveWickRule({ minWickPct: globalWickMinPct, minWickAtr: globalWickMinAtr }, symbolChartDisplay);
  const wickConfirmBars = useWickRejectionSettingsStore((s) => s.confirmBars);
  const wickBase = useMemo(() => {
    if (!showWick) return null;
    // Vest feeds: the drawn continuous-open bars (the glyph sits on them); CME: the raw bars, same array.
    const bars = closedWickBars(
      wickInputBars(
        symbol,
        seriesSnapshot.aggregatedCandles.map((c) => ({ time: c.time as number, open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume || 0 })),
      ),
      formingMs,
      TIMEFRAME_MINUTES[selectedTimeframe],
    );
    const vwap = new Map(seriesSnapshot.vwapData.map((p) => [p.time as number, p.value]));
    return computeWickRejections(bars, { minWickPct: wickMinPct, minWickAtr: wickMinAtr, confirmBars: wickConfirmBars }, vwap);
  }, [showWick, symbol, seriesSnapshot, formingMs, selectedTimeframe, wickMinPct, wickMinAtr, wickConfirmBars]);
  // Bars drawn brighter in the volume pane (CME volume spikes toggle).
  const cmeSpikeKey = useMemo(() => {
    if (!cmeRel || !showCmeVolSpikes) {
      cmeSpikeTimesRef.current = new Set();
      return '';
    }
    const times = [...cmeRel.values()].filter((r) => r.relVol >= cmeSpikeSettings.threshold).map((r) => r.time);
    cmeSpikeTimesRef.current = new Set(times);
    return times.join(',');
  }, [cmeRel, cmeSpikeSettings.threshold, showCmeVolSpikes]);
  const aggCandlesRef = useRef(aggregatedCandles);
  aggCandlesRef.current = aggregatedCandles;
  // Overlays on the main chart must use the chart timeframe's bars: a series
  // with any other time point adds it to the main time scale and shifts every
  // logical index the volume pane is synced by (raw 2m candles on a 5m chart
  // did exactly that).
  const donchianCandles = useMemo<Candle[]>(
    () =>
      aggregatedCandles
        .filter((c) => typeof c.time === 'number')
        .map((c) => ({ timestamp: (c.time as number) * 1000, open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume || 0 })),
    [aggregatedCandles],
  );
  const vestFirstCandleMs = aggregatedCandles.length > 0 ? (aggregatedCandles[0].time as number) * 1000 : null;
  useEffect(() => {
    if (!vestFeedOn) return;
    useVestVolumeStore.getState().initialize();
    return () => useVestVolumeStore.getState().teardown();
  }, [vestFeedOn]);
  // Vest history from the first chart candle or, live, from the time-of-day
  // baseline window (~16 days, #593), whichever is earlier. The pane still
  // draws only the chart's candles; the older bars are baseline only.
  // baselineFromMs is stable for a day, so timeframe switches hit the store's
  // "already covered" short-circuit.
  useEffect(() => {
    if (!vestFeedOn || vestFirstCandleMs === null || !Number.isFinite(vestFirstCandleMs)) return;
    const from = baselineLive ? Math.min(vestFirstCandleMs, baselineFromMs(Date.now())) : vestFirstCandleMs;
    void useVestVolumeStore.getState().fetchHistory(symbol, from);
  }, [vestFeedOn, symbol, vestFirstCandleMs, baselineLive]);

  // Update current ATR value when atrData changes
  useEffect(() => {
    if (atrData.length > 0) {
      const finalATR = atrData[atrData.length - 1].value;
      setCurrentATR(finalATR);
    }
  }, [atrData]);

  // Update countdown timer
  useEffect(() => {
    const updateCountdown = () => {
      if (!candles || candles.length === 0) {
        setCountdown('--:--');
        return;
      }

      const intervalMs = TIMEFRAME_MINUTES[selectedTimeframe] * 60 * 1000;

      // In backtest mode, countdown doesn't make sense - show fixed duration
      if (dataMode === 'backtest') {
        const minutes = Math.floor(intervalMs / 60000);
        setCountdown(`${minutes}:00`);
        return;
      }

      // In live mode but market closed - show market status
      if (dataMode === 'live' && !isMarketOpen && !isCrypto) {
        setCountdown('CLOSED');
        return;
      }

      // In live mode with market open - calculate time remaining until next bar
      const now = Date.now();
      const currentBarStart = Math.floor(now / intervalMs) * intervalMs;
      const nextBarStart = currentBarStart + intervalMs;
      const remaining = nextBarStart - now;

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setCountdown(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateCountdown();

    // Only update timer in live mode when market is open
    if (dataMode === 'live' && (isMarketOpen || isCrypto)) {
      const timer = setInterval(updateCountdown, 1000);
      return () => clearInterval(timer);
    }
  }, [candles, selectedTimeframe, dataMode, isMarketOpen, isCrypto]);

  // Update countdown position (X and Y) to track current candle and price
  useEffect(() => {
    const updateCountdownPosition = () => {
      if (!chartRef.current || !candlestickSeriesRef.current || !currentPrice || !candles || candles.length === 0) {
        setCountdownYPosition(null);
        setCountdownXPosition(null);
        return;
      }

      try {
        // Get the Y coordinate of the current price
        const y = candlestickSeriesRef.current.priceToCoordinate(currentPrice);
        if (y !== null && y !== undefined) {
          setCountdownYPosition(y);
        } else {
          setCountdownYPosition(null);
        }

        // Get the X coordinate of the current (last) candle
        // Must align to the selected timeframe boundary (raw candles are 2-min, but chart shows aggregated)
        const lastCandle = candles[candles.length - 1];
        const intervalMs = TIMEFRAME_MINUTES[selectedTimeframe] * 60 * 1000;
        const alignedTimestamp = Math.floor(lastCandle.timestamp / intervalMs) * intervalMs;
        const timeScale = chartRef.current.timeScale();
        // Convert aligned timestamp (ms) to seconds for lightweight-charts
        const x = timeScale.timeToCoordinate(alignedTimestamp / 1000 as any);
        if (x !== null && x !== undefined) {
          // Position slightly to the right of the candle (add offset for candle width)
          setCountdownXPosition(x + 15);
        } else {
          setCountdownXPosition(null);
        }
      } catch {
        setCountdownYPosition(null);
        setCountdownXPosition(null);
      }
    };

    updateCountdownPosition();

    // Subscribe to visible range changes to update position
    const chart = chartRef.current;
    if (chart) {
      chart.timeScale().subscribeVisibleLogicalRangeChange(updateCountdownPosition);
      return () => {
        chart.timeScale().unsubscribeVisibleLogicalRangeChange(updateCountdownPosition);
      };
    }
  }, [currentPrice, candles, selectedTimeframe]);

  // Lazy loading: load more historical data when user scrolls to the left edge
  // Use separate selectors to avoid creating new objects on each render
  const isLoadingOlderData = useMarketStore((state) => state.isLoadingOlderData);
  const hasMoreOlderData = useMarketStore((state) => state.hasMoreOlderData);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !candles.length || dataMode === 'backtest') return;

    const checkScrollPosition = () => {
      if (isLoadingOlderData || !hasMoreOlderData) return;

      const logicalRange = chart.timeScale().getVisibleLogicalRange();
      if (!logicalRange) return;

      // If the visible range's left edge is within 50 bars of the start of loaded data, load more
      // logicalRange.from is the leftmost visible bar index (0-based from oldest loaded candle)
      const loadThreshold = 50;
      if (logicalRange.from < loadThreshold) {
        // Get oldest candle timestamp to request data before it
        const oldestCandle = candles[0];
        if (oldestCandle) {
          console.log(`📜 Lazy loading: visible range near left edge (from=${logicalRange.from.toFixed(0)}), loading older data before ${new Date(oldestCandle.timestamp).toISOString()}`);
          useMarketStore.getState().setLoadingOlderData(true);
          wsService.requestHistoricalDataBefore(symbol, oldestCandle.timestamp, 720);
        }
      }
    };

    chart.timeScale().subscribeVisibleLogicalRangeChange(checkScrollPosition);
    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(checkScrollPosition);
    };
  }, [candles, symbol, dataMode, isLoadingOlderData, hasMoreOlderData]);

  // Prepending older candles shifts every logical index right, so the view that
  // was at the oldest bar suddenly has history to its left again. Recompute on
  // candle-count changes so the "go to oldest" button reappears without waiting
  // for the user to pan.
  useEffect(() => {
    const timeScale = chartRef.current?.timeScale();
    if (!timeScale) return;
    setAtOldestLoadedBar(isAtOldestLoadedBar(timeScale.getVisibleLogicalRange()?.from));
  }, [candles.length]);

  // ===========================================================================
  // Go-to-date: long-press the TIME AXIS to open a calendar (issue #340)
  // ===========================================================================

  /**
   * Is this client point on the time axis strip (and not on the corner cell
   * under the price scale)?
   *
   * Measured from the chart rather than from the DOM: lightweight-charts renders
   * the axis into canvases inside a table it owns, so there is no stable element
   * to hit-test. `timeScale().height()` and `priceScale('right').width()` are
   * public API and stay correct across font/size/option changes.
   */
  const isPointOnTimeAxis = useCallback((clientX: number, clientY: number): boolean => {
    const el = chartContainerRef.current;
    const chart = chartRef.current;
    if (!el || !chart) return false;
    const axisHeight = chart.timeScale().height();
    if (!axisHeight || axisHeight <= 0) return false;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (y < rect.height - axisHeight || y > rect.height) return false;
    // The bottom-right corner belongs to the price scale, not the time axis.
    const priceScaleWidth = chart.priceScale('right').width();
    return x >= 0 && x <= rect.width - priceScaleWidth;
  }, []);

  /**
   * Move the viewport so `timestampMs` is visible.
   *
   * Deferred a frame on purpose. Logical indices are positions in the series the
   * chart currently holds, and the effect that pushes prepended candles into
   * `setData` is declared further down this component — so on the render where
   * older history lands, this effect runs BEFORE the chart has the new bars.
   * Scrolling then would aim at indices that shift underneath us the moment
   * setData runs. A rAF puts the jump after every passive effect has flushed,
   * and the index is read from the store inside the callback so it matches what
   * the chart is holding by then.
   */
  const scrollChartToTimestamp = useCallback((timestampMs: number) => {
    if (goToDateScrollRafRef.current !== null) cancelAnimationFrame(goToDateScrollRafRef.current);
    goToDateScrollRafRef.current = requestAnimationFrame(() => {
      goToDateScrollRafRef.current = null;
      const timeScale = chartRef.current?.timeScale();
      const series = useMarketStore.getState().candles;
      if (!timeScale || series.length === 0) return;
      // Same binary search the diamond overlay's OHLC lookup uses, asked for the
      // index instead of the bar: a picked midnight normally falls inside a
      // session gap, so "the first bar at or after this instant" is the answer,
      // and logical indices are what the time scale wants (see chartEdges).
      const index = Math.min(findBarIndexAtOrAfter(series, timestampMs), series.length - 1);
      timeScale.setVisibleLogicalRange(
        targetLogicalRange(index, timeScale.getVisibleLogicalRange())
      );
    });
  }, []);

  useEffect(
    () => () => {
      if (goToDateScrollRafRef.current !== null) cancelAnimationFrame(goToDateScrollRafRef.current);
    },
    []
  );

  const closeGoToDate = useCallback(() => {
    // Set the ref synchronously, not from an effect: the popup dismisses on a
    // capture-phase pointerdown, and the chart's own pointerdown listener runs
    // later in the SAME event. If the ref still said "open", that press would be
    // ignored and re-opening would need a second long-press.
    goToDateAnchorRef.current = false;
    setGoToDateAnchor(null);
    setGoToDatePending(null);
    setGoToDateFetch(null);
  }, []);

  const handleGoToDatePick = useCallback(
    (resolution: PickResolution, day: DayKey) => {
      if (resolution.kind === 'unavailable') return;
      if (resolution.kind === 'scroll') {
        scrollChartToTimestamp(resolution.timestamp);
        closeGoToDate();
        return;
      }
      // Not loaded yet: hand the target to the paging driver below, which walks
      // the SAME lazy-load path the left-edge scroll uses. The popup stays open
      // showing progress until it lands or honestly gives up.
      setGoToDateFetch({ day, pages: 0, status: 'fetching' });
      setGoToDatePending({ day, timestamp: resolution.timestamp, pages: 0, lastOldest: null });
    },
    [scrollChartToTimestamp, closeGoToDate]
  );

  /**
   * Long-press detection on the time axis.
   *
   * Drag-to-pan is the thing this must not break, and it is protected two ways:
   * these handlers never call `preventDefault` or `stopPropagation`, so
   * lightweight-charts' own axis drag runs exactly as before; and any movement
   * past LONG_PRESS_MOVE_THRESHOLD (10px) before the 500ms timer fires kills the
   * timer, so a press that becomes a drag pans and never opens the popup.
   *
   * Pointer events rather than touch events so one code path covers mouse, pen
   * and touch. Move/up are on `window` so a drag that leaves the axis — which is
   * most of them — still cancels.
   */
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;

    const cancel = () => {
      if (goToDateTimerRef.current) {
        clearTimeout(goToDateTimerRef.current);
        goToDateTimerRef.current = null;
      }
      goToDatePressRef.current = null;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!e.isPrimary || goToDateAnchorRef.current) return;
      if (!isPointOnTimeAxis(e.clientX, e.clientY)) return;
      goToDatePressRef.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
      goToDateTimerRef.current = setTimeout(() => {
        goToDateTimerRef.current = null;
        const press = goToDatePressRef.current;
        goToDatePressRef.current = null;
        if (!press) return;
        // Open on the month the user is already looking at.
        const visibleTo = chartRef.current?.timeScale().getVisibleRange()?.to;
        const focusMs = typeof visibleTo === 'number' ? visibleTo * 1000 : null;
        goToDateAnchorRef.current = true;
        setGoToDateAnchor({ x: press.x, y: press.y, focusMs });
        if (navigator.vibrate) navigator.vibrate(50);
      }, LONG_PRESS_DURATION);
    };

    const onPointerMove = (e: PointerEvent) => {
      const press = goToDatePressRef.current;
      if (!press || e.pointerId !== press.pointerId) return;
      const dx = e.clientX - press.x;
      const dy = e.clientY - press.y;
      if (Math.sqrt(dx * dx + dy * dy) > LONG_PRESS_MOVE_THRESHOLD) cancel();
    };

    el.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', cancel);
    window.addEventListener('pointercancel', cancel);
    return () => {
      cancel();
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', cancel);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [isPointOnTimeAxis, LONG_PRESS_DURATION, LONG_PRESS_MOVE_THRESHOLD]);

  /**
   * Paging driver for a picked date that is older than the loaded series.
   *
   * Deliberately drives the EXISTING lazy-load path — the same
   * `requestHistoricalDataBefore` + `prependCandles` round trip the left-edge
   * scroll handler uses — one page at a time, rather than inventing a
   * fetch-a-date-range mechanism the backend does not have.
   *
   * It stops and says so in three cases instead of landing somewhere wrong:
   * the engine reports no more older data, a page comes back without moving the
   * oldest bar (a stall), or the page cap is hit.
   */
  useEffect(() => {
    if (!goToDatePending) return;
    if (candles.length === 0) return;

    const oldest = candles[0].timestamp;

    if (oldest <= goToDatePending.timestamp) {
      // Landed. Close the popup rather than leaving it covering the bars the
      // user just travelled to see.
      scrollChartToTimestamp(goToDatePending.timestamp);
      closeGoToDate();
      return;
    }

    // A page is in flight; wait for prependCandles to re-run this effect.
    if (isLoadingOlderData) return;

    const stalled = goToDatePending.pages > 0 && goToDatePending.lastOldest === oldest;
    if (!hasMoreOlderData || stalled) {
      setGoToDateFetch({
        day: goToDatePending.day,
        pages: goToDatePending.pages,
        status: 'exhausted',
      });
      setGoToDatePending(null);
      return;
    }

    if (goToDatePending.pages >= GO_TO_DATE_MAX_PAGES) {
      setGoToDateFetch({ day: goToDatePending.day, pages: goToDatePending.pages, status: 'capped' });
      setGoToDatePending(null);
      return;
    }

    const nextPage = goToDatePending.pages + 1;
    setGoToDatePending({ ...goToDatePending, pages: nextPage, lastOldest: oldest });
    setGoToDateFetch({ day: goToDatePending.day, pages: nextPage, status: 'fetching' });
    useMarketStore.getState().setLoadingOlderData(true);
    wsService.requestHistoricalDataBefore(symbolRef.current, oldest, 720);
  }, [
    goToDatePending,
    candles,
    isLoadingOlderData,
    hasMoreOlderData,
    scrollChartToTimestamp,
    closeGoToDate,
    GO_TO_DATE_MAX_PAGES,
  ]);

  // Watchdog: a request that never answers would otherwise leave the popup
  // saying "loading…" forever. Re-armed on every page, so only a genuinely dead
  // request trips it.
  useEffect(() => {
    if (!goToDatePending || !isLoadingOlderData) return;
    const timer = setTimeout(() => {
      setGoToDateFetch({
        day: goToDatePending.day,
        pages: goToDatePending.pages,
        status: 'stalled',
      });
      setGoToDatePending(null);
      useMarketStore.getState().setLoadingOlderData(false);
    }, GO_TO_DATE_PAGE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [goToDatePending, isLoadingOlderData, GO_TO_DATE_PAGE_TIMEOUT_MS]);

  /** Span of the loaded series, for the popup's scroll-vs-fetch decision. */
  const goToDateLoadedSpan = useMemo<LoadedSpan | null>(
    () =>
      candles.length > 0
        ? { first: candles[0].timestamp, last: candles[candles.length - 1].timestamp }
        : null,
    [candles]
  );

  // Update candle range marker for current candle only
  useEffect(() => {
    const updateCurrentCandleRangeMarker = () => {
      // Don't show range marker in backtest mode
      if (dataMode === 'backtest') {
        // Clear any existing markers
        candleRangeMarkersRef.current.forEach(marker => marker.remove());
        candleRangeMarkersRef.current.clear();
        return;
      }

      if (!chartContainerRef.current || !chartRef.current || !candlestickSeriesRef.current) {
        return;
      }

      // Clear existing marker
      candleRangeMarkersRef.current.forEach(marker => marker.remove());
      candleRangeMarkersRef.current.clear();

      // Get all candle data
      const allData = candlestickSeriesRef.current.data() as Array<CandlestickData & { time: Time }>;
      if (!allData || allData.length === 0) {
        return;
      }

      // Get the last (current) candle
      const currentCandle = allData[allData.length - 1];
      // CME: the drawn bar to the quarter point (unchanged). Vest feeds: the
      // raw aggregated bar (the drawn one is widened by continuous open) at
      // the symbol's tick (#628).
      const rawCandles = aggCandlesRef.current;
      const rangeText = candleRangeLabel(symbolRef.current, currentCandle, rawCandles.length > 0 ? rawCandles[rawCandles.length - 1] : null);

      // Get coordinates for the current candle
      const timeCoordinate = chartRef.current.timeScale().timeToCoordinate(currentCandle.time);
      const priceCoordinate = candlestickSeriesRef.current.priceToCoordinate(currentCandle.low);

      if (timeCoordinate === null || priceCoordinate === null) {
        return;
      }

      // Create marker element for current candle - simple text label
      const marker = document.createElement('div');
      marker.style.cssText = `
        position: absolute;
        left: ${timeCoordinate - 10}px;
        top: ${priceCoordinate + 4}px;
        font-size: 9px;
        color: rgba(79, 195, 247, 0.8);
        pointer-events: none;
        white-space: nowrap;
        font-weight: 500;
        font-family: var(--font-mono), monospace;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
        z-index: 50;
      `;
      marker.textContent = rangeText;

      chartContainerRef.current.appendChild(marker);
      candleRangeMarkersRef.current.set(currentCandle.time as number, marker);
    };

    // Store function in ref so it can be called from other places
    updateRangeMarkerRef.current = updateCurrentCandleRangeMarker;

    // Update marker after data changes
    const timer = setTimeout(() => {
      updateCurrentCandleRangeMarker();
    }, 100);

    return () => clearTimeout(timer);
  }, [aggregatedCandles]);

  useEffect(() => {
    console.log('🎨 [CHART INIT] Initializing chart component');

    if (!chartContainerRef.current) {
      console.log('⚠️ [CHART INIT] Container ref not ready');
      return;
    }

    console.log('🎨 [CHART INIT] Creating chart instance');

    // Get theme colors from CSS variables
    const rootStyles = getComputedStyle(document.documentElement);
    const getThemeColor = (varName: string) => rootStyles.getPropertyValue(varName).trim();

    // Create chart
    const isMobile = window.innerWidth <= 768;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      layout: {
        background: { color: getThemeColor('--theme-chart-background') || '#0a0e27' },
        textColor: getThemeColor('--theme-chart-text') || '#d1d4dc',
        fontSize: isMobile ? 10 : 12, // Smaller font for axis labels on mobile
      },
      grid: {
        vertLines: { color: 'transparent' },
        horzLines: { color: 'transparent' },
      },
      crosshair: {
        mode: isMobile ? 1 : 0, // Hide crosshair on mobile to prevent tooltip
      },
      localization: {
        timeFormatter: (time: Time) => {
          const date = new Date((time as number) * 1000);
          return date.toLocaleString('en-US', {
            timeZone: 'America/Los_Angeles',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }) + ' PST';
        },
      },
      rightPriceScale: {
        borderColor: getThemeColor('--theme-chart-border') || '#2a3150',
      },
      timeScale: {
        borderColor: getThemeColor('--theme-chart-border') || '#2a3150',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: isMobile ? 5 : 12,
        barSpacing: isMobile ? 6 : 8,
        fixLeftEdge: false,
        fixRightEdge: false,
        tickMarkFormatter: (time: Time) => {
          const date = new Date((time as number) * 1000);
          return date.toLocaleTimeString('en-US', {
            timeZone: 'America/Los_Angeles',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        },
      },
    });

    chartRef.current = chart;

    // Edge tracking for the SR nearest-N filter and for both jump-to-edge
    // buttons (scroll + touch + any pan). One subscription covers both edges.
    const onSrRangeChange = () => {
      // scrollPosition() = distance from right edge to latest bar (in bars)
      // At live edge it's ~0; when panned left into history it goes negative
      const rightOffset = chart.timeScale().scrollPosition();
      setSrAtLiveEdge(rightOffset >= -3);
      // logical.from is bars from the OLDEST loaded candle, so <= 0 means the
      // start of loaded history is already on screen.
      setAtOldestLoadedBar(isAtOldestLoadedBar(chart.timeScale().getVisibleLogicalRange()?.from));
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(onSrRangeChange);

    // Add candlestick series (v5 API)
    const { minMove, precision } = getSymbolPriceFormat(symbol);
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: getThemeColor('--theme-candle-up') || '#26a69a',
      downColor: getThemeColor('--theme-candle-down') || '#ef5350',
      borderVisible: false,
      wickUpColor: getThemeColor('--theme-candle-up') || '#26a69a',
      wickDownColor: getThemeColor('--theme-candle-down') || '#ef5350',
      priceFormat: {
        type: 'price' as const,
        minMove,
        precision,
      },
    });

    candlestickSeriesRef.current = candlestickSeries;
    // Before anything attaches: every attachPrimitive on this series now goes
    // through the registry, which makes attaching idempotent and lets Clean
    // suppress primitives by class without the owners knowing (see
    // utils/primitiveRegistry.ts for why the raw API stacked duplicates).
    installPrimitiveRegistry(candlestickSeries, makeCleanSuppressor(CLEAN_PRIMITIVE_GROUPS));
    // The shared marker layout: every glyph primitive below joins it, so no two
    // candle markers overlap whichever primitive drew them. A data change (the
    // live candle's high/low moving) re-lays the pane on the next frame.
    const markerLayout = new MarkerLayoutRegistry(chartLayoutEnv(chart, candlestickSeries));
    markerLayoutRef.current = markerLayout;
    const onLayoutDataChanged = () => markerLayout.invalidate();
    candlestickSeries.subscribeDataChanged(onLayoutDataChanged);
    const unexposeMarkerLayout = import.meta.env.DEV && chartContainerRef.current
      ? exposeMarkerLayout(markerLayout, chartContainerRef.current)
      : () => {};
    // Series markers (BOS/CHoCH arrows, other-symbol tags): lightweight-charts'
    // createSeriesMarkers stacks on its own, so a layout-aware stand-in draws them.
    const seriesMarkers = new LayoutSeriesMarkersPrimitive();
    seriesMarkers.setMarkerLayout(markerLayout);
    candlestickSeries.attachPrimitive(seriesMarkers);
    seriesMarkersRef.current = seriesMarkers;
    // Trigger alert line position recalculation now that series is ready
    setChartSeriesVersion(v => v + 1);

    // Detect mobile for smaller marker sizes
    const isMobileDevice = window.innerWidth <= 768;

    // Attach skipped marker primitive for custom marker positioning
    const skippedMarkerPrimitive = new SkippedMarkerPrimitive();
    skippedMarkerPrimitive.setSize(isMobileDevice ? 4 : 6); // Smaller on mobile
    skippedMarkerPrimitive.setOffset(isMobileDevice ? 8 : 12);
    if (!drawingPrimitiveRef.current) {
      const dp = new DrawingPrimitive();
      candlestickSeries.attachPrimitive(dp);
      drawingPrimitiveRef.current = dp;
      // Mirrored into state so the interaction hook re-runs once the primitive
      // exists; a ref alone would never retrigger the effect.
      setDrawingPrimitive(dp);
    }
    skippedMarkerPrimitive.setMarkerLayout(markerLayout);
    candlestickSeries.attachPrimitive(skippedMarkerPrimitive);
    skippedMarkerPrimitiveRef.current = skippedMarkerPrimitive;

    // Attach volume marker primitive for emoji markers (fire/iceberg)
    const volumeMarkerPrimitive = new VolumeMarkerPrimitive();
    volumeMarkerPrimitive.setFontSize(isMobileDevice ? 10 : 16); // Smaller emoji on mobile
    volumeMarkerPrimitive.setOffset(isMobileDevice ? 16 : 24);
    volumeMarkerPrimitive.setMarkerLayout(markerLayout);
    candlestickSeries.attachPrimitive(volumeMarkerPrimitive);
    volumeMarkerPrimitiveRef.current = volumeMarkerPrimitive;

    // Attach Donchian marker primitive for small triangle markers
    const donchianMarkerPrimitive = new DonchianMarkerPrimitive();
    donchianMarkerPrimitive.setFontSize(isMobileDevice ? 9 : 11);
    donchianMarkerPrimitive.setMarkerLayout(markerLayout);
    candlestickSeries.attachPrimitive(donchianMarkerPrimitive);
    donchianMarkerPrimitiveRef.current = donchianMarkerPrimitive;

    // Attach signal marker primitive for confidence, ML, confluence, and profitability labels
    const signalMarkerPrimitive = new SignalMarkerPrimitive();
    signalMarkerPrimitive.setFontSize(isMobileDevice ? 7 : 9); // Smaller on mobile
    signalMarkerPrimitive.setOffset(isMobileDevice ? 24 : 20); // More padding on mobile
    // Load persisted settings from localStorage
    const savedTextOverlay = localStorage.getItem('signalMarker_showTextOverlay');
    const savedCollision = localStorage.getItem('signalMarker_collisionEnabled');
    if (savedTextOverlay !== null) {
      signalMarkerPrimitive.setShowTextOverlay(savedTextOverlay === 'true');
    }
    if (savedCollision !== null) {
      signalMarkerPrimitive.setCollisionEnabled(savedCollision === 'true');
    }
    signalMarkerPrimitive.setMarkerLayout(markerLayout);
    candlestickSeries.attachPrimitive(signalMarkerPrimitive);
    signalMarkerPrimitiveRef.current = signalMarkerPrimitive;

    // Attach news marker primitive for breaking news lightning bolts above volume
    const newsMarkerPrimitive = new NewsMarkerPrimitive();
    newsMarkerPrimitive.setFontSize(isMobileDevice ? 10 : 16); // Smaller on mobile
    newsMarkerPrimitive.setBottomOffset(isMobileDevice ? 8 : 12);
    newsMarkerPrimitive.setMarkerLayout(markerLayout);
    candlestickSeries.attachPrimitive(newsMarkerPrimitive);
    newsMarkerPrimitiveRef.current = newsMarkerPrimitive;

    // Attach calendar line primitive for upcoming event vertical lines
    const calendarLinePrimitive = new CalendarLinePrimitive();
    calendarLinePrimitive.setFontSize(isMobileDevice ? 8 : 10); // Smaller on mobile
    candlestickSeries.attachPrimitive(calendarLinePrimitive);
    calendarLinePrimitiveRef.current = calendarLinePrimitive;

    // Attach upcoming events primitive for star icons after last candle
    const upcomingEventsPrimitive = new UpcomingEventsPrimitive();
    candlestickSeries.attachPrimitive(upcomingEventsPrimitive);
    upcomingEventsPrimitiveRef.current = upcomingEventsPrimitive;

    // Gamma exposure levels. Drawn under the candles - context, not price action.
    const gexLevelsPrimitive = new GexLevelsPrimitive();
    candlestickSeries.attachPrimitive(gexLevelsPrimitive);
    gexLevelsPrimitiveRef.current = gexLevelsPrimitive;

    // Confluence zones (stacked key levels). Also under the candles.
    const confluencePrimitive = new ConfluenceZonePrimitive();
    candlestickSeries.attachPrimitive(confluencePrimitive);
    confluencePrimitiveRef.current = confluencePrimitive;

    // Attach FVG zone primitive for ICT Fair Value Gap zones
    const fvgZonePrimitive = new FVGZonePrimitive();
    const savedInvalidatedBadges = localStorage.getItem('fvgZone_showInvalidatedBadges');
    const showInvalidatedBadges = savedInvalidatedBadges === null ? true : savedInvalidatedBadges === 'true';
    fvgZonePrimitive.showInvalidatedBadges = showInvalidatedBadges;
    candlestickSeries.attachPrimitive(fvgZonePrimitive);
    fvgZonePrimitiveRef.current = fvgZonePrimitive;

    // Attach trailing candle primitive for mini-candles inside main candles
    const trailingCandlePrimitive = new TrailingCandlePrimitive();
    // Initialize with current settings value
    const initialTrailingEnabled = useSettingsStore.getState().trailingCandle.enabled;
    trailingCandlePrimitive.setEnabled(initialTrailingEnabled);
    trailingCandlePrimitive.setMinCandleWidth(4); // Hide when zoomed out
    // Set theme-aware colors (CSS vars with fallbacks)
    const trailingColors: TrailingCandleColors = {
      bullishFill: getThemeColor('--theme-trailing-up') || '#00d4ff',
      bullishForming: getThemeColor('--theme-trailing-up-forming') || '#5fffff',
      bearishFill: getThemeColor('--theme-trailing-down') || '#ff6b35',
      bearishForming: getThemeColor('--theme-trailing-down-forming') || '#ff9966',
      border: getThemeColor('--theme-trailing-border') || '#ffffff',
    };
    trailingCandlePrimitive.setColors(trailingColors);
    candlestickSeries.attachPrimitive(trailingCandlePrimitive);
    trailingCandlePrimitiveRef.current = trailingCandlePrimitive;

    // Attach volume profile primitive for horizontal histogram
    const volumeProfilePrimitive = new VolumeProfilePrimitive();
    // Initialize with current settings value
    const initialVPEnabled = useVolumeProfileStore.getState().settings.enabled;
    const vpSettings = useVolumeProfileStore.getState().settings;
    volumeProfilePrimitive.setEnabled(initialVPEnabled);
    volumeProfilePrimitive.updateOptions({
      showDelta: vpSettings.showDelta,
      showValueArea: vpSettings.showValueArea ?? true,
      showVahVal: vpSettings.showVahVal ?? true,
    });
    candlestickSeries.attachPrimitive(volumeProfilePrimitive);
    volumeProfilePrimitiveRef.current = volumeProfilePrimitive;

    // Attach cumulative delta primitive for delta line overlay
    if (vpSettings.enabled && vpSettings.showCumulativeDelta) {
      const cumulativeDeltaPrimitive = new CumulativeDeltaPrimitive();
      candlestickSeries.attachPrimitive(cumulativeDeltaPrimitive);
      cumulativeDeltaPrimitiveRef.current = cumulativeDeltaPrimitive;
    }

    // Attach price level labels primitive for left-edge labels
    const priceLevelLabelsPrimitive = new PriceLevelLabelsPrimitive();
    candlestickSeries.attachPrimitive(priceLevelLabelsPrimitive);
    priceLevelLabelsPrimitiveRef.current = priceLevelLabelsPrimitive;

    const roundLevelWarningPrimitive = new RoundLevelWarningPrimitive();
    candlestickSeries.attachPrimitive(roundLevelWarningPrimitive);
    roundLevelWarningPrimitiveRef.current = roundLevelWarningPrimitive;

    // Attach BOS label primitive: draws BOS/CHoCH text ON the level segment line
    // (line passes through the text), instead of on the diamond marker.
    const bosLabelPrimitive = new BosLabelPrimitive();
    bosLabelPrimitive.setMarkerLayout(markerLayout);
    candlestickSeries.attachPrimitive(bosLabelPrimitive);
    bosLabelPrimitiveRef.current = bosLabelPrimitive;

    // Attach trailing pullback marker primitive for custom TP triangle markers
    const trailingPullbackPrimitive = new TrailingPullbackMarkerPrimitive();
    trailingPullbackPrimitive.setFontSize(isMobileDevice ? 9 : 11);
    trailingPullbackPrimitive.setMarkerLayout(markerLayout);
    candlestickSeries.attachPrimitive(trailingPullbackPrimitive);
    trailingPullbackPrimitiveRef.current = trailingPullbackPrimitive;

    // Attach magnet candle primitive for horizontal magnet level lines
    const magnetCandlePrimitive = new MagnetCandlePrimitive();
    magnetCandlePrimitive.setMarkerLayout(markerLayout);
    candlestickSeries.attachPrimitive(magnetCandlePrimitive);
    magnetCandlePrimitiveRef.current = magnetCandlePrimitive;

    // "+N" chips for markers a bar side had no room for at this zoom, and the
    // dotted leaders from each marker's anchor (drawn under the markers).
    candlestickSeries.attachPrimitive(new MarkerChipPrimitive(markerLayout));
    candlestickSeries.attachPrimitive(new MarkerLeaderPrimitive(markerLayout));

    // Add EMA 9 series (v5 API) - using proper styling
    const ema9Series = chart.addSeries(LineSeries, {
      color: getThemeColor('--theme-ema9') || '#f50057',  // Pink
      // 9 thin against a heavier 21. The pair is read as a cross, so weight
      // says which is which without having to resolve two similar colours.
      lineWidth: 1,
      title: '',
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
      visible: true,
      priceScaleId: 'right',
    });
    ema9SeriesRef.current = ema9Series;
    // Reset EMA data tracking when new series are created (critical for React StrictMode)
    emaDataSetRef.current = false;

    // Add EMA 21 series (v5 API) - using proper styling
    const ema21Series = chart.addSeries(LineSeries, {
      color: getThemeColor('--theme-ema21') || '#ab47bc',  // Purple
      lineWidth: 3,
      title: '',
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
      visible: true,
      priceScaleId: 'right',
    });
    ema21SeriesRef.current = ema21Series;

    // Add EMA 200 series (v5 API) - backend now provides properly calculated values
    const ema200Series = chart.addSeries(LineSeries, {
      color: getThemeColor('--theme-ema200') || '#ffc107',
      lineWidth: 2,
      title: '',
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });
    ema200SeriesRef.current = ema200Series;

    // Add VWAP series (v5 API)
    const vwapSeries = chart.addSeries(LineSeries, {
      color: getThemeColor('--theme-vwap') || '#4fc3f7',
      lineWidth: 2,
      lineStyle: 2, // Dashed line
      title: '',
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });
    vwapSeriesRef.current = vwapSeries;

    // Volume series is now in a separate chart pane (created below)

    // Subscribe to crosshair move events for CandleInfoOverlay
    // Use a ref to track last hovered time to avoid unnecessary state updates
    let lastHoveredTime: number | null = null;
    chart.subscribeCrosshairMove((param) => {
      if (
        param.point === undefined ||
        !param.time ||
        param.point.x < 0 ||
        param.point.y < 0
      ) {
        // When mouse leaves chart, show latest candle (null = use default)
        if (lastHoveredTime !== null) {
          lastHoveredTime = null;
          setHoveredCandle(null);
        }
      } else {
        const currentTime = param.time as number;
        // Only update if hovering over a different candle
        if (lastHoveredTime !== currentTime) {
          lastHoveredTime = currentTime;
          const data = param.seriesData.get(candlestickSeries) as CandlestickData | undefined;
          if (data) {
            setHoveredCandle({
              time: currentTime,
              open: data.open,
              high: data.high,
              low: data.low,
              close: data.close,
              volume: 0 // Volume not available in crosshair data
            });
          } else {
            setHoveredCandle(null);
          }
        }
      }

      // DCA live hover tracking
      if (dcaModeRef.current && !dcaPinnedRef.current && param.point && !isLinePaneEvent(chart, chartContainerRef.current, param)) {
        const price = candlestickSeries.coordinateToPrice(param.point.y);
        if (price != null) {
          const sym = symbolRef.current;
          const snapped = roundToTick(price as number, sym);
          const storeState = useBrokerStore.getState();
          const activeBroker = storeState.brokers.find(b => b.id === storeState.activeBrokerId);
          const positions: BrokerPosition[] = activeBroker?.positions ?? [];
          const chartBase = normalizeToBase(sym);
          const pos = positions.find(p => p.symbol && normalizeToBase(p.symbol) === chartBase && p.netPos !== 0);
          const mktPrice = useMarketStore.getState().currentPrice;
          if (pos && mktPrice > 0) {
            const q1 = Math.abs(pos.netPos);
            const pAvg = pos.netPrice;
            const q2 = calculateDCAQuantity(pAvg, q1, snapped, mktPrice, pos.netPos > 0);
            if (q2 > 0) {
              const newAvg = (q1 * pAvg + q2 * mktPrice) / (q1 + q2);
              setDcaTarget({ price: snapped, contracts: q2, newAvg, currentPos: q1, avgPrice: pAvg });
            } else {
              setDcaTarget({ price: snapped, contracts: 0, newAvg: 0, currentPos: 0, avgPrice: 0 });
            }
              console.log(`[DEBUG:DCA] hover: price=${snapped}, contracts=${q2}, pinned=${dcaPinnedRef.current}, y=${param.point.y}`);
          }
          setDcaTargetY(param.point.y);
          setDcaTargetPrice(snapped);
        }
      }
    });

    // Add right-click context menu handler
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();

      // Disable context menu on mobile devices
      if (isMobile) {
        return;
      }

      // Get the time coordinate at the click position
      const rect = chartContainerRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      const x = e.clientX - rect.left;

      // Convert pixel coordinates to time
      const timeCoordinate = chart.timeScale().coordinateToTime(x);

      if (timeCoordinate === null) {
        return;
      }

      // Find the candle at this time
      const candleData = candlestickSeries.data() as Array<CandlestickData & { time: Time }>;

      const clickedCandle = candleData.find(c => c.time === timeCoordinate);

      if (clickedCandle) {
        const timestamp = (clickedCandle.time as number) * 1000;

        // Show context menu
        setContextMenu({
          x: e.clientX,
          y: e.clientY,
          timestamp
        });
      }
    };

    // Add event listener to chart container
    if (chartContainerRef.current) {
      chartContainerRef.current.addEventListener('contextmenu', handleContextMenu);
    }

    // Add click handler to create order lines (with Ctrl/Cmd key)
    const handleChartClick = (e: MouseEvent) => {
      const rect = chartContainerRef.current?.getBoundingClientRect();
      if (!rect || !chartRef.current) {
        return;
      }

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      // Flow line panes under the price: a click there is no price, and the
      // price pane (where the bottom-anchored markers sit) ends above them.
      const lineSpan = linePaneSpan(chartRef.current);
      if (lineSpan && y >= lineSpan.top && y < lineSpan.bottom) return;
      const chartHeight = rect.height - (lineSpan ? lineSpan.bottom - lineSpan.top : 0);

      // Check if clicking on a news marker (lightning bolt)
      if (newsMarkerPrimitiveRef.current) {
        const hitMarker = newsMarkerPrimitiveRef.current.checkClick(x, y, chartHeight);
        if (hitMarker) {
          // Show tooltip with the headline
          setNewsTooltip({
            x: e.clientX,
            y: e.clientY,
            headline: hitMarker.headline,
          });
          return;
        }
      }

      // Check if clicking on an upcoming event star
      if (upcomingEventsPrimitiveRef.current) {
        const hitEvent = upcomingEventsPrimitiveRef.current.checkClick(x, y, chartHeight);
        if (hitEvent) {
          // Show positioned tooltip with event details
          const eventTime = new Date(hitEvent.time).toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/Los_Angeles'
          });
          setEventTooltip({
            x: e.clientX,
            y: e.clientY,
            event: hitEvent.event,
            country: hitEvent.country,
            time: eventTime + ' PST'
          });
          return;
        }
      }

      // Check if clicking on a calendar line star
      if (calendarLinePrimitiveRef.current) {
        const hitLine = calendarLinePrimitiveRef.current.checkClick(x, y, chartHeight);
        if (hitLine) {
          const eventTime = new Date(hitLine.time).toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/Los_Angeles'
          });
          setEventTooltip({
            x: e.clientX,
            y: e.clientY,
            event: hitLine.event,
            country: hitLine.country,
            time: eventTime + ' PST'
          });
          return;
        }
      }

      // Hide tooltips if clicking elsewhere
      if (newsTooltip) {
        setNewsTooltip(null);
      }
      if (eventTooltip) {
        setEventTooltip(null);
      }

      // Check if clicking near price axis area (right side) for S/R level tooltip
      const priceAxisWidth = 60; // Approximate price axis width
      const chartWidth = rect.width;
      const isNearPriceAxis = x > chartWidth - priceAxisWidth - 20; // 20px margin for label area

      if (isNearPriceAxis && srEnabled && srLevels.length > 0 && candlestickSeriesRef.current) {
        // Find nearest S/R level to click Y position
        let nearestLevel: SRLevel | null = null;
        let nearestDistance = Infinity;

        for (const level of srLevels) {
          const levelY = candlestickSeriesRef.current.priceToCoordinate(level.price);
          if (levelY !== null) {
            const distance = Math.abs(y - levelY);
            if (distance < nearestDistance && distance < 20) { // 20px threshold
              nearestDistance = distance;
              nearestLevel = level;
            }
          }
        }

        if (nearestLevel) {
          // Expand the cluster that owns this level rather than the level on
          // its own, so this path and a click on the label itself open exactly
          // the same card.
          const owner = srLabelGroupsRef.current.find((g) => g.levels.includes(nearestLevel!));
          setExpandedSRKey(owner ? owner.key : srGroupKey([nearestLevel]));
          return;
        }
      }

      // Collapse the expanded S/R label if clicking elsewhere
      if (expandedSRKeyRef.current !== null) {
        setExpandedSRKey(null);
      }


      // Handle DCA calculator mode - click toggles pin
      if (dcaModeRef.current) {
        console.log(`[DEBUG:DCA] pin toggled: was=${dcaPinnedRef.current}, dcaTarget=${JSON.stringify(dcaTarget)}`);
        setDcaPinned(prev => !prev);
        return;
      }

      // Handle alert placement mode - price pane creates price alerts
      if (placementModeRef.current) {
        const price = candlestickSeriesRef.current?.coordinateToPrice(y);
        if (price) {
          const snappedPrice = roundToTick(price as number, symbol);
          // Determine if price is above or below current price
          const alertType = snappedPrice >= currentPrice ? 'price_above' : 'price_below';
          createAlert({
            symbol,
            alertType,
            price: snappedPrice,
            triggerMode: 'once_per_minute',
          }).then((newAlert) => {
            if (newAlert) {
              addToast('success', `Alert created: ${alertType === 'price_above' ? 'Above' : 'Below'} ${snappedPrice.toFixed(2)}`);
            }
          });
          exitPlacementMode();
        }
        return;
      }

      // Only create order line when Ctrl (Windows/Linux) or Cmd (Mac) is held
      if (!e.ctrlKey && !e.metaKey) {
        return;
      }

      // Get price from coordinate using the series
      const price = candlestickSeriesRef.current?.coordinateToPrice(y);

      if (price) {
        const id = `order-line-${Date.now()}`;

        // NOTE: We no longer create a native TradingView price line here
        // The React OrderLine component now renders its own horizontal line
        // This prevents orphaned price lines if the page is refreshed

        // Add order line UI (React component will draw the line)
        setOrderLines(prev => [...prev, {
          id,
          price: price as number,
          yPosition: y
        }]);
      }
    };

    // Long-press handlers for mobile order placement
    const handleTouchStart = (e: TouchEvent) => {
      // Only handle single touch
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      const rect = chartContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // A press on the time axis belongs to the go-to-date popup, not to the
      // price-preview line. Without this both long-presses fire off the same
      // touch and the user gets a calendar plus a stray order line.
      if (isPointOnTimeAxis(touch.clientX, touch.clientY)) return;

      const y = touch.clientY - rect.top;
      // Not from a flow line pane: that y is no price.
      const lineSpan = chartRef.current ? linePaneSpan(chartRef.current) : null;
      if (lineSpan && y >= lineSpan.top && y < lineSpan.bottom) return;

      // Store the starting position
      longPressStartRef.current = { x: touch.clientX, y: touch.clientY };

      // Start the long-press timer
      longPressTimerRef.current = setTimeout(() => {
        // On mobile, the TKT button on the trading panel is the dedicated shortcut for
        // dropping an order/alert preview line at the current price. Long-press is disabled
        // there to avoid accidental triggers; desktop touchscreens still get the original gesture.
        if (isMobile) return;
        // Get price from coordinate
        const price = candlestickSeriesRef.current?.coordinateToPrice(y);
        if (price) {
          // Show the preview line
          setLongPressPreview({ y, price: price as number });

          // Vibrate to provide haptic feedback (if supported)
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
        }
      }, LONG_PRESS_DURATION);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!longPressStartRef.current) return;

      const touch = e.touches[0];
      const dx = touch.clientX - longPressStartRef.current.x;
      const dy = touch.clientY - longPressStartRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Cancel long-press if finger moved too much (allow normal scrolling/panning)
      if (distance > LONG_PRESS_MOVE_THRESHOLD) {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        longPressStartRef.current = null;
        setLongPressPreview(null);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // If timer is still running, this was a quick tap (not a long press)
      const wasQuickTap = longPressTimerRef.current !== null;

      // Clear the timer if it hasn't fired yet
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      // Triple-tap detection for mobile - toggle news overlay
      if (wasQuickTap) {
        tripleTapCountRef.current += 1;

        if (tripleTapTimerRef.current) {
          clearTimeout(tripleTapTimerRef.current);
        }

        if (tripleTapCountRef.current >= 3) {
          // Triple-tap detected - toggle news overlay
          tripleTapCountRef.current = 0;
          toggleNewsOverlay();
          if (navigator.vibrate) {
            navigator.vibrate(30);
          }
          longPressStartRef.current = null;
          return;
        }

        // Reset tap count after 400ms of no taps
        tripleTapTimerRef.current = setTimeout(() => {
          tripleTapCountRef.current = 0;
        }, 400);
      }

      // Check for tap on news marker (quick tap only, not after long press)
      if (longPressStartRef.current && wasQuickTap) {
        const rect = chartContainerRef.current?.getBoundingClientRect();
        if (rect && chartRef.current && newsMarkerPrimitiveRef.current) {
          const touch = e.changedTouches[0];
          const x = touch.clientX - rect.left;
          const y = touch.clientY - rect.top;
          const lineSpan = linePaneSpan(chartRef.current);
          const chartHeight = rect.height - (lineSpan ? lineSpan.bottom - lineSpan.top : 0);

          // Check if tapping on a news marker
          const hitMarker = newsMarkerPrimitiveRef.current.checkClick(x, y, chartHeight);
          if (hitMarker) {
            setNewsTooltip({
              x: touch.clientX,
              y: touch.clientY,
              headline: hitMarker.headline,
            });
            return;
          }

          // Check if tapping on an upcoming event star
          if (upcomingEventsPrimitiveRef.current) {
            const hitEvent = upcomingEventsPrimitiveRef.current.checkClick(x, y, chartHeight);
            if (hitEvent) {
              const eventTime = new Date(hitEvent.time).toLocaleString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: 'America/Los_Angeles'
              });
              setEventTooltip({
                x: touch.clientX,
                y: touch.clientY,
                event: hitEvent.event,
                country: hitEvent.country,
                time: eventTime + ' PST'
              });
              return;
            }
          }

          // Check if tapping on a calendar line star
          if (calendarLinePrimitiveRef.current) {
            const hitLine = calendarLinePrimitiveRef.current.checkClick(x, y, chartHeight);
            if (hitLine) {
              const eventTime = new Date(hitLine.time).toLocaleString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: 'America/Los_Angeles'
              });
              setEventTooltip({
                x: touch.clientX,
                y: touch.clientY,
                event: hitLine.event,
                country: hitLine.country,
                time: eventTime + ' PST'
              });
              return;
            }
          }

          // Tap elsewhere closes tooltips
          setNewsTooltip(null);
          setEventTooltip(null);
        }
      }

      // If we have a preview (long-press succeeded), keep it visible
      // User must tap the "Add" button explicitly to create the order line
      // Preview stays visible until user taps Add or Cancel

      longPressStartRef.current = null;
    };

    if (chartContainerRef.current) {
      chartContainerRef.current.addEventListener('click', handleChartClick);
      // Touch events for mobile long-press order placement
      chartContainerRef.current.addEventListener('touchstart', handleTouchStart, { passive: true });
      chartContainerRef.current.addEventListener('touchmove', handleTouchMove, { passive: true });
      chartContainerRef.current.addEventListener('touchend', handleTouchEnd);
      chartContainerRef.current.addEventListener('touchcancel', handleTouchEnd);
    }

    // Mobile TKT button (TradingPanel) dispatches this event as a shortcut to long-press.
    // We render the same preview overlay (+ / 🔔 / × buttons) anchored at the live current price.
    const handleShowOrderPreview = () => {
      if (!candlestickSeriesRef.current) return;
      const livePrice = useMarketStore.getState().currentPrice;
      if (!livePrice) return;
      const yCoord = candlestickSeriesRef.current.priceToCoordinate(livePrice);
      if (yCoord === null || yCoord === undefined) return;
      setLongPressPreview({ y: yCoord as number, price: livePrice });
      if (navigator.vibrate) navigator.vibrate(50);
    };
    window.addEventListener('mobile-show-order-preview', handleShowOrderPreview);


    // Register range marker updater with RAF coordinator
    chartUpdatersRef.current.rangeMarker = () => updateRangeMarkerRef.current?.();

    // Recording service disabled - to re-enable, uncomment the initialization below
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let recordingInitialized = false;
    // const recordingTimeout = setTimeout(() => {
    //   const chartContainer = chartContainerRef.current;
    //   if (chartContainer) {
    //     const initResult = recordingService.initialize(chartContainer, { captureMode: 'fullpage' });
    //     if (initResult) {
    //       const captureResult = recordingService.startCapture();
    //       recordingInitialized = true;
    //     }
    //   }
    // }, 1000);

    // Handle resize using ResizeObserver (catches both window resize and sidebar toggle)
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    };

    // Use ResizeObserver to detect container size changes (e.g., when sidebar toggles)
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (chartContainerRef.current) {
        chartContainerRef.current.removeEventListener('contextmenu', handleContextMenu);
        chartContainerRef.current.removeEventListener('click', handleChartClick);
        // Remove touch event listeners
        chartContainerRef.current.removeEventListener('touchstart', handleTouchStart);
        chartContainerRef.current.removeEventListener('touchmove', handleTouchMove);
        chartContainerRef.current.removeEventListener('touchend', handleTouchEnd);
        chartContainerRef.current.removeEventListener('touchcancel', handleTouchEnd);
      }
      window.removeEventListener('mobile-show-order-preview', handleShowOrderPreview);
      // Clear any pending long-press timer
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
      // Clear any pending triple-tap timer
      if (tripleTapTimerRef.current) {
        clearTimeout(tripleTapTimerRef.current);
      }
      // Note: Order lines are now React components, not native TradingView price lines
      // They will be cleaned up automatically when the component unmounts

      // Recording service disabled
      // clearTimeout(recordingTimeout);
      // if (recordingInitialized) {
      //   recordingService.stopCapture();
      // }

      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onSrRangeChange);
      chartUpdatersRef.current.rangeMarker = null;
      // chart.remove() destroys all series and detaches primitives; drop the BOS
      // overlay primitive ref so the next chart instance starts clean.
      bosLabelPrimitiveRef.current = null;
      try { candlestickSeries.unsubscribeDataChanged(onLayoutDataChanged); } catch { /* series gone */ }
      unexposeMarkerLayout();
      markerLayout.dispose();
      markerLayoutRef.current = null;
      chart.remove();
      // Reset data loading flags when chart is destroyed
      initialLoadRef.current = false;
      lastCandleCountRef.current = 0;
      emaDataSetRef.current = false; // Reset EMA data tracking when chart is destroyed
    };
  }, []);

  // Create volume chart pane (separate TradingView chart with synced time axis)
  useEffect(() => {
    if (!volumeChartContainerRef.current || !chartRef.current) return;

    const isMobile = window.innerWidth <= 768;
    const rootStyles = getComputedStyle(document.documentElement);
    const getThemeColor = (varName: string) => rootStyles.getPropertyValue(varName).trim();

    // Create volume chart with same time scale settings
    const volumeChart = createChart(volumeChartContainerRef.current, {
      width: volumeChartContainerRef.current.clientWidth,
      height: volumeChartContainerRef.current.clientHeight,
      layout: {
        background: { color: getThemeColor('--theme-chart-background') || '#0a0e27' },
        textColor: getThemeColor('--theme-chart-text') || '#d1d4dc',
        fontSize: isMobile ? 10 : 12,
      },
      grid: {
        vertLines: { color: 'transparent' },
        horzLines: { color: 'transparent' },
      },
      crosshair: {
        mode: isMobile ? 1 : 0,
      },
      rightPriceScale: {
        borderColor: getThemeColor('--theme-chart-border') || '#2a3150',
        visible: true,
        scaleMargins: {
          top: 0.05,
          bottom: 0,
        },
      },
      timeScale: {
        borderColor: getThemeColor('--theme-chart-border') || '#2a3150',
        timeVisible: false, // Hide time labels on volume pane (shown on main chart)
        secondsVisible: false,
        rightOffset: isMobile ? 5 : 12,
        barSpacing: isMobile ? 6 : 8,
        fixLeftEdge: false,
        fixRightEdge: false,
        visible: false, // Hide the time axis completely on volume pane
      },
      handleScroll: true, // Enable scrolling - syncs bidirectionally with main chart
      handleScale: {
        axisPressedMouseMove: {
          time: true, // Enable time axis drag - syncs with main chart
          price: true, // Enable price (Y-axis) drag for volume scaling
        },
        mouseWheel: true, // Allow scroll wheel
        pinch: true, // Allow pinch zoom
      },
    });

    volumeChartRef.current = volumeChart;

    // Add volume histogram series (v5 API)
    const volumeSeries = volumeChart.addSeries(HistogramSeries, {
      color: '#26a69a',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: 'right',
      lastValueVisible: false,
      priceLineVisible: false,
    });

    volumeSeriesRef.current = volumeSeries;
    setVolumeChartReady(true);
    setVolumeSeriesVersion(v => v + 1); // Trigger volume alert position recalculation

    // Bidirectional time scale sync between main chart and volume chart
    const mainChart = chartRef.current;
    let isSyncing = false; // Prevent infinite sync loops
    let isInitializing = true; // Skip volume->main sync during initialization

    const syncMainToVolume = () => {
      if (isSyncing) return;
      isSyncing = true;
      const visibleRange = mainChart.timeScale().getVisibleLogicalRange();
      if (visibleRange) {
        volumeChart.timeScale().setVisibleLogicalRange(visibleRange);
      }
      setTimeout(() => { isSyncing = false; }, 0);
    };

    const syncVolumeToMain = () => {
      // Skip volume->main sync during initialization or when setting data
      // This prevents the volume chart's auto-fit from overriding main chart's visible range
      if (isSyncing || isInitializing || isSettingDataRef.current) return;
      isSyncing = true;
      const visibleRange = volumeChart.timeScale().getVisibleLogicalRange();
      if (visibleRange) {
        mainChart.timeScale().setVisibleLogicalRange(visibleRange);
      }
      setTimeout(() => { isSyncing = false; }, 0);
    };

    // Sync main chart's current range to volume chart BEFORE subscribing
    // This ensures volume chart starts with the correct range
    const initialRange = mainChart.timeScale().getVisibleLogicalRange();
    if (initialRange) {
      volumeChart.timeScale().setVisibleLogicalRange(initialRange);
    }

    // Subscribe to both chart time scale changes for bidirectional sync
    mainChart.timeScale().subscribeVisibleLogicalRangeChange(syncMainToVolume);
    volumeChart.timeScale().subscribeVisibleLogicalRangeChange(syncVolumeToMain);

    // Allow volume->main sync after a delay (once volume chart has stabilized)
    setTimeout(() => { isInitializing = false; }, 1000);

    // Same logical range only means same x when both plot areas are the
    // same width: keep the two right price scales equally wide (utils/paneSync).
    let appliedScaleWidth = 0;
    const alignScaleWidths = () => {
      try {
        const next = sharedPriceScaleWidth(
          [mainChart.priceScale('right').width(), volumeChart.priceScale('right').width()],
          appliedScaleWidth,
        );
        if (next === null) return;
        appliedScaleWidth = next;
        mainChart.applyOptions({ rightPriceScale: { minimumWidth: next } });
        volumeChart.applyOptions({ rightPriceScale: { minimumWidth: next } });
      } catch {
        // chart removed
      }
    };
    let alignFrame = 0;
    const scheduleAlign = () => {
      if (alignFrame) return;
      alignFrame = requestAnimationFrame(() => {
        alignFrame = 0;
        alignScaleWidths();
      });
    };
    // Labels widen on scroll, zoom, new data and price-axis drags; a slow
    // poll catches the rest (a price move alone fires no time-scale event).
    mainChart.timeScale().subscribeVisibleLogicalRangeChange(scheduleAlign);
    // Guard: the logical-range sync is only right while both time scales
    // index the candles the same. Two ways that has broken:
    //  - a main-chart series with off-candle times (fixed at the source, but
    //    any new overlay can reintroduce it): warn, it can't be healed here;
    //  - the volume histogram's rows going stale against the pane's time
    //    scale (seen on 1m, no error thrown; its own data re-set heals it):
    //    re-set the pane's CME bars from the candles.
    let warnedIndexDrift = false;
    let healedKey = '';
    const checkIndexDrift = () => {
      const candles = aggCandlesRef.current;
      const last = candles.length ? candles[candles.length - 1].time : null;
      if (typeof last !== 'number' || isSettingDataRef.current) return;
      try {
        const a = mainChart.timeScale().timeToIndex(last as Time);
        const b = volumeChart.timeScale().timeToIndex(last as Time);
        if (a === null || a === b) return;
        const series = volumeSeriesRef.current;
        // Once per candle set: if a re-set doesn't fix it the cause is on the
        // main chart, and re-setting every second would only burn CPU.
        const key = `${candles.length}:${last}`;
        if (series && healedKey !== key) {
          healedKey = key;
          // Colour from the drawn bar (Vest continuous open, #628); CME unchanged.
          series.setData(volumePaneData(symbolRef.current, candles, cmeSpikeTimesRef.current));
          volumeChart.timeScale().setVisibleLogicalRange(mainChart.timeScale().getVisibleLogicalRange() ?? { from: 0, to: candles.length });
        }
        if (!warnedIndexDrift) {
          warnedIndexDrift = true;
          console.warn(`[volume pane] last candle is index ${a} on the price chart but ${b} on the volume pane; re-set the volume bars`);
        }
      } catch {
        // chart removed
      }
    };
    const alignTimer = setInterval(() => {
      alignScaleWidths();
      checkIndexDrift();
    }, 1000);

    // Also sync crosshair between charts
    mainChart.subscribeCrosshairMove((param) => {
      if (param.time && volumeSeriesRef.current) {
        try {
          volumeChart.setCrosshairPosition(0, param.time, volumeSeriesRef.current);
        } catch {
          // Ignore errors when time doesn't exist in volume series
        }
      } else {
        volumeChart.clearCrosshairPosition();
      }
    });

    volumeChart.subscribeCrosshairMove((param) => {
      // Only the pointer on the volume pane drives the price chart. The volume
      // chart re-fires this on every live bar update (no source event, at the
      // synthetic position the price chart gave it); passing that back moved
      // the price chart's crosshair off the pointer on every tick, so its next
      // re-fire hit-tested the wrong point and closed whatever tooltip the
      // pointer was on (TradeFinder / absorption markers blinking).
      if (!isPointerEvent(param)) return;
      if (param.time && candlestickSeriesRef.current) {
        try {
          mainChart.setCrosshairPosition(0, param.time, candlestickSeriesRef.current);
        } catch {
          // Ignore errors when time doesn't exist in main series
        }
      } else {
        mainChart.clearCrosshairPosition();
      }
    });

    // Handle volume chart click for volume alert placement
    // Click anywhere on volume pane - Y position determines volume threshold
    const handleVolumeChartClick = (e: MouseEvent) => {
      if (!placementModeRef.current) return;
      if (!volumeSeriesRef.current) return;

      const rect = volumeChartContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const y = e.clientY - rect.top;
      // Get volume value at clicked Y position
      const clickedVolume = volumeSeriesRef.current.coordinateToPrice(y);

      if (clickedVolume && clickedVolume > 0) {
        // Create absolute volume threshold alert
        createAlert({
          symbol: symbolRef.current,
          alertType: 'volume_above',
          volumeThreshold: clickedVolume,
          triggerMode: 'once_per_bar',
        }).then((newAlert) => {
          if (newAlert) {
            addToast('success', `Volume alert: Above ${Math.round(clickedVolume).toLocaleString()}`);
          }
        });
        exitPlacementMode();
      }
    };

    volumeChartContainerRef.current.addEventListener('click', handleVolumeChartClick);

    // Touch events for mobile long-press volume alert
    const handleVolumeTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const rect = volumeChartContainerRef.current?.getBoundingClientRect();
      if (!rect || !volumeSeriesRef.current) return;

      const y = touch.clientY - rect.top;
      volumeLongPressStartRef.current = { x: touch.clientX, y: touch.clientY };

      volumeLongPressTimerRef.current = setTimeout(() => {
        const volume = volumeSeriesRef.current?.coordinateToPrice(y);
        if (volume && volume > 0) {
          setVolumeLongPressPreview({ y, volume: volume as number });
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
        }
      }, LONG_PRESS_DURATION);
    };

    const handleVolumeTouchMove = (e: TouchEvent) => {
      if (!volumeLongPressStartRef.current) return;

      const touch = e.touches[0];
      const dx = touch.clientX - volumeLongPressStartRef.current.x;
      const dy = touch.clientY - volumeLongPressStartRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > LONG_PRESS_MOVE_THRESHOLD) {
        if (volumeLongPressTimerRef.current) {
          clearTimeout(volumeLongPressTimerRef.current);
          volumeLongPressTimerRef.current = null;
        }
        volumeLongPressStartRef.current = null;
        setVolumeLongPressPreview(null);
      }
    };

    const handleVolumeTouchEnd = () => {
      if (volumeLongPressTimerRef.current) {
        clearTimeout(volumeLongPressTimerRef.current);
        volumeLongPressTimerRef.current = null;
      }
      // Keep preview visible if it was shown (user will interact with buttons)
      volumeLongPressStartRef.current = null;
    };

    volumeChartContainerRef.current.addEventListener('touchstart', handleVolumeTouchStart, { passive: true });
    volumeChartContainerRef.current.addEventListener('touchmove', handleVolumeTouchMove, { passive: true });
    volumeChartContainerRef.current.addEventListener('touchend', handleVolumeTouchEnd, { passive: true });

    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      if (volumeChartContainerRef.current && volumeChartRef.current) {
        volumeChart.applyOptions({
          width: volumeChartContainerRef.current.clientWidth,
          height: volumeChartContainerRef.current.clientHeight,
        });
      }
    });
    if (volumeChartContainerRef.current) {
      resizeObserver.observe(volumeChartContainerRef.current);
    }

    // Initial sync
    syncMainToVolume();
    scheduleAlign();

    return () => {
      resizeObserver.disconnect();
      clearInterval(alignTimer);
      if (alignFrame) cancelAnimationFrame(alignFrame);
      try {
        mainChart.timeScale().unsubscribeVisibleLogicalRangeChange(scheduleAlign);
        mainChart.applyOptions({ rightPriceScale: { minimumWidth: 0 } });
      } catch {
        // main chart already removed
      }
      if (volumeChartContainerRef.current) {
        volumeChartContainerRef.current.removeEventListener('click', handleVolumeChartClick);
        volumeChartContainerRef.current.removeEventListener('touchstart', handleVolumeTouchStart);
        volumeChartContainerRef.current.removeEventListener('touchmove', handleVolumeTouchMove);
        volumeChartContainerRef.current.removeEventListener('touchend', handleVolumeTouchEnd);
      }
      if (volumeLongPressTimerRef.current) {
        clearTimeout(volumeLongPressTimerRef.current);
      }
      volumeChart.remove();
      volumeSeriesRef.current = null;
      volumeChartRef.current = null;
      setVolumeChartReady(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - chart created once, uses refs for current values

  // Apply theme changes to chart dynamically
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // Get fresh theme colors from CSS variables
    const rootStyles = getComputedStyle(document.documentElement);
    const getThemeColor = (varName: string) => rootStyles.getPropertyValue(varName).trim();

    // Update chart layout colors
    chart.applyOptions({
      layout: {
        background: { color: getThemeColor('--theme-chart-background') || '#0a0e27' },
        textColor: getThemeColor('--theme-chart-text') || '#d1d4dc',
      },
      grid: {
        vertLines: { color: 'transparent' },
        horzLines: { color: 'transparent' },
      },
      rightPriceScale: {
        borderColor: getThemeColor('--theme-chart-border') || '#2a3150',
      },
      timeScale: {
        borderColor: getThemeColor('--theme-chart-border') || '#2a3150',
      },
    });

    // Update candlestick series colors
    if (candlestickSeriesRef.current) {
      candlestickSeriesRef.current.applyOptions({
        upColor: getThemeColor('--theme-candle-up') || '#26a69a',
        downColor: getThemeColor('--theme-candle-down') || '#ef5350',
        wickUpColor: getThemeColor('--theme-candle-up') || '#26a69a',
        wickDownColor: getThemeColor('--theme-candle-down') || '#ef5350',
      });
    }

    // Update EMA series colors from theme
    if (ema9SeriesRef.current) {
      ema9SeriesRef.current.applyOptions({
        color: getThemeColor('--theme-ema9') || '#f50057',
      });
    }
    if (ema21SeriesRef.current) {
      ema21SeriesRef.current.applyOptions({
        color: getThemeColor('--theme-ema21') || '#ab47bc',
      });
    }
    if (ema200SeriesRef.current) {
      ema200SeriesRef.current.applyOptions({
        color: getThemeColor('--theme-ema200') || '#ffc107',
      });
    }

    // Update VWAP series color
    if (vwapSeriesRef.current) {
      vwapSeriesRef.current.applyOptions({
        color: getThemeColor('--theme-vwap') || '#4fc3f7',
      });
    }

    // Update trailing candle primitive colors
    if (trailingCandlePrimitiveRef.current) {
      trailingCandlePrimitiveRef.current.setColors({
        bullishFill: getThemeColor('--theme-trailing-up') || '#00d4ff',
        bullishForming: getThemeColor('--theme-trailing-up-forming') || '#5fffff',
        bearishFill: getThemeColor('--theme-trailing-down') || '#ff6b35',
        bearishForming: getThemeColor('--theme-trailing-down-forming') || '#ff9966',
        border: getThemeColor('--theme-trailing-border') || '#ffffff',
      });
    }

    // Update volume chart theme (separate pane)
    if (volumeChartRef.current) {
      volumeChartRef.current.applyOptions({
        layout: {
          background: { color: getThemeColor('--theme-chart-background') || '#0a0e27' },
          textColor: getThemeColor('--theme-chart-text') || '#d1d4dc',
        },
        grid: {
          vertLines: { color: 'transparent' },
          horzLines: { color: 'transparent' },
        },
        rightPriceScale: {
          borderColor: getThemeColor('--theme-chart-border') || '#2a3150',
        },
      });
    }
  }, [currentTheme]);

  // Apply EMA/VWAP visibility toggles from levelsStore
  useEffect(() => {
    if (ema9SeriesRef.current) {
      ema9SeriesRef.current.applyOptions({ visible: levelVisibility.ema9 });
    }
    if (ema21SeriesRef.current) {
      ema21SeriesRef.current.applyOptions({ visible: levelVisibility.ema21 });
    }
    if (ema200SeriesRef.current) {
      ema200SeriesRef.current.applyOptions({ visible: levelVisibility.ema200 });
    }
    if (vwapSeriesRef.current) {
      vwapSeriesRef.current.applyOptions({ visible: levelVisibility.vwap });
    }
  }, [levelVisibility.ema9, levelVisibility.ema21, levelVisibility.ema200, levelVisibility.vwap]);

  // GEX: engine pushes converted levels; the store holds display prefs.
  //
  // The engine owns the index->future conversion, so everything arriving here is
  // already in futures points. It must NOT be rescaled client-side: the
  // conversion needs the index spot and the live future sampled together, and
  // the browser has neither.
  // Each chart shows its own index's levels: NDX on NQ/MNQ, SPX on ES/MES,
  // none on anything else.
  const gexUnderlying = gexUnderlyingForSymbol(symbol);
  const gexSlot = useGexStore(selectGexSlot(gexUnderlying));
  const gexLevels = gexSlot.levels;
  const gexAvailable = gexUnderlying !== null && gexSlot.available;
  const gexVisibility = useGexStore((s) => s.visibility);
  const gexLadderCount = useGexStore((s) => s.ladderCount);

  useEffect(() => {
    const setLevels = useGexStore.getState().setLevels;
    const setUnavailable = useGexStore.getState().setUnavailable;

    // Every payload is filed under its own underlying, whichever chart
    // received it; the selector above decides what THIS chart draws. NDX
    // arrives on `gex:levels` (unchanged for older clients), SPX on
    // `gex:levels:spx` so a tab still running an older bundle never mistakes
    // SPX levels for NDX ones.
    // The EVENT decides the underlying; the payload's own label is only a
    // cross-check, and a payload that disagrees with its event is dropped.
    const handle = (expected: GexUnderlying) => (payload: any) => {
      if (payload?.underlying || payload?.levels?.underlying) {
        if (payloadUnderlying(payload) !== expected) return;
      }
      if (payload?.levels) setLevels(expected, payload.levels);
      else setUnavailable(expected, payload?.reason ?? 'engine reported no levels');
    };
    const onNdx = handle('NDX');
    const onSpx = handle('SPX');
    // Expiry window + method are engine settings; any tab changing them is
    // broadcast to all, and the matching levels re-emit right after.
    const onSettings = (payload: unknown) => useGexStore.getState().applySettingsPayload(payload);
    wsService.on('gex:levels', onNdx);
    wsService.on('gex:levels:spx', onSpx);
    wsService.on('gex:settings', onSettings);
    useGexStore.getState().fetchSettings();
    return () => {
      wsService.off('gex:levels', onNdx);
      wsService.off('gex:levels:spx', onSpx);
      wsService.off('gex:settings', onSettings);
    };
  }, []);

  // Seed from REST so a fresh page does not wait up to a minute for the first
  // socket emit. Only for the underlying this chart shows.
  useEffect(() => {
    if (!gexUnderlying) return;
    const u: GexUnderlying = gexUnderlying;
    const { setLevels, setUnavailable } = useGexStore.getState();
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/gex?underlying=${u}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d) return;
        // An engine without SPX support ignores `?underlying=` and answers
        // NDX; never file that under SPX.
        if (d.available && d.levels && payloadUnderlying(d) !== u) return;
        if (d.available && d.levels) setLevels(u, d.levels);
        else setUnavailable(u, d.reason ?? 'unavailable');
      })
      .catch(() => { /* engine may still be booting; the socket will catch up */ });
    return () => { cancelled = true; };
  }, [gexUnderlying]);

  useEffect(() => {
    // `available: false` means the engine has no trustworthy ratio, so draw
    // nothing rather than the last known levels against a price they were never
    // converted for.
    gexLevelsPrimitiveRef.current?.setLevels(gexAvailable ? gexLevels : null);
  }, [gexLevels, gexAvailable]);

  useEffect(() => {
    gexLevelsPrimitiveRef.current?.setVisibility(gexVisibility, gexLadderCount);
  }, [gexVisibility, gexLadderCount]);

  // ---------------------------------------------------------------------------
  // Key levels: GEX (walls, zero gamma, IV bands) and volume profile (session +
  // weekly POC/VAH/VAL) as price lines with coloured right-axis tags, plus
  // confluence zones where they stack. Hovering (or tapping) a line or a zone
  // explains it.
  // ---------------------------------------------------------------------------
  const profileLevels = useProfileLevelsStore((s) => s.bySymbol[symbol]);
  const kpSession = useKeyLevelPrefsStore((s) => s.sessionProfile);
  const kpWeekly = useKeyLevelPrefsStore((s) => s.weeklyProfile);
  const kpConfluence = useKeyLevelPrefsStore((s) => s.confluence);
  const kpTolerance = useKeyLevelPrefsStore((s) => s.confluenceTolerance);
  const vpValueAreaPercent = useVolumeProfileStore((s) => s.settings.valueAreaPercent);
  // GEX is converted to this chart's own futures prices (NDX -> NQ/MNQ,
  // SPX -> ES/MES); any other symbol has none.
  const hasGex = gexUnderlying !== null;
  // ONH/ONL from this chart's raw base bars (independent of the timeframe).
  const onhOnl = useMemo(() => computeOnhOnl(marketCandles), [marketCandles]);
  const onhOnlVisible = levelVisibility.onhOnl ?? true;
  const onhOnlColors = levelColors.onhOnl ?? { high: '#a3e635', low: '#fb7185' };
  // Clean mode hides these with the other price levels.
  const keyLevelsHidden = cleanChart && cleanHides.levels;

  useEffect(() => {
    useProfileLevelsStore.getState().fetchLevels(symbol);
  }, [symbol]);

  const keyLevelsComputed = useMemo<KeyLevel[]>(() => {
    if (dataMode !== 'live' || keyLevelsHidden) return [];
    return [
      ...(hasGex && gexAvailable ? gexKeyLevels(gexLevels, gexVisibility) : []),
      ...profileKeyLevels(profileLevels, { sessionProfile: kpSession, weeklyProfile: kpWeekly }),
      ...(onhOnlVisible ? onhOnlKeyLevels(onhOnl, onhOnlColors) : []),
    ];
  }, [dataMode, keyLevelsHidden, hasGex, gexAvailable, gexLevels, gexVisibility,
    profileLevels, kpSession, kpWeekly, onhOnlVisible, onhOnl, onhOnlColors]);
  // Stable by VALUE: the engine re-emits identical GEX levels every 60s as a
  // fresh object, which would otherwise tear down and recreate every price
  // line (and re-run confluence) once a minute for nothing.
  const keyLevelsSig = JSON.stringify(keyLevelsComputed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const keyLevels = useMemo(() => keyLevelsComputed, [keyLevelsSig]);

  // The pref is NQ points. Vest feeds scale it to their own median 2m true
  // range on the raw closed base bars, rounded to the tick (#630); every
  // other symbol uses it as is. Recomputed once per new bar, not per tick.
  const vestTick = isVestFeedSymbol(symbol) ? vestTickSize(symbol) : null;
  const marketCandlesRef = useRef(marketCandles);
  marketCandlesRef.current = marketCandles;
  const confluenceTolerance = useMemo(
    () => confluenceToleranceFor(symbol, kpTolerance, marketCandlesRef.current.slice(0, -1), vestTick),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [symbol, kpTolerance, vestTick, vestTick === null ? 0 : marketCandles.length],
  );

  const confluenceZones = useMemo(() => {
    if (!kpConfluence || keyLevelsHidden || dataMode !== 'live') return [];
    // PDH/PDL and MOC join the check when they are shown; they draw elsewhere.
    const extra = sessionKeyLevels({
      pdh: levelVisibility.prevDayHodLod ? priceLevels?.prevDayHigh : undefined,
      pdl: levelVisibility.prevDayHodLod ? priceLevels?.prevDayLow : undefined,
      moc: levelVisibility.mocOpen ? mocOpenPrice : undefined,
    });
    return computeConfluenceZones([...keyLevels, ...extra], confluenceTolerance);
  }, [kpConfluence, keyLevelsHidden, dataMode, keyLevels, confluenceTolerance, levelVisibility.prevDayHodLod,
    levelVisibility.mocOpen, priceLevels?.prevDayHigh, priceLevels?.prevDayLow, mocOpenPrice]);

  useEffect(() => {
    const series = candlestickSeriesRef.current;
    if (!series) return;
    const lines = keyLevels.map((level) => ({
      level,
      line: series.createPriceLine({
        price: level.price,
        color: level.color,
        lineWidth: level.kind === 'poc' || level.kind === 'zeroGamma' || level.kind === 'putWall' || level.kind === 'callWall' ? 2 : 1,
        lineStyle: level.lineStyle,
        axisLabelVisible: true, // the coloured right-axis tag (2 decimals from the series price format)
        title: level.label,
      }),
    }));
    keyLevelLinesRef.current = lines;
    return () => {
      for (const { line } of lines) {
        try { series.removePriceLine(line); } catch { /* series already gone */ }
      }
      keyLevelLinesRef.current = [];
    };
  }, [keyLevels, chartSeriesVersion]);

  // #636: the open Vest position's entry / TP / SL on a .VEST chart. Display
  // only; a no-op (no rows read, no lines) on every other symbol.
  useVestPositionLines(candlestickSeriesRef, chartSeriesVersion, symbol, currentPrice);
  // #637: ATR TP / SL + size suggestions on a .VEST chart (lines + legend).
  // No requests, lines or legend on any other symbol, with the toggle off, or in backtest.
  const vestSizingLegend = useVestSizingLines(
    candlestickSeriesRef, chartSeriesVersion, symbol, currentPrice, levelVisibility.vestAtrSizing && dataMode === 'live',
  );

  // The key-level price-line titles ("MOC", "PUT WALL") are drawn inside the
  // pane at its right edge: fixed obstacles in the shared marker layout, so a
  // marker or its level code never lands on one.
  useEffect(() => {
    const registry = markerLayoutRef.current;
    if (!registry || keyLevels.length === 0) return;
    return registry.register('levelTitles', (ctx) => {
      const out = [];
      for (const l of keyLevels) {
        const y = ctx.priceToY(l.price);
        if (y === null || y < 0 || y > ctx.paneHeight) continue;
        const w = ctx.measure(l.label, '11px sans-serif') + 12;
        out.push({
          key: `${l.kind}:${l.price}`,
          time: ctx.toTime,
          x: ctx.paneWidth - w / 2 - 2,
          side: 'above' as const,
          anchorY: y,
          w,
          h: 16,
          priority: 0,
          fixed: true,
          noLeader: true,
          label: l.label,
        });
      }
      return out;
    });
  }, [keyLevels, chartSeriesVersion]);

  useEffect(() => {
    confluencePrimitiveRef.current?.setZones(confluenceZones, confluenceLabelFor(symbol));
  }, [confluenceZones, chartSeriesVersion, symbol]);

  // Latest context for the hover handler, which is subscribed once.
  const keyLevelCtxRef = useRef<{ va: number; source: string; gex: GexLevels | null; symbol: string; vestZone?: { tick: number; tolerance: number; nqTolerancePts: number } }>({ va: 70, source: '', gex: null, symbol: '' });
  keyLevelCtxRef.current = {
    va: profileLevels?.valueAreaPercent ?? vpValueAreaPercent ?? 70,
    source: gexSourceLine(gexLevels),
    gex: gexLevels,
    symbol,
    vestZone: vestTick === null ? undefined : { tick: vestTick, tolerance: confluenceTolerance, nqTolerancePts: kpTolerance },
  };
  const [keyLevelHover, setKeyLevelHover] = useState<KeyLevelTooltipData | null>(null);
  // What the hover handler last showed, whether a tap pinned it, and the
  // grace timer for leaving a marker (markerHover.ts).
  const keyLevelHoverStateRef = useRef<{ current: KeyLevelTooltipData | null; pinned: boolean; graceTimer: ReturnType<typeof setTimeout> | null }>({
    current: null,
    pinned: false,
    graceTimer: null,
  });

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const describe = (x: number, y: number): KeyLevelTooltipData | null => {
      const series = candlestickSeriesRef.current;
      if (!series) return null;
      // A "+N" chip: what the shared marker layout had no room for here.
      const layoutHit = markerLayoutRef.current?.hitTest(x, y) ?? null;
      if (layoutHit?.kind === 'chip') {
        const { title, lines } = markerChipTooltip(layoutHit.chip);
        return { hoverId: `${layoutHit.chip.key}:${layoutHit.chip.hidden.length}`, x, y, title, lines, color: '#94a3b8' };
      }
      // Absorption dots sit on top of everything else under the cursor.
      const absItem = absorptionPrimitiveRef.current?.markerAt(x, y) ?? null;
      if (absItem) {
        const all = useAbsorptionStore.getState().bySymbol;
        const marker = Object.values(all).flat().find((m) => m.id === absItem.id);
        if (marker) {
          const { title, lines } = absorptionTooltip(marker);
          return { hoverId: `abs:${marker.id}`, x, y, title, lines, color: absItem.color };
        }
      }
      const flowHit = flowPrimitiveRef.current?.markerAt(x, y) ?? null;
      if (flowHit) {
        const list = useFlowSignalsStore.getState().bySymbol[normalizeToBase(symbolRef.current)] ?? [];
        const ids = new Set(flowHit.ids);
        const hits = list.filter((m) => ids.has(m.id));
        if (hits.length === 1) {
          const { title, lines } = flowTooltip(hits[0]);
          return { hoverId: `flow:${hits[0].id}`, x, y, title, lines, color: flowHit.color };
        }
        if (hits.length > 1) {
          const lines = hits.slice(0, 8).map((m) => `${m.label || m.kind} ${m.price.toFixed(2)}: ${flowTooltip(m).lines[0]}`);
          if (hits.length > 8) lines.push(`+${hits.length - 8} more`);
          return { hoverId: `flow:${flowHit.ids.join(',')}`, x, y, title: `${hits.length} order-flow signals`, lines, color: flowHit.color };
        }
      }
      const spike = vestSpikePrimitiveRef.current?.markerAt(x, y) ?? null;
      if (spike) {
        const { title, lines } = candleSpikeTooltip(spike, chartContractName(symbolRef.current));
        return { hoverId: `spike:${spike.id}`, x, y, title, lines, color: '#38bdf8' };
      }
      const placed: { level: KeyLevel; y: number }[] = [];
      for (const { level } of keyLevelLinesRef.current) {
        const py = series.priceToCoordinate(level.price);
        if (py !== null) placed.push({ level, y: py });
      }
      const hit = hitTestY(placed, y, 4);
      if (hit) {
        const { level } = hit;
        const lines = [keyLevelExplanation(level.kind, keyLevelCtxRef.current.va)];
        if (isGexKind(level.kind)) {
          const method = gexMethodLine(level.kind, keyLevelCtxRef.current.gex);
          if (method) lines.push(method);
          lines.push(keyLevelCtxRef.current.source);
        }
        if (level.developing) lines.push(ONH_ONL_DEVELOPING_NOTE);
        return { hoverId: `${level.kind}:${level.price}`, x, y, title: `${level.label} ${level.price.toFixed(2)}`, lines, color: level.color };
      }
      const zone = confluencePrimitiveRef.current?.zoneAt(x, y) ?? null;
      if (zone) {
        const ctx = keyLevelCtxRef.current;
        return { hoverId: `zone:${zone.low}:${zone.high}`, x, y, title: confluenceLabelFor(ctx.symbol), lines: confluenceTooltipFor(ctx.symbol, zone, ctx.vestZone), color: '#ff8a50' };
      }
      return null;
    };

    const state = keyLevelHoverStateRef.current;
    const show = (h: KeyLevelTooltipData | null) => {
      if (state.graceTimer !== null) {
        clearTimeout(state.graceTimer);
        state.graceTimer = null;
      }
      // Keep the same object while the same thing stays under the cursor, so
      // moving along a line does not re-render the whole chart on every pixel.
      if (state.current === h) return;
      state.current = h;
      setKeyLevelHover(h);
    };
    const handle = (param: MouseEventParams, kind: 'move' | 'click'): void => {
      // A point in a flow line pane is relative to that pane, not the price pane.
      const next = param.point && !isLinePaneEvent(chart, chartContainerRef.current, param) ? describe(param.point.x, param.point.y) : null;
      const touch = kind === 'click' && isCoarsePointer();
      const fromPointer = isPointerEvent(param);
      const d = decideHover(state.current, state.pinned, next, { kind, fromPointer, touch });
      state.pinned = d.pinned;
      // A live-update re-fire that keeps what is shown changes nothing, and
      // must not cancel a pending grace hide (the pointer already left).
      if (!fromPointer && d.show === state.current) return;
      if (!d.graceClear) {
        show(d.show);
        return;
      }
      // Just off a marker: hide after a short grace unless the pointer comes back.
      if (state.graceTimer === null) {
        state.graceTimer = setTimeout(() => {
          state.graceTimer = null;
          state.current = null;
          setKeyLevelHover(null);
        }, MARKER_HOVER_GRACE_MS);
      }
    };
    const onMove = (param: MouseEventParams) => handle(param, 'move');
    // Click as well: on a touch screen a tap is the only way to "hover" (and pins a marker's tooltip).
    const onClick = (param: MouseEventParams) => handle(param, 'click');

    chart.subscribeCrosshairMove(onMove);
    chart.subscribeClick(onClick);
    return () => {
      chart.unsubscribeCrosshairMove(onMove);
      chart.unsubscribeClick(onClick);
      if (state.graceTimer !== null) clearTimeout(state.graceTimer);
      state.graceTimer = null;
    };
    // No deps: everything is reached through refs, same as the handlers below.
  }, []);

  // Click a level's label (its right-axis price tag, the title pill inside the
  // pane, a Fair Price anchor label or a left-edge level label) to switch off
  // the existing toggle that draws it, with an Undo toast. See
  // Chart/levelLabelHide.ts. Pointer listeners in the CAPTURE phase and never
  // preventDefault: lightweight-charts still gets every event, so dragging the
  // price scale still zooms; only a press that did not move acts.
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    type PtrEvent = Parameters<NonNullable<HTMLElement['onpointerdown']>>[0];
    // Text widths for the label boxes, in the fonts the labels are drawn in.
    const measureCtx = document.createElement('canvas').getContext('2d');
    const measure = (text: string, font: string): number => {
      if (!measureCtx) return text.length * 7;
      measureCtx.font = font;
      return measureCtx.measureText(text).width;
    };

    const targets = (): LabelTarget[] => {
      const chart = chartRef.current;
      const series = candlestickSeriesRef.current;
      if (!chart || !series) return [];
      const { fontSize, fontFamily } = chart.options().layout;
      const paneWidth = chart.timeScale().width();
      const axisWidth = chart.priceScale('right').width();
      const paneHeight = chart.panes()[0]?.getHeight() ?? 0;
      const inPane = (y: number | null): y is number => y !== null && y >= 0 && y <= paneHeight;
      const out: LabelTarget[] = [];

      // Left-edge labels (PriceLevelLabelsPrimitive, already merged + placed).
      for (const l of priceLevelLabelsPrimitiveRef.current?.getRendererData() ?? []) {
        const category = categoryForPriceLevelLabel(l.label);
        if (!category || !inPane(l.y)) continue;
        out.push(leftLabelTarget(l.y, measure(`${l.label} ${l.price.toFixed(2)}`, '800 10px sans-serif'), category));
      }
      // Fair Price anchor labels (GLBX / ASIA / PM, the body time, FAIR PRICE ->, NEWS).
      for (const r of fairPricePrimitiveRef.current?.labelRects() ?? []) {
        out.push({ left: r.left, right: r.right, top: r.top, bottom: r.bottom, category: 'fairPrice' });
      }
      // Price-line tags: the axis tag, plus the title pill inside the pane when
      // the line has a title. Both are painted at the tag's de-overlapped y
      // (fixAxisTagOverlap), so collect every tag on the scale first. The
      // candle series' live price tag comes first, as lightweight-charts
      // collects it, and is the centre the others are pushed away from.
      type Tag = { y: number; title: string; category: HideCategoryId | null };
      const tags: Tag[] = [];
      const last = series.dataByIndex(Number.MAX_SAFE_INTEGER, MismatchDirection.NearestLeft) as { close?: number } | null;
      const lastY = last?.close !== undefined ? series.priceToCoordinate(last.close) : null;
      const lastTagShown = series.options().lastValueVisible !== false;
      if (lastY !== null && lastTagShown) tags.push({ y: lastY, title: '', category: null });
      const line = (l: unknown, category: HideCategoryId) => {
        const pl = l as { options(): { price: number; title: string; axisLabelVisible: boolean } } | null;
        if (!pl) return;
        const { price, title, axisLabelVisible } = pl.options();
        if (!axisLabelVisible) return;
        const y = series.priceToCoordinate(price);
        if (y !== null) tags.push({ y, title, category });
      };
      line(hodLineRef.current, 'level:sessionHodLod');
      line(lodLineRef.current, 'level:sessionHodLod');
      line(rthHodLineRef.current, 'level:rthHodLod');
      line(rthLodLineRef.current, 'level:rthHodLod');
      line(pdhLineRef.current, 'level:prevDayHodLod');
      line(pdlLineRef.current, 'level:prevDayHodLod');
      line(ibHighLineRef.current, 'level:ibRange');
      line(ibLowLineRef.current, 'level:ibRange');
      line(midnightOpenLineRef.current, 'level:midnightOpen');
      line(prevWeekHighLineRef.current, 'level:prevWeekHodLod');
      line(prevWeekLowLineRef.current, 'level:prevWeekHodLod');
      line(currWeekHighLineRef.current, 'level:currWeekHodLod');
      line(currWeekLowLineRef.current, 'level:currWeekHodLod');
      line(prevMonthHighLineRef.current, 'level:prevMonthHodLod');
      line(prevMonthLowLineRef.current, 'level:prevMonthHodLod');
      line(mocOpenLineRef.current, 'level:mocOpen');
      for (const { level, line: l } of keyLevelLinesRef.current) line(l, KEY_LEVEL_CATEGORY[level.kind]);
      const center = lastY ?? paneHeight / 2;
      const painted = fixAxisTagOverlap(tags.map((t) => t.y), center, paneHeight, axisLabelHeight(fontSize));
      const font = `${fontSize}px ${fontFamily}`;
      tags.forEach((t, k) => {
        const y = painted[k];
        if (!inPane(y) || t.category === null) return;
        if (t.title) out.push(paneTitleTarget(y, paneWidth, measure(t.title, font), fontSize, t.category));
        out.push(axisTagTarget(y, paneWidth, axisWidth, fontSize, t.category));
      });
      // The live price tag paints over the price-line tags: not hideable, and
      // a click on it must not reach a tag underneath.
      if (lastY !== null && lastTagShown && inPane(painted[0])) {
        out.push(axisTagTarget(painted[0], paneWidth, axisWidth, fontSize, null));
      }
      return out;
    };

    const busy = () =>
      placementModeRef.current || dcaModeRef.current || useDrawingStore.getState().activeTool !== null;
    const local = (e: PtrEvent) => {
      const rect = container.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    let pendingHide: ReturnType<typeof setTimeout> | null = null;
    const onDblClick = () => {
      if (pendingHide !== null) clearTimeout(pendingHide);
      pendingHide = null;
    };
    const hide = (category: HideCategoryId) => {
      if (hideCategoryWithUndo(category)) {
        // The same click still reaches lightweight-charts after this capture
        // listener and may open (or pin) the hidden level's tooltip; clear it
        // once that dispatch is over.
        setTimeout(() => {
          const hover = keyLevelHoverStateRef.current;
          if (hover.graceTimer !== null) clearTimeout(hover.graceTimer);
          hover.graceTimer = null;
          hover.current = null;
          hover.pinned = false;
          setKeyLevelHover(null);
          setAnchorHover(null);
        }, 0);
      }
    };

    let down: { x: number; y: number; id: number } | null = null;
    const onDown = (e: PtrEvent) => {
      down = e.button === 0 && !busy() ? { ...local(e), id: e.pointerId } : null;
    };
    const onUp = (e: PtrEvent) => {
      const start = down;
      down = null;
      if (!start || start.id !== e.pointerId || busy()) return;
      const p = local(e);
      if (!isClickNotDrag(start, p)) return;
      const category = hitTestLabelTargets(targets(), p.x, p.y);
      if (!category) return;
      // On the price scale a double-click resets autoscale: wait out the
      // double-click window there, and let a dblclick cancel the hide.
      const onScale = p.x > (chartRef.current?.timeScale().width() ?? Infinity);
      if (pendingHide !== null) clearTimeout(pendingHide);
      pendingHide = null;
      if (onScale) {
        pendingHide = setTimeout(() => {
          pendingHide = null;
          hide(category);
        }, SCALE_DBLCLICK_WAIT_MS);
      } else {
        hide(category);
      }
    };
    const onCancel = () => { down = null; };
    // Pointer cursor over a hideable label (mouse only; nothing to show on touch).
    let hoverable = false;
    const onMove = (e: PtrEvent) => {
      if (e.pointerType !== 'mouse') return;
      const p = local(e);
      const next = !busy() && e.buttons === 0 && hitTestLabelTargets(targets(), p.x, p.y) !== null;
      if (next === hoverable) return;
      hoverable = next;
      container.classList.toggle('level-label-hoverable', next);
    };
    const onLeave = () => {
      hoverable = false;
      container.classList.remove('level-label-hoverable');
    };

    const unexpose = import.meta.env.DEV ? exposeLevelLabels(container, targets) : () => {};
    container.addEventListener('pointerdown', onDown, true);
    container.addEventListener('pointerup', onUp, true);
    container.addEventListener('pointercancel', onCancel, true);
    container.addEventListener('dblclick', onDblClick, true);
    container.addEventListener('pointermove', onMove, true);
    container.addEventListener('pointerleave', onLeave);
    return () => {
      container.removeEventListener('pointerdown', onDown, true);
      container.removeEventListener('pointerup', onUp, true);
      container.removeEventListener('pointercancel', onCancel, true);
      container.removeEventListener('dblclick', onDblClick, true);
      if (pendingHide !== null) clearTimeout(pendingHide);
      container.removeEventListener('pointermove', onMove, true);
      container.removeEventListener('pointerleave', onLeave);
      container.classList.remove('level-label-hoverable');
      unexpose();
    };
    // No deps: everything is reached through refs and store getState().
  }, []);

  // Clean Chart mode: hide/show chart elements, per the Clean configuration.
  //
  // Nothing here attaches, detaches, creates or removes anything itself — that
  // is what used to go wrong. The old effect called attachPrimitive() on every
  // overlay whenever Clean was off (including on mount), and lightweight-charts
  // does not dedupe, so each overlay was drawn twice from the start and its
  // owner's own detach/attach lifecycle was overridden.
  //
  // Now each kind of element is hidden at its source, from derived state:
  //  - primitives: the series' primitive registry suppresses them by class
  //    (CLEAN_PRIMITIVE_GROUPS); this just asks it to re-evaluate.
  //  - levels, EMAs, VWAP, Donchian, spikes: masked into levelVisibility above.
  //  - key levels / GEX / confluence lines: keyLevelsHidden.
  //  - S/R lines and labels: srShownOnChart.
  //  - series (signal) markers: cleanHidesSignals in the marker effect.
  useEffect(() => {
    getPrimitiveRegistry(candlestickSeriesRef.current)?.refresh();
  }, [cleanChart, cleanHides, chartSeriesVersion]);

  // Track if initial data has been loaded
  const initialLoadRef = useRef(false);
  const lastCandleCountRef = useRef(0);
  const lastFirstCandleTimeRef = useRef(0); // Track first candle time for visibility refresh detection
  const lastCandleTimeRef = useRef(0); // Track last candle time for gap detection
  // Track when we're setting data to prevent volume->main chart sync from overriding our range
  const isSettingDataRef = useRef(false);

  // FIX: Track if setData has been called successfully for EMA series
  // This ensures EMAs are always set at least once after series creation, regardless of ref state
  const emaDataSetRef = useRef(false);
  const lastCandlesVersionRef = useRef(0);

  const previousTimeframeRef = useRef<Timeframe>(selectedTimeframe);

  useEffect(() => {
    if (!candlestickSeriesRef.current || !aggregatedCandles || aggregatedCandles.length === 0) {
      return;
    }
    if (!ema9SeriesRef.current || !ema21SeriesRef.current || !ema200SeriesRef.current || !vwapSeriesRef.current) {
      return;
    }

    // Check if timeframe changed
    const timeframeChanged = previousTimeframeRef.current !== selectedTimeframe;
    if (timeframeChanged) {
      previousTimeframeRef.current = selectedTimeframe;
    }

    // Detect if candles were cleared (mode switch or reset)
    const candlesWereCleared = lastCandleCountRef.current > 0 && aggregatedCandles.length > 0 &&
                                 lastCandleCountRef.current > aggregatedCandles.length * 2; // Significant drop indicates clear+reload

    // Detect if candles changed significantly (not just last candle update)
    // This happens when seeking in backtest mode OR when visibility changes and new data arrives
    const candlesChangedSignificantly = lastCandleCountRef.current > 0 && aggregatedCandles.length > 0 &&
                                         Math.abs(lastCandleCountRef.current - aggregatedCandles.length) > 1;

    // Detect if timestamp range changed significantly (visibility restore with new candles)
    // This handles the case where candle count is similar but the data is actually different
    // e.g., app closed at 7:40 PM with 100 candles, reopened at 8:05 PM with ~100 candles but different timestamps
    const firstCandleChanged = lastFirstCandleTimeRef.current > 0 && aggregatedCandles.length > 0 &&
                               aggregatedCandles[0].time !== lastFirstCandleTimeRef.current;
    const barIntervalSeconds = TIMEFRAME_MINUTES[selectedTimeframe] * 60;
    const lastCandleTimeJumped = lastCandleTimeRef.current > 0 && aggregatedCandles.length > 0 &&
                                  // If the last candle time jumped by more than one bar interval, we missed candles and need a full refresh
                                  (aggregatedCandles[aggregatedCandles.length - 1].time as number) - lastCandleTimeRef.current > barIntervalSeconds;

    // Initial load or timeframe change: use setData
    // Also use setData when candles changed significantly (backtest seek or visibility restore)
    // FIX: Always call setData if EMA data hasn't been set yet (emaDataSetRef tracks this)
    const emaDataNotYetSet = !emaDataSetRef.current && ema9Data.length > 0;
    const candlesJustLoaded = aggregatedCandles.length > 0 && lastCandleCountRef.current === 0;
    // Detect catch-up: candlesVersion bumps when catch-up merges data
    const catchUpOccurred = candlesVersion !== lastCandlesVersionRef.current;
    if (catchUpOccurred) {
      lastCandlesVersionRef.current = candlesVersion;
    }
    const needsFullRefresh = !initialLoadRef.current || candlesJustLoaded || timeframeChanged || candlesWereCleared ||
        candlesChangedSignificantly || firstCandleChanged || lastCandleTimeJumped || emaDataNotYetSet || catchUpOccurred;

    if (needsFullRefresh) {

      // Sanitize candle data: ensure all times are numbers
      const sanitizedCandles = aggregatedCandles.map(candle => {
        let time: Time = candle.time;
        // Convert Date objects to Unix timestamps in seconds
        if ((time as unknown) instanceof Date) {
          time = Math.floor((time as unknown as Date).getTime() / 1000) as Time;
          console.warn(`⚠️ Converted Date object to timestamp:`, candle.time, '→', time);
        } else if (typeof time !== 'number') {
          console.error(`❌ Invalid time type:`, typeof time, time);
          return null;
        }
        return { ...candle, time };
      }).filter(c => c !== null) as typeof aggregatedCandles;

      // Sort candles by time in ascending order (required by TradingView)
      sanitizedCandles.sort((a, b) => {
        const timeA = typeof a.time === 'number' ? a.time : (a.time as unknown as Date).getTime() / 1000;
        const timeB = typeof b.time === 'number' ? b.time : (b.time as unknown as Date).getTime() / 1000;
        return timeA - timeB;
      });

      if (sanitizedCandles.length === 0) {
        console.error(`❌ No valid candles after sanitization`);
        return;
      }

      if (sanitizedCandles.length < aggregatedCandles.length) {
        console.warn(`⚠️ Filtered out ${aggregatedCandles.length - sanitizedCandles.length} invalid candles`);
      }

      // Log the reason for full refresh
      const refreshReason = !initialLoadRef.current ? 'initial load' :
                           lastCandleCountRef.current === 0 ? 'no previous data' :
                           timeframeChanged ? 'timeframe changed' :
                           candlesWereCleared ? 'candles cleared' :
                           candlesChangedSignificantly ? 'candle count changed' :
                           firstCandleChanged ? 'first candle changed' :
                           lastCandleTimeJumped ? 'visibility refresh (time gap)' : 'unknown';
      console.log(`📊 [CHART] Full setData (${refreshReason}): ${sanitizedCandles.length} candles`, {
        dataMode,
        timeframe: selectedTimeframe,
        firstCandle: sanitizedCandles[0],
        lastCandle: sanitizedCandles[sanitizedCandles.length - 1],
        prevFirstTime: lastFirstCandleTimeRef.current,
        prevLastTime: lastCandleTimeRef.current
      });

      // Set flag to prevent volume->main sync from overriding our range during data loading
      isSettingDataRef.current = true;

      // Update price format for current symbol (handles symbol switches e.g. MNQ -> BTC)
      const { minMove, precision } = getSymbolPriceFormat(symbol);
      candlestickSeriesRef.current.applyOptions({
        priceFormat: { type: 'price' as const, minMove, precision },
      });

      // Vest feeds draw continuous candles (open = previous close, #626);
      // display only, sanitizedCandles / aggregatedCandles stay raw.
      const seriesCandles = priceSeriesCandles(symbol, sanitizedCandles);
      candlestickSeriesRef.current.setData(seriesCandles);

      // Set volume data with color based on (drawn) candle direction
      if (volumeSeriesRef.current) {
        const volumeData = seriesCandles.map(candle => ({
          time: candle.time,
          value: displayVolume(symbolRef.current, candle.volume),
          color: cmeVolumeColor(candle, cmeSpikeTimesRef.current),
        }));
        volumeSeriesRef.current.setData(volumeData);
      }

      // Set indicator data (EMAs, VWAP), empty included: an indicator with no
      // value on this timeframe (EMA200 on 15m/1h has < 200 bars) must clear,
      // or it keeps the previous timeframe's points and those extra time
      // points shift the volume pane's logical indices off the candles.
      ema9SeriesRef.current?.setData(ema9Data);
      ema21SeriesRef.current?.setData(ema21Data);
      ema200SeriesRef.current?.setData(ema200Data);
      vwapSeriesRef.current?.setData(vwapData);
      emaDataSetRef.current = true; // Mark that EMA data has been set successfully

      // IMPORTANT: Use module-level Set to survive React StrictMode double-mount
      // The ref-based check fails because refs reset on remount, causing visible range to be reset
      const chartKey = getChartKey(symbol, selectedTimeframe);
      let isInitialLoad = !initializedCharts.has(chartKey);
      const isVisibilityRefresh = lastCandleTimeJumped || firstCandleChanged;

      // Check if current visible range is valid for the new data
      // This handles the case where we get more data first (e.g., 3457 candles) and set range,
      // then get less data (e.g., 2160 candles) which makes the range out of bounds
      if (!isInitialLoad && chartRef.current && sanitizedCandles.length > 0) {
        const currentRange = chartRef.current.timeScale().getVisibleLogicalRange();
        if (currentRange && currentRange.from >= sanitizedCandles.length) {
          // Range is completely out of bounds, need to reset
          isInitialLoad = true;
        }
      }

      // Now update the refs after using their previous values
      initialLoadRef.current = true;
      lastCandleCountRef.current = aggregatedCandles.length;
      // Track first and last candle times for visibility refresh detection
      if (sanitizedCandles.length > 0) {
        lastFirstCandleTimeRef.current = sanitizedCandles[0].time as number;
        lastCandleTimeRef.current = sanitizedCandles[sanitizedCandles.length - 1].time as number;
        // Update upcoming events primitive with last candle time for positioning
        upcomingEventsPrimitiveRef.current?.setLastCandleTime(lastCandleTimeRef.current);
      }

      if (chartRef.current && sanitizedCandles.length > 0) {
        // Only reset chart position on true initial load (new symbol+timeframe combo)
        // Preserve position when returning from background (visibility refresh) or switching back to previously viewed timeframe
        if (isInitialLoad) {
          // Apply default view (both X and Y axis)
          console.log('📊 [CANDLE-EFFECT] Applying default view. isInitialLoad:', isInitialLoad, 'timeframeChanged:', timeframeChanged);

          // Calculate visible range, snapping the latest candle to the live edge.
          const totalBars = sanitizedCandles.length;
          const isMobileDevice = window.innerWidth <= 768;
          // Keep ~60 bars visible (desktop) / 23 (mobile).
          const visibleBars = isMobileDevice ? 23 : 60;
          // Small right margin so the latest candle sits near the right edge for
          // ANY timeframe. A fixed 45-bar margin ballooned in wall-clock time as
          // the timeframe grew (90min on 2m but ~3.75h of blank space on 5m),
          // shoving candles left and making higher timeframes look sparse/lean.
          const rightMarginBars = isMobileDevice ? 3 : 6;

          // Calculate the starting bar index (from the end)
          const fromBar = Math.max(0, totalBars - visibleBars);

          // Set visible logical range SYNCHRONOUSLY to avoid race conditions
          // This prevents the chart from briefly showing all candles before the range is applied
          chartRef.current.timeScale().setVisibleLogicalRange({
            from: fromBar,
            to: totalBars + rightMarginBars,
          });

          // Apply fixed Y-axis range centered on the current price (v5 API)
          // This gives consistent vertical scaling regardless of data variance
          const yAxisRange = getYAxisRange(symbol);
          const halfRange = yAxisRange / 2;

          // Use current price as center (more accurate than last candle midpoint)
          const centerPrice = currentPrice > 0 ? currentPrice : (sanitizedCandles[sanitizedCandles.length - 1]?.close || 0);

          if (centerPrice > 0 && candlestickSeriesRef.current) {
            // Disable autoScale so our fixed range sticks
            candlestickSeriesRef.current.priceScale().applyOptions({
              autoScale: false,
            });
            const applied = { from: centerPrice - halfRange, to: centerPrice + halfRange };
            candlestickSeriesRef.current.priceScale().setVisibleRange(applied);
            if (isVestFeedSymbol(symbol)) {
              // The scale margins widen what setVisibleRange shows (BTC 1000 drew
              // ~1600): re-apply so the pane shows the table span. Vest only.
              const series = candlestickSeriesRef.current;
              const paneHeight = chartRef.current.panes()[0]?.getHeight() ?? 0;
              const shownTo = paneHeight > 0 ? series.coordinateToPrice(0) : null;
              const shownFrom = paneHeight > 0 ? series.coordinateToPrice(paneHeight) : null;
              const fixed = shownTo !== null && shownFrom !== null
                ? compensatedVisibleRange(centerPrice, yAxisRange, applied, { from: shownFrom, to: shownTo })
                : null;
              if (fixed) series.priceScale().setVisibleRange(fixed);
            }
            console.log(`📊 [CHART] Applied Y-axis range: ${(centerPrice - halfRange).toFixed(2)} - ${(centerPrice + halfRange).toFixed(2)} (${yAxisRange} pts)`);
          }

          console.log(`📊 [CHART] Applied default zoom: ${visibleBars} bars visible (${isMobileDevice ? 'mobile' : 'desktop'}), range: ${fromBar} to ${totalBars + rightMarginBars}`);


          // Mark this symbol+timeframe as initialized to prevent reset on remount
          initializedCharts.add(chartKey);
        } else if (isVisibilityRefresh) {
          // Visibility refresh: keep existing chart position, just update data
          console.log('📊 [CHART] Visibility refresh - preserving chart position');
        }
      }

      // Reset the flag after a delay to allow range to settle.
      // Before re-enabling the volume->main sync, pin the volume chart to the
      // main chart's current range so the sync mirrors our intended zoom instead
      // of pushing the volume chart's auto-fit range back onto the main chart
      // (which overrode the zoom on timeframe switches -> lean/cramped candles).
      setTimeout(() => {
        if (chartRef.current && volumeChartRef.current) {
          const mainRange = chartRef.current.timeScale().getVisibleLogicalRange();
          if (mainRange) {
            volumeChartRef.current.timeScale().setVisibleLogicalRange(mainRange);
          }
        }
        isSettingDataRef.current = false;
      }, 200);
    } else {
      // Real-time update: just update the last candle and indicators
      if (aggregatedCandles.length > 0) {
        const lastCandle = aggregatedCandles[aggregatedCandles.length - 1];

        // Validate lastCandle.time before update
        if (typeof lastCandle.time !== 'number') {
          console.error(`Invalid time type in lastCandle!`, lastCandle);
          return;
        }

        // Vest feeds: the live candle opens at the previous close (#626),
        // display only. When a new candle has just opened, redraw the one
        // before it too (its final ticks may not have been drawn), so the
        // close it shows is exactly the new candle's open.
        const drawnLast = priceSeriesLastBar(symbol, aggregatedCandles) ?? lastCandle;
        const prevDrawn = vestPrevBarRedraw(symbol, aggregatedCandles, lastCandleCountRef.current);
        if (prevDrawn && typeof prevDrawn.time === 'number') {
          try {
            candlestickSeriesRef.current.update(prevDrawn, true);
          } catch (err) {
            console.warn('[CHART] vest previous candle redraw failed', err);
          }
          // Its volume bar too: the colour follows the drawn candle (#628).
          if (volumeSeriesRef.current) {
            try {
              volumeSeriesRef.current.update({
                time: prevDrawn.time,
                value: displayVolume(symbolRef.current, prevDrawn.volume),
                color: cmeVolumeColor(prevDrawn, cmeSpikeTimesRef.current),
              }, true);
            } catch (err) {
              console.warn('[CHART] vest previous volume bar redraw failed', err);
            }
          }
        }
        candlestickSeriesRef.current.update(drawnLast);

        // Update volume for the last candle
        if (volumeSeriesRef.current) {
          volumeSeriesRef.current.update({
            time: lastCandle.time,
            value: displayVolume(symbolRef.current, lastCandle.volume),
            color: cmeVolumeColor(drawnLast, cmeSpikeTimesRef.current),
          });
        }

        // Update indicators (with validation)
        if (ema9Data.length > 0) {
          const ema9Point = ema9Data[ema9Data.length - 1];
          if (typeof ema9Point.time === 'number') {
            ema9SeriesRef.current.update(ema9Point);
          }
        }
        if (ema21Data.length > 0) {
          const ema21Point = ema21Data[ema21Data.length - 1];
          if (typeof ema21Point.time === 'number') {
            ema21SeriesRef.current.update(ema21Point);
          }
        }
        if (ema200Data.length > 0) {
          const ema200Point = ema200Data[ema200Data.length - 1];
          if (typeof ema200Point.time === 'number') {
            ema200SeriesRef.current.update(ema200Point);
          }
        }
        if (vwapData.length > 0) {
          const vwapPoint = vwapData[vwapData.length - 1];
          if (typeof vwapPoint.time === 'number') {
            vwapSeriesRef.current.update(vwapPoint);
          }
        }

        lastCandleCountRef.current = aggregatedCandles.length;
        // Update last candle time for visibility refresh detection
        lastCandleTimeRef.current = lastCandle.time as number;
        // Update upcoming events primitive with last candle time for positioning
        upcomingEventsPrimitiveRef.current?.setLastCandleTime(lastCandleTimeRef.current);
      }
    }
  // chartSeriesVersion is critical: it forces this effect to re-run when chart is recreated (React StrictMode)
  // Without it, the effect's dependencies might not change after chart recreation, causing setData to be called
  // on destroyed series refs
  }, [aggregatedCandles, atrData, ema9Data, ema21Data, ema200Data, vwapData, formatPSTTime, currentATR, chartSeriesVersion, candlesVersion]);

  // CRITICAL: Ensure EMA data is set on chart series whenever chart is recreated
  // This runs on every chartSeriesVersion change to guarantee EMAs are visible
  useEffect(() => {
    if (chartSeriesVersion === 0) return; // Chart not yet created

    // Set EMA data if we have both series and data
    if (ema9SeriesRef.current) {
      try {
        ema9SeriesRef.current.setData(ema9Data);
      } catch (e) {
        // Series might be destroyed, ignore
      }
    }
    if (ema21SeriesRef.current) {
      try {
        ema21SeriesRef.current.setData(ema21Data);
      } catch (e) {}
    }
    if (ema200SeriesRef.current) {
      try {
        ema200SeriesRef.current.setData(ema200Data);
      } catch (e) {}
    }
    if (vwapSeriesRef.current) {
      try {
        vwapSeriesRef.current.setData(vwapData);
      } catch (e) {}
    }
  }, [chartSeriesVersion, ema9Data, ema21Data, ema200Data, vwapData]);

  // Update trailing candle primitive when candles change
  // Trailing data comes from raw candles (ExtendedCandle), not aggregated
  useEffect(() => {
    if (!trailingCandlePrimitiveRef.current) return;

    // Build trailing candle data from raw candles with trailing field
    // Only render trailing candles for 1m and 2m timeframes (sub-minute trailing makes no sense for higher TFs)
    const intervalMinutes = TIMEFRAME_MINUTES[selectedTimeframe];
    if (intervalMinutes > 2) {
      // Hide trailing candles for higher timeframes
      trailingCandlePrimitiveRef.current.setData([]);
      return;
    }

    // Get the last candle timestamp to mark forming bar
    const lastCandleTimestamp = candles.length > 0 ? candles[candles.length - 1].timestamp : 0;

    // Build trailing data from candles with valid trailing
    const trailingData: TrailingCandleData[] = [];
    for (const candle of candles as ExtendedCandle[]) {
      if (!candle.trailing || !candle.trailing.isValid) continue;

      trailingData.push({
        time: candle.timestamp,
        mainOpen: candle.open,
        mainHigh: candle.high,
        mainLow: candle.low,
        mainClose: candle.close,
        trailingOpen: candle.trailing.open,
        trailingHigh: candle.trailing.high,
        trailingLow: candle.trailing.low,
        trailingClose: candle.trailing.close,
        isValid: candle.trailing.isValid,
        isFormingBar: candle.timestamp === lastCandleTimestamp,
      });
    }

    trailingCandlePrimitiveRef.current.setData(trailingData);
  }, [candles, selectedTimeframe]);

  // Subscribe to trailing candle settings changes
  // Use Zustand subscription pattern to avoid re-render on every settings change
  useEffect(() => {
    let prevEnabled = useSettingsStore.getState().trailingCandle.enabled;

    const unsubscribe = useSettingsStore.subscribe((state) => {
      if (state.trailingCandle.enabled !== prevEnabled) {
        prevEnabled = state.trailingCandle.enabled;
        if (trailingCandlePrimitiveRef.current) {
          trailingCandlePrimitiveRef.current.setEnabled(prevEnabled);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Subscribe to magnet candle settings and markers changes
  useEffect(() => {
    const updateMagnetLevels = () => {
      if (!magnetCandlePrimitiveRef.current) return;

      const base = normalizeToBase(symbol);
      const magnetEnabled = useSettingsStore.getState().getMagnetCandleForSymbol(base)?.enabled ?? false;
      if (!magnetEnabled) {
        magnetCandlePrimitiveRef.current.setLevels([]);
        return;
      }

      const magnetMarkers = useMarketStore.getState().magnetMarkers ?? [];
      const levels: MagnetLevel[] = magnetMarkers
        .filter((m: any) => normalizeToBase(m.symbol) === base)
        .map((m: any) => ({
          timestamp: m.timestamp,
          price: m.price,
          direction: m.direction as 'bullish' | 'bearish',
          valid: m.valid,
          invalidatedAt: m.invalidatedAt ?? null,
          range: m.range,
        }));

      magnetCandlePrimitiveRef.current.setLevels(levels);

      // Sync extension line count from settings
      const magnetSettings = useSettingsStore.getState().getMagnetCandleForSymbol(base);
      magnetCandlePrimitiveRef.current.setExtensionLineCount(magnetSettings?.extensionLineCount ?? 5);
    };

    // Initial sync
    updateMagnetLevels();

    // Subscribe to magnetMarkers changes
    let prevMarkers = useMarketStore.getState().magnetMarkers;
    const unsubMarkers = useMarketStore.subscribe((state) => {
      if (state.magnetMarkers !== prevMarkers) {
        prevMarkers = state.magnetMarkers;
        updateMagnetLevels();
      }
    });

    // Subscribe to candle updates so breach detection recalculates as new bars arrive
    let prevCandlesVersion = useMarketStore.getState().candlesVersion;
    const unsubCandles = useMarketStore.subscribe((state) => {
      if (state.candlesVersion !== prevCandlesVersion) {
        prevCandlesVersion = state.candlesVersion;
        updateMagnetLevels();
      }
    });

    // Subscribe to magnetCandle setting changes (enabled, extensionLineCount, etc.)
    let prevMagnet = useSettingsStore.getState().magnetCandle;
    let prevPerSymbol = useSettingsStore.getState().perSymbolMagnetCandle;
    const unsubSettings = useSettingsStore.subscribe((state) => {
      if (state.magnetCandle !== prevMagnet || state.perSymbolMagnetCandle !== prevPerSymbol) {
        prevMagnet = state.magnetCandle;
        prevPerSymbol = state.perSymbolMagnetCandle;
        updateMagnetLevels();
      }
    });

    return () => {
      unsubMarkers();
      unsubCandles();
      unsubSettings();
    };
  }, [symbol]);

  // Subscribe to volume profile settings and data changes
  // Use Zustand subscription pattern to avoid re-render on every settings change
  useEffect(() => {
    let prevEnabled = useVolumeProfileStore.getState().settings.enabled;

    // Subscribe to enabled toggle
    const unsubscribeEnabled = useVolumeProfileStore.subscribe((state) => {
      if (state.settings.enabled !== prevEnabled) {
        prevEnabled = state.settings.enabled;
        if (volumeProfilePrimitiveRef.current) {
          volumeProfilePrimitiveRef.current.setEnabled(prevEnabled);
        }
        // Fetch profile data when enabling
        if (prevEnabled) {
          useVolumeProfileStore.getState().fetchProfile(symbol);
        }
      }
    });

    // Subscribe to profile snapshot updates
    let prevSnapshot = useVolumeProfileStore.getState().profileSnapshots[symbol];
    const unsubscribeProfile = useVolumeProfileStore.subscribe((state) => {
      const snapshot = state.profileSnapshots[symbol];
      if (snapshot !== prevSnapshot) {
        prevSnapshot = snapshot;
        if (snapshot && volumeProfilePrimitiveRef.current) {
          volumeProfilePrimitiveRef.current.updateData(snapshot);
        }
      }
    });

    // Subscribe to settings changes for options update
    let prevSettings = useVolumeProfileStore.getState().settings;
    const unsubscribeSettings = useVolumeProfileStore.subscribe((state) => {
      if (state.settings !== prevSettings) {
        prevSettings = state.settings;
        if (volumeProfilePrimitiveRef.current) {
          volumeProfilePrimitiveRef.current.updateOptions({
            showDelta: state.settings.showDelta,
            showValueArea: state.settings.showValueArea ?? true,
            showVahVal: state.settings.showVahVal ?? true,
          });
        }
      }
    });

    // Fetch initial profile data if enabled
    if (useVolumeProfileStore.getState().settings.enabled) {
      useVolumeProfileStore.getState().fetchProfile(symbol);
    }

    return () => {
      unsubscribeEnabled();
      unsubscribeProfile();
      unsubscribeSettings();
    };
  }, [symbol]);

  // Subscribe to cumulative delta history updates
  // Updates the delta line primitive when new delta data arrives
  useEffect(() => {
    let prevHistory = useVolumeProfileStore.getState().deltaHistory[symbol];
    const unsubscribe = useVolumeProfileStore.subscribe((state) => {
      const history = state.deltaHistory[symbol];
      if (history !== prevHistory) {
        prevHistory = history;
        if (history && cumulativeDeltaPrimitiveRef.current) {
          cumulativeDeltaPrimitiveRef.current.setData(history);
        }
      }
    });
    return () => unsubscribe();
  }, [symbol]);

  // Fetch initial delta history when settings enable cumulative delta
  useEffect(() => {
    const settings = useVolumeProfileStore.getState().settings;
    if (settings.enabled && settings.showCumulativeDelta && cumulativeDeltaPrimitiveRef.current) {
      useVolumeProfileStore.getState().fetchDeltaHistory(symbol);
    }
  }, [symbol]);

  // Subscribe to showCumulativeDelta settings changes
  // Creates/destroys the delta primitive based on setting toggle
  useEffect(() => {
    let prevShow = useVolumeProfileStore.getState().settings.showCumulativeDelta;
    let prevEnabled = useVolumeProfileStore.getState().settings.enabled;

    const unsubscribe = useVolumeProfileStore.subscribe((state) => {
      const show = state.settings.showCumulativeDelta;
      const enabled = state.settings.enabled;

      // Only act if relevant settings changed
      if (show === prevShow && enabled === prevEnabled) return;

      const shouldShow = enabled && show;
      prevShow = show;
      prevEnabled = enabled;

      if (shouldShow && !cumulativeDeltaPrimitiveRef.current) {
        // Create and attach the primitive
        const primitive = new CumulativeDeltaPrimitive();
        candlestickSeriesRef.current?.attachPrimitive(primitive);
        cumulativeDeltaPrimitiveRef.current = primitive;
        // Fetch delta history for the new primitive
        useVolumeProfileStore.getState().fetchDeltaHistory(symbol);
      } else if (!shouldShow && cumulativeDeltaPrimitiveRef.current) {
        // Detach and cleanup the primitive
        candlestickSeriesRef.current?.detachPrimitive(cumulativeDeltaPrimitiveRef.current);
        cumulativeDeltaPrimitiveRef.current = null;
      }
    });

    return () => unsubscribe();
  }, [symbol]);

  // Cleanup cumulative delta primitive on unmount
  useEffect(() => {
    return () => {
      if (cumulativeDeltaPrimitiveRef.current) {
        candlestickSeriesRef.current?.detachPrimitive(cumulativeDeltaPrimitiveRef.current);
        cumulativeDeltaPrimitiveRef.current = null;
      }
    };
  }, []);

  // ── Fair Price (JJ Simon) indicator ──────────────────────────────────────
  // Socket listeners ("fair-price:update" / "fair-price:signal") live only
  // while the indicator is enabled; teardown removes them on disable/unmount.
  useEffect(() => {
    if (!fairPriceEnabled) return;
    useFairPriceStore.getState().initialize();
    return () => {
      useFairPriceStore.getState().teardown();
    };
  }, [fairPriceEnabled]);

  // Attach/detach the Fair Price canvas primitive on the candle series and
  // fetch initial state — on enable AND on symbol change (mirrors the gated
  // CumulativeDeltaPrimitive pattern; BosLabelPrimitive stays attached because
  // it has no user toggle, this one does).
  useEffect(() => {
    if (!fairPriceEnabled) return;
    const store = useFairPriceStore.getState();
    store.fetchSettings();
    store.fetchState(symbol);
    if (!fairPricePrimitiveRef.current && candlestickSeriesRef.current) {
      const primitive = new FairPricePrimitive();
      primitive.setMarkerLayout(markerLayoutRef.current);
      candlestickSeriesRef.current.attachPrimitive(primitive);
      fairPricePrimitiveRef.current = primitive;
    }
    return () => {
      if (fairPricePrimitiveRef.current) {
        candlestickSeriesRef.current?.detachPrimitive(fairPricePrimitiveRef.current);
        fairPricePrimitiveRef.current = null;
      }
    };
  }, [fairPriceEnabled, symbol]);

  // Feed store state/signals into the primitive whenever they change. Times
  // are converted to bar-aligned SECONDS so timeToCoordinate resolves them on
  // the existing time scale (same trick the BOS overlay uses).
  useEffect(() => {
    if (!fairPriceEnabled) return;
    const intervalMs = TIMEFRAME_MINUTES[selectedTimeframe] * 60 * 1000;
    // Backend times are ms epoch; tolerate seconds just in case (< 1e12).
    const alignSecs = (ts: number): number => {
      const ms = ts < 1e12 ? ts * 1000 : ts;
      return Math.floor((Math.floor(ms / intervalMs) * intervalMs) / 1000);
    };
    const chartBase = normalizeToBase(symbol);

    const feedPrimitive = () => {
      const primitive = fairPricePrimitiveRef.current;
      if (!primitive) return;
      // Per-symbol state: reading the shared `state` field meant this chart's
      // anchors blanked out every time the engine pushed an update for a
      // different symbol.
      const { statesBySymbol, signals } = useFairPriceStore.getState();
      const state = statesBySymbol[chartBase] ?? null;
      const stateMatches = !!state;
      const anchors: FairPriceAnchorItem[] =
        stateMatches && state
          ? state.anchors.map((a) => ({
              kind: a.kind,
              price: a.price,
              priceHigh: a.priceHigh,
              priceLow: a.priceLow,
              setAtTime: alignSecs(a.setAt),
              active: a.active,
            }))
          : [];
      const signalItems: FairPriceSignalItem[] = signals
        .filter((sg) => normalizeToBase(sg.symbol) === chartBase)
        .map((sg) => ({
          id: sg.id,
          time: alignSecs(sg.timestamp),
          price: sg.entry,
          grade: sg.grade,
          direction: sg.direction,
          riskText: `${sg.slPoints}/${sg.tpPoints}${sg.halfSize ? '½' : ''}`,
          anchorKind: sg.anchorKind,
          distance: sg.distance,
          status: sg.status,
        }));
      primitive.update({
        anchors,
        signals: signalItems,
        activeAnchorKind: stateMatches && state ? state.activeAnchorKind ?? null : null,
        armed: stateMatches && state ? state.armed : false,
      });
    };

    feedPrimitive();
    const unsubscribe = useFairPriceStore.subscribe(feedPrimitive);
    return () => unsubscribe();
  }, [fairPriceEnabled, symbol, selectedTimeframe]);

  // ── Displacement candle (JJ Simon) indicator ─────────────────────────────
  // Socket listeners live only while the indicator is enabled; teardown
  // removes them on disable/unmount (same shape as the Fair Price wiring).
  useEffect(() => {
    if (!displacementEnabled) return;
    useDisplacementStore.getState().initialize();
    return () => {
      useDisplacementStore.getState().teardown();
    };
  }, [displacementEnabled]);

  // Attach/detach the displacement canvas primitive and fetch initial markers —
  // on enable AND on symbol change.
  useEffect(() => {
    if (!displacementEnabled) return;
    const store = useDisplacementStore.getState();
    store.fetchSettings();
    store.fetchMarkers(symbol);
    if (!displacementPrimitiveRef.current && candlestickSeriesRef.current) {
      const primitive = new DisplacementPrimitive();
      primitive.setMarkerLayout(markerLayoutRef.current);
      candlestickSeriesRef.current.attachPrimitive(primitive);
      displacementPrimitiveRef.current = primitive;
    }
    return () => {
      if (displacementPrimitiveRef.current) {
        candlestickSeriesRef.current?.detachPrimitive(displacementPrimitiveRef.current);
        displacementPrimitiveRef.current = null;
      }
    };
  }, [displacementEnabled, symbol]);

  // Feed store markers into the primitive whenever they change. The primitive
  // resolves x from the raw ms timestamp itself, so no bar alignment is needed
  // here — but markers are filtered to the chart's symbol.
  useEffect(() => {
    if (!displacementEnabled) return;
    const chartBase = normalizeToBase(symbol);

    const feedPrimitive = () => {
      const primitive = displacementPrimitiveRef.current;
      if (!primitive) return;
      const { markers, showFvg } = useDisplacementStore.getState();
      const levels: DisplacementLevel[] = markers
        .filter((m) => normalizeToBase(m.symbol) === chartBase)
        .map((m) => ({
          id: m.id,
          timestamp: m.timestamp,
          direction: m.direction,
          open: m.open,
          high: m.high,
          low: m.low,
          close: m.close,
          rangePoints: m.rangePoints,
          grade: m.grade,
          score: m.score,
          status: m.status ?? 'live',
          createdFvg: m.createdFvg,
          fvgTop: m.fvgTop,
          fvgBottom: m.fvgBottom,
          skipped: m.skipped,
          skipReason: m.skipReason,
        }));
      primitive.setShowFvg(showFvg);
      primitive.setLevels(levels);
    };

    feedPrimitive();
    const unsubscribe = useDisplacementStore.subscribe(feedPrimitive);
    return () => unsubscribe();
  }, [displacementEnabled, symbol]);

  // ── Absorption-at-level markers ("ABS PW") ───────────────────────────────
  // Detected by the engine on raw prints at each base-bar close. Shown/hidden
  // and coloured from the Levels menu (levelsStore `absorption`); Clean hides
  // them with the other levels because the visibility is masked above.
  const absorptionVisible = levelVisibility.absorption;
  const absorptionColors = levelColors.absorption;

  useEffect(() => {
    if (!absorptionVisible) return;
    useAbsorptionStore.getState().initialize();
    return () => {
      useAbsorptionStore.getState().teardown();
    };
  }, [absorptionVisible]);

  useEffect(() => {
    if (!absorptionVisible) return;
    useAbsorptionStore.getState().fetchMarkers(symbol);
    if (!absorptionPrimitiveRef.current && candlestickSeriesRef.current) {
      const primitive = new AbsorptionMarkerPrimitive();
      // Where the dots landed, on the container: lets a headless check hover a
      // real marker without reaching into the canvas.
      primitive.setOnPlaced((placed) => {
        const el = chartContainerRef.current;
        if (!el) return;
        const onScreen = placed.filter((p) => p.x >= 0 && p.y >= 0 && p.x <= el.clientWidth && p.y <= el.clientHeight);
        const last = onScreen[onScreen.length - 1];
        el.dataset.absPlaced = String(onScreen.length);
        el.dataset.absLast = last ? `${Math.round(last.x)},${Math.round(last.y)}` : '';
      });
      primitive.setMarkerLayout(markerLayoutRef.current);
      candlestickSeriesRef.current.attachPrimitive(primitive);
      absorptionPrimitiveRef.current = primitive;
    }
    return () => {
      if (absorptionPrimitiveRef.current) {
        try { candlestickSeriesRef.current?.detachPrimitive(absorptionPrimitiveRef.current); } catch { /* gone */ }
        absorptionPrimitiveRef.current = null;
      }
    };
  }, [absorptionVisible, symbol]);

  useEffect(() => {
    if (!absorptionVisible) return;
    const chartBase = normalizeToBase(symbol);
    const intervalMs = TIMEFRAME_MINUTES[selectedTimeframe] * 60 * 1000;
    const feed = () => {
      const primitive = absorptionPrimitiveRef.current;
      if (!primitive) return;
      const markers = useAbsorptionStore.getState().bySymbol[chartBase] ?? [];
      primitive.setItems(toAbsorptionItems(markers, intervalMs, { bull: absorptionColors.low, bear: absorptionColors.high }));
    };
    feed();
    const unsubscribe = useAbsorptionStore.subscribe(feed);
    return () => unsubscribe();
  }, [absorptionVisible, symbol, selectedTimeframe, absorptionColors.low, absorptionColors.high]);

  // ── Order-flow markers (flow-signals) ────────────────────────────────────
  // One primitive for every kind; the levelsStore flow* toggles (set from
  // the TradeFinder panel, behind its master switch) pick which kinds it
  // draws, and Clean masks them with the other signal markers.
  const flowShown = useMemo(() => {
    const master = !!levelVisibility[FLOW_MASTER_KEY];
    return Object.fromEntries(FLOW_LEVEL_KEYS.map((k) => [k, master && !!levelVisibility[k]])) as Record<FlowLevelKey, boolean>;
  }, [levelVisibility]);
  const flowShownKey = FLOW_LEVEL_KEYS.filter((k) => flowShown[k]).join(',');
  const flowVisible = flowShownKey.length > 0;
  const flowColorsKey = FLOW_LEVEL_KEYS.map((k) => `${levelColors[k].low}/${levelColors[k].high}`).join(',');

  // TradeFinder trading UX: markers on a key level are emphasised + tagged,
  // kinds without a reliable edge dimmed, older ones faded, the newest pulses;
  // the strip lists the latest few. Levels for the match are every level the
  // chart knows (whether drawn or not), the set the engine's push uses too.
  const tfLevelEmphasis = useTradeFinderUxStore((s) => s.levelEmphasis);
  const tfDimNoEdge = useTradeFinderUxStore((s) => s.dimNoEdge);
  const tfFade = useTradeFinderUxStore((s) => s.fade);
  const tfPulse = useTradeFinderUxStore((s) => s.pulse);
  const tfFadeStartMin = useTradeFinderUxStore((s) => s.fadeStartMin);
  const tfLevelTolMap = useTradeFinderUxStore((s) => s.levelTol);
  const tfStripEnabled = useTradeFinderUxStore((s) => s.stripEnabled);
  const tfSound = useTradeFinderUxStore((s) => s.sound);
  const flowMatchLevelsComputed = useMemo<KeyLevel[]>(() => {
    if (dataMode !== 'live') return [];
    return [
      ...(hasGex && gexAvailable
        ? gexKeyLevels(gexLevels, { callWall: true, putWall: true, gammaFlip: true, ivBands: true, strikes: false })
        : []),
      ...profileKeyLevels(profileLevels, { sessionProfile: true, weeklyProfile: true }),
      ...onhOnlKeyLevels(onhOnl),
      ...sessionKeyLevels({ pdh: priceLevels?.prevDayHigh, pdl: priceLevels?.prevDayLow, moc: mocOpenPrice || undefined }),
    ];
  }, [dataMode, hasGex, gexAvailable, gexLevels, profileLevels, onhOnl, priceLevels?.prevDayHigh, priceLevels?.prevDayLow, mocOpenPrice]);
  const flowMatchLevelsSig = JSON.stringify(flowMatchLevelsComputed.map((l) => [l.kind, l.price]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const flowMatchLevels = useMemo(() => flowMatchLevelsComputed, [flowMatchLevelsSig]);
  const flowMatchLevelsRef = useRef<KeyLevel[]>(flowMatchLevels);
  flowMatchLevelsRef.current = flowMatchLevels;
  const tfTol = levelTolFor(normalizeToBase(symbol), tfLevelTolMap);
  const tfStripVisible = tfStripEnabled && flowVisible && !isCleanHidden({ active: cleanChart, hides: cleanHides }, 'signals');

  // Queue tilt band + ribbon (TradeFinder Context): ES / MES only, behind the
  // master switch; levelVisibility already has Clean applied (signals group).
  const queueTiltOn = !!levelVisibility[FLOW_MASTER_KEY] && !!levelVisibility.flowQueueTilt;
  const getChartForBand = useCallback(() => chartRef.current, []);
  const getSeriesForBand = useCallback(() => candlestickSeriesRef.current as ISeriesApi<SeriesType, Time> | null, []);

  // TradeFinder recent list: centre the chart on a signal's bar, keeping the zoom.
  const jumpToFlowSignal = useCallback((ms: number) => {
    const ts = chartRef.current?.timeScale();
    const range = ts?.getVisibleLogicalRange();
    if (!ts || !range) return;
    const idx = ts.timeToIndex(flowBarTime(ms, TIMEFRAME_MINUTES[selectedTimeframe]) as Time, true);
    if (idx === null) return;
    const half = (range.to - range.from) / 2;
    ts.setVisibleLogicalRange({ from: idx - half, to: idx + half });
  }, [selectedTimeframe]);

  useEffect(() => {
    if (!flowVisible) return;
    useFlowSignalsStore.getState().initialize();
    return () => useFlowSignalsStore.getState().teardown();
  }, [flowVisible]);

  useEffect(() => {
    if (!flowVisible) return;
    const unwatch = useFlowSignalsStore.getState().watchSymbol(symbol);
    if (!flowPrimitiveRef.current && candlestickSeriesRef.current) {
      const primitive = new FlowSignalMarkerPrimitive();
      // Where the glyphs landed, on the container, for headless checks.
      primitive.setOnPlaced((placed) => {
        const el = chartContainerRef.current;
        if (!el) return;
        const onScreen = placed.filter((p) => p.x >= 0 && p.y >= 0 && p.x <= el.clientWidth && p.y <= el.clientHeight);
        const last = onScreen[onScreen.length - 1];
        setFlowPlacedData(el, onScreen.length, last ? `${Math.round(last.x)},${Math.round(last.y)}` : '');
      });
      primitive.setMarkerLayout(markerLayoutRef.current);
      candlestickSeriesRef.current.attachPrimitive(primitive);
      flowPrimitiveRef.current = primitive;
    }
    return () => {
      unwatch();
      if (flowPrimitiveRef.current) {
        flowPrimitiveRef.current.setOnPlaced(undefined);
        try { candlestickSeriesRef.current?.detachPrimitive(flowPrimitiveRef.current); } catch { /* gone */ }
        flowPrimitiveRef.current = null;
        // Nothing is drawn any more: the headless hook must say so too.
        const el = chartContainerRef.current;
        if (el) setFlowPlacedData(el, 0, '');
      }
    };
  }, [flowVisible, symbol]);

  useEffect(() => {
    if (!flowVisible) return;
    const chartBase = normalizeToBase(symbol);
    const tfMinutes = TIMEFRAME_MINUTES[selectedTimeframe];
    const { colors } = useLevelsStore.getState();
    const styled = tfLevelEmphasis || tfDimNoEdge || tfFade || tfPulse;
    const feed = () => {
      const primitive = flowPrimitiveRef.current;
      if (!primitive) return;
      const signals = useFlowSignalsStore.getState().bySymbol[chartBase] ?? [];
      if (!styled) {
        primitive.setItems(toFlowItems(signals, tfMinutes, flowShown, colors, undefined, PULSE_MS, isMobile));
        return;
      }
      const now = Date.now();
      const newest = recentSignals(signals, flowShown, 1)[0];
      const opts: MarkerStyleOptions = {
        now,
        fade: tfFade,
        dimNoEdge: tfDimNoEdge,
        emphasis: tfLevelEmphasis,
        pulse: tfPulse,
        levels: flowMatchLevels,
        tol: tfTol,
        edgeByKind: currentEdgeByKind(signals),
        newestId: newest?.id ?? null,
        fadeStartMs: tfFadeStartMin * 60_000,
      };
      primitive.setItems(toFlowItems(signals, tfMinutes, flowShown, colors, (s) => markerStyle(s, opts), PULSE_MS, isMobile));
    };
    feed();
    // Only this chart's root: a flush for another root leaves its slice as is.
    const unsubscribe = useFlowSignalsStore.subscribe((s, prev) => {
      if (s.bySymbol[chartBase] !== prev.bySymbol[chartBase]) feed();
    });
    // The recency fade moves with the clock, not only with new signals.
    const timer = tfFade ? setInterval(feed, 15_000) : null;
    return () => {
      unsubscribe();
      if (timer !== null) clearInterval(timer);
    };
    // flowShown / colours are tracked through their string keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowVisible, symbol, selectedTimeframe, flowShownKey, flowColorsKey,
    tfLevelEmphasis, tfDimNoEdge, tfFade, tfPulse, flowMatchLevels, tfTol, tfFadeStartMin, isMobile]);

  // DEV only (staging's Vite server; never in a production build): what the
  // level match and the flow primitive see, for the headless spec.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as unknown as Record<string, unknown>;
    w.__uttFlowLevels = () => flowMatchLevelsRef.current.map((l) => ({ kind: l.kind, price: l.price }));
    w.__uttFlowPlaced = () => {
      const prim = flowPrimitiveRef.current;
      const placed = prim?.place() ?? [];
      const now = prim?.now() ?? Date.now();
      return placed.map((p) => ({
        ids: p.ids, x: p.x, y: p.y, size: p.size, emphasis: !!p.emphasis, tag: p.tag ?? null, alpha: p.alpha ?? 1,
        glyph: p.glyph.symbol,
        // sweep_follow timer ring: remaining fraction (null = no ring).
        hold: p.hold ? holdFraction(now, p.hold.startMs, p.hold.holdMs / 1000) : null,
      }));
    };
    // The newest displayed bar (unix s) and its close, to inject a signal on it.
    w.__uttLastBar = () => {
      const c = aggCandlesRef.current[aggCandlesRef.current.length - 1];
      return c ? { time: c.time as number, close: c.close, high: c.high, low: c.low } : null;
    };
    return () => {
      delete w.__uttFlowLevels;
      delete w.__uttFlowPlaced;
      delete w.__uttLastBar;
    };
  }, []);

  // Strip click: centre on the signal's bar and blink its marker.
  const selectFlowSignal = useCallback((s: FlowSignal) => {
    jumpToFlowSignal(s.timestamp);
    flowPrimitiveRef.current?.flash(s.id);
  }, [jumpToFlowSignal]);

  // Optional chimes for live signals on this chart's root (default off).
  const tfSoundOn = tfSound.enabled && Object.values(tfSound.kinds).some(Boolean);
  useEffect(() => {
    if (!tfSoundOn) return;
    const chartBase = normalizeToBase(symbol);
    void useFlowAlertsStore.getState().fetch();
    const stopListen = useFlowAlertsStore.getState().listen();
    const stopLive = onLiveFlowSignals((batch) => {
      const mine = batch.filter((s) => normalizeToBase(s.symbol) === chartBase);
      if (mine.length === 0) return;
      chimeForLive(mine, useTradeFinderUxStore.getState().sound, useFlowAlertsStore.getState().settings, flowMatchLevelsRef.current);
    });
    return () => {
      stopLive();
      stopListen();
    };
  }, [tfSoundOn, symbol]);

  // ── Order-flow line panes (flow-signals) ─────────────────────────────────
  // The levelsStore flowLine* toggles (TradeFinder Lines tab; Clean hides
  // them with the volume studies) pick the lines; flowLinePanes.ts owns the
  // series and panes under the price pane, flowLinesStore the data.
  const flowLineNamesKey = FLOW_LINES.filter((l) => levelVisibility[l.key]).map((l) => l.name).join(',');
  const flowLinesOn = flowLineNamesKey.length > 0;
  const flowLineColorsKey = FLOW_LINES.map((l) => levelColors[l.key]).join(',');
  // Re-snap when a bar is added or history changes, not on every tick.
  const flowLineBarsKey = aggregatedCandles.length > 0 ? `${aggregatedCandles.length}:${aggregatedCandles[0].time}` : '';
  const flowLinePanesRef = useRef<FlowLinePanes | null>(null);

  useEffect(() => {
    if (!flowLinesOn) return;
    useFlowLinesStore.getState().initialize();
    return () => useFlowLinesStore.getState().teardown();
  }, [flowLinesOn]);

  // Series and panes follow the toggles and outlive symbol / timeframe switches.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || (!flowLinesOn && !flowLinePanesRef.current)) return;
    const panes = flowLinePanesRef.current ?? (flowLinePanesRef.current = new FlowLinePanes(chart));
    const { colors } = useLevelsStore.getState();
    const names = flowLinesOn ? (flowLineNamesKey.split(',') as FlowLineName[]) : [];
    panes.sync(names, Object.fromEntries(FLOW_LINES.map((l) => [l.name, colors[l.key]])));
    // For headless checks: which lines and how many panes are up.
    const el = chartContainerRef.current;
    if (el) {
      el.dataset.flowLines = panes.names().join(',');
      el.dataset.flowLinePanes = String(panes.paneCount());
    }
    // The price pane just changed height: re-place the price-anchored overlays.
    scheduleChartUpdate();
  }, [flowLinesOn, flowLineNamesKey, flowLineColorsKey, scheduleChartUpdate]);

  useEffect(() => () => {
    // Declared after the chart effect, so on unmount chart.remove() already
    // ran; destroy() tolerates that.
    flowLinePanesRef.current?.destroy();
    flowLinePanesRef.current = null;
  }, []);

  useEffect(() => {
    if (!flowLinesOn) return;
    return useFlowLinesStore.getState().watchLines(symbol, flowLineNamesKey.split(',') as FlowLineName[]);
  }, [flowLinesOn, symbol, flowLineNamesKey]);

  useEffect(() => {
    const panes = flowLinePanesRef.current;
    if (!flowLinesOn || !panes) return;
    const root = normalizeToBase(symbol);
    const names = flowLineNamesKey.split(',') as FlowLineName[];
    const barTimes = aggCandlesRef.current.map((c) => c.time as number);
    const stepSec = TIMEFRAME_MINUTES[selectedTimeframe] * 60;
    let held = useFlowLinesStore.getState().byRoot[root];
    const full = (name: FlowLineName) => panes.setPoints(name, snapLinePoints(held?.[name] ?? [], barTimes, stepSec));
    for (const n of names) full(n);
    // Only this root's slice: another root's points must not re-snap these lines.
    return useFlowLinesStore.subscribe((s) => {
      const next = s.byRoot[root];
      if (next === held) return;
      const prev = held;
      held = next;
      for (const n of names) {
        const added = appendedPoints(prev?.[n], next?.[n]);
        if (added === null) {
          full(n);
          continue;
        }
        for (const [ts, value] of added) {
          const t = snapToBar(ts, barTimes, stepSec);
          if (t === null) continue;
          if (!panes.update(n, { time: t as Time, value })) {
            full(n);
            break;
          }
        }
      }
    });
    // aggregatedCandles is read through its ref; flowLineBarsKey says when it changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowLinesOn, symbol, selectedTimeframe, flowLineNamesKey, flowLineBarsKey]);

  // Price-anchored DOM overlays vs the line panes: where the price pane ends
  // (null = no line panes, overlays unchanged) and which prices sit below it,
  // shown as badges on that edge. Refreshed by the RAF coordinator, so pans,
  // scale changes, pane toggles and separator drags all re-measure.
  const [linePaneTop, setLinePaneTop] = useState<number | null>(null);
  const [paneEdgeItems, setPaneEdgeItems] = useState<PriceOverlayItem[]>([]);
  const paneEdgeSourcesRef = useRef<PriceOverlaySources | null>(null);
  paneEdgeSourcesRef.current = {
    orders: filteredLiveOrders,
    accountId: selectedAccountId,
    position: currentLivePosition ?? null,
    orderLines,
    alerts: symbolAlerts,
    draft: currentLivePosition && currentLivePosition.netPos !== 0 ? draftBracket : null,
  };
  useEffect(() => {
    chartUpdatersRef.current.paneEdge = () => {
      const chart = chartRef.current;
      const series = candlestickSeriesRef.current;
      const top = chart ? linePaneSpan(chart)?.top ?? null : null;
      setLinePaneTop((prev) => (prev === top ? prev : top));
      const sources = paneEdgeSourcesRef.current;
      const items = top === null || !series || !sources
        ? []
        : belowPricePane(priceOverlayItems(sources), (p) => series.priceToCoordinate(p), top);
      setPaneEdgeItems((prev) => (sameOverlayItems(prev, items) ? prev : items));
    };
    return () => { chartUpdatersRef.current.paneEdge = null; };
  }, []);
  useEffect(() => {
    scheduleChartUpdate();
  }, [filteredLiveOrders, selectedAccountId, currentLivePosition, orderLines, symbolAlerts, draftBracket, scheduleChartUpdate]);

  // ── Vest NQ-PERP candle overlay (price pane) ──────────────────────────────
  // Hollow green/red candles on top of the CME candles, Vest prices minus a
  // rolling Vest-CME gap (utils/vestCandles: median over the last 30 closed
  // 2m pairs before each bar). autoscaleInfoProvider returns null so only the
  // CME candles set the price range: the overlay can never squash the chart;
  // a Vest-only wick past the visible range is clipped at the pane edge.
  // Declared after the chart effect: chartRef is set by the time this runs.
  const vestCandleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const [vestCandleSeriesVersion, setVestCandleSeriesVersion] = useState(0);
  const [vestCandleGap, setVestCandleGap] = useState<number | null>(null);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !showVestCandles) return;
    const { minMove, precision } = getSymbolPriceFormat(symbol);
    const series = chart.addSeries(CandlestickSeries, {
      upColor: 'rgba(0, 0, 0, 0)',
      downColor: 'rgba(0, 0, 0, 0)',
      borderVisible: true,
      borderUpColor: VEST_CANDLE_COLORS.up,
      borderDownColor: VEST_CANDLE_COLORS.down,
      wickVisible: true,
      wickUpColor: VEST_CANDLE_COLORS.up,
      wickDownColor: VEST_CANDLE_COLORS.down,
      lastValueVisible: false,
      priceLineVisible: false,
      autoscaleInfoProvider: () => null,
      priceFormat: { type: 'price' as const, minMove, precision },
    });
    vestCandleSeriesRef.current = series;
    setVestCandleSeriesVersion((v) => v + 1);
    return () => {
      try { chart.removeSeries(series); } catch { /* chart gone */ }
      if (vestCandleSeriesRef.current === series) vestCandleSeriesRef.current = null;
      setVestCandleSeriesVersion((v) => v + 1);
      setVestCandleGap(null);
      const el = chartContainerRef.current;
      if (el) {
        delete el.dataset.vestCandles;
        delete el.dataset.vestGap;
      }
    };
  }, [showVestCandles, symbol]);
  // CME 2m closes for the gap: closed buckets only, rebuilt once a bar.
  const vestCandleCmeCloses = useMemo(
    () => (showVestCandles ? cmeClosedCloses(historyCandles, formingMs) : null),
    [showVestCandles, historyCandles, formingMs],
  );
  const lastChartTime = aggregatedCandles.length > 0 ? (aggregatedCandles[aggregatedCandles.length - 1].time as number) : null;
  const vestCandleDrawRef = useRef<{ data: VestCandle[]; series: unknown }>({ data: [], series: null });
  useEffect(() => {
    const series = vestCandleSeriesRef.current;
    if (!showVestCandles || !series || !vestCandleCmeCloses) return;
    const tf = TIMEFRAME_MINUTES[selectedTimeframe];
    const refresh = () => {
      const times = aggCandlesRef.current.filter((c) => typeof c.time === 'number').map((c) => c.time as number);
      const { bars } = useVestVolumeStore.getState();
      const data = vestCandleData(times, bars.values(), rollingGaps(bars.values(), vestCandleCmeCloses), tf);
      const prev = vestCandleDrawRef.current;
      const idx = prev.series === series ? candleTailUpdates(prev.data, data) : null;
      const toPoint = (c: VestCandle) => ({ time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close });
      if (idx === null) {
        series.setData(data.map(toPoint));
      } else {
        for (const i of idx) series.update(toPoint(data[i]), i < data.length - 1);
      }
      vestCandleDrawRef.current = { data, series };
      const gap = latestGap(data);
      setVestCandleGap((g) => (g === gap ? g : gap));
      const el = chartContainerRef.current;
      if (el) {
        el.dataset.vestCandles = String(data.length);
        el.dataset.vestGap = gap === null ? '' : gap.toFixed(2);
      }
    };
    refresh();
    let lastBars = useVestVolumeStore.getState().bars;
    const unsub = useVestVolumeStore.subscribe((st) => {
      if (st.bars === lastBars) return;
      lastBars = st.bars;
      refresh();
    });
    return unsub;
  }, [showVestCandles, vestCandleSeriesVersion, vestCandleCmeCloses, lastChartTime, seriesSnapshot, selectedTimeframe]);

  // ── CME MES (ES) candle overlay (price pane, MNQ/NQ, #620) ────────────────
  // Hollow indigo/amber candles: utt's own MES 2m candles times a rolling
  // chart/MES close ratio (utils/mesCandles: median over the last 30 2m
  // buckets closed on both before each bar). Same series treatment as the
  // Vest overlay above (autoscale ignores it, no last value / price line);
  // independent toggle, both can be on. Feed (REST history + market-data
  // broadcast, mesCandleFeedStore) runs only while shown on a live chart.
  const showMesCandles = useMesCandleOverlayStore((s) => s.enabled) && isVestSymbol;
  const activeSymbols = useSymbolStore((s) => s.activeSymbols);
  const mesSymbol = useMemo(() => resolveMesSymbol(activeSymbols, symbol), [activeSymbols, symbol]);
  const mesFeedOn = showMesCandles && dataMode === 'live' && !!mesSymbol;
  const historyCountRef = useRef(0);
  historyCountRef.current = historyCandles.length;
  useEffect(() => {
    if (!mesFeedOn || !mesSymbol) return;
    useMesCandleFeedStore.getState().start(mesSymbol, historyCountRef.current + 1);
    return () => useMesCandleFeedStore.getState().stop();
  }, [mesFeedOn, mesSymbol]);
  // The chart lazy-loaded older bars: fetch enough MES history to cover them.
  useEffect(() => {
    if (mesFeedOn) useMesCandleFeedStore.getState().want(historyCandles.length + 1);
  }, [mesFeedOn, historyCandles.length]);
  const mesCandleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const [mesCandleSeriesVersion, setMesCandleSeriesVersion] = useState(0);
  const [mesCandleRatio, setMesCandleRatio] = useState<number | null>(null);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !showMesCandles) return;
    const { minMove, precision } = getSymbolPriceFormat(symbol);
    const series = chart.addSeries(CandlestickSeries, {
      upColor: 'rgba(0, 0, 0, 0)',
      downColor: 'rgba(0, 0, 0, 0)',
      borderVisible: true,
      borderUpColor: MES_CANDLE_COLORS.up,
      borderDownColor: MES_CANDLE_COLORS.down,
      wickVisible: true,
      wickUpColor: MES_CANDLE_COLORS.up,
      wickDownColor: MES_CANDLE_COLORS.down,
      lastValueVisible: false,
      priceLineVisible: false,
      autoscaleInfoProvider: () => null,
      priceFormat: { type: 'price' as const, minMove, precision },
    });
    mesCandleSeriesRef.current = series;
    setMesCandleSeriesVersion((v) => v + 1);
    const el = chartContainerRef.current;
    return () => {
      try { chart.removeSeries(series); } catch { /* chart gone */ }
      if (mesCandleSeriesRef.current === series) mesCandleSeriesRef.current = null;
      setMesCandleSeriesVersion((v) => v + 1);
      setMesCandleRatio(null);
      if (el) {
        delete el.dataset.mesCandles;
        delete el.dataset.mesRatio;
      }
    };
  }, [showMesCandles, symbol]);
  // Chart 2m closes for the ratio: closed buckets only, rebuilt once a bar.
  const mesChartCloses = useMemo(
    () => (showMesCandles ? cmeClosedCloses(historyCandles, formingMs) : null),
    [showMesCandles, historyCandles, formingMs],
  );
  const mesCandleDrawRef = useRef<{ data: MesCandle[]; series: unknown }>({ data: [], series: null });
  useEffect(() => {
    const series = mesCandleSeriesRef.current;
    if (!showMesCandles || !series || !mesChartCloses) return;
    const tf = TIMEFRAME_MINUTES[selectedTimeframe];
    const refresh = () => {
      const times = aggCandlesRef.current.filter((c) => typeof c.time === 'number').map((c) => c.time as number);
      const bars = [...useMesCandleFeedStore.getState().bars.values()];
      // Ratios from the bars on hand on every update: never cached or stored.
      // Same forming bucket left out on both sides.
      const pairs = closedPairs(mesChartCloses, mesCloses(bars, formingMs));
      const data = mesCandleData(times, bars, rollingRatios(bars.map((b) => b.t), pairs), tf);
      const prev = mesCandleDrawRef.current;
      const idx = prev.series === series ? candleTailUpdates(prev.data, data) : null;
      const toPoint = (c: MesCandle) => ({ time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close });
      if (idx === null) {
        series.setData(data.map(toPoint));
      } else {
        for (const i of idx) series.update(toPoint(data[i]), i < data.length - 1);
      }
      mesCandleDrawRef.current = { data, series };
      const ratio = latestRatio(data);
      setMesCandleRatio((r) => (r === ratio ? r : ratio));
      const el = chartContainerRef.current;
      if (el) {
        el.dataset.mesCandles = String(data.length);
        el.dataset.mesRatio = ratio === null ? '' : ratio.toFixed(4);
      }
    };
    refresh();
    let lastBars = useMesCandleFeedStore.getState().bars;
    return useMesCandleFeedStore.subscribe((st) => {
      if (st.bars === lastBars) return;
      lastBars = st.bars;
      refresh();
    });
    // formingMs only moves with mesChartCloses (same memo inputs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMesCandles, mesCandleSeriesVersion, mesChartCloses, lastChartTime, seriesSnapshot, selectedTimeframe]);

  // ── Volume pane: Vest thin bars + unusual-volume markers ─────────────────
  // One pane. Vest NQ-PERP bars are a custom series centred inside the CME
  // bars: own invisible scale (independent) or stacked on the CME scale in
  // chart-contract equivalents (same units). Levels "Vest NQ volume".
  useEffect(() => {
    const chart = volumeChartRef.current;
    if (!volumeChartReady || !chart || !showVestVolume) return;
    const independent = vestScale !== 'same';
    const series = chart.addCustomSeries(new VestVolumeSeriesView(), {
      priceScaleId: independent ? 'vest' : 'right',
      lastValueVisible: false,
      priceLineVisible: false,
    });
    if (independent) {
      // Tallest visible Vest bar reaches ~70% of the pane; the CME bars and
      // the markers above them stay readable.
      chart.priceScale('vest').applyOptions({ visible: false, scaleMargins: { top: 0.3, bottom: 0 } });
    }
    vestSeriesRef.current = series as unknown as ISeriesApi<'Custom'>;
    setVestSeriesVersion((v) => v + 1);
    return () => {
      try { chart.removeSeries(series); } catch { /* chart gone */ }
      if (vestSeriesRef.current === (series as unknown)) vestSeriesRef.current = null;
      setVestSeriesVersion((v) => v + 1);
    };
  }, [volumeChartReady, showVestVolume, vestScale]);

  const volumeMarkersOn = showCmeVolSpikes || showVestVolSpikes;
  useEffect(() => {
    const series = volumeSeriesRef.current;
    const chart = volumeChartRef.current;
    if (!volumeChartReady || !series || !chart || !volumeMarkersOn) return;
    const primitive = new VolumeSpikeMarkersPrimitive();
    primitive.setOnPlaced(({ cme, vest }) => {
      const el = volumeChartContainerRef.current;
      if (!el) return;
      el.dataset.cmeVolSpikes = String(cme);
      el.dataset.vestVolSpikes = String(vest);
    });
    primitive.setVestCoordinate((v) => (vestSeriesRef.current ? vestSeriesRef.current.priceToCoordinate(v) : null));
    primitive.setShowLabels(useVolumePaneSettingsStore.getState().showSpikeMultiples);
    series.attachPrimitive(primitive);
    volumeMarkersRef.current = primitive;
    // Room above the tallest CME bar for the "2.4x" labels.
    chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.18, bottom: 0 } });
    return () => {
      try { series.detachPrimitive(primitive); } catch { /* gone */ }
      if (volumeMarkersRef.current === primitive) volumeMarkersRef.current = null;
      try { chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.05, bottom: 0 } }); } catch { /* gone */ }
    };
  }, [volumeChartReady, volumeMarkersOn]);

  // "2.4x" multiples on/off (Levels > CME volume spikes). Only the labels:
  // the brighter CME bar and the 🐋 do not depend on it.
  useEffect(() => {
    volumeMarkersRef.current?.setShowLabels(showSpikeMultiples);
  }, [showSpikeMultiples, volumeChartReady, volumeMarkersOn]);

  // CME spike bars drawn brighter: re-colour the histogram when the set
  // changes (setData keeps the logical range; the sync guard stops the
  // volume->main echo while it lands).
  const recolouredKeyRef = useRef('');
  useEffect(() => {
    const series = volumeSeriesRef.current;
    if (!volumeChartReady || !series) return;
    if (cmeSpikeKey === recolouredKeyRef.current) return;
    recolouredKeyRef.current = cmeSpikeKey;
    const candles = aggCandlesRef.current;
    if (!candles.length) return;
    isSettingDataRef.current = true;
    // Colour from the drawn bar (Vest continuous open, #628); CME unchanged.
    series.setData(volumePaneData(symbolRef.current, candles, cmeSpikeTimesRef.current));
    setTimeout(() => {
      isSettingDataRef.current = false;
    }, 200);
  }, [cmeSpikeKey, volumeChartReady]);

  // Vest bars + both venues' markers. Live Vest updates arrive via the store
  // (no re-render of this component); candles via this effect's deps.
  const vestDrawRef = useRef<{ data: VestBarPoint[]; seriesVersion: number; historyVersion: number }>({ data: [], seriesVersion: -1, historyVersion: -1 });
  // Forming CME ticks re-run the effect below; the Vest aggregation and spike
  // scan only change with the Vest bars, timeframe or settings.
  const vestCalcRef = useRef<{ key: unknown[]; points: ReturnType<typeof aggregateVestBars>; spikes: ReturnType<typeof computeVestSpikes> } | null>(null);
  useEffect(() => {
    const tf = TIMEFRAME_MINUTES[selectedTimeframe];
    const pv = chartPointValue(symbol) ?? 20;
    const refresh = () => {
      const candles = aggCandlesRef.current
        .filter((c) => typeof c.time === 'number')
        .map((c) => ({ time: c.time as number, close: c.close, volume: c.volume || 0 }));
      const st = useVestVolumeStore.getState();
      const needVest = showVestVolume || showVestVolSpikes;
      const { lookback, threshold, baseline } = useVestSpikeSettingsStore.getState();
      const minute = Math.floor(Date.now() / 60_000);
      const fromSec = candles.length > 0 ? candles[0].time : undefined;
      const key = [st.bars, tf, lookback, threshold, baseline, minute, needVest, fromSec];
      const cached = vestCalcRef.current;
      if (!cached || cached.key.some((k, i) => k !== key[i])) {
        vestCalcRef.current = {
          key,
          points: needVest ? aggregateVestBars(st.bars.values(), tf) : new Map(),
          spikes: needVest ? [...vestRelVolsFor(st.bars, tf, { lookback, threshold, baseline }, Date.now(), { fromSec }).values()].filter((x) => x.relVol >= clampSpikeSettings({ lookback, threshold, baseline }).threshold) : [],
        };
      }
      const { points, spikes: vestSpikesNow } = vestCalcRef.current!;
      const vest = vestSeriesRef.current;
      let data: VestBarPoint[] = [];
      if (vest && showVestVolume) {
        data = vestBarData(candles, points, tf, vestScale, pv);
        const prev = vestDrawRef.current;
        const full = prev.seriesVersion !== vestSeriesVersion || prev.historyVersion !== st.historyVersion;
        const idx = full ? null : vestTailUpdates(prev.data, data, 8);
        if (idx === null) {
          vest.setData(data as never);
        } else {
          for (const i of idx) vest.update(data[i] as never, i < data.length - 1);
        }
        vestDrawRef.current = { data, seriesVersion: vestSeriesVersion, historyVersion: st.historyVersion };
        const el = volumeChartContainerRef.current;
        if (el) el.dataset.vestBars = String(data.filter((d) => 'top' in d && d.top > d.base).length);
      }
      const markers = volumeMarkersRef.current;
      if (markers) {
        const byTime = new Map<number, VolumeSpikeItem>();
        const candleAt = new Map(candles.map((c) => [c.time, c]));
        if (showCmeVolSpikes && cmeRel) {
          for (const r of cmeRel.values()) {
            if (r.relVol < cmeSpikeSettings.threshold || !candleAt.has(r.time)) continue;
            byTime.set(r.time, { time: r.time, cme: { value: r.v, label: relVolLabel(r.relVol) } });
          }
        }
        if (showVestVolSpikes && showVestVolume) {
          const dataAt = new Map(data.map((d) => [d.time as number, d]));
          for (const sp of vestSpikesNow) {
            const d = dataAt.get(sp.time);
            if (!d || !('top' in d)) continue;
            const item = byTime.get(sp.time) ?? { time: sp.time };
            item.vest = { top: d.top };
            byTime.set(sp.time, item);
          }
        }
        markers.setItems([...byTime.values()].sort((a, b) => a.time - b.time));
      }
    };
    refresh();
    let lastBars = useVestVolumeStore.getState().bars;
    const unsub = useVestVolumeStore.subscribe((st) => {
      if (st.bars === lastBars) return;
      lastBars = st.bars;
      refresh();
    });
    const unsubSpike = useVestSpikeSettingsStore.subscribe(refresh);
    return () => {
      unsub();
      unsubSpike();
    };
  }, [aggregatedCandles, selectedTimeframe, symbol, showVestVolume, showVestVolSpikes, showCmeVolSpikes, vestScale, vestSeriesVersion, cmeRel, cmeSpikeSettings.threshold, volumeChartReady, volumeMarkersOn]);

  // ── Candle markers: 🐋 Vest, 🦈 CME, 🐋🦈 both agree, 🐱, 🪝 wick rejection ──
  // Rel vol on this chart's timeframe (utils/vestSpikes, utils/cmeVolumeSpikes),
  // closed bars only; agreement by delta sign (utils/volumeAgreement). Levels
  // toggles "Vest spikes 🐋 on candles", "CME spikes 🦈 on candles", "Both
  // agree 🐋🦈", "Vest bar above CME 🐱", "Wick rejection 🪝" (Clean: signal
  // markers). The 🪝 is graded strong by the same 🐋/🦈 hits and CME delta.
  const needCmeDelta = showBothSpikes || showCmeCandleSpikes || showWick;
  const deltaFirstMs = aggregatedCandles.length > 0 ? (aggregatedCandles[0].time as number) * 1000 : null;
  useEffect(() => {
    if (!needCmeDelta || deltaFirstMs === null) return;
    // seriesSnapshot changes when a bar closes: pick up that bar's delta.
    void useCmeDeltaStore.getState().fetchDeltas(symbol, deltaFirstMs);
  }, [needCmeDelta, symbol, deltaFirstMs, seriesSnapshot]);

  useEffect(() => {
    if (!showCandleSpikes || !candlestickSeriesRef.current) return;
    const series = candlestickSeriesRef.current;
    const primitive = new CandleSpikePrimitive();
    primitive.setOccupied(occupiedAt);
    // Where the markers landed, on the container (for headless checks).
    primitive.setOnPlaced((placed: { id: string; x: number; y: number }[]) => {
      const el = chartContainerRef.current;
      if (!el) return;
      const onScreen = placed.filter((p) => p.x >= 0 && p.y >= 0 && p.x <= el.clientWidth && p.y <= el.clientHeight);
      const last = onScreen[onScreen.length - 1];
      el.dataset.vestSpikes = String(onScreen.length);
      el.dataset.vestSpikeLast = last ? `${Math.round(last.x)},${Math.round(last.y)}` : '';
      el.dataset.wickMarks = String(onScreen.filter((p) => p.id.startsWith('wick:')).length);
    });
    primitive.setMarkerLayout(markerLayoutRef.current);
    series.attachPrimitive(primitive);
    vestSpikePrimitiveRef.current = primitive;
    return () => {
      try { series.detachPrimitive(primitive); } catch { /* chart gone */ }
      if (vestSpikePrimitiveRef.current === primitive) vestSpikePrimitiveRef.current = null;
      const el = chartContainerRef.current;
      if (el) {
        delete el.dataset.vestSpikes;
        delete el.dataset.vestSpikeLast;
        delete el.dataset.wickMarks;
      }
    };
  }, [showCandleSpikes, symbol, occupiedAt]);

  useEffect(() => {
    if (!showCandleSpikes) return;
    const tfMinutes = TIMEFRAME_MINUTES[selectedTimeframe];
    const needVest = showVestSpikes || showBothSpikes;
    const needCme = showCmeCandleSpikes || showBothSpikes;
    // 🐱 reads the volume pane as drawn (never changes it): the Vest bar's top
    // via the Vest series, the CME bar's top via the histogram, each on its own
    // scale. No Vest series / pane not laid out = no cats.
    const pv = chartPointValue(symbol) ?? 20;
    let catPoints: { bars: unknown; points: ReturnType<typeof aggregateVestBars> } | null = null;
    const catExtruding = (bars: ReturnType<typeof useVestVolumeStore.getState>['bars']): Set<number> => {
      const vest = vestSeriesRef.current;
      const cmeSeries = volumeSeriesRef.current;
      const paneH = volumeChartContainerRef.current?.clientHeight ?? 0;
      if (!showVestVolume || !volumeChartReady || !vest || !cmeSeries || paneH <= 0) return new Set();
      if (!catPoints || catPoints.bars !== bars) catPoints = { bars, points: aggregateVestBars(bars.values(), tfMinutes) };
      const candles = aggCandlesRef.current
        .filter((c) => typeof c.time === 'number')
        .map((c) => ({ time: c.time as number, close: c.close, volume: c.volume || 0 }));
      // The same data the pane draws (vestBarData), so `top` is the drawn top in either scale mode.
      const drawn = vestBarData(candles, catPoints.points, tfMinutes, vestScale, pv);
      return extrudingCatSlots(
        candles.map((c, i) => {
          const d = drawn[i];
          return { time: c.time, cme: c.volume, vestTop: d && 'top' in d ? d.top : undefined };
        }),
        (v) => vest.priceToCoordinate(v),
        (v) => cmeSeries.priceToCoordinate(v),
        tfMinutes,
      );
    };
    const feed = () => {
      const primitive = vestSpikePrimitiveRef.current;
      if (!primitive) return;
      const { bars } = useVestVolumeStore.getState();
      const { lookback, threshold, baseline } = useVestSpikeSettingsStore.getState();
      // One rel-vol pass serves the 🐋 (threshold) and the 🪝 grade.
      // Only the chart's bars; the older store bars are the baseline history.
      const first = aggCandlesRef.current[0]?.time;
      const fromSec = typeof first === 'number' ? first : undefined;
      const vestRel = needVest || (showWick && isVestSymbol) ? vestRelVolsFor(bars, tfMinutes, { lookback, threshold, baseline }, Date.now(), { fromSec }) : null;
      useRelVolStatusStore.getState().setVest(summarizeRelVols(vestRel));
      const vestHits = vestRel ? [...vestRel.values()].filter((s) => s.relVol >= clampSpikeSettings({ lookback, threshold, baseline }).threshold) : [];
      const cmeHits = cmeRel ? [...cmeRel.values()].filter((r) => r.relVol >= cmeSpikeSettings.threshold) : [];
      const vest = needVest ? vestHits : [];
      const cme = needCme ? cmeHits : [];
      const deltas = needCme || showWick ? cmeDeltaByChartBar(useCmeDeltaStore.getState().bySymbol[symbol], tfMinutes) : new Map<number, number>();
      const cats = showVestCat && catCmeVols ? vestCats(bars.values(), catCmeVols, tfMinutes, catExtruding(bars)) : [];
      const chartEl = chartContainerRef.current;
      if (chartEl) chartEl.dataset.vestCats = String(cats.length);
      const wickSettings = useWickRejectionSettingsStore.getState();
      const wicks: GradedWick[] = showWick && wickBase
        ? visibleWicks(
            gradeWicks(wickBase, { vestSpikeTimes: new Set(vestHits.map((s) => s.time)), cmeSpikeTimes: new Set(cmeHits.map((r) => r.time)), deltas }),
            wickSettings.requireLevel,
            wickSettings.brokenHooks,
          )
        : [];
      primitive.setItems(candleSpikeMarkers(vest, cme, deltas, { vest: showVestSpikes, cme: showCmeCandleSpikes, both: showBothSpikes }, cats, wicks, wickSettings.emoji));
    };
    feed();
    let lastBars = useVestVolumeStore.getState().bars;
    const unsubBars = useVestVolumeStore.subscribe((st) => {
      if (st.bars === lastBars) return;
      lastBars = st.bars;
      feed();
    });
    const unsubSettings = useVestSpikeSettingsStore.subscribe(feed);
    // 🐱 heights follow the volume pane's visible range and autoscale: when
    // either scale's value -> y mapping moves (pan, zoom, new data rescaling,
    // axis drag, resize), re-run the feed, at most once a frame and only on a
    // real change. The watcher draws nothing.
    const cmeSeriesForCat = volumeSeriesRef.current;
    const volumeChartForCat = volumeChartRef.current;
    let catWatcher: PaneGeometryWatcherPrimitive | null = null;
    const onCatRange = () => catWatcher?.check();
    if (showVestCat && showVestVolume && volumeChartReady && cmeSeriesForCat && volumeChartForCat) {
      const probe = (f: ((v: number) => number | null) | undefined) =>
        f ? [0, 1_000, 100_000].map((v) => { const y = f(v); return y == null ? 'n' : y.toFixed(1); }).join(',') : '-';
      catWatcher = new PaneGeometryWatcherPrimitive(() => {
        const vest = vestSeriesRef.current;
        return [
          volumeChartContainerRef.current?.clientHeight ?? 0,
          probe(vest ? (v) => vest.priceToCoordinate(v) : undefined),
          probe((v) => cmeSeriesForCat.priceToCoordinate(v)),
        ].join('|');
      }, feed);
      cmeSeriesForCat.attachPrimitive(catWatcher);
      volumeChartForCat.timeScale().subscribeVisibleLogicalRangeChange(onCatRange);
    }
    // Emoji / level-only / broken hooks (the rule fields re-run wickBase and this effect).
    const unsubWick = useWickRejectionSettingsStore.subscribe(feed);
    const unsubDelta = useCmeDeltaStore.subscribe(feed);
    // A chart bar can end without a live update (quiet market): re-check each minute.
    const timer = setInterval(feed, 60_000);
    return () => {
      unsubBars();
      unsubSettings();
      unsubWick();
      unsubDelta();
      clearInterval(timer);
      if (catWatcher) {
        catWatcher.cancel();
        try { volumeChartForCat?.timeScale().unsubscribeVisibleLogicalRangeChange(onCatRange); } catch { /* chart gone */ }
        try { cmeSeriesForCat?.detachPrimitive(catWatcher); } catch { /* series gone */ }
      }
      const el = chartContainerRef.current;
      if (el) delete el.dataset.vestCats;
    };
  }, [showCandleSpikes, showVestSpikes, showCmeCandleSpikes, showBothSpikes, showVestCat, showWick, isVestSymbol, wickBase, symbol, selectedTimeframe, cmeRel, catCmeVols, cmeSpikeSettings.threshold, showVestVolume, vestScale, volumeChartReady, vestSeriesVersion]);

  // ── Diamonds (engine-scored setup candidates) ────────────────────────────
  //
  // There is no local scorer. Every diamond drawn here was scored by
  // `utt-engine/src/strategy/setup_candidate.rs` — the same call, the same
  // settings and the same serialised struct the auto-bot acts on — and arrives
  // by one of two routes into one key space:
  //
  //   live bars   `diamond-signal` socket   -> recordEngineDiamond
  //   older bars  GET /api/diamonds/history -> applyHistory
  //
  // The chart used to re-derive the older half from the candles, and that copy
  // drifted: a bar the bot shorted at 63/strong rendered here as 53/moderate.
  // A bar with no engine diamond now draws NO marker rather than an estimate.

  /**
   * The bars this symbol's engine diamonds cover, oldest first.
   *
   * Built here rather than in a selector on purpose: a selector returning a
   * fresh array makes Zustand's snapshot compare unequal on every render and
   * takes React into error #185, which blanks the chart. The store hands back
   * the stable Record; the array is derived out here where a memo can hold it.
   */
  const engineDiamondList = useMemo(() => {
    if (!candidatesEnabled) return [];
    return Object.values(engineDiamonds)
      .filter((d) => d.symbol === symbol)
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [candidatesEnabled, engineDiamonds, symbol]);

  /**
   * Bars the engine judged and threw out, for this symbol.
   *
   * Only the ones you hand-labelled `missed` reach the review set — see
   * `buildReviewSet` — but the whole set is kept so the overlay can explain any
   * bar the crosshair lands on.
   */
  const engineRejectedList = useMemo(() => {
    if (!candidatesEnabled) return [];
    return Object.values(engineRejected).filter((r) => r.symbol === symbol);
  }, [candidatesEnabled, engineRejected, symbol]);

  /**
   * Newest COMPLETED bar the chart holds.
   *
   * A scalar, and it feeds both the fetch (as the snapshot boundary) and the
   * lookback cutoff. The last candle in the array is the FORMING one whenever
   * the market is open, and the engine excludes forming bars from the history
   * response — so using it as the boundary would make every response look like
   * it was missing its newest bar.
   */
  const newestCompletedMs = useMemo(() => {
    if (candles.length === 0) return 0;
    const last = candles[candles.length - 1];
    // `isCompleted` is not carried on the chart's candle type; the second-newest
    // bar is completed by construction whenever there is more than one.
    return candles.length > 1 ? candles[candles.length - 2].timestamp : last.timestamp;
  }, [candles]);

  /**
   * How many CME sessions of engine history to ask for.
   *
   * A scalar so the fetch effect can depend on it without an object identity
   * changing every render.
   *
   * Bounded by the bars the chart actually holds. `lookbackDays: -1` means
   * "everything loaded", and "loaded" is the chart's array — a few days — not
   * the engine's, which is `DAYS_TO_LOAD=365` and would answer with a year of
   * diamonds for bars this chart cannot draw. The engine clamps again to
   * sessions that actually have candles, and reports what it served.
   *
   * `lookbackDays + 1` because the store's cutoff for N days is N whole days
   * BEFORE today, i.e. N+1 sessions inclusive of today.
   */
  const candidateHistoryDays = useMemo(() => {
    if (candles.length === 0) return 1;
    const spanMs = candles[candles.length - 1].timestamp - candles[0].timestamp;
    const loadedSessions = Math.max(1, Math.ceil(spanMs / 86_400_000) + 1);
    if (candidateLookbackDays < 0) return loadedSessions;
    return Math.max(1, Math.min(candidateLookbackDays + 1, loadedSessions));
  }, [candles, candidateLookbackDays]);

  /**
   * Fetch engine-scored history.
   *
   * The lifecycle, deliberately spelled out because each trigger closes a
   * specific hole:
   *
   *   on mount / on enable  nothing older than this page load has a diamond
   *                         otherwise — this is the whole reason the endpoint
   *                         exists
   *   on symbol switch      diamonds are per-symbol; without this the new chart
   *                         shows the old symbol's coverage (or nothing)
   *   on lookback change    widening the window asks about bars never fetched
   *   on recalc             the rule may have been retuned here or in another
   *                         browser, so every score in the window is stale
   *   on reconnect          `diamond-signal` has no replay, so a drop loses
   *                         every bar that completed while we were away
   *
   * Deliberately NOT on every candle change. Live bars arrive on the socket,
   * which is the point of having both sources; refetching per bar would re-walk
   * ~294k candles in the engine every two minutes.
   *
   * `candlesReady` rather than `candles`: the effect needs to run once the bars
   * exist, not on every tick that mutates the newest one.
   */
  const candlesReady = candles.length > 0;
  useEffect(() => {
    if (!candidatesEnabled || !candlesReady || !symbol) return;
    void fetchCandidateHistory({
      symbol,
      days: candidateHistoryDays,
      throughMs: newestCompletedMs,
    });
    // `newestCompletedMs` is intentionally absent: it moves with every closed
    // bar, and refetching on each one is what the socket is for. It is read at
    // call time so the snapshot boundary is still accurate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    candidatesEnabled,
    candlesReady,
    symbol,
    candidateHistoryDays,
    candidateRecalcNonce,
    socketConnected,
    fetchCandidateHistory,
  ]);

  const candidateReviewSet = useMemo(() => {
    if (!candidatesEnabled) return [];
    // Anchor the window to the newest bar we actually HAVE, not to the clock.
    //
    // A calendar cutoff dies every weekend: on a sunday "last 1 day" means
    // since saturday midnight, friday's bars are two days back, nothing
    // qualifies, and the chart draws no diamonds while explaining nothing.
    // That is the bug that survived two fixes. Holidays would do the same.
    //
    // "reset to now" still pins an absolute instant — that one is a deliberate
    // "from here", so it is left alone.
    const newest = candles.length ? candles[candles.length - 1].timestamp : 0;
    const cutoff =
      candidateScoreFromPinned || !newest
        ? candidateScoreFrom
        : candidateLookbackDays < 0
          ? 0
          : newest - candidateLookbackDays * 86_400_000;
    return buildReviewSet(engineDiamondList, engineRejectedList, candidateLabels).filter(
      (c) => c.timestamp >= cutoff
    );
    // The two engine-sourced inputs below are the ONLY things that can change
    // what a bar scores. ChartView.deps.test.ts guards this array by name so
    // dropping one is caught rather than shipped. Keep comments here free of
    // commas — the guard splits this array on them.
  }, [
    candidatesEnabled,
    engineDiamondList,
    engineRejectedList,
    candles,
    candidateLabels,
    candidateScoreFrom,
    candidateScoreFromPinned,
    candidateLookbackDays,
    // Lets "recalc" force a redraw even when the refetched rule is identical.
    candidateRecalcNonce,
  ]);

  // Suggestions for the unlabelled backlog, derived from the hand labels only.
  // Never persisted: saving them would make them training data for the next
  // round and the classifier would drift toward its own first guess.
  const candidateAutoTags = useMemo(() => {
    if (!candidatesEnabled || !autoTagEnabled) return {};
    return autoTagCandidates(candidateReviewSet, candidateLabels, autoTagOptions);
  }, [candidatesEnabled, autoTagEnabled, candidateReviewSet, candidateLabels, autoTagOptions]);

  // Mirror the review set into a ref: the click handler is bound once, so it
  // must read the latest set rather than close over a stale one.
  const candidateReviewSetRef = useRef<typeof candidateReviewSet>([]);
  useEffect(() => {
    candidateReviewSetRef.current = candidateReviewSet;
  }, [candidateReviewSet]);

  // Attach/detach the candidate marker primitive.
  useEffect(() => {
    if (!candidatesEnabled) return;
    useCandidateStore.getState().hydrateFromPreferences();
    // Take the engine's saved rule, so the diamonds drawn here are scored with
    // the same thresholds the bot trades on rather than this browser's stale
    // localStorage copy.
    void hydrateThresholdsFromEngine();
    // So the chart can show which bars the bot would actually trade.
    void hydrateBotThresholdFromEngine();
    if (!candidatePrimitiveRef.current && candlestickSeriesRef.current) {
      const primitive = new CandidatePrimitive();
      primitive.setMarkerLayout(markerLayoutRef.current);
      candlestickSeriesRef.current.attachPrimitive(primitive);
      candidatePrimitiveRef.current = primitive;
    }
    return () => {
      if (candidatePrimitiveRef.current) {
        candlestickSeriesRef.current?.detachPrimitive(candidatePrimitiveRef.current);
        candidatePrimitiveRef.current = null;
      }
    };
  }, [candidatesEnabled, symbol]);

  // Feed markers, coloured by label state.
  useEffect(() => {
    const primitive = candidatePrimitiveRef.current;
    if (!primitive) return;
    // The bar's own geometry, for placing the glyph. The engine sends `entry`
    // (the close) but no OHLC, and the marker sits clear of the high or the
    // low. Looked up from the candles the chart is already drawing — reading a
    // bar's high is not scoring it, and a bar the chart does not hold cannot be
    // drawn on at all.
    const markers: CandidateMarker[] = [];
    for (const c of candidateReviewSet) {
      // The forming bar first, then the index of everything behind it. That
      // ordering reproduces what `new Map(candles.map(...))` did — later
      // entries won, and the forming bar is the last candle — without paying
      // to rebuild the whole index on every tick (#344).
      const bar =
        formingCandle && formingCandle.timestamp === c.timestamp
          ? formingCandle
          : historyCandleByTime.get(c.timestamp);
      // Outside the loaded window. Silently skipped: there is no pixel column
      // for this timestamp, so there is nothing to draw on.
      if (!bar) continue;

      const l = candidateLabels[c.id];
      const verdict = l ? normaliseVerdict(l.verdict) : undefined;

      // The grade shown is the ENGINE's — the exact one handed to the auto-bot.
      // There is no second grade to prefer it over any more.
      const state = markerStateFor({ verdict, engineGrade: c.grade });

      markers.push({
        id: c.id,
        timestamp: c.timestamp,
        high: bar.high,
        low: bar.low,
        // The engine's "long"/"short" is the parent's direction, which is what
        // decides whether the glyph sits above the high or below the low. A
        // rejected bar can lack one; above the high is the neutral default.
        direction: c.direction === 'short' ? 'bear' : 'bull',
        state,
        selected: selectedCandidateId === c.id,
        // Dashed whenever the grade on show is the engine's rather than yours,
        // so an unreviewed bar never reads as a decision you made.
        auto: !l,
        // Null on a hand-flagged `missed` bar: the engine rejected it and gave
        // it no score, and there is nothing here that could invent one.
        score: c.score,
        // Grade alone cannot show this: the bot's threshold sits mid-band, so
        // a 55 and a 41 read the same tier while only one gets taken.
        botWouldTake:
          botMinConfidence !== null && c.score !== null && c.score >= botMinConfidence,
        // What the simulated trade did, drawn under the grade tag. Read off
        // the engine's block rather than recomputed, so the chart, the popup
        // and the scoreboard cannot disagree about one signal.
        simResult: c.simulated?.result ?? null,
        simPoints: c.simulated?.points ?? null,
        // Trend stance, third line under the outcome. Engine-computed; `null`
        // on a bar with too little history behind it to have a run.
        counterTrend: c.trend?.counterTrend ?? null,
        // The EMA 9/21 stack's own read of the same trade, fourth line. A
        // different measurement from the run above, not a restatement of it;
        // `null` on a bar predating the indicator series the engine retains,
        // and both false is an exact tie rather than agreement.
        stackAligned: c.trendAlignment?.stackAligned ?? null,
        stackOpposed: c.trendAlignment?.variants?.stackOpposed ?? null,
        // How many of the three EMAs cut this candle's body, fifth line. Off
        // the same block as the stack read and `null` on the same bars — never
        // 0, which would print `body 0/3` and claim a clean candle on a bar
        // nobody measured.
        emasThroughBody: c.trendAlignment?.emasThroughBody ?? null,
      });
    }
    // `candles` stays in the deps below even though the body now reads the two
    // derived lookups instead: the marker's anchor high/low comes off the
    // charted bar, so a candle change has to repaint, and it is a superset of
    // both derived values. ChartView.deps.test.ts guards it by name and splits
    // that array on commas — keep comments out of the brackets.
    primitive.setMarkers(markers);
  }, [
    candidateReviewSet,
    candidateLabels,
    selectedCandidateId,
    candles,
    historyCandleByTime,
    formingCandle,
    engineDiamonds,
    symbol,
    botMinConfidence,
  ]);

  // ── Trades actually taken ────────────────────────────────────────────────
  // The anchor the entry/exit walk runs backward from: the broker's net
  // position for the charted instrument RIGHT NOW. The stored fill history is
  // lossy and does not start flat, so walking forward from zero labels a long's
  // exit as a short entry — see buildTradeMarkers.
  //
  // `null` means "not known yet", which is NOT the same as flat — and flat is
  // the normal resting state, so the two must not be conflated. The distinction
  // rides on `positionsLoadedAt`; all the reasoning lives with the resolver.
  //
  // Reads every broker rather than just the active one: the fills the anchor is
  // walked against are fetched by symbol alone, so they already span brokers.
  const tradeMarkerAnchor = useMemo<number | null>(
    () => resolveNetPositionAnchor(brokers, symbol, cleanSymbol),
    [brokers, symbol, cleanSymbol]
  );

  // Entry and exit markers for THIS symbol. Empty (and not polling) while the
  // TRADES toggle is off — see useTradeMarkerFeed. Recomputes when the position
  // moves, not only when the poll returns.
  //
  // The candles ride along for the max adverse excursion on each close: how far
  // a trade went against you before it closed is not in the fills, it is in the
  // bars, and this component is where both are already in hand. `candles` is
  // the array this component already subscribes to — no new store selector, and
  // in particular nothing that builds a fresh object per call, which is the
  // shape that has taken this chart down with React #185 before. The hook keys
  // its rebuild on a fingerprint of these rather than on the array's identity,
  // which churns on every tick.
  // Bot executions carry the only record of which signal produced which fill —
  // a broker fill comes back with no trace of it. Passed down rather than read
  // inside the hook, which is deliberately store-free so it can be tested
  // without one; same reason `candles` and the anchor are passed.
  const botExecutions = useAutoBotStore((s) => s.executions);
  const fetchBotExecutions = useAutoBotStore((s) => s.fetchExecutions);

  // Pull the execution HISTORY, because nothing else does.
  //
  // `auto-bot:execution` (singular) arrives over the socket as trades happen,
  // so a bot entry taken while you are watching resolves its signal fine. The
  // history only ever arrived via `fetchExecutions`, and the sole caller was
  // AutoBotPanel when it opened — so on a fresh load, with the panel never
  // opened, `executions` was empty and every signal connector silently did not
  // draw. Shipped that way and caught by looking at the chart, not by a test:
  // the resolver's tests hand it executions, so none of them noticed that in
  // production there were none to hand.
  //
  // Guarded on the markers being on, so a chart with the feature switched off
  // does not poll for data it will not draw.
  useEffect(() => {
    if (!tradeMarkersEnabled) return;
    // `quiet`: this is background enrichment for a dotted line. A failure means
    // the line does not draw until the next poll, which is not worth a toast —
    // and utt-engine restarts on every deploy, so a 30s poll WILL catch one.
    const load = () => fetchBotExecutions(undefined, 200, 0, true);
    load();
    // Same cadence as the fill poll behind the markers; the two are read
    // together and there is nothing to gain from refreshing one without the
    // other.
    const t = setInterval(load, TRADE_MARKER_POLL_MS);
    return () => clearInterval(t);
  }, [tradeMarkersEnabled, fetchBotExecutions]);
  const signalRecords = useMemo(
    () =>
      botExecutions
        .filter((e) => e.botId && e.signalTimestamp && e.signalPrice)
        .map((e) => ({
          botId: e.botId,
          signalTimestamp: e.signalTimestamp,
          signalPrice: e.signalPrice,
          timestamp: e.timestamp,
        })),
    [botExecutions]
  );

  const tradeMarkers = useTradeMarkerFeed(
    symbol,
    tradeMarkersEnabled,
    tradeMarkerAnchor,
    candles,
    signalRecords
  );

  // Hiding the markers has to close the popup too, or the details of a fill you
  // just switched off would stay pinned over the chart.
  useEffect(() => {
    if (!tradeMarkersEnabled) setTradeInfo(null);
  }, [tradeMarkersEnabled]);

  // Attach/detach the trade marker primitive.
  useEffect(() => {
    if (!candlestickSeriesRef.current) return;
    if (!tradeMarkerPrimitiveRef.current) {
      const p = new TradeMarkerPrimitive();
      // Seeded at construction, not left to the effect below: this runs again
      // on every symbol change, and the effect that pushes the flag is keyed on
      // the flag itself, so it would not re-fire for a fresh primitive. Read
      // straight off the store rather than closing over the subscribed value —
      // this effect deliberately does not depend on it.
      p.setShowLinks(useTradeMarkerStore.getState().showLinks);
      p.setMarkerLayout(markerLayoutRef.current);
      candlestickSeriesRef.current.attachPrimitive(p);
      tradeMarkerPrimitiveRef.current = p;
    }
    return () => {
      if (tradeMarkerPrimitiveRef.current && candlestickSeriesRef.current) {
        try {
          candlestickSeriesRef.current.detachPrimitive(tradeMarkerPrimitiveRef.current);
        } catch {
          // series already gone
        }
        tradeMarkerPrimitiveRef.current = null;
      }
    };
  }, [symbol]);

  useEffect(() => {
    tradeMarkerPrimitiveRef.current?.setMarkers(tradeMarkers);
  }, [tradeMarkers]);

  // Connector visibility, pushed rather than subscribed to inside the
  // primitive. A no-op when the value has not actually changed, so this cannot
  // churn redraws on unrelated renders.
  useEffect(() => {
    tradeMarkerPrimitiveRef.current?.setShowLinks(tradeLinksEnabled);
  }, [tradeLinksEnabled]);

  // Clicking a trade marker — entry or exit — opens its read-only fill details.
  //
  // Separate subscription from the diamond one below, and BOTH fire on every
  // click — lightweight-charts fans a click out to all subscribers. Trade
  // markers take precedence: a marker sits a few px off the candle and can land
  // on a diamond, and the labeller is the heavier popup to open by accident.
  // The diamond handler enforces that by bailing when this one has a hit; the
  // reverse check is deliberately absent, because if both handlers deferred to
  // the other an overlapping click would open nothing at all.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const handler = (param: MouseEventParams): void => {
      if (!param.point || isLinePaneEvent(chart, chartContainerRef.current, param)) {
        setTradeInfo(null);
        return;
      }
      const id = tradeMarkerPrimitiveRef.current?.markerAt(param.point.x, param.point.y) ?? null;
      // Empty space (or a bare candle) closes it, same as the diamond popup.
      if (!id) {
        setTradeInfo(null);
        return;
      }
      setTradeInfo({ id, x: param.point.x, y: param.point.y });
    };

    chart.subscribeClick(handler);
    return () => chart.unsubscribeClick(handler);
    // No deps: the primitive is reached through a ref, so the handler never
    // goes stale and re-subscribing on every marker change would be churn.
  }, []);

  // Push drawing state into the primitive. Subscribed rather than read once so
  // a drag repaints every frame.
  const drawingsForSymbol = useDrawingStore((s) => s.bySymbol[symbol]);
  const drawingSelectedId = useDrawingStore((s) => s.selectedId);
  const drawingPending = useDrawingStore((s) => s.pending);
  const drawingActiveTool = useDrawingStore((s) => s.activeTool);

  useEffect(() => {
    drawingPrimitiveRef.current?.setState(
      drawingsForSymbol ?? [],
      drawingSelectedId,
      drawingPending,
      drawingActiveTool
    );
  }, [drawingsForSymbol, drawingSelectedId, drawingPending, drawingActiveTool, drawingPrimitive]);

  useDrawingInteraction(chartContainerRef.current, chartRef.current, drawingPrimitive, symbol);

  // Hovering an anchor label explains what that level is. Crosshair rather than
  // click: these are reference lines, not things you interact with, so the
  // explanation should be available without changing any state.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const handler = (param: MouseEventParams): void => {
      if (!param.point || isLinePaneEvent(chart, chartContainerRef.current, param)) {
        setAnchorHover(null);
        return;
      }
      const kind = fairPricePrimitiveRef.current?.labelAt(param.point.x, param.point.y) ?? null;
      if (!kind) {
        setAnchorHover(null);
        return;
      }
      setAnchorHover({ kind, x: param.point.x, y: param.point.y });
    };

    chart.subscribeCrosshairMove(handler);
    return () => chart.unsubscribeCrosshairMove(handler);
    // No deps: the primitive is reached through a ref, so the handler cannot go
    // stale — same reasoning as the trade-marker click handler above.
  }, []);

  // Clicking a candidate diamond opens the inline labeller. Hit-tests by BAR
  // rather than by pixel: lightweight-charts hands us the bar time, and one
  // candidate per bar means that's unambiguous and survives zooming.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !candidatesEnabled) return;

    const handler = (param: MouseEventParams): void => {
      if (param.time === undefined || !param.point || isLinePaneEvent(chart, chartContainerRef.current, param)) {
        setQuickLabel(null);
        return;
      }
      // A trade marker under the cursor owns the click — see the handler above.
      // Without this, clicking an entry that happens to overlap a diamond would
      // pop the labeller open on top of the fill details.
      if (tradeMarkerPrimitiveRef.current?.markerAt(param.point.x, param.point.y)) {
        setQuickLabel(null);
        return;
      }
      // Hit-test the DIAMOND, not the bar. Clicking anywhere on a candle used
      // to open the labeller, which made the chart unusable for anything else.
      const id = candidatePrimitiveRef.current?.markerAt(param.point.x, param.point.y) ?? null;
      if (!id) {
        setQuickLabel(null);
        return;
      }
      useCandidateStore.getState().select(id);
      setQuickLabel({ id, x: param.point.x, y: param.point.y });
    };

    chart.subscribeClick(handler);
    return () => chart.unsubscribeClick(handler);
  }, [candidatesEnabled]);

  // Handle mode switching
  const handleLiveModeClick = () => {
    if (dataMode === 'live') return; // Already in live mode
    wsService.switchToLiveMode(symbol);
    setBacktestConfig(null);

    // Clear URL parameters
    const params = new URLSearchParams(window.location.search);
    params.delete('mode');
    params.delete('startDate');
    params.delete('endDate');
    params.delete('preset');
    const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  };

  // Listen for backtest date selector event
  useEffect(() => {
    const handleShowBacktestDateSelector = () => {
      setShowDateRangeSelector(true);
    };

    window.addEventListener('showBacktestDateSelector', handleShowBacktestDateSelector);

    return () => {
      window.removeEventListener('showBacktestDateSelector', handleShowBacktestDateSelector);
    };
  }, []);

  // Handle date range selection
  const handleDateRangeApply = (startDate: Date, endDate: Date, preset: DateRangePreset) => {
    setShowDateRangeSelector(false);

    const config = { startDate, endDate, preset };
    setBacktestConfig(config);

    wsService.switchToBacktestMode(symbol, startDate, endDate);

    // Update URL with backtest parameters
    const params = new URLSearchParams(window.location.search);
    params.set('mode', 'backtest');
    params.set('startDate', startDate.getTime().toString());
    params.set('endDate', endDate.getTime().toString());
    params.set('preset', preset);
    window.history.replaceState({}, '', `?${params.toString()}`);
  };

  const handleDateRangeCancel = () => {
    setShowDateRangeSelector(false);
  };

  // Handle context menu action - start backtest from selected candle
  const handleBacktestFromCandle = () => {
    if (!contextMenu) return;

    const clickedDate = new Date(contextMenu.timestamp);

    // Set date range: 12 hours before clicked candle to 7 days after
    const startDate = new Date(clickedDate.getTime() - 12 * 60 * 60 * 1000); // 12 hours before
    const endDate = new Date(clickedDate.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days after

    // Close context menu
    setContextMenu(null);

    // If already in backtest mode, stop the player first to prevent conflicts
    if (dataMode === 'backtest') {
      const player = getBacktestPlayer();
      player.stop();
    }

    // CRITICAL: Set data mode to 'backtest' IMMEDIATELY to block any incoming live data
    // This prevents race condition where live data arrives before switchToBacktestMode completes
    setDataMode('backtest');
    setBacktestLoading(true);
    setCandles([]); // Clear existing candles immediately

    // Set backtest config with initial seek timestamp
    const config = {
      startDate,
      endDate,
      preset: 'custom' as DateRangePreset,
      initialSeekTimestamp: contextMenu.timestamp // Store the clicked candle timestamp for seeking
    };
    setBacktestConfig(config);

    // Switch to backtest mode (this will unsubscribe from live data and load new backtest data)
    wsService.switchToBacktestMode(symbol, startDate, endDate, contextMenu.timestamp);
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu) {
        setContextMenu(null);
      }
    };

    if (contextMenu) {
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);


  // Subscribe to paper trading store for position updates and trade markers
  // Paper trading position line removed - now using LivePositionLine for Tradovate positions

  // Add strategy candidate markers with polling (works in both live and backtest modes)
  useEffect(() => {
    if (!candlestickSeriesRef.current || !chartRef.current) return;

    // Compute base symbol once at effect level for per-symbol settings lookups
    const baseSymbol = normalizeToBase(symbol);

    const updateMarkers = () => {
      if (!candlestickSeriesRef.current || !chartRef.current) return;

      // Get signal markers from backend (via marketStore)
      const allSignalMarkers = useMarketStore.getState().signalMarkers;
      const storeCandles = useMarketStore.getState().candles;

      // Use the chart's symbol (from props in panel mode), not global marketStore symbol
      // This ensures each panel shows only its own symbol's markers
      const chartSymbol = symbol; // 'symbol' is from component props/state

      // Filter markers by chart's symbol (strip exchange suffix and month code for comparison)
      const normalizeForComparison = (s: string | undefined) => s ? normalizeToBase(s) : '';

      // Own symbol markers (existing behavior)
      const signalMarkers = allSignalMarkers.filter(m => {
        const markerBaseSymbol = normalizeForComparison(m.symbol);
        return markerBaseSymbol === baseSymbol;
      });

      // Other symbol markers (new feature: show confluence signals from correlated symbols)
      const showOtherSymbols = useSettingsStore.getState().getWholeCandleForSymbol(baseSymbol).showOtherSymbolSignals ?? false;
      const otherSymbolMarkers = showOtherSymbols
        ? allSignalMarkers.filter(m => {
            const markerBaseSymbol = normalizeForComparison(m.symbol);
            // Must be a different symbol and not a volume spike (only WCC signals)
            const isVolumeSpike = m.type === 'volume_spike_bullish' || m.type === 'volume_spike_bearish';
            return markerBaseSymbol !== baseSymbol && !isVolumeSpike;
          })
        : [];

      // Build a map of candle timestamps to candle data for quick lookup
      // Use aggregatedCandles (which respects selectedTimeframe) so markers align with the displayed candles
      // Fall back to storeCandles if aggregatedCandles is empty (closure race on initial load)
      const intervalMs = TIMEFRAME_MINUTES[selectedTimeframe] * 60 * 1000;
      const candleMap = new Map<number, { high: number; low: number }>();

      if (aggregatedCandles.length > 0) {
        // Build map from aggregated candles (time is in seconds, convert to milliseconds for key)
        aggregatedCandles.forEach(c => {
          const timestampMs = (c.time as number) * 1000;
          candleMap.set(timestampMs, { high: c.high, low: c.low });
        });
      } else if (storeCandles.length > 0) {
        // Fallback: use raw store candles (timestamp already in ms)
        storeCandles.forEach(c => {
          const aligned = Math.floor(c.timestamp / intervalMs) * intervalMs;
          const existing = candleMap.get(aligned);
          if (!existing) {
            candleMap.set(aligned, { high: c.high, low: c.low });
          } else {
            // Merge candles in same interval: expand high/low
            candleMap.set(aligned, {
              high: Math.max(existing.high, c.high),
              low: Math.min(existing.low, c.low),
            });
          }
        });
      }

      // Helper to align any timestamp to the current timeframe's candle boundary
      const alignTimestamp = (ts: number): number => {
        return Math.floor(ts / intervalMs) * intervalMs;
      };

      // Get enabled settings from stores
      const wholeCandleEnabled = useSettingsStore.getState().getWholeCandleForSymbol(baseSymbol).enabled;
      const pinningVolumeEnabled = useSettingsStore.getState().getPinningVolumeForSymbol(baseSymbol).enabled;
      const trailingPullbackEnabled = useSettingsStore.getState().trailingPullbackSettings.enabled;

      // Create native TradingView markers for non-skipped signals
      const allMarkers: any[] = [];
      // Custom primitive markers for skipped whole-candle signals (with gap from candle)
      const skippedPrimitiveMarkers: SkippedMarker[] = [];
      // Custom primitive markers for volume spikes (emoji markers)
      const volumePrimitiveMarkers: VolumeMarker[] = [];
      // Custom primitive markers for signal markers (confidence, ML, confluence, profitability)
      const signalMarkers_: SignalMarkerData[] = [];
      // Donchian / trailing-pullback triangles, for the 🐋 stacking estimate
      const smallMarkerSlots: { timeMs: number; below: boolean; px: number }[] = [];

      signalMarkers.forEach(marker => {
        const isVolumeSpike = marker.type === 'volume_spike_bullish' || marker.type === 'volume_spike_bearish';
        const isTrailingPullback = marker.type === 'trailing_pullback_bullish' || marker.type === 'trailing_pullback_bearish';

        // Skip marker if its strategy is disabled
        if (isVolumeSpike && !pinningVolumeEnabled) return;
        if (isTrailingPullback && !trailingPullbackEnabled) return;
        if (!isVolumeSpike && !isTrailingPullback && !wholeCandleEnabled) return;

        // Align marker timestamp to the selected timeframe's candle boundary
        const alignedTimestamp = alignTimestamp(marker.timestamp);
        const alignedTimeInSeconds = Math.floor(alignedTimestamp / 1000) as Time;
        const isBullish = marker.type === 'bullish' || marker.type === 'volume_spike_bullish' || marker.type === 'trailing_pullback_bullish';
        const isSkipped = marker.skipped === true;

        // For skipped whole-candle markers, use custom primitive with gap
        if (isSkipped && !isVolumeSpike) {
          const candleData = candleMap.get(alignedTimestamp);
          if (candleData) {
            skippedPrimitiveMarkers.push({
              time: alignedTimestamp,
              price: isBullish ? candleData.low : candleData.high,
              color: isBullish ? '#87CEEB' : '#FFB6C1', // Light blue / Light pink
              isBullish,
            });
          } else {
            // Fallback: if candle not found, still render as a square using native marker
            // This can happen if marker arrives before candle data is fully loaded
            allMarkers.push({
              time: alignedTimeInSeconds,
              position: isBullish ? 'belowBar' as const : 'aboveBar' as const,
              color: isBullish ? '#87CEEB' : '#FFB6C1', // Same light colors for skipped
              shape: 'square' as const,
              size: 0.5,
              text: `⊠ ${marker.skipReason || 'SKIP'}`,
            });
          }

          // Add confidence/confluence/profitability text for skipped signals
          if (marker.confidence !== undefined && candleData) {
            const wccSettings = useSettingsStore.getState().getWholeCandleForSymbol(baseSymbol);
            signalMarkers_.push({
              time: alignedTimestamp,
              price: isBullish ? candleData.low : candleData.high,
              isBullish,
              confidence: marker.confidence,
              confluence: marker.confluenceScore,
              confluenceSymbols: marker.confluenceSymbols,
              confluenceDetails: marker.confluenceDetails?.map(d => ({ symbol: d.symbol, points: d.points, confidence: d.confidence, skipped: d.skipped })),
              primarySymbol: marker.symbol,
              takeItEnabled: wccSettings.takeItEnabled ?? true,
              takeItThreshold: wccSettings.takeItThreshold ?? 80,
              mlScore: marker.mlScore,
              mlRecommendation: marker.mlRecommendation,
              // Profitability data (rendered at end of stack, farthest from candle)
              isProfitable: marker.isProfitable,
              pnl: marker.pnl,
              maePercent: marker.maePercent,
            });
          }
          return; // Skip adding to native markers
        }

        // Volume spike markers use custom emoji primitive (fire/iceberg)
        if (isVolumeSpike) {
          const candleData = candleMap.get(alignedTimestamp);
          if (candleData) {
            volumePrimitiveMarkers.push({
              time: alignedTimestamp,
              price: isBullish ? candleData.low : candleData.high,
              isBullish,
              volumeRatio: marker.volumeRatio,
              isSkipped,
              confluenceScore: marker.confluenceScore,
            });

            // Add profitability for volume spikes (uses signal marker primitive with minimal data)
            if (marker.isProfitable !== undefined && marker.pnl !== undefined) {
              signalMarkers_.push({
                time: alignedTimestamp,
                price: isBullish ? candleData.low : candleData.high,
                isBullish,
                confidence: 0, // Volume spikes don't have confidence scores
                isProfitable: marker.isProfitable,
                pnl: marker.pnl,
                maePercent: marker.maePercent,
              });
            }
          }
          return; // Skip native marker for volume spikes
        }

        // Trailing pullback markers - handled by custom primitive (TrailingPullbackMarkerPrimitive)
        if (isTrailingPullback) {
          return;
        }

        // WCC markers - skip native markers (now drawn as custom circles with colored rings)

        // Add confidence/confluence/profitability text to custom primitive (smaller font size)
        if (marker.confidence !== undefined) {
          const candleData = candleMap.get(alignedTimestamp);
          if (candleData) {
            const wccSettings = useSettingsStore.getState().getWholeCandleForSymbol(baseSymbol);
            signalMarkers_.push({
              time: alignedTimestamp,
              price: isBullish ? candleData.low : candleData.high,
              isBullish,
              confidence: marker.confidence,
              confluence: marker.confluenceScore,
              confluenceSymbols: marker.confluenceSymbols,
              confluenceDetails: marker.confluenceDetails?.map(d => ({ symbol: d.symbol, points: d.points, confidence: d.confidence, skipped: d.skipped })),
              primarySymbol: marker.symbol,
              takeItEnabled: wccSettings.takeItEnabled ?? true,
              takeItThreshold: wccSettings.takeItThreshold ?? 80,
              mlScore: marker.mlScore,
              mlRecommendation: marker.mlRecommendation,
              // Profitability data (rendered at end of stack, farthest from candle)
              isProfitable: marker.isProfitable,
              pnl: marker.pnl,
              maePercent: marker.maePercent,
            });
          }
        }
      });

      // Render other symbol markers (confluence signals from correlated symbols)
      // These use diamond shape and muted colors to distinguish from own symbol's signals
      otherSymbolMarkers.forEach(marker => {
        // Only show non-skipped signals from other symbols (or optionally skipped too)
        // Currently showing all signals regardless of skip status for visibility
        const alignedTs = alignTimestamp(marker.timestamp);
        const alignedTimeSecs = Math.floor(alignedTs / 1000) as Time;
        const isBullish = marker.type === 'bullish';

        // Get short symbol name for display (e.g., "NQ" from "NQH6.CME")
        const shortSymbol = normalizeForComparison(marker.symbol);

        // Use muted gray colors and diamond shape
        const markerColor = '#888888'; // Muted gray for both directions
        const markerText = `${shortSymbol}${isBullish ? '▲' : '▼'}`;

        allMarkers.push({
          time: alignedTimeSecs,
          position: isBullish ? 'belowBar' as const : 'aboveBar' as const,
          color: markerColor,
          shape: 'diamond' as const,
          size: 0.6,
          text: markerText,
        });
      });

      // Render Break of Structure (BOS / CHoCH) markers: a directional arrow at
      // the break candle (belowBar+bullColor for bullish, aboveBar+bearColor for
      // bearish) plus a SHORT horizontal segment at the broken swing level with
      // its text label drawn ON the line.
      //
      // The segment + label are painted entirely by the BosLabelPrimitive (a
      // canvas primitive), NOT by a LineSeries. A LineSeries would add time-scale
      // data points for every BOS level, perturbing the shared time scale and
      // breaking the chart's load-time snap-to-live-edge. The primitive paints at
      // coordinates derived from the existing time scale without adding any data
      // points, so the time scale is untouched.
      {
        const bosSettings = useSettingsStore.getState().bos;
        const bosMarkers = useBosStore.getState().markers.filter(
          (m) => normalizeForComparison(m.symbol) === baseSymbol
        );

        // Color/label per structure type: CHoCH uses chochColor + "CHoCH";
        // BOS uses bull/bear color + "BOS".
        const bosColor = (m: BosMarker): string =>
          m.structureType === 'choch'
            ? bosSettings.chochColor
            : m.direction === 'bullish'
              ? bosSettings.bullColor
              : bosSettings.bearColor;
        const bosText = (m: BosMarker): string =>
          m.structureType === 'choch' ? 'CHoCH' : 'BOS';
        // Distinguish BOS vs CHoCH by line style so they're never confusable:
        // BOS uses the configured style, CHoCH uses the opposite (0 = solid, 2 = dashed).
        const bosLineStyle = (m: BosMarker): number => {
          const base = bosSettings.lineStyle === 'dashed' ? 2 : 0;
          return m.structureType === 'choch' ? (base === 2 ? 0 : 2) : base;
        };

        // Arrow markers at the break candle. These sit at REAL candle times, so
        // they do not perturb the time scale. The text label no longer lives on
        // the arrow — it is rendered ON the level segment line by the
        // BosLabelPrimitive (see below), so the arrow carries no text.
        if (bosSettings.enabled) {
          bosMarkers.forEach((m) => {
            if (m.structureType === 'choch' && !bosSettings.showChoch) return;
            const isBullish = m.direction === 'bullish';
            const alignedTs = alignTimestamp(m.timestamp);
            const alignedTimeSecs = Math.floor(alignedTs / 1000) as Time;
            allMarkers.push({
              time: alignedTimeSecs,
              position: isBullish ? 'belowBar' as const : 'aboveBar' as const,
              color: bosColor(m),
              // lightweight-charts marker shapes are circle|square|arrowUp|arrowDown ONLY —
              // 'diamond' is invalid and renders nothing (why BOS markers were missing).
              // BOS = directional arrow (up=bullish, down=bearish); CHoCH = circle.
              shape: m.structureType === 'choch'
                ? ('circle' as const)
                : (isBullish ? ('arrowUp' as const) : ('arrowDown' as const)),
              size: 1,
              text: '',
            });
          });
        }

        // Build the BOS overlay items (short level segment + optional label),
        // drawn on canvas by the primitive. Each item carries the swing pivot
        // time (left clamp) and the break candle time (right end) in seconds, the
        // break level price, and the line style. Respects enabled, showLabel, and
        // the showChoch gate.
        if (bosSettings.enabled) {
          const bosItems: BosOverlayItem[] = [];
          bosMarkers.forEach((m) => {
            if (m.structureType === 'choch' && !bosSettings.showChoch) return;
            bosItems.push({
              swingTime: Math.floor(alignTimestamp(m.swingTimestamp) / 1000),
              breakTime: Math.floor(alignTimestamp(m.timestamp) / 1000),
              price: m.breakLevel,
              text: bosText(m),
              color: bosColor(m),
              lineStyle: bosLineStyle(m), // BOS = configured style, CHoCH = opposite
              lineWidth: bosSettings.lineWidth,
              lineLengthBars: bosSettings.lineLengthBars,
              showLabel: bosSettings.showLabel,
              baseGrade: bosSettings.gradingEnabled ? m.baseGrade : undefined,
              grade: bosSettings.gradingEnabled ? m.grade : undefined,
              retestStatus: m.retestStatus,
              pending: m.pending,
            });
          });
          bosLabelPrimitiveRef.current?.update(bosItems);
        } else {
          bosLabelPrimitiveRef.current?.update([]);
        }
      }

      // Sort markers by time and set them
      allMarkers.sort((a, b) => {
        const timeA = typeof a.time === 'number' ? a.time : 0;
        const timeB = typeof b.time === 'number' ? b.time : 0;
        return timeA - timeB;
      });
      // Clean (signal markers) blanks the series markers. Checked here, on
      // every pass, because this runs on a poll: clearing them once when Clean
      // turned on only lasted until the next poll put them back.
      seriesMarkersRef.current?.setMarkers(
        isCleanHidden(useCleanChartStore.getState(), 'signals') ? [] : allMarkers,
      );


      // Update custom primitive with skipped markers
      if (skippedMarkerPrimitiveRef.current) {
        skippedMarkerPrimitiveRef.current.setMarkers(skippedPrimitiveMarkers);
      }

      // Update custom primitive with volume emoji markers
      if (volumeMarkerPrimitiveRef.current) {
        volumeMarkerPrimitiveRef.current.setMarkers(volumePrimitiveMarkers);
      }

      // Update custom Donchian primitive with small triangle + volume-ratio text markers
      if (donchianMarkerPrimitiveRef.current) {
        const allDonchianMarkers = useDonchianStore.getState().markers;
        const donchianPrimitiveMarkers: DonchianPrimitiveMarker[] = allDonchianMarkers
          .filter((m) => normalizeForComparison(m.symbol) === baseSymbol)
          .map((m) => {
            const alignedTs = alignTimestamp(m.timestamp);
            const isLong = m.markerType === 'long';
            const candleData = candleMap.get(alignedTs);
            const price = isLong
              ? (candleData?.low ?? m.close)
              : (candleData?.high ?? m.close);
            return {
              time: alignedTs,
              price,
              isLong,
              volumeRatio: m.volumeRatio,
              trendAligned: m.trendAligned,
            };
          });
        donchianMarkerPrimitiveRef.current.setMarkers(donchianPrimitiveMarkers);
        for (const m of donchianPrimitiveMarkers) smallMarkerSlots.push({ timeMs: m.time, below: m.isLong, px: MARKER_HEIGHTS.small });
      }

      // Update custom primitive with signal markers (confidence, ML, confluence, profitability)
      if (signalMarkerPrimitiveRef.current) {
        // Calculate candle bounds for marker-candle collision detection
        const timeScale = chartRef.current!.timeScale();
        const series = candlestickSeriesRef.current!;
        const candleBounds: import('./Chart/SignalMarkerPrimitive').CandleBounds[] = [];

        // Get visible candles and calculate their pixel bounds
        for (const candle of candles) {
          const timeInSeconds = Math.floor(candle.timestamp / 1000) as Time;
          const x = timeScale.timeToCoordinate(timeInSeconds);
          if (x === null) continue;

          const highY = series.priceToCoordinate(candle.high);
          const lowY = series.priceToCoordinate(candle.low);
          const openY = series.priceToCoordinate(candle.open);
          const closeY = series.priceToCoordinate(candle.close);

          if (highY === null || lowY === null || openY === null || closeY === null) continue;

          // Estimate candle width based on bar spacing
          const candleWidth = 8; // Approximate width in pixels

          candleBounds.push({
            time: candle.timestamp,
            x,
            highY,
            lowY,
            openY,
            closeY,
            bodyTop: Math.min(openY, closeY),
            bodyBottom: Math.max(openY, closeY),
            width: candleWidth,
          });
        }

        signalMarkerPrimitiveRef.current.setCandleBounds(candleBounds);
        signalMarkerPrimitiveRef.current.setMarkers(signalMarkers_);
      }

      // Update custom Trailing Pullback primitive with small triangle + "TP" text markers
      if (trailingPullbackPrimitiveRef.current) {
        if (trailingPullbackEnabled) {
          const tpMarkers: TPPrimitiveMarker[] = signalMarkers
            .filter((m) => m.type === 'trailing_pullback_bullish' || m.type === 'trailing_pullback_bearish')
            .reduce<TPPrimitiveMarker[]>((acc, m) => {
              const alignedTs = alignTimestamp(m.timestamp);
              const isBullish = m.type === 'trailing_pullback_bullish';
              const candleData = candleMap.get(alignedTs);
              if (!candleData) return acc; // skip if candle not loaded yet
              const price = isBullish ? candleData.low : candleData.high;
              acc.push({ time: alignedTs, price, isBullish });
              return acc;
            }, []);
          trailingPullbackPrimitiveRef.current.setMarkers(tpMarkers);
          for (const m of tpMarkers) smallMarkerSlots.push({ timeMs: m.time, below: m.isBullish, px: MARKER_HEIGHTS.small });
        } else {
          trailingPullbackPrimitiveRef.current.setMarkers([]);
        }
      }

      // Where the other candle markers sit, so the 🐋 Vest spike stacks past them.
      markerOccupancyRef.current = buildOccupancy(
        {
          native: allMarkers,
          primitives: [
            ...volumePrimitiveMarkers.map((m) => ({ timeMs: m.time, below: m.isBullish, px: MARKER_HEIGHTS.volumeEmoji })),
            ...signalMarkers_.map((m) => ({ timeMs: m.time, below: m.isBullish, px: signalStackPx(m) })),
            ...skippedPrimitiveMarkers.map((m) => ({ timeMs: m.time, below: m.isBullish, px: MARKER_HEIGHTS.small })),
            ...smallMarkerSlots,
          ],
        },
        TIMEFRAME_MINUTES[selectedTimeframe] * 60 * 1000,
      );
      vestSpikePrimitiveRef.current?.setOccupied(occupiedAt);
    };

    // Initial update
    updateMarkers();

    // Subscribe to chart events for real-time updates
    const timeScale = chartRef.current.timeScale();
    timeScale.subscribeVisibleTimeRangeChange(updateMarkers);

    // Subscribe to store changes for new markers or symbol changes
    let prevMarkers = useMarketStore.getState().signalMarkers;
    let prevSymbol = useMarketStore.getState().symbol;
    const unsubscribeMarkers = useMarketStore.subscribe((state) => {
      if (state.signalMarkers !== prevMarkers || state.symbol !== prevSymbol) {
        const oldMarkerCount = prevMarkers.length;
        prevMarkers = state.signalMarkers;
        prevSymbol = state.symbol;
        updateMarkers();

        // Check for new TAKE IT signals (only on new markers, not bulk updates or initial load)
        // Skip initial marker load (when markers are fetched on page load)
        if (initialMarkerLoadRef.current) {
          // First marker update is always the initial load - skip it
          if (state.signalMarkers.length > 0) {
            console.log(`[TAKE IT] Initial load complete, ${state.signalMarkers.length} markers loaded`);
            initialMarkerLoadRef.current = false;
          }
        } else if (state.signalMarkers.length > oldMarkerCount && state.dataMode === 'live') {
          const wccSettings = useSettingsStore.getState().getWholeCandleForSymbol(baseSymbol);
          const takeItEnabled = wccSettings.takeItEnabled !== false;
          const takeItThreshold = wccSettings.takeItThreshold ?? 80;

          console.log(`[TAKE IT] New markers detected: ${state.signalMarkers.length - oldMarkerCount} new, enabled=${takeItEnabled}, threshold=${takeItThreshold}`);

          if (takeItEnabled) {
            // Check new markers for TAKE IT condition
            const newMarkers = state.signalMarkers.slice(oldMarkerCount);
            const now = Date.now();
            for (const marker of newMarkers) {
              // Only check WCC markers (not volume spike markers)
              const isVolumeSpike = marker.type === 'volume_spike_bullish' || marker.type === 'volume_spike_bearish';
              if (isVolumeSpike || marker.skipped) {
                console.log(`[TAKE IT] Skipping marker: isVolumeSpike=${isVolumeSpike}, skipped=${marker.skipped}`);
                continue;
              }

              // Only trigger for recent markers (within last 2 minutes)
              const markerAge = now - marker.timestamp;
              if (markerAge > 120000) {
                console.log(`[TAKE IT] Skipping old marker: age=${Math.round(markerAge/1000)}s`);
                continue;
              }

              const confidence = marker.confidence ?? 0;
              const confluence = marker.confluenceScore ?? 0;
              const combinedScore = confidence + confluence;

              console.log(`[TAKE IT] Checking marker: score=${Math.round(combinedScore)}, threshold=${takeItThreshold}, age=${Math.round(markerAge/1000)}s`);

              if (combinedScore > takeItThreshold && marker.timestamp !== lastTakeItTimestampRef.current) {
                lastTakeItTimestampRef.current = marker.timestamp;
                const isBullish = marker.type === 'bullish';
                const direction = isBullish ? 'LONG' : 'SHORT';
                setTakeItBanner({
                  visible: true,
                  direction,
                  confidence,
                  confluence,
                  symbol: marker.symbol || '',
                });
                console.log(`🔥 [TAKE IT] Signal triggered! Score: ${Math.round(combinedScore)} (conf: ${Math.round(confidence)}, cf: ${Math.round(confluence)})`);
                // Note: take-it-alert is now emitted from Rust backend for Android widget
                break; // Only show one banner at a time
              } else {
                console.log(`[TAKE IT] Not triggered: score=${Math.round(combinedScore)} <= ${takeItThreshold} OR duplicate timestamp`);
              }
            }
          }
        } else if (state.signalMarkers.length > oldMarkerCount) {
          console.log(`[TAKE IT] New markers but dataMode=${state.dataMode}, not live`);
        }
      }
    });

    // Subscribe to settings changes for marker visibility (per-symbol aware)
    const getEffectiveWC = () => useSettingsStore.getState().getWholeCandleForSymbol(baseSymbol).enabled;
    let prevWC = getEffectiveWC();
    const unsubscribeWC = useSettingsStore.subscribe(() => {
      const current = getEffectiveWC();
      if (current !== prevWC) {
        prevWC = current;
        updateMarkers();
      }
    });
    const getEffectivePV = () => useSettingsStore.getState().getPinningVolumeForSymbol(baseSymbol).enabled;
    let prevPV = getEffectivePV();
    const unsubscribePV = useSettingsStore.subscribe(() => {
      const current = getEffectivePV();
      if (current !== prevPV) {
        prevPV = current;
        updateMarkers();
      }
    });
    let prevTP = useSettingsStore.getState().trailingPullbackSettings.enabled;
    const unsubscribeTP = useSettingsStore.subscribe((state) => {
      if (state.trailingPullbackSettings.enabled !== prevTP) {
        prevTP = state.trailingPullbackSettings.enabled;
        updateMarkers();
      }
    });
    // Subscribe to showOtherSymbolSignals changes (per-symbol aware)
    const getEffectiveShowOther = () => useSettingsStore.getState().getWholeCandleForSymbol(baseSymbol).showOtherSymbolSignals;
    let prevShowOther = getEffectiveShowOther();
    const unsubscribeShowOther = useSettingsStore.subscribe(() => {
      const current = getEffectiveShowOther();
      if (current !== prevShowOther) {
        prevShowOther = current;
        updateMarkers();
      }
    });

    // Subscribe to donchian store changes
    let prevDonchianMarkers = useDonchianStore.getState().markers;
    const unsubscribeDonchian = useDonchianStore.subscribe((state) => {
      if (state.markers !== prevDonchianMarkers) {
        prevDonchianMarkers = state.markers;
        updateMarkers();
      }
    });

    // Subscribe to BOS store changes
    let prevBosMarkers = useBosStore.getState().markers;
    const unsubscribeBos = useBosStore.subscribe((state) => {
      if (state.markers !== prevBosMarkers) {
        prevBosMarkers = state.markers;
        updateMarkers();
      }
    });

    // Redraw when BOS settings change (enable toggle, showChoch, colors). The
    // marker pass reads them via getState(), so without this a toggle in the
    // settings modal only showed up on the next scroll or new marker.
    let prevBosSettings = useSettingsStore.getState().bos;
    const unsubscribeBosSettings = useSettingsStore.subscribe((state) => {
      if (state.bos !== prevBosSettings) {
        prevBosSettings = state.bos;
        updateMarkers();
      }
    });

    return () => {
      timeScale.unsubscribeVisibleTimeRangeChange(updateMarkers);
      unsubscribeMarkers();
      unsubscribeWC();
      unsubscribePV();
      unsubscribeTP();
      unsubscribeShowOther();
      unsubscribeDonchian();
      unsubscribeBos();
      unsubscribeBosSettings();
    };
  }, [aggregatedCandles, showOrderMarkers, selectedTimeframe, cleanHidesSignals]);

  // Update news markers and calendar lines on chart
  useEffect(() => {
    if (!chartRef.current) return;

    // Update breaking news markers (lightning bolts)
    if (newsMarkerPrimitiveRef.current) {
      const newsMarkers: NewsMarker[] = breakingNewsForChart.map(h => ({
        time: h.timestamp,
        headline: h.headline,
        isBreaking: h.category === 'BREAKING' || h.importance === 'high',
      }));
      newsMarkerPrimitiveRef.current.setMarkers(newsMarkers);
    }

    // Update calendar event lines
    if (calendarLinePrimitiveRef.current) {
      const now = Date.now();
      const calendarLines: CalendarLine[] = calendarEventsForChart.map(e => ({
        time: e.timestamp,
        event: e.event,
        country: e.country,
        importance: e.importance,
        isPast: e.timestamp < now,
      }));
      calendarLinePrimitiveRef.current.setLines(calendarLines);
    }

    // Update upcoming events (star icons) - next 3 future events
    if (upcomingEventsPrimitiveRef.current) {
      const upcomingEvents: UpcomingEvent[] = calendarEventsForChart.map(e => ({
        time: e.timestamp,
        event: e.event,
        country: e.country,
        importance: e.importance,
        category: e.category,
      }));
      upcomingEventsPrimitiveRef.current.setEvents(upcomingEvents);
    }
  }, [breakingNewsForChart, calendarEventsForChart]);

  // Update FVG zones on chart - subscribe to store changes
  useEffect(() => {
    if (!fvgZonePrimitiveRef.current) return;

    // Compute base symbol once for per-symbol ICT settings lookups
    const ictBaseSymbol = normalizeToBase(symbol);

    // Update zones from store
    const updateFVGZones = () => {
      const state = useMarketStore.getState();
      const zones = state.fvgZones;

      // Filter zones for current symbol
      const normalizedSymbol = normalizeToBase(symbol);

      const symbolZones = zones.filter(zone => {
        const zoneSymbol = normalizeToBase(zone.symbol);
        return zoneSymbol === normalizedSymbol;
      });

      // Get ICT settings for visibility control (per-symbol)
      const ictSettings = useSettingsStore.getState().getICTForSymbol(ictBaseSymbol);
      if (ictSettings.enabled) {
        fvgZonePrimitiveRef.current!.showFilledZones = ictSettings.fvgShowFilled;
        fvgZonePrimitiveRef.current!.showFVGZones = ictSettings.showFvgOnChart;
        fvgZonePrimitiveRef.current!.showIFVGZones = ictSettings.showIfvgOnChart && ictSettings.ifvgEnabled;
        fvgZonePrimitiveRef.current!.showTimeframeBadge = ictSettings.showZoneBadges;
        fvgZonePrimitiveRef.current!.visibleTimeframes = new Set([
          ...ictSettings.fvgTimeframes,
          ...(ictSettings.ifvgEnabled ? ictSettings.ifvgTimeframes : []),
        ]);
        fvgZonePrimitiveRef.current!.fvgBullishColor = ictSettings.fvgBullishColor;
        fvgZonePrimitiveRef.current!.fvgBearishColor = ictSettings.fvgBearishColor;
        fvgZonePrimitiveRef.current!.fvgFilledOpacity = ictSettings.fvgFilledOpacity;
        fvgZonePrimitiveRef.current!.fvgUnfilledOpacity = ictSettings.fvgUnfilledOpacity;
        fvgZonePrimitiveRef.current!.ifvgBullishColor = ictSettings.ifvgBullishColor;
        fvgZonePrimitiveRef.current!.ifvgBearishColor = ictSettings.ifvgBearishColor;
        fvgZonePrimitiveRef.current!.ifvgFilledOpacity = ictSettings.ifvgFilledOpacity;
        fvgZonePrimitiveRef.current!.ifvgUnfilledOpacity = ictSettings.ifvgUnfilledOpacity;
        fvgZonePrimitiveRef.current!.showInvalidatedIFVG = ictSettings.ifvgShowFilled;
        fvgZonePrimitiveRef.current!.setZones(symbolZones);
      } else {
        fvgZonePrimitiveRef.current!.clearZones();
      }
    };

    // Initial update
    updateFVGZones();

    // Subscribe to zone changes
    let prevZones = useMarketStore.getState().fvgZones;
    const unsubscribeFVG = useMarketStore.subscribe((state) => {
      if (state.fvgZones !== prevZones) {
        prevZones = state.fvgZones;
        updateFVGZones();
      }
    });

    // Subscribe to ICT settings changes (per-symbol aware)
    const getEffectiveICT = () => useSettingsStore.getState().getICTForSymbol(ictBaseSymbol);
    let prevICT = getEffectiveICT();
    const unsubscribeICT = useSettingsStore.subscribe(() => {
      const current = getEffectiveICT();
      if (JSON.stringify(current) !== JSON.stringify(prevICT)) {
        prevICT = current;
        updateFVGZones();
      }
    });

    return () => {
      unsubscribeFVG();
      unsubscribeICT();
    };
  }, [symbol]);

  // Render HOD/LOD lines on chart
  useEffect(() => {
    if (!candlestickSeriesRef.current || !strategyService.isInitialized() || dataMode !== 'live') return;

    // Remove existing HOD/LOD lines
    if (hodLineRef.current) {
      candlestickSeriesRef.current.removePriceLine(hodLineRef.current);
      hodLineRef.current = null;
    }
    if (lodLineRef.current) {
      candlestickSeriesRef.current.removePriceLine(lodLineRef.current);
      lodLineRef.current = null;
    }

    // Get session high/low from strategy (use panel's symbol for multi-chart support)
    const { high, low } = strategyService.getSessionHighLow(symbol);

    // Initialize the ref with current values
    lastHodLodRef.current = { high, low };

    // Check if session HOD/LOD visibility is enabled
    const sessionHodLodVisible = levelVisibility.sessionHodLod;

    // Only create lines if we have valid values and visibility is enabled (no titles - labels drawn by primitive)
    if (sessionHodLodVisible && high > 0 && low < 999999) {
      // Create HOD line
      hodLineRef.current = candlestickSeriesRef.current.createPriceLine({
        price: high,
        color: levelColors.sessionHodLod.high,
        lineWidth: 2,
        lineStyle: 2, // Dashed
        axisLabelVisible: true,
        title: '', // Labels drawn by PriceLevelLabelsPrimitive at left edge
      });

      // Create LOD line
      lodLineRef.current = candlestickSeriesRef.current.createPriceLine({
        price: low,
        color: levelColors.sessionHodLod.low,
        lineWidth: 2,
        lineStyle: 2, // Dashed
        axisLabelVisible: true,
        title: '', // Labels drawn by PriceLevelLabelsPrimitive at left edge
      });

      // Update labels for session HOD/LOD
      const sessionLabels: PriceLevelLabel[] = [];
      if (high > 0) sessionLabels.push({ price: high, label: 'HOD', color: levelColors.sessionHodLod.high });
      if (low > 0 && low < 999999) sessionLabels.push({ price: low, label: 'LOD', color: levelColors.sessionHodLod.low });
      priceLevelLabelsPrimitiveRef.current?.setLabels('sessionHodLod', sessionLabels);
    } else {
      // Clear session HOD/LOD labels if visibility is off
      priceLevelLabelsPrimitiveRef.current?.clearSource('sessionHodLod');
    }

    // Set up interval to update HOD/LOD lines every second
    const intervalId = setInterval(() => {
      if (!candlestickSeriesRef.current || !strategyService.isInitialized()) return;
      if (!sessionHodLodVisible) return; // Skip if visibility is off

      const { high: newHigh, low: newLow } = strategyService.getSessionHighLow(symbol);
      const { high: lastHigh, low: lastLow } = lastHodLodRef.current;
      let labelsUpdated = false;

      // Update HOD line if value changed
      if (newHigh > 0 && newHigh !== lastHigh) {
        if (hodLineRef.current) {
          candlestickSeriesRef.current.removePriceLine(hodLineRef.current);
        }
        hodLineRef.current = candlestickSeriesRef.current.createPriceLine({
          price: newHigh,
          color: levelColors.sessionHodLod.high,
          lineWidth: 2,
          lineStyle: 2,
          axisLabelVisible: true,
          title: '', // Labels drawn by PriceLevelLabelsPrimitive at left edge
        });
        // Update the ref with new high
        lastHodLodRef.current.high = newHigh;
        labelsUpdated = true;
        console.log(`📈 [HOD] Updated to ${newHigh.toFixed(2)}`);
      }

      // Update LOD line if value changed
      if (newLow < 999999 && newLow !== lastLow) {
        if (lodLineRef.current) {
          candlestickSeriesRef.current.removePriceLine(lodLineRef.current);
        }
        lodLineRef.current = candlestickSeriesRef.current.createPriceLine({
          price: newLow,
          color: levelColors.sessionHodLod.low,
          lineWidth: 2,
          lineStyle: 2,
          axisLabelVisible: true,
          title: '', // Labels drawn by PriceLevelLabelsPrimitive at left edge
        });
        // Update the ref with new low
        lastHodLodRef.current.low = newLow;
        labelsUpdated = true;
        console.log(`📉 [LOD] Updated to ${newLow.toFixed(2)}`);
      }

      // Update labels if any value changed
      if (labelsUpdated && priceLevelLabelsPrimitiveRef.current) {
        const { high: h, low: l } = lastHodLodRef.current;
        const sessionLabels: PriceLevelLabel[] = [];
        if (h > 0) sessionLabels.push({ price: h, label: 'HOD', color: levelColors.sessionHodLod.high });
        if (l > 0 && l < 999999) sessionLabels.push({ price: l, label: 'LOD', color: levelColors.sessionHodLod.low });
        priceLevelLabelsPrimitiveRef.current.setLabels('sessionHodLod', sessionLabels);
      }
    }, 1000); // Update every second

    return () => {
      clearInterval(intervalId);
      if (hodLineRef.current && candlestickSeriesRef.current) {
        candlestickSeriesRef.current.removePriceLine(hodLineRef.current);
      }
      if (lodLineRef.current && candlestickSeriesRef.current) {
        candlestickSeriesRef.current.removePriceLine(lodLineRef.current);
      }
    };
  }, [dataMode, sessionHighLow, symbol, levelColors, levelVisibility]); // Re-run when dataMode, symbol, session data, colors, or visibility change

  // Render additional price level lines (RTH HOD/LOD, PDH/PDL, IB, Midnight Open)
  // Uses chartSeriesVersion to trigger when chart becomes ready (event-driven, not polling)
  useEffect(() => {
    if (dataMode !== 'live' || !candlestickSeriesRef.current || !priceLevels) return;

    // Helper function to safely remove a price line
    const removeLine = (lineRef: React.MutableRefObject<any>) => {
      if (lineRef.current && candlestickSeriesRef.current) {
        try {
          candlestickSeriesRef.current.removePriceLine(lineRef.current);
        } catch { /* ignore */ }
        lineRef.current = null;
      }
    };

    // Helper to create or update a price line (no title - labels drawn by primitive)
    const updateLine = (
      lineRef: React.MutableRefObject<any>,
      price: number,
      color: string,
      lineStyle: number = 2
    ) => {
      if (!candlestickSeriesRef.current) return;

      // Remove existing line first
      if (lineRef.current) {
        try {
          candlestickSeriesRef.current.removePriceLine(lineRef.current);
        } catch { /* ignore */ }
        lineRef.current = null;
      }

      // Create new line if price is valid (no title - labels drawn at left edge by primitive)
      if (price > 0) {
        lineRef.current = candlestickSeriesRef.current.createPriceLine({
          price,
          color,
          lineWidth: 1,
          lineStyle,
          axisLabelVisible: true,
          title: '', // Labels drawn by PriceLevelLabelsPrimitive at left edge
        });
      }
    };

    // Collect labels for the primitive (Session HOD/LOD handled separately by its own effect)
    const labels: PriceLevelLabel[] = [];

    // RTH HOD/LOD (blue/orange)
    if (levelVisibility.rthHodLod) {
      updateLine(rthHodLineRef, priceLevels.rthHigh, levelColors.rthHodLod.high);
      updateLine(rthLodLineRef, priceLevels.rthLow, levelColors.rthHodLod.low);
      if (priceLevels.rthHigh > 0) labels.push({ price: priceLevels.rthHigh, label: 'RTH-H', color: levelColors.rthHodLod.high });
      if (priceLevels.rthLow > 0) labels.push({ price: priceLevels.rthLow, label: 'RTH-L', color: levelColors.rthHodLod.low });
    } else {
      removeLine(rthHodLineRef);
      removeLine(rthLodLineRef);
    }

    // Previous Day High/Low (purple/pink)
    if (levelVisibility.prevDayHodLod) {
      updateLine(pdhLineRef, priceLevels.prevDayHigh, levelColors.prevDayHodLod.high);
      updateLine(pdlLineRef, priceLevels.prevDayLow, levelColors.prevDayHodLod.low);
      if (priceLevels.prevDayHigh > 0) labels.push({ price: priceLevels.prevDayHigh, label: 'PDH', color: levelColors.prevDayHodLod.high });
      if (priceLevels.prevDayLow > 0) labels.push({ price: priceLevels.prevDayLow, label: 'PDL', color: levelColors.prevDayHodLod.low });
    } else {
      removeLine(pdhLineRef);
      removeLine(pdlLineRef);
    }

    // Initial Balance High/Low (teal/amber)
    if (levelVisibility.ibRange) {
      updateLine(ibHighLineRef, priceLevels.ibHigh, levelColors.ibRange.high);
      updateLine(ibLowLineRef, priceLevels.ibLow, levelColors.ibRange.low);
      if (priceLevels.ibHigh > 0) labels.push({ price: priceLevels.ibHigh, label: 'IB-H', color: levelColors.ibRange.high });
      if (priceLevels.ibLow > 0) labels.push({ price: priceLevels.ibLow, label: 'IB-L', color: levelColors.ibRange.low });
    } else {
      removeLine(ibHighLineRef);
      removeLine(ibLowLineRef);
    }

    // Midnight Open (purple) - solid line
    if (levelVisibility.midnightOpen) {
      updateLine(midnightOpenLineRef, priceLevels.midnightOpen, levelColors.midnightOpen, 1);
      if (priceLevels.midnightOpen > 0) labels.push({ price: priceLevels.midnightOpen, label: 'MO', color: levelColors.midnightOpen });
    } else {
      removeLine(midnightOpenLineRef);
    }

    // Previous Week High/Low (cyan/rose)
    if (levelVisibility.prevWeekHodLod) {
      updateLine(prevWeekHighLineRef, priceLevels.prevWeekHigh, levelColors.prevWeekHodLod.high);
      updateLine(prevWeekLowLineRef, priceLevels.prevWeekLow, levelColors.prevWeekHodLod.low);
      if (priceLevels.prevWeekHigh > 0) labels.push({ price: priceLevels.prevWeekHigh, label: 'PWH', color: levelColors.prevWeekHodLod.high });
      if (priceLevels.prevWeekLow > 0) labels.push({ price: priceLevels.prevWeekLow, label: 'PWL', color: levelColors.prevWeekHodLod.low });
    } else {
      removeLine(prevWeekHighLineRef);
      removeLine(prevWeekLowLineRef);
    }

    // Current Week High/Low (emerald/rose)
    if (levelVisibility.currWeekHodLod) {
      updateLine(currWeekHighLineRef, priceLevels.currWeekHigh, levelColors.currWeekHodLod.high);
      updateLine(currWeekLowLineRef, priceLevels.currWeekLow, levelColors.currWeekHodLod.low);
      if (priceLevels.currWeekHigh > 0) labels.push({ price: priceLevels.currWeekHigh, label: 'CWH', color: levelColors.currWeekHodLod.high });
      if (priceLevels.currWeekLow > 0) labels.push({ price: priceLevels.currWeekLow, label: 'CWL', color: levelColors.currWeekHodLod.low });
    } else {
      removeLine(currWeekHighLineRef);
      removeLine(currWeekLowLineRef);
    }

    // Previous Month High/Low (fuchsia/orange)
    if (levelVisibility.prevMonthHodLod) {
      updateLine(prevMonthHighLineRef, priceLevels.prevMonthHigh, levelColors.prevMonthHodLod.high);
      updateLine(prevMonthLowLineRef, priceLevels.prevMonthLow, levelColors.prevMonthHodLod.low);
      if (priceLevels.prevMonthHigh > 0) labels.push({ price: priceLevels.prevMonthHigh, label: 'PMH', color: levelColors.prevMonthHodLod.high });
      if (priceLevels.prevMonthLow > 0) labels.push({ price: priceLevels.prevMonthLow, label: 'PML', color: levelColors.prevMonthHodLod.low });
    } else {
      removeLine(prevMonthHighLineRef);
      removeLine(prevMonthLowLineRef);
    }

    // Update the price level labels primitive (using source key to allow merging with session HOD/LOD)
    if (priceLevelLabelsPrimitiveRef.current) {
      priceLevelLabelsPrimitiveRef.current.setLabels('priceLevels', labels);
    }

    return () => {
      removeLine(rthHodLineRef);
      removeLine(rthLodLineRef);
      removeLine(pdhLineRef);
      removeLine(pdlLineRef);
      removeLine(ibHighLineRef);
      removeLine(ibLowLineRef);
      removeLine(midnightOpenLineRef);
      removeLine(prevWeekHighLineRef);
      removeLine(prevWeekLowLineRef);
      removeLine(currWeekHighLineRef);
      removeLine(currWeekLowLineRef);
      removeLine(prevMonthHighLineRef);
      removeLine(prevMonthLowLineRef);
    };
  }, [dataMode, priceLevels, levelVisibility, levelColors, symbol, chartSeriesVersion]); // chartSeriesVersion triggers when chart is ready

  // MOC imbalance line (🔥 MOC). Computed client-side from raw candles, so it
  // lives in its own effect and uses its own label source. It shows the most
  // recent MOC in the loaded data (mocOpenPrice is 0 only when there is none).
  const mocVisible = levelVisibility.mocOpen ?? true;
  const mocColor = levelColors.mocOpen ?? '#ff5a1f';
  useEffect(() => {
    const series = candlestickSeriesRef.current;
    if (dataMode !== 'live' || !series) return;

    const clear = () => {
      if (mocOpenLineRef.current && candlestickSeriesRef.current) {
        try { candlestickSeriesRef.current.removePriceLine(mocOpenLineRef.current); } catch { /* ignore */ }
      }
      mocOpenLineRef.current = null;
      priceLevelLabelsPrimitiveRef.current?.setLabels('mocOpen', []);
      if (chartContainerRef.current) delete chartContainerRef.current.dataset.mocPrice;
    };

    clear();
    if (mocVisible && mocOpenPrice > 0) {
      mocOpenLineRef.current = series.createPriceLine({
        price: mocOpenPrice,
        color: mocColor,
        lineWidth: 1,
        lineStyle: 0, // solid, so it reads apart from the dotted Midnight Open
        axisLabelVisible: true,
        title: MOC_LABEL, // shows on the price axis label
      });
      priceLevelLabelsPrimitiveRef.current?.setLabels('mocOpen', [
        { price: mocOpenPrice, label: MOC_LABEL, color: mocColor },
      ]);
      // Exposes the drawn line's price for the e2e check (tests/moc-level.spec.ts).
      if (chartContainerRef.current) {
        chartContainerRef.current.dataset.mocPrice = String(mocOpenLineRef.current.options().price);
      }
    }

    return clear;
  }, [dataMode, mocOpenPrice, mocVisible, mocColor, symbol, chartSeriesVersion]);

  // Render S/R lines on chart (multi-timeframe support/resistance)
  useEffect(() => {
    if (!candlestickSeriesRef.current || !srShownOnChart) {
      // Clean up existing lines if S/R is disabled or in table mode
      srLinesRef.current.forEach((line) => {
        try {
          candlestickSeriesRef.current?.removePriceLine(line);
        } catch { /* ignore */ }
      });
      srLinesRef.current.clear();
      return;
    }

    // Build set of current level keys
    const currentKeys = new Set(srLevels.map((level) => `${level.price}-${level.timeframe}`));

    // Remove lines that no longer exist
    srLinesRef.current.forEach((line, key) => {
      if (!currentKeys.has(key)) {
        try {
          candlestickSeriesRef.current?.removePriceLine(line);
        } catch { /* ignore */ }
        srLinesRef.current.delete(key);
      }
    });

    // Add or update lines
    srLevels.forEach((level) => {
      const key = `${level.price}-${level.timeframe}`;
      const config = SR_TIMEFRAME_CONFIG[level.timeframe];

      // Skip if line already exists at same price
      if (srLinesRef.current.has(key)) {
        return;
      }

      // Apply opacity to line color (convert hex to rgba)
      const hexToRgba = (hex: string, alpha: number): string => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };
      const lineColor = hexToRgba(config.color, srLineOpacity);

      // Create new price line (no axis label - using custom consolidated labels)
      const line = candlestickSeriesRef.current!.createPriceLine({
        price: level.price,
        color: lineColor,
        lineWidth: config.width,
        lineStyle: config.style === 'dashed' ? 2 : 0, // 0 = solid, 2 = dashed
        axisLabelVisible: false, // Disabled - using custom consolidated labels
        title: '', // No title needed since we use custom labels
      });

      srLinesRef.current.set(key, line);
    });

    // Cleanup on unmount
    return () => {
      srLinesRef.current.forEach((line) => {
        try {
          candlestickSeriesRef.current?.removePriceLine(line);
        } catch { /* ignore */ }
      });
      srLinesRef.current.clear();
    };
  }, [srShownOnChart, srLevels, srLineOpacity]);

  // Calculate S/R label groups (consolidate nearby levels into single labels)
  // Full recomputation on data changes; RAF coordinator handles Y-position-only updates during scroll
  const srLabelGroupsDataRef = useRef<Array<{ price: number }>>([]);
  useEffect(() => {
    if (!candlestickSeriesRef.current || !chartRef.current || !srEnabled || srDisplayMode !== 'chart' || srLevels.length === 0) {
      setSrLabelGroups([]);
      srLabelGroupsDataRef.current = [];
      srLabelGroupsRef.current = [];
      if (fvgZonePrimitiveRef.current) fvgZonePrimitiveRef.current.setSRLabelPositions([]);
      return;
    }

    const updateSRLabels = () => {
      const series = candlestickSeriesRef.current;
      if (!series) return;

      // Calculate Y position for each level
      const levelsWithY: Array<{ level: SRLevel; y: number }> = [];
      // With flow line panes the price pane ends above them: no labels over the lines.
      const priceBottom = (chartRef.current && linePaneSpan(chartRef.current)?.top) ?? Infinity;
      for (const level of srLevels) {
        const y = series.priceToCoordinate(level.price);
        if (y !== null && y <= priceBottom) {
          levelsWithY.push({ level, y: y as number });
        }
      }

      if (levelsWithY.length === 0) {
        setSrLabelGroups([]);
        srLabelGroupsDataRef.current = [];
        srLabelGroupsRef.current = [];
        return;
      }

      // Collapse to one label per price cluster. Every timeframe keeps its own
      // line \u2014 only the text labels merge.
      const groups = groupSRLevelsByProximity(levelsWithY, SR_LABEL_GROUP_THRESHOLD_PX);

      // Convert clusters to label data
      const labelGroups = groups.map((group) => {
        const levels = group.map((g) => g.level);
        const avgY = group.reduce((sum, g) => sum + g.y, 0) / group.length;

        // Concise content: price, summed touches, and the single highest
        // timeframe present (was a "WD4H1H" concatenation of every timeframe).
        const content = buildSRLabelContent(levels)!;

        // Breach detection: count how many of last 5 candles traded through this level
        const candles = useMarketStore.getState().candles;
        const recentCandles = candles.length > 1 ? candles.slice(-6, -1) : [];
        const breachCount = recentCandles.filter(
          (c) => c.high >= content.priceValue && c.low <= content.priceValue
        ).length;

        return {
          key: srGroupKey(levels),
          yPosition: avgY,
          levels,
          priceText: content.price,
          count: content.count,
          timeframeLabel: content.timeframeLabel,
          color: content.color,
          levelType: content.levelType,
          price: content.priceValue,
          breachCount,
        };
      });

      setSrLabelGroups(labelGroups);
      srLabelGroupsDataRef.current = labelGroups.map(g => ({ price: g.price }));
      srLabelGroupsRef.current = labelGroups.map(g => ({ key: g.key, levels: g.levels }));

      // Push S/R label Y positions to FVG primitive for collision avoidance
      if (fvgZonePrimitiveRef.current) {
        fvgZonePrimitiveRef.current.setSRLabelPositions(labelGroups.map(g => g.yPosition));
      }
    };

    // Initial update (setState for mount)
    updateSRLabels();

    // RAF updater: update Y positions via DOM only (no setState, no regrouping)
    const rafUpdateSRLabels = () => {
      const series = candlestickSeriesRef.current;
      if (!series) return;
      const groupData = srLabelGroupsDataRef.current;
      groupData.forEach((group, idx) => {
        const el = srLabelElRefs.current.get(idx);
        if (!el) return;
        const y = series.priceToCoordinate(group.price);
        if (y !== null) el.style.top = `${y}px`;
      });
    };

    chartUpdatersRef.current.srLabels = rafUpdateSRLabels;

    return () => {
      chartUpdatersRef.current.srLabels = null;
    };
  }, [srEnabled, srDisplayMode, srLevels]);

  // Escape collapses the expanded S/R label. The card sits over the chart, so
  // the usual dismiss key has to work as well as clicking away.
  useEffect(() => {
    if (expandedSRKey === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedSRKey(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expandedSRKey]);

  // A cluster can dissolve as levels reload or the user hides a timeframe.
  // Drop the expansion when its cluster is gone so no card is left orphaned.
  useEffect(() => {
    if (expandedSRKey === null) return;
    if (!srLabelGroups.some((g) => g.key === expandedSRKey)) {
      setExpandedSRKey(null);
    }
  }, [srLabelGroups, expandedSRKey]);


  // RAF coordinator: subscribe to chart interaction events and dispatch all updaters in a single frame
  useEffect(() => {
    const chart = chartRef.current;
    const container = chartContainerRef.current;
    if (!chart || !container) return;

    // Pan/zoom on time axis
    chart.timeScale().subscribeVisibleLogicalRangeChange(scheduleChartUpdate);

    // Y-axis zoom via wheel
    container.addEventListener('wheel', scheduleChartUpdate, { passive: true });

    // Mouse drag on price scale
    let isDragging = false;
    const handleMouseDown = () => { isDragging = true; };
    const handleMouseMove = () => { if (isDragging) scheduleChartUpdate(); };
    const handleMouseUp = () => { isDragging = false; };
    container.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Touch drag on price scale
    const handleTouchStart = () => { isDragging = true; };
    const handleTouchMove = () => { if (isDragging) scheduleChartUpdate(); };
    const handleTouchEnd = () => { isDragging = false; };
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(scheduleChartUpdate);
      container.removeEventListener('wheel', scheduleChartUpdate);
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [scheduleChartUpdate]);

  // Render Auto Fibonacci levels on chart (from HOD to LOD) using React overlay
  // Fib level configuration
  const FIB_LEVEL_CONFIG = useMemo(() => [
    { level: 0.236, label: '.236', isKey: false },
    { level: 0.382, label: '.382', isKey: false },
    { level: 0.5, label: '.5', isKey: true },      // Key level
    { level: 0.618, label: '.618', isKey: true },  // Golden ratio - key level
    { level: 0.786, label: '.786', isKey: false },
  ], []);

  const [fibLevels, setFibLevels] = useState<Array<{ level: number; label: string; yPosition: number; price: number; isKey: boolean }>>([]);

  useEffect(() => {
    if (!candlestickSeriesRef.current || !chartRef.current || !strategyService.isInitialized() || dataMode !== 'live') {
      setFibLevels([]);
      return;
    }

    // Full computation: data + positions (for initial render and periodic HOD/LOD changes)
    const computeFibLevels = () => {
      if (!candlestickSeriesRef.current || !strategyService.isInitialized()) return null;
      const { high, low } = strategyService.getSessionHighLow(symbol);
      if (high <= 0 || low >= 999999 || high <= low) return null;
      const range = high - low;
      return FIB_LEVEL_CONFIG.map(({ level, label, isKey }) => {
        const price = low + (range * level);
        const yCoord = candlestickSeriesRef.current!.priceToCoordinate(price);
        return { level, label, price, yPosition: yCoord !== null ? yCoord : -1000, isKey };
      }).filter(l => l.yPosition > 0);
    };

    // Initial render (setState to mount fib level elements)
    const data = computeFibLevels();
    setFibLevels(data ?? []);

    // RAF updater: update Y positions via DOM only (no setState)
    const rafUpdateFibLevels = () => {
      if (!candlestickSeriesRef.current || !strategyService.isInitialized()) return;
      const { high, low } = strategyService.getSessionHighLow(symbol);
      if (high <= 0 || low >= 999999 || high <= low) return;
      const range = high - low;
      const positions = new Map<number, number>();
      FIB_LEVEL_CONFIG.forEach(({ level }) => {
        const price = low + (range * level);
        const yCoord = candlestickSeriesRef.current!.priceToCoordinate(price);
        if (yCoord !== null && yCoord > 0) positions.set(level, yCoord as number);
      });
      fibLevelsOverlayRef.current?.updatePositions(positions);
    };

    chartUpdatersRef.current.fibLevels = rafUpdateFibLevels;

    // Periodic update for HOD/LOD changes (uses setState — runs at 1Hz, not causing jitter)
    const intervalId = setInterval(() => {
      const newData = computeFibLevels();
      setFibLevels(newData ?? []);
    }, 1000);

    return () => {
      clearInterval(intervalId);
      chartUpdatersRef.current.fibLevels = null;
    };
  }, [dataMode, FIB_LEVEL_CONFIG, sessionHighLow, symbol]); // Re-run when symbol or session data changes

  // Update live position line Y position (React-only approach, no TradingView price line)
  useEffect(() => {
    if (!candlestickSeriesRef.current || !chartRef.current || !chartContainerRef.current) {
      return;
    }

    // Initial render (setState to control mount/unmount of LivePositionLine)
    const initLivePositionY = () => {
      if (!candlestickSeriesRef.current) return;
      if (currentLivePosition && currentLivePosition.netPrice > 0) {
        const yCoord = candlestickSeriesRef.current.priceToCoordinate(currentLivePosition.netPrice);
        if (yCoord !== null && yCoord !== undefined) {
          setLivePositionLineY(yCoord);
        }
      } else {
        setLivePositionLineY(null);
      }
    };

    // RAF updater: update Y via DOM only (no setState)
    const rafUpdateLivePositionY = () => {
      if (!candlestickSeriesRef.current) return;
      if (currentLivePosition && currentLivePosition.netPrice > 0) {
        const yCoord = candlestickSeriesRef.current.priceToCoordinate(currentLivePosition.netPrice);
        if (yCoord !== null && yCoord !== undefined) {
          livePositionLineRef.current?.updateY(yCoord as number);
        }
      }
    };

    initLivePositionY();
    chartUpdatersRef.current.positionY = rafUpdateLivePositionY;

    return () => {
      chartUpdatersRef.current.positionY = null;
    };
  }, [currentLivePosition, brokerLastUpdated]);

  // Update live order Y positions for draggable LiveOrderLine components
  useEffect(() => {
    if (!candlestickSeriesRef.current || !chartRef.current || !chartContainerRef.current) return;

    // Shared computation for order positions and label offsets
    const computeOrderPositions = () => {
      if (!candlestickSeriesRef.current) return null;
      const workingOrders = liveOrders.filter(order =>
        (selectedAccountId === null || order.accountId === selectedAccountId) &&
        (order.ordStatus === 'Working' || order.ordStatus === 'Accepted' || order.ordStatus === 'Pending')
      );
      const entryPrice = currentLivePosition?.netPrice ?? 0;
      const entryY = entryPrice > 0
        ? candlestickSeriesRef.current?.priceToCoordinate(entryPrice) ?? null
        : null;
      const OVERLAP_THRESHOLD_PX = 14;
      const LABEL_OFFSET_PX = 20;
      const positions = new Map<number, number>();
      const labelOffsets = new Map<number, number>();
      const usedLabelYs: number[] = entryY !== null ? [entryY] : [];
      workingOrders.forEach((order) => {
        const orderPrice = order.stopPrice || order.price;
        if (!orderPrice || orderPrice <= 0) return;
        const yPosition = candlestickSeriesRef.current?.priceToCoordinate(orderPrice);
        if (yPosition === null || yPosition === undefined) return;
        let labelOffset = 0;
        for (const usedY of usedLabelYs) {
          if (Math.abs(yPosition - usedY) < OVERLAP_THRESHOLD_PX) {
            labelOffset = yPosition >= usedY ? LABEL_OFFSET_PX : -LABEL_OFFSET_PX;
            break;
          }
        }
        usedLabelYs.push(yPosition + labelOffset);
        positions.set(order.id, yPosition);
        labelOffsets.set(order.id, labelOffset);
      });
      return { positions, labelOffsets };
    };

    // Initial render (setState for mount)
    const result = computeOrderPositions();
    if (result) {
      setLiveOrderLinePositions(result.positions);
      setLiveOrderLabelOffsets(result.labelOffsets);
    }

    // RAF updater: update Y positions via imperative handles (no setState)
    const rafUpdateLiveOrderPositions = () => {
      if (!candlestickSeriesRef.current) return;
      const workingOrders = liveOrders.filter(order =>
        (selectedAccountId === null || order.accountId === selectedAccountId) &&
        (order.ordStatus === 'Working' || order.ordStatus === 'Accepted' || order.ordStatus === 'Pending')
      );
      const entryPrice = currentLivePosition?.netPrice ?? 0;
      const entryY = entryPrice > 0
        ? candlestickSeriesRef.current?.priceToCoordinate(entryPrice) ?? null
        : null;
      const OVERLAP_THRESHOLD_PX = 14;
      const LABEL_OFFSET_PX = 20;
      const usedLabelYs: number[] = entryY !== null ? [entryY as number] : [];
      workingOrders.forEach((order) => {
        const orderPrice = order.stopPrice || order.price;
        if (!orderPrice || orderPrice <= 0) return;
        const yPosition = candlestickSeriesRef.current?.priceToCoordinate(orderPrice);
        if (yPosition === null || yPosition === undefined) return;
        let labelOffset = 0;
        for (const usedY of usedLabelYs) {
          if (Math.abs(yPosition - usedY) < OVERLAP_THRESHOLD_PX) {
            labelOffset = yPosition >= usedY ? LABEL_OFFSET_PX : -LABEL_OFFSET_PX;
            break;
          }
        }
        usedLabelYs.push(yPosition + labelOffset);
        liveOrderLineRefsMap.current.get(order.id)?.updateY(yPosition as number, labelOffset);
      });
    };

    chartUpdatersRef.current.liveOrderPositions = rafUpdateLiveOrderPositions;

    return () => {
      chartUpdatersRef.current.liveOrderPositions = null;
    };
  }, [liveOrders, selectedAccountId, brokerLastUpdated]);

  // Helper function to convert Y coordinate to price (for drag support)
  const coordinateToPrice = (yCoordinate: number): number | null => {
    if (!candlestickSeriesRef.current) return null;
    return candlestickSeriesRef.current.coordinateToPrice(yCoordinate);
  };

  // ── Draft TP/SL bracket line ────────────────────────────────────────────
  // The control bar drives a single active position (currentLivePosition for
  // THIS chart's symbol + selected account). The draft line lets the user
  // place/move a TP or SL leg and only commit it via the ✓ confirm control.

  // Working exit orders for the active position, filtered to the exit side.
  const draftExitOrders = useMemo(() => {
    if (!currentLivePosition || currentLivePosition.netPos === 0) {
      return { exitAction: 'Sell' as 'Buy' | 'Sell', tp: undefined as typeof liveOrders[number] | undefined, sl: undefined as typeof liveOrders[number] | undefined };
    }
    const isLong = currentLivePosition.netPos > 0;
    const exitAction: 'Buy' | 'Sell' = isLong ? 'Sell' : 'Buy';
    const working = filteredLiveOrders.filter(o =>
      (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending') &&
      o.action === exitAction
    );
    const tp = working.find(o =>
      o.ordType?.toLowerCase().includes('limit') &&
      !o.ordType?.toLowerCase().includes('stop') &&
      o.price
    );
    const sl = working.find(o => o.ordType?.toLowerCase().includes('stop'));
    return { exitAction, tp, sl };
  }, [currentLivePosition, filteredLiveOrders, liveOrders]);

  // Start (or "edit") a draft TP/SL line. If a leg of that kind already exists,
  // seed the draft at its current price; otherwise default off the entry price.
  const startDraftBracket = useCallback((kind: 'TP' | 'SL') => {
    if (!currentLivePosition || currentLivePosition.netPos === 0) return;
    const isLong = currentLivePosition.netPos > 0;
    const entry = currentLivePosition.netPrice;
    const sym = currentLivePosition.symbol || symbol;
    let price: number;
    if (kind === 'TP') {
      const existing = draftExitOrders.tp?.price;
      price = existing ?? (isLong ? entry + chartOcoTPOffset : entry - chartOcoTPOffset);
    } else {
      const existing = draftExitOrders.sl?.stopPrice ?? draftExitOrders.sl?.price;
      price = existing ?? (isLong ? entry - chartOcoSLOffset : entry + chartOcoSLOffset);
    }
    setDraftBracket({ kind, price: roundToTick(price, sym) });
  }, [currentLivePosition, symbol, draftExitOrders, chartOcoTPOffset, chartOcoSLOffset]);

  // Y-coordinate of the draft line. Recomputed when the chart range/zoom
  // changes (chartRangeVersion) or the chart series is recreated.
  const draftBracketY = useMemo(() => {
    if (!draftBracket || !candlestickSeriesRef.current) return null;
    const y = candlestickSeriesRef.current.priceToCoordinate(draftBracket.price);
    return (y === null || y === undefined) ? null : (y as number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftBracket, chartRangeVersion, chartSeriesVersion, volumeSeriesVersion]);

  // Confirm the draft: place the new leg while preserving the opposite leg.
  const confirmDraftBracket = useCallback(async () => {
    if (!draftBracket || !currentLivePosition || currentLivePosition.netPos === 0) return;
    const sym = currentLivePosition.symbol || symbol;
    const newQuantity = Math.abs(currentLivePosition.netPos);
    const { exitAction, tp: existingTP, sl: existingSL } = draftExitOrders;

    // Build BOTH legs: the new one from the draft, the opposite from existing
    // working orders so replaceBracket (cancel-all-then-replace) preserves it.
    const tpPrice = draftBracket.kind === 'TP' ? draftBracket.price : existingTP?.price;
    const slPrice = draftBracket.kind === 'SL' ? draftBracket.price : (existingSL?.stopPrice ?? existingSL?.price);

    setDraftBracketSubmitting(true);
    try {
      const { replaceBracket } = useBrokerStore.getState();
      const result = await replaceBracket({
        tpOrderId: existingTP?.id,
        slOrderId: existingSL?.id,
        symbol: sym,
        action: exitAction,
        newQuantity,
        tpPrice,
        slPrice,
      });
      if (result.success) {
        addToast('success', `${draftBracket.kind} set @ ${draftBracket.price.toFixed(2)}`);
        setDraftBracket(null);
      } else {
        addToast('error', `${draftBracket.kind} failed: ${result.error || 'unknown error'}`);
        // Keep the draft so the user can retry.
      }
    } catch (err) {
      addToast('error', `${draftBracket.kind} failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setDraftBracketSubmitting(false);
    }
  }, [draftBracket, currentLivePosition, symbol, draftExitOrders]);

  // Cleanup: clear the draft when the position closes or the chart symbol changes.
  useEffect(() => {
    if (!currentLivePosition || currentLivePosition.netPos === 0) {
      setDraftBracket(null);
    }
  }, [currentLivePosition]);

  useEffect(() => {
    setDraftBracket(null);
  }, [symbol]);

  // Update order line positions when chart moves/zooms
  useEffect(() => {
    if (!chartRef.current || !candlestickSeriesRef.current || !chartContainerRef.current || orderLines.length === 0) {
      return;
    }

    // Initial render: update state so OrderLine components have correct initial Y
    const updateOrderLinePositions = () => {
      if (!candlestickSeriesRef.current) return;
      setOrderLines(prev =>
        prev.map(line => {
          const newY = candlestickSeriesRef.current?.priceToCoordinate(line.price);
          if (newY !== null && newY !== undefined) {
            return { ...line, yPosition: newY };
          }
          return line;
        })
      );
    };

    updateOrderLinePositions();

    // RAF updater: update Y positions via imperative handles (no setState)
    const rafUpdateOrderLinePositions = () => {
      if (!candlestickSeriesRef.current) return;
      orderLinesDataRef.current.forEach(line => {
        const newY = candlestickSeriesRef.current?.priceToCoordinate(line.price);
        if (newY !== null && newY !== undefined) {
          orderLineRefsMap.current.get(line.id)?.updateY(newY as number);
        }
      });
    };

    chartUpdatersRef.current.orderLinePositions = rafUpdateOrderLinePositions;

    return () => {
      chartUpdatersRef.current.orderLinePositions = null;
    };
  }, [orderLines.length]); // Only re-subscribe when number of order lines changes

  // Order line handlers
  // Uses trading panel settings for quantity and OCO (TP/SL)
  const handlePlaceOrder = async (type: 'BUY_LIMIT' | 'SELL_LIMIT' | 'BUY_STOP' | 'SELL_STOP', price: number, _quantity: number) => {
    try {
      // Use quantity from trading panel preferences
      const quantity = chartOrderQuantity;

      // Map order types to Tradovate format
      const action: 'Buy' | 'Sell' = type.includes('BUY') ? 'Buy' : 'Sell';

      // Determine Tradovate order type
      // BUY_LIMIT/SELL_LIMIT -> Limit with price
      // BUY_STOP/SELL_STOP -> Stop with stopPrice
      const isLimitOrder = type.includes('LIMIT');
      const orderType: 'Limit' | 'Stop' = isLimitOrder ? 'Limit' : 'Stop';

      // Use live trading via Tradovate (requires connection)
      if (!isLiveConnected) {
        console.error('Cannot place order: not connected to Tradovate');
        return;
      }

      // In a position the opposite side is exit-only: capped at the position and
      // sent as a plain limit/stop - never a bracket, whose fresh TP/SL legs
      // would open a position the other way once the exit fills.
      const plan = planManualOrder(currentLivePosition?.netPos, action, quantity);

      // If OCO is enabled, place bracket order with TP/SL
      if (chartUseOCO && plan.kind !== 'exit') {
        const profitTargetPoints = action === 'Buy' ? chartOcoTPOffset : -chartOcoTPOffset;
        const stopLossPoints = action === 'Buy' ? -chartOcoSLOffset : chartOcoSLOffset;

        console.log(`[ChartView] Placing ${type} BRACKET order via Tradovate:`, {
          action,
          symbol,
          quantity,
          orderType,
          price: isLimitOrder ? price : undefined,
          stopPrice: !isLimitOrder ? price : undefined,
          profitTargetPoints,
          stopLossPoints
        });

        // Use bracket order API - supports limit/stop entry with TP/SL
        const result = await placeBracketOrder({
          action,
          symbol,
          quantity,
          orderType,
          price: isLimitOrder ? price : undefined,
          stopPrice: !isLimitOrder ? price : undefined,
          profitTargetPoints,
          stopLossPoints
        });

        if (result.success) {
          console.log(`[ChartView] ✅ ${type} bracket order placed with TP/SL`);
        } else {
          console.error(`[ChartView] ❌ ${type} bracket order failed:`, result.error);
        }
      } else {
        // No OCO - simple limit/stop order
        console.log(`[ChartView] Placing ${type} order via Tradovate:`, {
          action,
          symbol,
          quantity,
          orderType,
          price: isLimitOrder ? price : undefined,
          stopPrice: !isLimitOrder ? price : undefined
        });

        const result = await placeOrder({
          action,
          symbol,
          quantity: plan.quantity,
          orderType,
          price: isLimitOrder ? price : undefined,
          stopPrice: !isLimitOrder ? price : undefined
        });

        if (result.success) {
          console.log(`[ChartView] ✅ ${type} order placed successfully`);
        } else {
          console.error(`[ChartView] ❌ ${type} order failed:`, result.error);
        }
      }
    } catch (error) {
      console.error(`Failed to place order:`, error);
    }
  };

  const handleRemoveOrderLine = (id: string) => {
    // Simply remove from state - the React OrderLine component handles the visual line
    // No native TradingView price lines are created anymore
    setOrderLines(prev => prev.filter(line => line.id !== id));
  };

  const handleDragOrderLine = (id: string, newYPosition: number) => {
    const rect = chartContainerRef.current?.getBoundingClientRect();
    if (!rect || !candlestickSeriesRef.current) return;

    // Convert Y position to price using the series
    const newPrice = candlestickSeriesRef.current.coordinateToPrice(newYPosition);

    if (newPrice) {
      // Simply update state - the React OrderLine component handles the visual line
      // No native TradingView price lines to update
      setOrderLines(prev =>
        prev.map(line =>
          line.id === id
            ? { ...line, price: newPrice as number, yPosition: newYPosition }
            : line
        )
      );
    }
  };

  return (
    <div className="chart-view">
      <div className="chart-view-content">
      <div className="chart-header unified">
        <div className="header-left">
          <div className="brand-logo">
            <AnimatedLogo />
          </div>
          <div className="header-divider"></div>
          <span className="symbol">{symbol ? symbol.replace(/[A-Z]\d+\.(CME|COMEX|NYMEX|CBOT)$/i, '') : (symbolRegistry[0]?.base || 'MNQ')}</span>
          <span className={`price ${currentPrice && currentPrice > 0 ? 'price-live' : ''}`}>{currentPrice?.toFixed(2) || '--'}</span>
        </div>

        <div className="header-controls">
          <select
            className="symbol-dropdown"
            value={symbol ? symbol.replace(/[A-Z]\d+\.(CME|COMEX|NYMEX|CBOT)$/i, '') : (symbolRegistry[0]?.base || 'MNQ')}
            onChange={(e) => {
              if (onSymbolChange) {
                // Find the selected symbol in the registry to get the correct exchange
                const selectedBase = e.target.value;
                const regSymbol = symbolRegistry.find(s => s.base === selectedBase);
                if (regSymbol) {
                  // Use the fullSymbol from registry (includes contract month and exchange)
                  onSymbolChange(regSymbol.fullSymbol);
                } else {
                  // Fallback: use current contract month and CME
                  const currentSuffix = symbol?.match(/([A-Z]\d+)\.(CME|COMEX|NYMEX|CBOT)$/i)?.[1] || 'H6';
                  onSymbolChange(`${selectedBase}${currentSuffix}.CME`);
                }
              }
              e.target.blur();
            }}
          >
            {symbolRegistry.filter(s => s.enabled).map(s => (
              <option key={s.base} value={s.base}>{s.base}</option>
            ))}
          </select>
          <select
            className="timeframe-dropdown"
            value={selectedTimeframe}
            onChange={async (e) => {
              const newTimeframe = e.target.value as Timeframe;
              setSelectedTimeframe(newTimeframe);
              e.target.blur();

              // Recalculate markers for the new timeframe
              const timeframeMinutes = TIMEFRAME_MINUTES[newTimeframe];
              console.log(`[Timeframe] Changed to ${newTimeframe} (${timeframeMinutes} minutes), recalculating markers...`);

              try {
                const response = await apiFetch(
                  `${CONFIG.getBackendUrl(window.location.hostname)}/api/signal-markers/recalculate?timeframe_minutes=${timeframeMinutes}`,
                  { method: 'POST' }
                );
                if (response.ok) {
                  const data = await response.json();
                  console.log(`[Timeframe] Markers recalculated: ${data.count} markers for ${timeframeMinutes}m timeframe`);
                } else {
                  console.error('[Timeframe] Failed to recalculate markers:', response.statusText);
                }
              } catch (err) {
                console.error('[Timeframe] Error recalculating markers:', err);
              }
            }}
          >
            {(['1m', '2m', '3m', '5m', '10m', '15m', '30m', '1h', '2h', '4h', '1D'] as Timeframe[]).map((tf) => (
              <option key={tf} value={tf}>{tf}</option>
            ))}
          </select>
          {/* Backtest button temporarily disabled */}
          {/* Settings button in header for mobile */}
          {isMobile && (
            <button
              className="header-settings-btn"
              onClick={() => setShowStrategySettings(true)}
              title="Strategy Settings"
            >
              <Settings size={16} />
            </button>
          )}
          {/* Notifications bell - compact */}
          <button
            className={`header-alert-btn ${toastHistory.length > 0 ? 'has-alerts' : ''}`}
            onClick={toggleHistoryVisible}
            title="Notifications"
          >
            <Bell size={14} />
            {toastHistory.length > 0 && (
              <span className="header-alert-badge">{toastHistory.length > 99 ? '99+' : toastHistory.length}</span>
            )}
          </button>
        </div>
        {/* Animated rainbow border at bottom of header */}
        <AnimatedRainbowBorder />
      </div>
      {/* News Ticker - between header and action bar */}
      <NewsTicker />
      {/* Chart content row - sidebar + main chart area */}
      <div className="chart-content-row">
        {/* News sidebar on desktop, modal on mobile */}
        <NewsEventsOverlay />
        <div className="chart-main">
          {/* Action Bar - unified scrollable toolbar */}
          <div className="action-bar">
        {/* Symbol selector - first item */}
        <SymbolSelector onSymbolChange={onSymbolChange} />
        {/* Chart controls */}
        <OverlayLibrary />
        {/* Signal Predictor button - DEPRECATED (functionality still works via store, button hidden) */}
        {false && (
          <button
            className={`action-bar-btn ${!spIsMinimized ? 'active' : ''}`}
            onClick={() => {
                spSetMinimized(!spIsMinimized);
            }}
            title={spIsMinimized ? 'Show Signal Predictor' : 'Hide Signal Predictor'}
          >
            <Activity size={14} className="action-bar-icon" />
            <span className="action-bar-label">Signals</span>
          </button>
        )}
        {/* S/R Levels toggle with dropdown */}
        <SRToggle onOpenSRAlerts={() => { setStrategySettingsTab('sr-alerts'); setShowStrategySettings(true); }} />
        {/* Price Levels toggle (RTH, PDH/PDL, IB, Midnight Open) */}
        <LevelsToggle />
        {/* Signal overlays (Fair Price, Diamonds, Displacement, Trade Entries)
            and their settings, collapsed into one dropdown. This used to be
            seven separate buttons in the bar. */}
        <SignalsMenu
          onOpenFairPriceSettings={() => setShowFairPriceSettings(true)}
          onOpenDisplacementSettings={() => setShowDisplacementSettings(true)}
        />
        {/* Everything order-flow (flow-signals): markers, status, later lines/settings. */}
        <TradeFinderToggle symbol={symbol} onJumpTo={jumpToFlowSignal} />
        {/* No order-panel toggle: the panel is always mounted on desktop, so a
            button that could only hide it was how it went missing. */}
        <CleanToggle
          active={cleanChart}
          onToggle={toggleCleanChart}
        />
        <ThemeSelector />
        <div className="action-bar-divider" />

        {/* Mobile only. On desktop these exact controls live in the floating
            order panel, which is always mounted — two copies of order entry on
            one screen is a way to fire the wrong one.

            The old comment here claimed this was "hidden on mobile", but nothing
            hid it: `.action-bar-trading` only gets tightened at <=768px, never
            display:none. It was rendering in both bars on mobile. */}
        {isMobile && (
          <ActionBarTrading
            symbol={symbol}
            currentPrice={currentPrice}
            intervalMinutes={TIMEFRAME_MINUTES[selectedTimeframe]}
          />
        )}

        <div className="action-bar-divider" />

        {/* WebSocket connection indicator - prominent when disconnected */}
        {connectionStatus !== 'connected' && (
          <button
            className="action-bar-btn ws-disconnected"
            onClick={() => {
              addToast('info', 'Reconnecting...');
              wsService.connect();
            }}
            title="WebSocket disconnected - tap to reconnect"
          >
            <WifiOff size={14} className="action-bar-icon" style={{ color: '#ef4444' }} />
            <span className="action-bar-label" style={{ color: '#ef4444' }}>Offline</span>
          </button>
        )}

        {/* Trading controls - Position & Orders embedded in action bar */}
        <ActionBarPositions
          symbol={symbol}
          currentPrice={currentPrice}
          onOpenSettings={() => setShowStrategySettings(true)}
        />

        {/* Price & Volume Alerts */}
        <AlertButton symbol={symbol} />

        <button
          className={`action-bar-btn broker-btn ${connectedBrokerCount > 0 ? 'has-connected' : ''} ${selectedAccountName ? 'has-account' : ''}`}
          onClick={() => setShowBrokerModal(true)}
          title={selectedAccountName ? `Account: ${selectedAccountName}` : "Manage Brokers"}
        >
          <Users size={14} className="action-bar-icon" />
          <span className="action-bar-label">
            {selectedAccountName || 'Brokers'}
          </span>
          {connectedBrokerCount > 0 && !selectedAccountName && (
            <span className="broker-count-badge">{connectedBrokerCount}</span>
          )}
        </button>
        <button
          className={`action-bar-btn ${copyTradingEnabled ? 'copy-enabled' : ''}`}
          onClick={() => setShowCopyTradingModal(true)}
          title="Copy Trading Settings"
        >
          <Copy size={14} className="action-bar-icon" />
          <span className="action-bar-label">
            {copyTradingEnabled ? 'ON' : 'Copy'}
          </span>
        </button>
        <button
          className={`action-bar-btn ${autoBotEnabled ? 'autobot-enabled' : ''}`}
          onClick={() => setShowAutoBotPanel(true)}
          title="Auto-Bot"
        >
          <Bot size={14} className="action-bar-icon" />
          <span className="action-bar-label">Bots</span>
          {activeBots > 0 && (
            <span className="broker-count-badge">{activeBots}</span>
          )}
        </button>
        {/* Analytics button temporarily disabled */}
        {/* News button removed - news ticker label provides this on mobile */}
        {/* Health - expandable service status panel */}
        <ActionBarHealth />
        {/* Settings button hidden on mobile - moved to header */}
        {!isMobile && (
          <button
            className="action-bar-btn"
            onClick={() => setShowStrategySettings(true)}
            title="Strategy Settings"
          >
            <Settings size={14} className="action-bar-icon" />
            <span className="action-bar-label">Settings</span>
          </button>
        )}
        {/* Deliberately OUTSIDE the !isMobile guard above: the trade log is most
            wanted on a phone, where the chart cannot show a day's results. */}
        <button
          className="action-bar-btn"
          // New tab for the same reason as the planner below: /trades renders
          // instead of the terminal, so opening it in place would tear down the
          // chart, the websocket and the broker session behind it.
          onClick={() => window.open('/trades', '_blank', 'noopener')}
          title="Completed trades (opens in a new tab)"
        >
          <Receipt size={14} className="action-bar-icon" />
          <span className="action-bar-label">Trades</span>
        </button>
        <button
          className="action-bar-btn"
          // New tab on purpose: the planner is a separate tool and opening it
          // in place would tear down the chart, the websocket and the broker
          // session behind it.
          onClick={() => window.open('/planner', '_blank', 'noopener')}
          title="Prop hedge & revival planner (opens in a new tab)"
        >
          <Calculator size={14} className="action-bar-icon" />
          <span className="action-bar-label">Planner</span>
        </button>
        <button
          className="action-bar-btn"
          // Outside the !isMobile guard like Trades: a read-only market scan is
          // just as useful on a phone. New tab so the chart, websocket and
          // broker session behind it survive.
          onClick={() => window.open('/scanner', '_blank', 'noopener')}
          title="Vest Markets scanner (opens in a new tab)"
        >
          <Radar size={14} className="action-bar-icon" />
          <span className="action-bar-label">Scanner</span>
        </button>
        <DrawingToggle symbol={symbol} />
      </div>
      <div className="chart-wrapper">
        {tfStripVisible && (
          <div className="tf-strip-anchor">
            <TradeFinderStrip
              symbol={symbol}
              shown={flowShown}
              levels={flowMatchLevels}
              onSelect={selectFlowSignal}
              compact={isMobile}
            />
          </div>
        )}
        <div className="chart-panes-container">
          <div className={`chart-price-pane ${placementMode ? 'placement-mode' : ''} ${dcaMode ? 'dca-mode' : ''}`}>
            <div
              className="chart-container"
              ref={chartContainerRef}
              onClick={(e) => {
                // Handle click on signal marker circles (desktop)
                if (signalMarkerPrimitiveRef.current && chartContainerRef.current) {
                  const rect = chartContainerRef.current.getBoundingClientRect();
                  signalMarkerPrimitiveRef.current.handleClick(e.clientX, e.clientY, rect);
                }
              }}
              onTouchEnd={(e) => {
                // Handle touch on signal marker circles (mobile)
                if (signalMarkerPrimitiveRef.current && chartContainerRef.current && e.changedTouches.length > 0) {
                  const touch = e.changedTouches[0];
                  const rect = chartContainerRef.current.getBoundingClientRect();
                  const wasMarkerTapped = signalMarkerPrimitiveRef.current.handleClick(touch.clientX, touch.clientY, rect);
                  if (wasMarkerTapped) {
                    e.preventDefault(); // Prevent other touch handlers if marker was tapped
                  }
                }
              }}
            />
        {/* The Text / Layered / Inv toggles used to sit here.

            Removed on request: they were three permanently-visible buttons for
            settings that get chosen once and then left alone, sitting in the
            top-right corner of the chart where price is.

            The settings themselves are UNCHANGED and still live. All three are
            restored from localStorage on init (see the signalMarker_* and
            fvgZone_showInvalidatedBadges reads around line 2326), so whatever
            they were last set to is what they stay: text overlay on, collision
            layering off, invalidated badges on.

            To change one now, set its localStorage key and reload:
              signalMarker_showTextOverlay
              signalMarker_collisionEnabled
              fvgZone_showInvalidatedBadges
        */}
        {/* Countdown Timer next to live candle (tracks current price) - only show when market is open or crypto */}
        {countdownYPosition !== null && countdownXPosition !== null && dataMode === 'live' && (isMarketOpen || isCrypto) && (
          <div
            ref={countdownYAxisRef}
            className="countdown-candle"
            style={{ top: `${countdownYPosition}px`, left: `${countdownXPosition}px` }}
          >
            {countdown}
          </div>
        )}
        {/* S/R labels \u2014 one per price cluster, two rows collapsed, everything on click */}
        {srShownOnChart && srLabelGroups.map((group, idx) => (
          <SRLabel
            key={group.key}
            ref={(el) => {
              if (el) srLabelElRefs.current.set(idx, el);
              else srLabelElRefs.current.delete(idx);
            }}
            group={group}
            expanded={group.key === expandedSRKey}
            onToggle={(key) => setExpandedSRKey((current) => toggleSRExpansion(current, key))}
            onHideTimeframe={(tf) => {
              if (hideSRTimeframeWithUndo(tf)) setExpandedSRKey(null);
            }}
          />
        ))}
        {(isLoadingData || isBacktestLoading || connectionStatus === 'connecting' || connectionStatus === 'disconnected') && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">
              {isBacktestLoading
                ? 'Loading backtest data...'
                : isLoadingData
                ? 'Loading data...'
                : connectionStatus === 'connecting'
                ? 'Connecting...'
                : 'Reconnecting...'}
            </div>
          </div>
        )}
        <div className="chart-overlay">
          {/* Symbol + ATR Watermark - Always Visible */}
          {dataMode === 'live' && (
            <div className={`low-atr-watermark ${signalPrediction?.isLowATR ? 'low' : ''}`}>
              <span className="watermark-symbol">{symbol ? symbol.replace(/[A-Z]\d+\.(CME|COMEX|NYMEX|CBOT)$/i, '') : (symbolRegistry[0]?.base || 'MNQ')}</span>
              {signalPrediction?.atr !== undefined ? (
                <>
                  {signalPrediction.isLowATR && 'LOW '}ATR {signalPrediction.atr.toFixed(1)}
                  <span className="low-atr-threshold">(min: {signalPrediction.atrMinThreshold})</span>
                </>
              ) : (
                // .VEST feeds get no engine prediction (WCC off): show the chart's own ATR(14).
                isVestFeedSymbol(symbol) && watermarkAtr(symbol, undefined, currentATR, tickDecimals(vestTickSize(symbol) ?? 0.25))?.text
              )}
            </div>
          )}
          <VestSizingLegend legend={vestSizingLegend} />
          {/*
            Diamonds are on but the engine's scores could not be fetched.

            Always visible, deliberately. The chart has no local scorer to fall
            back on, so this state draws exactly zero diamonds — and both places
            that could otherwise explain it (the Diamond Info overlay and the
            review panel) are off by default. Without this the user sees their
            diamonds silently disappear and nothing anywhere says why, which is
            the "blank and confusing" case rather than the acceptable "blank"
            one.

            Only on `error`. `loading` clears itself in a few hundred
            milliseconds on every symbol switch and a chip flashing that often
            is noise, not information.
          */}
          {candidatesEnabled && candidateHistoryStatus === 'error' && (
            <div className="diamond-history-error">
              no engine diamonds — {candidateHistoryError ?? 'history fetch failed'}
            </div>
          )}
        </div>
        {/* Info Overlays Stack - auto-stacking when overlays are hidden */}
        <InfoOverlayStack>
          {/* Fair Price chip lives IN the stack: it was absolutely positioned
              on its own, so it overlapped the stack and carried a different
              translucency to everything beside it. */}
          {fairPriceEnabled && (
            <InfoOverlayItem overlayId="fairPriceStatus">
              <FairPriceStatusChip symbol={symbol} />
            </InfoOverlayItem>
          )}
          <InfoOverlayItem overlayId="gexCoach">
            <GexInfoOverlay currentPrice={currentPrice} underlying={gexUnderlying} />
          </InfoOverlayItem>
          <InfoOverlayItem overlayId="signalInfo">
            <SignalInfoOverlay />
          </InfoOverlayItem>
          <InfoOverlayItem overlayId="accountStats">
            <AccountStatsOverlay />
          </InfoOverlayItem>
          <InfoOverlayItem overlayId="pinningVolume">
            <PinningVolumeInfoOverlay
              analysis={pinningVolumeAnalysis}
              isLoading={isLoadingPinningAnalysis}
            />
          </InfoOverlayItem>
          <InfoOverlayItem overlayId="wholeCandleContinuation">
            <WholeCandleContinuationInfoOverlay
              analysis={wholeCandleAnalysis}
              isLoading={isLoadingWccAnalysis}
              trailing={wccTrailing}
            />
          </InfoOverlayItem>
          <InfoOverlayItem overlayId="confidence">
            <ConfidenceInfoOverlay
              analysis={wholeCandleAnalysis}
              isLoading={isLoadingWccAnalysis}
            />
          </InfoOverlayItem>
          <InfoOverlayItem overlayId="cmeInfo">
            <CMEInfoOverlay />
          </InfoOverlayItem>
          <InfoOverlayItem overlayId="diamondInfo">
            <DiamondInfoOverlay
              candle={diamondInfoCandle}
              diamond={diamondInfoDiamond}
              rejection={diamondInfoRejection}
              thresholds={candidateThresholds}
              status={candidateHistoryStatus}
              error={candidateHistoryError}
              coverage={candidateHistoryCoverage}
            />
          </InfoOverlayItem>
          <InfoOverlayItem overlayId="fairPriceCoach">
            <FairPriceCoachOverlay
              symbol={symbol}
              price={currentPrice > 0 ? currentPrice : null}
              normalizeToBase={normalizeToBase}
            />
          </InfoOverlayItem>
          {/* Takes no props: it reads the engine diamonds already in the store
              and aggregates the `simulated` block the engine attaches to each.
              Nothing to thread through, and no second implementation of the
              walk that decides a win from a loss. */}
          <InfoOverlayItem overlayId="diamondStats">
            <DiamondStatsOverlay />
          </InfoOverlayItem>
          {/* Its own item, not nested under diamondStats. Sharing that id made
              it un-toggleable and welded it to the scoreboard's panel — they
              answer different questions and are read at different times. */}
          <InfoOverlayItem overlayId="diamondSignalExcursion">
            <DiamondSignalExcursionOverlay />
          </InfoOverlayItem>
        </InfoOverlayStack>
        <PricePaneClip bottom={linePaneTop}>
        {/* Long-press preview line for mobile order placement */}
        {longPressPreview && (
          <div
            className="long-press-preview-line"
            style={{
              position: 'absolute',
              left: 0,
              right: '80px',
              top: `${longPressPreview.y}px`,
              height: '2px',
              pointerEvents: 'none',
              zIndex: 100,
            }}
          >
            {/* Visible line spans the wrapper */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                height: '2px',
                background: 'linear-gradient(90deg, rgba(255,193,7,0.8), rgba(255,193,7,0.4))',
                pointerEvents: 'none',
                animation: 'pulse-glow 0.5s ease-in-out infinite alternate',
              }}
            />
            {/* Cluster: price label (drag handle) + action buttons. Anchored above the line. */}
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '-13px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                pointerEvents: 'auto',
              }}
            >
              {/* Price label is the explicit drag handle */}
              <span
                style={{
                  background: 'rgba(255,193,7,0.95)',
                  color: '#000',
                  padding: '4px 9px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  cursor: isDraggingPreview ? 'grabbing' : 'grab',
                  touchAction: 'none',
                  userSelect: 'none',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                  border: '1px solid rgba(0,0,0,0.2)',
                }}
                title="Drag to change price"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsDraggingPreview(true);
                }}
                onTouchStart={() => {
                  setIsDraggingPreview(true);
                }}
              >
                {longPressPreview.price.toFixed(2)}
              </span>
              <button
                onClick={() => {
                  const id = `order-line-${Date.now()}`;
                  setOrderLines(prev => [...prev, {
                    id,
                    price: longPressPreview.price,
                    yPosition: longPressPreview.y
                  }]);
                  setLongPressPreview(null);
                }}
                style={{
                  background: '#4caf50',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '3px 7px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  minWidth: '28px',
                }}
                title="Add order line"
              >
                +
              </button>
              <button
                onClick={() => {
                  const price = longPressPreview.price;
                  const alertType = price >= currentPrice ? 'price_above' : 'price_below';
                  createAlert({
                    symbol,
                    alertType,
                    price,
                    triggerMode: 'once_per_minute',
                  }).then((newAlert) => {
                    if (newAlert) {
                      addToast('success', `Alert: ${alertType === 'price_above' ? 'Above' : 'Below'} ${price.toFixed(2)}`);
                    }
                  });
                  setLongPressPreview(null);
                }}
                style={{
                  background: '#ff9800',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '3px 7px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  minWidth: '28px',
                }}
                title="Create price alert"
              >
                🔔
              </button>
              <button
                onClick={() => setLongPressPreview(null)}
                style={{
                  background: '#f44336',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '3px 7px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  minWidth: '28px',
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}
        </PricePaneClip>
        {/* News marker tooltip */}
        {newsTooltip && (
          <div
            className="news-marker-tooltip"
            style={{
              position: 'fixed',
              left: `${newsTooltip.x}px`,
              top: `${newsTooltip.y - 10}px`,
              transform: 'translate(-50%, -100%)',
              background: 'rgba(12, 14, 24, 0.95)',
              border: '1px solid rgba(251, 191, 36, 0.5)',
              borderRadius: '8px',
              padding: '10px 14px',
              maxWidth: '300px',
              zIndex: 9999,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(8px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '16px' }}>⚡</span>
              <span style={{ color: '#fbbf24', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Breaking News</span>
            </div>
            <div style={{ color: '#fff', fontSize: '12px', lineHeight: '1.4' }}>
              {newsTooltip.headline}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', marginTop: '6px' }}>
              ESC or tap outside to dismiss
            </div>
          </div>
        )}
        {/* Event marker tooltip */}
        {eventTooltip && (
          <div
            className="event-marker-tooltip"
            style={{
              position: 'fixed',
              left: `${eventTooltip.x}px`,
              top: `${eventTooltip.y - 10}px`,
              transform: 'translate(-50%, -100%)',
              background: 'rgba(12, 14, 24, 0.95)',
              border: '1px solid rgba(251, 191, 36, 0.5)',
              borderRadius: '8px',
              padding: '10px 14px',
              maxWidth: '300px',
              zIndex: 9999,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(8px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '16px' }}>⭐</span>
              <span style={{ color: '#fbbf24', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>{eventTooltip.country}</span>
            </div>
            <div style={{ color: '#fff', fontSize: '12px', lineHeight: '1.4', fontWeight: 500 }}>
              {eventTooltip.event}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', marginTop: '4px' }}>
              {eventTooltip.time}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', marginTop: '6px' }}>
              ESC or tap outside to dismiss
            </div>
          </div>
        )}
        {/* Chart Overlays */}
        <ChartOverlays
          chartContainerRef={chartContainerRef}
          candlestickSeriesRef={candlestickSeriesRef}
          fibLevels={fibLevels}
          chartWidth={chartContainerRef.current?.clientWidth || 800}
          orderLines={orderLines}
          currentPrice={currentPrice}
          onPlaceOrder={handlePlaceOrder}
          onRemoveOrderLine={handleRemoveOrderLine}
          onDragOrderLine={handleDragOrderLine}
          useOCO={chartUseOCO}
          ocoTPOffset={chartOcoTPOffset}
          ocoSLOffset={chartOcoSLOffset}
          livePositionLineY={livePositionLineY}
          currentLivePosition={currentLivePosition}
          livePositionKey={livePositionKey}
          onFlattenPosition={async () => {
            const { flattenPosition } = useBrokerStore.getState();
            const posInfo = currentLivePosition ? ` (was ${currentLivePosition.netPos > 0 ? '+' : ''}${currentLivePosition.netPos})` : '';
            const result = await flattenPosition(currentLivePosition?.symbol || symbol);
            if (result.success) {
              addToast('success', `Position closed${posInfo}`);
            } else {
              addToast('error', `Flatten failed: ${result.error}`);
            }
          }}
          liveOrders={filteredLiveOrders}
          selectedAccountId={selectedAccountId}
          symbol={symbol}
          onModifyOrder={modifyOrder}
          onModifyOrderQuantity={(orderId, newQuantity) => {
            const { modifyOrderQuantity } = useBrokerStore.getState();
            modifyOrderQuantity(orderId, newQuantity);
          }}
          onReplaceBracket={async (params) => {
            const { replaceBracket } = useBrokerStore.getState();
            return replaceBracket(params);
          }}
          onPartialExit={(exitQty) => {
            if (!currentLivePosition) return;
            const isLong = currentLivePosition.netPos > 0;
            placeOrder({
              action: isLong ? 'Sell' : 'Buy',
              symbol: currentLivePosition?.symbol || symbol,
              quantity: exitQty,
              orderType: 'Market',
              brokerId: currentLivePosition?.brokerId,
              accountId: currentLivePosition?.accountId,
            });
          }}
          onAddTP={() => startDraftBracket('TP')}
          onAddSL={() => startDraftBracket('SL')}
          liveOrderLinePositions={liveOrderLinePositions}
          liveOrderLabelOffsets={liveOrderLabelOffsets}
          coordinateToPrice={coordinateToPrice}
          onCancelOrder={cancelOrder}
          onSplitOrder={async (order, qty1, qty2, price1, price2) => {
            const { splitOrder } = useBrokerStore.getState();
            const result = await splitOrder({
              orderId: order.id,
              qty1,
              qty2,
              price1,
              price2,
              brokerId: order.brokerId,
            });
            if (result.success) {
              addToast('success', `Split order: ${qty1} @ ${price1.toFixed(2)}, ${qty2} @ ${price2.toFixed(2)}`);
            }
          }}
          showTradingOverlays={showTradingOverlays}
          showPositionControls
          showSignalPredictor={!spIsMinimized}
          dcaMode={dcaMode}
          onDcaToggle={() => {
            const next = !dcaMode;
            setDcaMode(next);
            if (!next) {
              setDcaTarget(null);
              setDcaTargetY(null);
              setDcaTargetPrice(null);
              setDcaPinned(false);
            }
          }}
          fibLevelsOverlayRef={fibLevelsOverlayRef}
          livePositionLineRef={livePositionLineRef}
          orderLineRefsMap={orderLineRefsMap}
          liveOrderLineRefsMap={liveOrderLineRefsMap}
          priceClipBottom={linePaneTop}
        />
        <PaneEdgeBadges top={linePaneTop} items={paneEdgeItems} />
        <QueueTiltBand
          getChart={getChartForBand}
          symbol={symbol}
          enabled={queueTiltOn}
          colors={levelColors.flowQueueTilt}
          getSeries={getSeriesForBand}
          tfMinutes={TIMEFRAME_MINUTES[selectedTimeframe]}
          compact={isMobile}
        />
        {/* Draft TP/SL bracket — draggable PENDING line; commit via ✓ */}
        <PricePaneClip bottom={linePaneTop}>
        {draftBracket && currentLivePosition && currentLivePosition.netPos !== 0 && draftBracketY !== null && (() => {
          const isLong = currentLivePosition.netPos > 0;
          const sym = currentLivePosition.symbol || symbol;
          const orderType: PendingOrder['type'] = draftBracket.kind === 'TP'
            ? (isLong ? 'SELL_LIMIT' : 'BUY_LIMIT')
            : (isLong ? 'SELL_STOP' : 'BUY_STOP');
          const draftOrder: PendingOrder = {
            id: 'draft-bracket',
            action: isLong ? 'SELL' : 'BUY',
            type: orderType,
            quantity: Math.abs(currentLivePosition.netPos),
            price: draftBracket.price,
            strategyName: 'manual',
            reason: `Draft ${draftBracket.kind}`,
            timestamp: Date.now(),
            symbol: sym,
          };
          return (
            <PendingOrderLine
              order={draftOrder}
              position={{
                avgPrice: currentLivePosition.netPrice,
                quantity: Math.abs(currentLivePosition.netPos),
                symbol: sym,
              }}
              yPosition={draftBracketY}
              onDragPrice={(newPrice) =>
                setDraftBracket(d => d && ({ ...d, price: roundToTick(newPrice, sym) }))
              }
              onCoordinateToPrice={coordinateToPrice}
              onUpdateOrder={(_id, newPrice) =>
                setDraftBracket(d => d && ({ ...d, price: roundToTick(newPrice, sym) }))
              }
              onConfirm={confirmDraftBracket}
              onCancel={() => setDraftBracket(null)}
              submitting={draftBracketSubmitting}
            />
          );
        })()}
        </PricePaneClip>
        {/* #639: Vest TP / SL drag pills - .VEST charts only (renders nothing elsewhere) */}
        {isVestFeedSymbol(symbol) && (
          <PricePaneClip bottom={linePaneTop}>
            <VestTpslHandles seriesRef={candlestickSeriesRef} containerRef={chartContainerRef} symbol={symbol} lastPrice={currentPrice} />
          </PricePaneClip>
        )}
        {/* Donchian Channel Overlay — 20/55-bar channels as line series on the chart */}
        {chartRef.current && (
          <DonchianOverlay
            chart={chartRef.current}
            candles={donchianCandles}
            visible={levelVisibility.donchian}
            entryLookback={donchianEntryLookback}
          />
        )}
        {/* Alert Lines */}
        <PricePaneClip bottom={linePaneTop}>
        {symbolAlerts.map((alert) => {
          const yPosition = alertLinePositions.get(alert.id);
          // Skip volume alerts - they render in volume pane
          if (yPosition == null || alert.alertType === 'volume_spike' || alert.alertType === 'volume_above') return null;
          return (
            <AlertLine
              key={alert.id}
              alert={alert}
              yPosition={yPosition}
              symbol={symbol}
              onCoordinateToPrice={coordinateToPrice}
              onUpdate={(id, price) => {
                const alertType = price >= currentPrice ? 'price_above' : 'price_below';
                updateAlert(id, { price, alertType });
              }}
              onDelete={deleteAlert}
              onOpenSettings={openSettingsModal}
            />
          );
        })}
        </PricePaneClip>
        {/* Vest NQ-PERP candle overlay toggle + current gap (MNQ/NQ only). */}
        {isVestSymbol && <VestCandleToggle gap={showVestCandles ? vestCandleGap : null} />}
        {/* MES (ES) candle overlay toggle + current ratio, one row above (MNQ/NQ only). */}
        {isVestSymbol && <MesCandleToggle ratio={showMesCandles ? mesCandleRatio : null} />}
        {/* Go to oldest button - twin of go-to-latest, mirrored to the left edge.
            Deliberately NOT gated on dataMode: unlike scrollToRealTime there is no
            live edge involved, so jumping to the start of loaded data is just as
            useful in backtest as it is live. setVisibleLogicalRange (rather than
            scrollToPosition, which is measured from the LATEST bar) because
            logical index 0 is the oldest loaded bar, so the target is exact and
            independent of how many candles are loaded. */}
        {candles.length > 0 && !atOldestLoadedBar && (
          <button
            className="go-to-oldest-btn"
            onClick={() => {
              const timeScale = chartRef.current?.timeScale();
              if (!timeScale) return;
              timeScale.setVisibleLogicalRange(oldestLogicalRange(timeScale.getVisibleLogicalRange()));
            }}
            title="Go to oldest loaded bar"
          >
            <ChevronsLeft size={isMobile ? 16 : 20} />
          </button>
        )}
        {/* Go to date - opened by long-pressing the time axis. Third member of
            the jump-somewhere family alongside the two buttons above, and the
            only one that can reach a date that was never loaded. Fixed-position
            and anchored to the press point, so it renders here purely for
            proximity to its siblings. */}
        {goToDateAnchor && (
          <GoToDatePopup
            symbol={symbol}
            anchor={goToDateAnchor}
            focusMs={goToDateAnchor.focusMs}
            loadedSpan={goToDateLoadedSpan}
            fetchState={goToDateFetch}
            onPick={handleGoToDatePick}
            onClose={closeGoToDate}
          />
        )}
        {/* Go to latest button - visible when scrolled away from live edge */}
        {dataMode === 'live' && !srAtLiveEdge && (
          <button
            className="go-to-latest-btn"
            onClick={() => chartRef.current?.timeScale().scrollToRealTime()}
            title="Go to latest"
          >
            <ChevronsRight size={isMobile ? 16 : 20} />
          </button>
        )}
        <PricePaneClip bottom={linePaneTop}>
        {/* DCA calculator overlay */}
        {dcaMode && dcaDisplayY != null && dcaTarget && dcaTarget.contracts > 0 && (
          <div
            className={`dca-overlay ${dcaPinned ? 'dca-pinned' : ''}`}
            style={{
              position: 'absolute',
              top: dcaDisplayY,
              left: 0,
              right: 60,
              zIndex: 200,
              pointerEvents: dcaPinned ? 'auto' : 'none',
            }}
            onMouseDown={dcaPinned ? (e) => {
              e.preventDefault();
              const startY = e.clientY;
              const startTop = dcaDisplayY!;
              const onMove = (me: MouseEvent) => {
                const newY = startTop + (me.clientY - startY);
                const price = candlestickSeriesRef.current?.coordinateToPrice(newY);
                if (price != null) {
                  const sym = symbolRef.current;
                  const snapped = roundToTick(price as number, sym);
                  const storeState = useBrokerStore.getState();
                  const activeBroker = storeState.brokers.find(b => b.id === storeState.activeBrokerId);
                  const positions: BrokerPosition[] = activeBroker?.positions ?? [];
                  const chartBase = normalizeToBase(sym);
                  const pos = positions.find(p => p.symbol && normalizeToBase(p.symbol) === chartBase && p.netPos !== 0);
                  const mktPrice = useMarketStore.getState().currentPrice;
                  if (pos && mktPrice > 0) {
                    const q1 = Math.abs(pos.netPos);
                    const pAvg = pos.netPrice;
                    const q2 = calculateDCAQuantity(pAvg, q1, snapped, mktPrice, pos.netPos > 0);
                    if (q2 > 0) {
                      const newAvg = (q1 * pAvg + q2 * mktPrice) / (q1 + q2);
                      setDcaTarget({ price: snapped, contracts: q2, newAvg, currentPos: q1, avgPrice: pAvg });
                    } else {
                      setDcaTarget({ price: snapped, contracts: 0, newAvg: 0, currentPos: 0, avgPrice: 0 });
                    }
                  }
                  setDcaTargetY(newY);
                  setDcaTargetPrice(snapped);
                }
              };
              const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
              };
              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onUp);
            } : undefined}
          >
            <div style={{ borderTop: '2px dashed #00bcd4', width: '100%' }} />
            <div className="dca-info-box">
              +{dcaTarget.contracts} to avg {dcaTarget.price.toFixed(2)}
              <button
                className="dca-sync-btn"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  updatePreferenceDirect('tradingPanel', { quantity: dcaTarget!.contracts });
                  window.dispatchEvent(new CustomEvent('dca-sync-quantity', { detail: { quantity: dcaTarget!.contracts } }));
                }}
              >
                Sync
              </button>
            </div>
          </div>
        )}
        {dcaMode && dcaDisplayY != null && dcaTarget && dcaTarget.contracts <= 0 && (
          <div
            className={`dca-overlay ${dcaPinned ? 'dca-pinned' : ''}`}
            style={{
              position: 'absolute',
              top: dcaDisplayY,
              left: 0,
              right: 60,
              zIndex: 200,
              pointerEvents: 'none',
            }}
          >
            <div style={{ borderTop: '2px dashed #ef5350', width: '100%' }} />
            <div className="dca-info-box dca-not-achievable">
              Target not achievable at current price
            </div>
          </div>
        )}
        </PricePaneClip>

          </div>
          {/* Volume Pane Resize Handle */}
          <div
            className={`volume-pane-resize-handle ${isResizingVolumePane ? 'resizing' : ''}`}
            onMouseDown={handleVolumePaneResizeStart}
            onTouchStart={handleVolumePaneResizeStart}
          />
          {/* Volume Pane - separate chart with synced time axis */}
          <div
            className={`chart-volume-pane ${placementMode ? 'placement-mode' : ''}`}
            style={{ height: volumePaneHeight }}
          >
            <div className="volume-chart-container" ref={volumeChartContainerRef} />
            {/* Volume pane label */}
            <VolumePaneLegend
              symbol={symbol}
              candles={aggregatedCandles as unknown as { time: number; close: number; volume?: number }[]}
              hoveredTime={hoveredCandle?.time ?? null}
              timeframeMinutes={TIMEFRAME_MINUTES[selectedTimeframe]}
              vestOn={showVestVolume}
              cmeRel={showCmeVolSpikes ? cmeRel : null}
              vestRelOn={showVestVolSpikes}
            />
            {/* Volume long-press preview for mobile alert creation */}
            {volumeLongPressPreview && (
              <div
                className="volume-long-press-preview"
                style={{
                  position: 'absolute',
                  left: 0,
                  right: '60px',
                  top: `${volumeLongPressPreview.y}px`,
                  height: '2px',
                  background: 'linear-gradient(90deg, rgba(168, 85, 247, 0.8), rgba(168, 85, 247, 0.4))',
                  pointerEvents: 'none',
                  zIndex: 100,
                  animation: 'pulse-glow 0.5s ease-in-out infinite alternate',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '-16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    pointerEvents: 'auto',
                  }}
                >
                  <span
                    style={{
                      background: 'rgba(168, 85, 247, 0.9)',
                      color: '#fff',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDisplayVolume(symbol, volumeLongPressPreview.volume)}
                  </span>
                  <button
                    onClick={() => {
                      const volume = volumeLongPressPreview.volume;

                      createAlert({
                        symbol,
                        alertType: 'volume_above',
                        // the pane shows display units; the engine compares raw volume
                        volumeThreshold: volume * volumeDivisorFor(symbol),
                        triggerMode: 'once_per_bar',
                      }).then((newAlert) => {
                        if (newAlert) {
                          addToast('success', `Volume alert: ${formatDisplayVolume(symbol, volume)}`);
                        }
                      });
                      setVolumeLongPressPreview(null);
                    }}
                    style={{
                      background: '#a855f7',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 10px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      minWidth: '36px',
                    }}
                    title="Create volume alert"
                  >
                    🔔
                  </button>
                  <button
                    onClick={() => setVolumeLongPressPreview(null)}
                    style={{
                      background: '#f44336',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 10px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      minWidth: '36px',
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
            {/* Volume Alert Lines - only volume_above (absolute threshold) alerts */}
            {symbolAlerts.map((alert) => {
              // Only render volume_above alerts (absolute threshold) on volume pane
              if (alert.alertType !== 'volume_above') return null;
              const yPosition = volumeAlertLinePositions.get(alert.id);
              if (yPosition == null) return null;
              return (
                <AlertLine
                  key={alert.id}
                  alert={alert}
                  yPosition={yPosition}
                  symbol={symbol}
                  onCoordinateToPrice={() => null} // Volume alerts are not draggable
                  onUpdate={() => {}} // No drag update for volume alerts
                  onDelete={deleteAlert}
                  onOpenSettings={openSettingsModal}
                />
              );
            })}
          </div>
        </div>
      </div>
      </div>{/* closes chart-main */}
      </div>{/* closes chart-content-row */}
      {/* Mobile Trading Panel - desktop uses ActionBarTrading in action bar */}
      {isTradingPanelVisible && isMobile && (
        <ErrorBoundary
          name="TradingPanel"
          fallback={<PanelErrorFallback error={null} onRetry={() => window.location.reload()} panelName="Trading Panel" />}
        >
          <TradingPanel />
        </ErrorBoundary>
      )}
      <ErrorBoundary
        name="LiveLog"
        fallback={<PanelErrorFallback error={null} onRetry={() => window.location.reload()} panelName="Live Log" />}
      >
        <LiveLog />
      </ErrorBoundary>

      {dataMode === 'backtest' && !isBacktestLoading && (
        <BacktestControls onClose={() => {
          // Stop playback and return to live mode
          const player = getBacktestPlayer();
          player.stop();
          handleLiveModeClick();
        }} />
      )}

      {/* Global Settings Modal */}
      <GlobalSettingsModal
        isOpen={showStrategySettings}
        onClose={() => { setShowStrategySettings(false); setStrategySettingsTab(undefined); }}
        initialSection="strategy"
        initialTab={strategySettingsTab}
        initialSymbol={symbol}
      />

      {/* Broker Management Modal */}
      <BrokerManagementModal
        isOpen={showBrokerModal}
        onClose={() => setShowBrokerModal(false)}
        onOpenAccountStats={() => setShowAccountStatsModal(true)}
      />

      {/* Copy Trading Settings Modal */}
      <CopyTradingSettingsModal
        isOpen={showCopyTradingModal}
        onClose={() => setShowCopyTradingModal(false)}
      />

      {/* Fair Price Settings Modal */}
      <FairPriceSettingsModal
        isOpen={showFairPriceSettings}
        onClose={() => setShowFairPriceSettings(false)}
      />

      {/* Floating order panel. Always mounted on desktop — it carries order
          entry and the arm control, so it is not something to go hunting for.
          The component itself returns null on mobile, where the bottom trading
          bar covers the same ground. */}
      <FloatingOrderPanel
        symbol={symbol}
        currentPrice={currentPrice}
        intervalMinutes={TIMEFRAME_MINUTES[selectedTimeframe]}
      />

      {/* Diamonds review panel */}
      {candidatesEnabled && candidatePanelOpen && (
        <CandidateReviewPanel
          candidates={candidateReviewSet}
          autoTags={candidateAutoTags}
          onClose={() => useCandidateStore.getState().setPanelOpen(false)}
          onJumpTo={(ts) => {
            // Centre the bar with more history to its left than right, which is
            // how you actually read a setup: context before, outcome after.
            const chart = chartRef.current;
            if (!chart) return;
            const ts_s = Math.floor(ts / 1000);
            const barSec = TIMEFRAME_MINUTES[selectedTimeframe] * 60;
            try {
              chart.timeScale().setVisibleRange({
                from: (ts_s - barSec * 45) as Time,
                to: (ts_s + barSec * 20) as Time,
              });
            } catch {
              // Range can be rejected if the bar is outside loaded data; the
              // selection ring still tells you which one is active.
            }
          }}
        />
      )}

      {/* Inline labeller, opened by clicking a candidate diamond */}
      {candidatesEnabled && quickLabel && (() => {
        const c = candidateReviewSet.find((x) => x.id === quickLabel.id);
        if (!c) return null;
        const rect = chartContainerRef.current?.getBoundingClientRect();
        return (
          <CandidateQuickLabel
            candidate={c}
            autoTag={candidateAutoTags[c.id] ?? null}
            x={quickLabel.x}
            y={quickLabel.y}
            containerWidth={rect?.width ?? 1200}
            containerHeight={rect?.height ?? 600}
            onClose={() => setQuickLabel(null)}
          />
        );
      })()}

      {/* Read-only fill details, opened by clicking an entry or exit marker */}
      {tradeInfo && (() => {
        // A poll can replace the marker set while this is open; if the fill it
        // described is gone, so is the popup.
        const m = tradeMarkers.find((x) => x.id === tradeInfo.id);
        if (!m) return null;
        const rect = chartContainerRef.current?.getBoundingClientRect();
        return (
          <TradeMarkerInfoOverlay
            marker={m}
            x={tradeInfo.x}
            y={tradeInfo.y}
            containerWidth={rect?.width ?? 1200}
            containerHeight={rect?.height ?? 600}
            onClose={() => setTradeInfo(null)}
          />
        );
      })()}

      {keyLevelHover && (() => {
        const rect = chartContainerRef.current?.getBoundingClientRect();
        return (
          <KeyLevelTooltip
            {...keyLevelHover}
            containerWidth={rect?.width ?? 1200}
            containerHeight={rect?.height ?? 600}
          />
        );
      })()}

      {anchorHover && (() => {
        const rect = chartContainerRef.current?.getBoundingClientRect();
        return (
          <FairPriceAnchorTooltip
            kind={anchorHover.kind}
            x={anchorHover.x}
            y={anchorHover.y}
            containerWidth={rect?.width ?? 1200}
            containerHeight={rect?.height ?? 600}
          />
        );
      })()}

      {/* Displacement Settings Modal */}
      <DisplacementSettingsModal
        isOpen={showDisplacementSettings}
        onClose={() => setShowDisplacementSettings(false)}
      />

      {/* Account Stats Modal */}
      <AccountStatsModal
        isOpen={showAccountStatsModal}
        onClose={() => setShowAccountStatsModal(false)}
      />

      {/* Trade Analyzer Modal */}
      <TradeAnalyzerModal
        isOpen={showTradeAnalyzer}
        onClose={() => setShowTradeAnalyzer(false)}
      />

      {/* Trade Analytics Modal */}
      <TradeAnalyticsModal
        isOpen={showTradeAnalytics}
        onClose={() => setShowTradeAnalytics(false)}
      />

      {/* AUTO-BOT Panel */}
      {showAutoBotPanel && (
        <AutoBotPanel
          visible={showAutoBotPanel}
          onClose={() => setShowAutoBotPanel(false)}
        />
      )}

      {/* Trade Log Panel removed */}

      {showDateRangeSelector && (
        <DateRangeSelector
          onApply={handleDateRangeApply}
          onCancel={handleDateRangeCancel}
        />
      )}

      {contextMenu && dataMode === 'live' && (
        <div
          className="chart-context-menu"
          style={{
            position: 'fixed',
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
            zIndex: 10000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="context-menu-item"
            onClick={handleBacktestFromCandle}
          >
            Start Backtest from Here
          </button>
        </div>
      )}

      {/* TAKE IT Banner - shows when high confidence + confluence signal detected */}
      <TakeItBanner
        isVisible={takeItBanner.visible}
        direction={takeItBanner.direction}
        confidence={takeItBanner.confidence}
        confluence={takeItBanner.confluence}
        symbol={takeItBanner.symbol}
        onDismiss={() => setTakeItBanner(prev => ({ ...prev, visible: false }))}
        onSymbolClick={(sym) => {
          // Extract base symbol (e.g., MNQH6.CME -> MNQ)
          const match = sym.match(/^(MNQ|NQ|MES|ES|M?CL|M?GC|M?SI)/i);
          if (match && onSymbolChange) {
            const selectedBase = match[1].toUpperCase();
            // Look up full symbol from registry (includes contract month and exchange)
            const regSymbol = symbolRegistry.find(s => s.base.toUpperCase() === selectedBase);
            if (regSymbol) {
              onSymbolChange(regSymbol.fullSymbol);
            } else {
              // Fallback: use the symbol as-is
              onSymbolChange(sym);
            }
            setTakeItBanner(prev => ({ ...prev, visible: false }));
          }
        }}
        autoDismissMs={10000}
      />

      {/* Alerts Panel */}
      {showAlertsPanel && (
        <AlertsPanel onClose={closeAlertsPanel} />
      )}

      {/* Alert Settings Modal */}
      {showSettingsModal && editingAlert && (
        <AlertSettingsModal
          alert={editingAlert}
          onClose={closeSettingsModal}
        />
      )}
      </div>
    </div>
  );
};

export default ChartView;
